import { EXCHANGE_ROUTES, exchangeMaximum, exchangeQuote, exchangeAmount, exchangeThemePayout } from './exchange-quote.js';
import { createUniversePicker } from './universe-picker.js';
import { changePanels } from './panel-motion.js';
import { warmPackOpening, playPackOpening } from './pack-opening.js';
import { createClanFund } from './clan-fund.js';
import { createFirstVisit } from './first-visit.js';
import { createHoldings } from './holdings.js';
import { createTutorialSystem } from './tutorials.js';
import { playerJourney } from './player-journey.js';
import { createPlayerMarket } from './player-market.js';
import { clanLandingTab, validWarTarget, clanMembers, warDefenseCards } from './clan-presentation.js';
import { playArenaCombatEvents, cancelArenaCombatMotion } from './arena-combat-motion.js';
import { DEFAULT_THEME_FILTERS, filterThemes, normalizeThemeFilters } from './theme-filters.js';
import { renderThemeHub } from './theme-hub.js';
import { createThemePreview } from './theme-preview.js';
import { createActionGuard } from './action-guard.js';
import { createBoundedQueue, createPerformanceCache, stableDataSignature } from './performance-cache.js';
import { createTelegramDeviceCache } from './telegram-device-cache.js';
import { createTelegramStorage } from './telegram-storage.js';
import { createArenaDeviceStore } from './arena-device-store.js';
import { chooseDeckCard, cardsAvailableForDeck, selectWorkbenchCards, useMinimalArenaEffects, patchArenaMarkup } from './arena-workbench.js';
import { ARENA_TUTORIAL_SLIDES, ARENA_TUTORIAL_STEPS, createGuidedTour, createArenaTutorialSeenStore } from './arena-tutorial.js';
import {
    normalizeRequestError,
    requestRetryDelay,
    shouldRetryRequest,
} from './request-resilience.js';
import {
    ARENA_RECOVERY_LABELS,
    arenaDeckChanged,
    arenaDeckCardCount,
    autoSelectArenaCards,
    formatRecovery,
    isArenaDeckComplete,
    normalizeArenaDecks,
    normalizeArenaSlots,
    recoveryRemaining,
    slotsFromCards,
} from './arena-decks.js';

'use strict';
// ── Config ──────────────────────────────────────────────
const API_BASE = ['127.0.0.1', 'localhost'].includes(location.hostname)
    ? location.origin
    : 'https://reversecard.duckdns.org';

// ── Telegram WebApp ───────────────────────────────────
const tg = window.Telegram?.WebApp;
const TGuser = tg?.initDataUnsafe?.user || null;
const IS_TG = !!(TGuser?.id);
const telegramStorage = createTelegramStorage({ webApp: tg });
const arenaDeviceStore = createArenaDeviceStore({ storage: telegramStorage.device });

function syncArenaViewportHeight() {
    const height = Number(tg?.viewportStableHeight) || Number(window.visualViewport?.height) || window.innerHeight;
    if (height > 0) document.documentElement.style.setProperty('--arena-viewport-height', `${Math.round(height)}px`);
}
syncArenaViewportHeight();
tg?.onEvent?.('viewportChanged', syncArenaViewportHeight);
window.addEventListener('resize', syncArenaViewportHeight, { passive: true });
window.visualViewport?.addEventListener('resize', syncArenaViewportHeight, { passive: true });

// Blur inputs on click outside to hide keyboard
document.addEventListener('click', (e) => {
    if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'BUTTON') {
        const active = document.activeElement;
        if (active && active.tagName === 'INPUT') active.blur();
    }
});

function currentTheme() {
    return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}
function applyTelegramThemeColors() {
    if (!tg) return;
    const light = currentTheme() === 'light';
    const header = light ? '#EFEDE9' : '#101215';
    const bg = light ? '#F4F2EF' : '#0B0C0E';
    try { tg.setHeaderColor(header); } catch { }
    try { tg.setBackgroundColor(bg); } catch { }
}
function toggleTheme() {
    const next = currentTheme() === 'light' ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', next);
    void telegramStorage.device.set('rv_theme_v2', next);
    applyTelegramThemeColors();
    haptic('light');
}
window.toggleTheme = toggleTheme;

if (tg && IS_TG) { tg.expand(); tg.ready(); applyTelegramThemeColors(); }

// ── Session ───────────────────────────────────────────
const SK = 'rv_uid';
let sessionUid = TGuser?.id ? String(TGuser.id) : null;
function getSession() { return sessionUid; }
function saveSession(uid) {
    sessionUid = uid ? String(uid) : null;
    if (sessionUid) void telegramStorage.secure.set(SK, sessionUid);
    else void telegramStorage.secure.remove(SK);
}

async function restoreLocalPreferences() {
    const [storedTheme, storedUid] = await Promise.all([
        telegramStorage.device.get('rv_theme_v2'),
        sessionUid ? Promise.resolve(null) : telegramStorage.secure.get(SK),
    ]);
    if (storedTheme === 'light' || storedTheme === 'dark') {
        document.documentElement.setAttribute('data-theme', storedTheme);
        applyTelegramThemeColors();
    }
    if (!sessionUid && storedUid) sessionUid = String(storedUid);
}

// ── State ─────────────────────────────────────────────
const S = { arenaReplaceSlot: null as number | null, arenaPreparingFight: false, arenaCardQuery: '', arenaCardSort: 'power', arenaLeaderboardOpen: false, user: null, cards: [], team: [], tasks: [], leaders: [], myRank: null, clan: null, filter: 'all', lbType: 'arena', tab: 'profile', profileReturnTab: 'arena', profileReturnScroll: 0, myFavoriteIds: null, publicProfile: null, profileDetailsLoading: '', page: 1, arenaPoll: null, arenaLobby: null, arenaActionBusy: false, arenaDecks: [], arenaActiveDeckId: '1', arenaEditingDeckId: '1', arenaDraftSlots: {}, arenaDrafts: {}, arenaDeckNotice: '', arenaCardFilter: 'all', arenaCardRenderLimit: 6, arenaRecoveryLoadedAt: 0, arena: { screen: 'lobby', opponent: null, battle: null, selectedAttackerId: null, selectedTargetId: null, replay: null }, themes: { market: [], mine: null, shopSubTab: 'market', loading: false, sort: 'popular', typeFilter: 'all', ownedFilter: 'all', query: '', error: '', notice: '' }, ref: null };

const LIVE_REFRESH_MS = 15000;
let liveRefreshTimer = null;
let liveRefreshEpoch = 0;
let arenaMutationEpoch = 0;
let loaderWatchdog = null;
let bootShellTimer = null;
let arenaSearchController = null;
let arenaLoadingTimer = null;
let arenaLoadingWatchdog = null;
let arenaLoadingAssetWarmed = false;
let arenaCardStreamObserver = null;
let arenaStatsLoadId = 0;
let arenaStateFetchedAt = 0;
let arenaRestoringBattle = false;
const refreshRuns = new Map();
const stateSignatures = new Map();
const apiDataCache = createPerformanceCache({ namespace: 'reverse-data-v3', maxEntries: 64 });
const telegramDeviceCache = createTelegramDeviceCache({ webApp: tg });
const mediaWarmupQueue = createBoundedQueue({ concurrency: 3 });
let deviceSnapshotTimer = null;
const themeActionGuard = createActionGuard();
const arenaDeckActionGuard = createActionGuard();
const webappActionGuard = createActionGuard();
const arenaFindActionGuard = createActionGuard();
const arenaBattleActionGuard = createActionGuard();
const arenaTutorialSeenStore = createArenaTutorialSeenStore({ webApp: tg });
const finishedBattleIds = new Set();
let arenaTutorialIndex = 0;
let arenaTutorialChecked = false;
setInterval(() => {
    if (document.hidden || !isArenaWorkbenchVisible()) return;
    let expired = false;
    document.querySelectorAll('[data-recovery-id]').forEach(node => {
        const remaining = arenaCardRecovery(node.getAttribute('data-recovery-id'));
        node.textContent = formatRecovery(remaining);
        if (!remaining) expired = true;
    });
    if (expired) renderTeam();
}, 1000);

const DEFAULT_HUB_COLLAGE = 'assets/hub/collage.jpg';


/** Демо/превью тема для hub-id — https://reversecard.duckdns.org/api/theme_media/70eb6e3fdd584ef7.jpeg */
const PREVIEW_HUB_THEME = {
    id: '70eb6e3fdd584ef7',
    title: 'Fight Club',
    author_name: 'Demo',
    media_url: '/api/theme_media/70eb6e3fdd584ef7.jpeg',
    media_type: 'photo',
    price: 500,
    status: 'approved',
};

// ── DOM ───────────────────────────────────────────────
const $ = id => document.getElementById(id);
const setText = (id, v) => { const e = $(id); if (e) e.textContent = v ?? '—'; };

// ── Init ──────────────────────────────────────────────
window.addEventListener('DOMContentLoaded', async () => {
    initNav(); initFilters();
    registerPerformanceWorker();
    // The visible Mini App shell never waits on DeviceStorage, network or a
    // stale service worker. Data replaces this safe view as it arrives.
    if (bootShellTimer) clearTimeout(bootShellTimer);
    bootShellTimer = setTimeout(() => {
        bootShellTimer = null;
        revealAppShell();
    }, 650);
    try {
        await restoreLocalPreferences();
        if (IS_TG) {
            if ($('app')?.style.display !== 'flex') showLoader(true);
            await loadAll();
        } else {
            showLoader(false);
            const uid = getSession();
            if (uid) {
                await bootWithUid(uid);
            } else {
                // Браузерный превью (ДЕМО): сразу показываем app + тему на hub-id
                S.user = initialUser();
                $('app').style.display = 'flex';
                const ls = $('login-screen'); if (ls) ls.style.display = 'none';
                initCosmosArt();
                renderProfile();
                if (!S.user._pending) seedPreviewHubTheme();
            }
        }
        if (S.tab === 'arena') playArenaHeroVideo();
        await openTelegramStartTarget();
        startLiveRefresh(S.tab);
        void maybeFirstVisit();
    } catch (e) {
        console.error('[INIT ERROR]', e);
        revealAppShell();
        showToast('Не удалось обновить данные — пробуем восстановить связь');
    } finally {
        if (bootShellTimer) clearTimeout(bootShellTimer);
        bootShellTimer = null;
    }
});

// ── Navigation ────────────────────────────────────────
function initNav() {
    // Bottom navigation already uses inline onclick handlers in index.html.
    // Registering a second click listener here made every Telegram tap run twice.
    // Replace the legacy inline clan exchange handler even when Telegram restores
    // an older cached index.html together with the current application bundle.
    const exchangeButton = $('btn-exchange');
    if (exchangeButton) {
        exchangeButton.onclick = (event) => {
            event?.preventDefault?.();
            openExchangeDen();
        };
    }
    const elite1 = $('elite-btn-1');
    const elite10 = $('elite-btn-10');
    const bindElite = (el, n) => {
        if (!el) return;
        const press = () => el.classList.add('is-pressed');
        const release = () => {
            el.classList.remove('is-pressed');
            try { el.blur(); } catch (_) {}
        };
        el.addEventListener('pointerdown', press);
        el.addEventListener('pointerup', release);
        el.addEventListener('pointercancel', release);
        el.addEventListener('pointerleave', release);
        el.addEventListener('click', (e) => {
            e.preventDefault();
            release();
            eliteSpin(n);
        });
    };
    bindElite(elite1, 1);
    bindElite(elite10, 10);
}

let navigationRevision = 0;
async function switchTab(name) {
    if (name === 'team') {
        if (S.tab === 'arena' && S.arena.screen === 'battle') {
            showToast('Сначала заверши текущий бой');
            return;
        }
        S.arena.screen = 'lobby';
        await switchTab('arena');
        renderTeam();
        $('team-slots')?.scrollIntoView({ block: 'nearest' });
        return;
    }
    if (!$(`tab-${name}`)) return;
    haptic('light');
    if (name === S.tab) {
        startLiveRefresh(name);
        return;
    }
    const revision = ++navigationRevision;
    const previousTab = $(`tab-${S.tab}`);
    const tabOrder = ['profile', 'cards', 'arena', 'clan', 'shop'];
    const direction = tabOrder.indexOf(name) < tabOrder.indexOf(S.tab) ? -1 : 1;
    if (isArenaWorkbenchVisible() && name !== 'arena') {
        // Navigation must never be held hostage by a slow autosave request.
        void autosaveCurrentArenaDeck({ reason: 'leave' });
    }
    tutorials.close();
    if (S.tab === 'holdings') holdingsUI?.stop();
    activeGuide?.close('navigation');
    suspendTabMedia(S.tab);
    if (S.tab === 'arena' && name !== 'arena' && S.arena.screen === 'loading') {
        arenaCancelSearch(true);
    }
    if (name !== 'profile') suspendHubThemeMedia();
    S.tab = name;
    document.body.classList.toggle('arena-workbench-open', name === 'arena');
    if (name !== 'player-profile') {
        const appContainer = $('app-container');
        appContainer?.classList.remove('profile-view-open');
        appContainer?.classList.toggle('exchange-view-open', name === 'exchange');
        const nav = $('bottom-nav');
        if (nav) nav.hidden = name === 'arena' && S.arena.screen === 'battle';
        bindProfileBackButton(false);
    }
    const sec = $(`tab-${name}`);
    changePanels(previousTab, sec, () => {
        document.querySelectorAll('.tab').forEach(t => t.classList.remove('active'));
        document.querySelectorAll('.nav-btn').forEach(b => b.classList.toggle('active', b.dataset.tab === name));
        sec.classList.add('active');
    }, direction);
    if (name === 'profile') {
        renderProfile();
        resumeHubThemeMedia();
    }
    if (name === 'cards') renderCards();
    if (name === 'team') renderTeam();
    if (name === 'tasks') renderTasks();
    if (name === 'arena') {
        renderArena(false);
        applyArenaEffects();
        void maybeShowArenaTutorial();
    }
    if (name === 'shop') {
        renderShop();
        switchShopSubTab(S.themes.shopSubTab || 'market');
    }
    if (name === 'exchange') {
        document.querySelector('.nav-btn[data-tab="shop"]')?.classList.add('active');
        renderExchangeDen();
        void loadExchangeDen();
    }
    if (name === 'player-market') {
        document.querySelector('.nav-btn[data-tab="shop"]')?.classList.add('active');
        playerMarketUI ||= createPlayerMarket({root: $('tab-player-market'), api, uid: effectiveUid, mediaBase: API_BASE,
            confirm: customConfirm, changed: loadAll, back: () => switchTab('shop'),
            balanceChanged: balance => {
                if (S.user) { S.user.coins = Number(balance.recoin); S.user.restars = Number(balance.restars); }
                syncExchangeEconomy();
            }});
        void playerMarketUI.open();
    }
    if (name === 'holdings') {
        holdingsUI ||= createHoldings({root: $('holdings-root'), api, uid: effectiveUid, confirm: customConfirm,
            changed: balance => { if (S.user) S.user.coins = balance; setText('h-coins', Number(balance).toLocaleString('ru-RU')); },
            help: () => tutorials.start('holdings')});
        await holdingsUI.open();
    }
    if (name === 'referral') void loadReferral();
    if (name === 'clan') await loadClan();
    if (revision !== navigationRevision) return;
    resumeTabMedia(name);
    startLiveRefresh(name);
    void maybeFirstVisit();
}
window.switchTab = switchTab;
let profileReady = false;
let playerMarketUI = null;
let holdingsUI = null;

function telegramStartTarget() {
    const query = new URLSearchParams(window.location.search);
    return String(tg?.initDataUnsafe?.start_param || query.get('tgWebAppStartParam') || query.get('target') || '').toLowerCase();
}

async function openTelegramStartTarget() {
    const target = telegramStartTarget();
    if (target.startsWith('holdings')) await switchTab('holdings');
    if (target.startsWith('help')) await tutorials.center();
    if (target.startsWith('market')) await switchTab('player-market');
    if (target === 'offers') openExchangeDen('market');
    if (target === 'sell-themes') openExchangeDen('themes');
    if (target === 'exchange') openExchangeDen();
    if (target.startsWith('arena')) await switchTab('arena');
    if (target.startsWith('shop')) {
        await switchTab('shop');
        requestAnimationFrame(() => scrollShopTo(target.includes('spin') ? 'shop-spins' : target.includes('themes') ? 'shop-themes' : 'tab-shop'));
    }
}

let activeGuide = null;
function showContextGuide(steps, onClose = () => {}) {
    activeGuide?.close('replace');
    activeGuide = createGuidedTour({ onClose });
    activeGuide.open(steps);
}
const tutorials = createTutorialSystem({storage: telegramStorage.device, uid: effectiveUid, navigate: switchTab,
    clanPanel: switchClanTab, currentTab: () => S.tab});
function openArenaTutorial(index = 0) { if(S.arena.screen === 'lobby') void tutorials.start('arena'); }
function closeArenaTutorial() { tutorials.close(); activeGuide?.close(); }
function arenaTutorialNext() { tutorials.next(); }
function arenaTutorialPrev() { tutorials.prev(); }
async function maybeShowArenaTutorial() { /* Contextual help is optional and never interrupts play. */ }
window.openArenaTutorial = openArenaTutorial;
window.closeArenaTutorial = closeArenaTutorial;
window.arenaTutorialNext = arenaTutorialNext;
window.arenaTutorialPrev = arenaTutorialPrev;
window.openHubGuide = () => tutorials.start('profile');
window.openShopGuide = () => tutorials.start('shop');
window.openBattleGuide = () => tutorials.start('battle');
const maybeFirstVisit = createFirstVisit({storage: telegramStorage.device,
    uid: effectiveUid,
    claim: () => api('/api/onboarding_visit', {method:'POST',body:{user_id:effectiveUid()}}),
    ready: () => profileReady && S.tab === 'profile' && !document.hidden && !tutorials.active &&
        !document.querySelector('.modal-overlay.open, .modal-overlay.active, .modal-overlay[aria-hidden="false"]'),
    show: () => tutorials.start('profile', true)});
window.openLearningCenter = () => tutorials.center();
window.openContextHelp = () => tutorials.help();

function startLiveRefresh(tab) {
    if (liveRefreshTimer) clearInterval(liveRefreshTimer);
    liveRefreshTimer = null;
    liveRefreshEpoch += 1;
    void refreshTabData(tab, false, liveRefreshEpoch);
    if (!isWebAppActive()) return;
    liveRefreshTimer = setInterval(() => {
        if (isWebAppActive() && S.tab === tab) void refreshTabData(tab, true, liveRefreshEpoch);
    }, LIVE_REFRESH_MS);
}

function isWebAppActive() {
    return !document.hidden && (!tg || tg.isActive !== false);
}

function pauseLiveRefresh() {
    if (liveRefreshTimer) clearInterval(liveRefreshTimer);
    liveRefreshTimer = null;
    liveRefreshEpoch += 1;
}

document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
        if (isArenaWorkbenchVisible()) void autosaveCurrentArenaDeck({ reason: 'hide', keepalive: true });
        suspendTabMedia(S.tab);
        suspendHubThemeMedia();
        pauseLiveRefresh();
    } else if (!document.hidden) {
        resumeTabMedia(S.tab);
        if (S.tab === 'profile') resumeHubThemeMedia();
        if (S.tab === 'arena') playArenaHeroVideo();
        startLiveRefresh(S.tab);
    }
});

try {
    tg?.onEvent?.('deactivated', () => {
        document.dispatchEvent(new Event('reverse-app-activity'));
        if (isArenaWorkbenchVisible()) void autosaveCurrentArenaDeck({ reason: 'hide', keepalive: true });
        persistArenaDeviceState();
        void arenaDeviceStore.flush();
        suspendTabMedia(S.tab);
        suspendHubThemeMedia();
        pauseLiveRefresh();
    });
    tg?.onEvent?.('activated', () => {
        document.dispatchEvent(new Event('reverse-app-activity'));
        resumeTabMedia(S.tab);
        if (S.tab === 'profile') resumeHubThemeMedia();
        if (S.tab === 'arena') playArenaHeroVideo();
        startLiveRefresh(S.tab);
    });
} catch (error) {
    console.warn('[TG LIFECYCLE]', error);
}

window.addEventListener('pagehide', () => {
    if (isArenaWorkbenchVisible()) void autosaveCurrentArenaDeck({ reason: 'pagehide', keepalive: true });
    persistArenaDeviceState();
    void arenaDeviceStore.flush();
    void telegramStorage.flush();
    void telegramDeviceCache.flush?.();
});

function commitState(key, value, apply) {
    if (value === undefined || value === null) return false;
    const signature = stableDataSignature(value);
    if (stateSignatures.get(key) === signature) return false;
    stateSignatures.set(key, signature);
    apply(value);
    return true;
}

async function refreshTabData(tab, periodic = false, epoch = liveRefreshEpoch) {
    const uid = effectiveUid();
    if (!uid || tab === 'player-profile') return;
    const existing = refreshRuns.get(tab);
    if (existing) {
        if (!periodic) existing.pending = true;
        return existing.promise;
    }

    const run = { pending: false, promise: null };
    run.promise = (async () => {
      const arenaEpochAtStart = arenaMutationEpoch;
      try {
        const requests = [api(`/api/user_stats?user_id=${uid}`)];
        const keys = ['stats'];

        if (tab === 'profile' || tab === 'cards') {
            requests.push(api(`/api/user_cards?user_id=${uid}`));
            keys.push('cards');
        }
        if (tab === 'team') {
            requests.push(api(`/api/user_cards?user_id=${uid}`));
            keys.push('cards');
            requests.push(loadArenaState({ maxAge: periodic ? 45_000 : 20_000 }));
            keys.push('arena');
        }
        if (tab === 'tasks') {
            requests.push(api(`/api/user_tasks?user_id=${uid}`));
            keys.push('tasks');
        }
        if (tab === 'arena' && S.arena.screen === 'lobby') {
            requests.push(api(`/api/user_cards?user_id=${uid}`, { cacheMode: 'cache-first', maxAge: periodic ? 45_000 : 20_000 }));
            keys.push('cards');
            requests.push(loadArenaState({ maxAge: periodic ? 45_000 : 20_000 }));
            keys.push('arena');
            requests.push(api(`/api/leaderboard?user_id=${uid}&type=${S.lbType}`));
            keys.push('leaderboard');
        }
        if (tab === 'shop' || tab === 'exchange') {
            requests.push(api(`/api/theme_mine?user_id=${uid}`));
            keys.push('themeMine');
            if (tab === 'shop' && !periodic) {
                requests.push(api('/api/theme_market'));
                keys.push('themeMarket');
            }
        }

        const settled = await Promise.allSettled(requests);
        const data = {};
        settled.forEach((result, index) => {
            if (result.status === 'fulfilled') data[keys[index]] = result.value;
        });

        const changed = {};
        changed.stats = data.stats && !data.stats.error && commitState('stats', data.stats, value => {
            profileReady = true;
            S.user = { ...(S.user || {}), ...value, _pending: false };
            S.user.losses = Number(value.losses ?? value.loses ?? S.user.losses ?? 0) || 0;
            S.user.loses = S.user.losses;
        });
        changed.cards = Array.isArray(data.cards) && commitState('cards', data.cards, value => {
            S.cards = value;
            scheduleMediaWarmup(value);
        });
        changed.team = Array.isArray(data.team) && commitState('team', data.team, value => { S.team = value; });
        changed.tasks = Array.isArray(data.tasks) && commitState('tasks', data.tasks, value => { S.tasks = value; });
        changed.arena = arenaEpochAtStart === arenaMutationEpoch && data.arena && !data.arena.error && commitState('arena', data.arena, value => {
            S.arenaLobby = value;
            mergeArenaDecksFromServer(value.decks);
            S.arenaActiveDeckId = String(value.active_deck_id || '1');
            S.arenaRecoveryLoadedAt = Date.now();
            syncArenaStats(value);
            if (Array.isArray(value.team)) {
                S.team = value.team.map((item, index) => item ? { ...item, slot: item.slot || index + 1 } : null);
            }
        });
        changed.leaderboard = data.leaderboard && !data.leaderboard.error && commitState(`leaderboard:${S.lbType}`, data.leaderboard, value => {
            S.leaders = value.list || [];
            S.myRank = value.me || null;
        });
        changed.themeMine = data.themeMine && !data.themeMine.error && commitState('themeMine', data.themeMine, value => { S.themes.mine = value; });
        changed.themeMarket = Array.isArray(data.themeMarket) && commitState('themeMarket', data.themeMarket, value => { S.themes.market = value; });

        // Arena owns a compact snapshot. Avoid serializing every owned card
        // when only the periodically refreshed lobby state changed.
        if (Object.entries(changed).some(([key, value]) => key !== 'arena' && value)) scheduleDeviceSnapshotSave(uid);
        if (changed.arena) persistArenaDeviceState(uid);

        if (S.tab !== tab || epoch !== liveRefreshEpoch) return;
        if (tab === 'profile' && (changed.stats || changed.cards)) renderProfile();
        if (tab === 'cards' && changed.cards) renderCards();
        if (tab === 'team' && (changed.cards || changed.arena)) renderTeam();
        if (tab === 'tasks' && changed.tasks) renderTasks();
        if (tab === 'arena' && S.arena.screen === 'lobby' && (changed.stats || changed.cards || changed.arena || changed.leaderboard)) renderArena(false);
        if (tab === 'shop' && (changed.stats || changed.themeMine || changed.themeMarket)) { renderShop(); renderCreatorHub(); }
        if (tab === 'exchange' && (changed.stats || changed.themeMine)) renderExchangeDen();

        // These sections already have dedicated loaders; refresh them on entry
        // and at the same live interval without blocking the common counters.
        if (tab === 'clan') await loadClan();
        if (tab === 'referral') await loadReferral();
        if (tab === 'games') await renderDice();
        if (tab === 'profile') void maybeFirstVisit();
      } catch (e) {
        console.error('[LIVE REFRESH]', e);
      } finally {
        refreshRuns.delete(tab);
        if (run.pending && S.tab === tab) void refreshTabData(tab, false, liveRefreshEpoch);
      }
    })();
    refreshRuns.set(tab, run);
    return run.promise;
}

function initFilters() {
    document.querySelectorAll('.filter-pill[data-rank]').forEach(chip => {
        chip.addEventListener('click', () => {
            document.querySelectorAll('.filter-pill[data-rank]').forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            S.filter = chip.dataset.rank;
            S.page = 1;
            renderCards();
        });
    });

    document.querySelectorAll('.lb-filter').forEach(chip => {
        chip.addEventListener('click', async () => {
            document.querySelectorAll('.lb-filter').forEach(c => c.classList.remove('active'));
            chip.classList.add('active');
            S.lbType = chip.dataset.lbt;
            haptic('light');
            const lb = $('leaderboard-list');
            if (lb) lb.innerHTML = '<div class="empty-state">◈ ЗАГРУЗКА...</div>';
            try {
                const lbData = await api(`/api/leaderboard?user_id=${effectiveUid()}&type=${S.lbType}`);
                S.leaders = lbData?.list || [];
                S.myRank = lbData?.me || null;
                renderArena();
            } catch (e) {
                console.error('[LB]', e);
                S.leaders = [];
                S.myRank = null;
                renderArena();
            }
        });
    });
}

// ── Data ──────────────────────────────────────────────
function restoreCachedState(uid) {
    const maxAge = 12 * 60 * 60 * 1000;
    const get = url => apiDataCache.peek(`GET:${url}`, maxAge);
    const stats = get(`/api/user_stats?user_id=${uid}`);
    const cards = get(`/api/user_cards?user_id=${uid}`);
    const team = get(`/api/user_team?user_id=${uid}`);
    const tasks = get(`/api/user_tasks?user_id=${uid}`);
    const leaderboard = get(`/api/leaderboard?user_id=${uid}&type=${S.lbType}`);
    const themeMine = get(`/api/theme_mine?user_id=${uid}`);
    const themeMarket = get('/api/theme_market');
    if (stats) commitState('stats', stats, value => { S.user = value; });
    if (Array.isArray(cards)) commitState('cards', cards, value => { S.cards = value; });
    if (Array.isArray(team)) commitState('team', team, value => { S.team = value; });
    if (Array.isArray(tasks)) commitState('tasks', tasks, value => { S.tasks = value; });
    if (leaderboard) commitState(`leaderboard:${S.lbType}`, leaderboard, value => {
        S.leaders = value.list || [];
        S.myRank = value.me || null;
    });
    if (themeMine) commitState('themeMine', themeMine, value => { S.themes.mine = value; });
    if (Array.isArray(themeMarket)) commitState('themeMarket', themeMarket, value => { S.themes.market = value; });
    return Boolean(stats);
}

function applyDeviceSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return false;
    if (snapshot.user) commitState('stats', snapshot.user, value => { S.user = value; });
    if (Array.isArray(snapshot.cards)) commitState('cards', snapshot.cards, value => { S.cards = value; });
    if (Array.isArray(snapshot.team)) commitState('team', snapshot.team, value => { S.team = value; });
    if (Array.isArray(snapshot.tasks)) commitState('tasks', snapshot.tasks, value => { S.tasks = value; });
    if (snapshot.arenaLobby) {
        S.arenaLobby = snapshot.arenaLobby;
        S.arenaDecks = normalizeArenaDecks(snapshot.arenaDecks || snapshot.arenaLobby.decks);
        S.arenaActiveDeckId = String(snapshot.arenaActiveDeckId || snapshot.arenaLobby.active_deck_id || '1');
        S.arenaRecoveryLoadedAt = Date.now();
        syncArenaStats(snapshot.arenaLobby);
    }
    if (snapshot.themeMine) S.themes.mine = snapshot.themeMine;
    if (Array.isArray(snapshot.themeMarket)) S.themes.market = snapshot.themeMarket;
    if (snapshot.leaderboard) {
        S.leaders = snapshot.leaderboard.list || [];
        S.myRank = snapshot.leaderboard.me || null;
    }
    return Boolean(S.user);
}

function applyArenaLobbyState(lobby, { persist = true } = {}) {
    if (!lobby || lobby.error) return false;
    S.arenaLobby = lobby;
    mergeArenaDecksFromServer(lobby.decks);
    S.arenaActiveDeckId = String(lobby.active_deck_id || S.arenaActiveDeckId || '1');
    S.arenaRecoveryLoadedAt = Date.now();
    syncArenaStats(lobby);
    if (Array.isArray(lobby.team)) {
        S.team = lobby.team.map((card, index) => card ? { ...card, slot: card.slot || index + 1 } : null);
    }
    if (persist) persistArenaDeviceState();
    return true;
}

function arenaDeviceSnapshot() {
    const activeBattle = S.arena?.battle && !S.arena.battle.is_finished ? S.arena.battle : null;
    return {
        version: 3,
        lobby: S.arenaLobby,
        decks: S.arenaDecks,
        activeDeckId: S.arenaActiveDeckId,
        editingDeckId: S.arenaEditingDeckId,
        drafts: S.arenaDrafts,
        team: S.team,
        battle: activeBattle,
        screen: activeBattle && S.arena.screen === 'battle' ? 'battle' : 'lobby',
    };
}

function persistArenaDeviceState(uid = effectiveUid()) {
    if (!uid) return;
    arenaDeviceStore.schedule(uid, arenaDeviceSnapshot());
}

function setArenaBattleState(battle, screen = null) {
    if (!battle) return;
    S.arena.battle = battle;
    arenaRestoringBattle = false;
    if (screen) S.arena.screen = screen;
    persistArenaDeviceState();
}

function isArenaBattleGoneError(error) {
    const status = Number(error?.status || 0);
    const message = String(error?.message || error || '').toLowerCase();
    return status === 404
        || status === 410
        || /battle[_\s-]*(not[_\s-]*found|finished|closed)/i.test(message)
        || /бой (не найден|уже заверш[её]н|заверш[её]н)/i.test(message);
}

function discardStaleArenaBattle({ render = true } = {}) {
    arenaRestoringBattle = false;
    resetArenaSelection();
    S.arena.battle = null;
    S.arena.opponent = null;
    S.arena.screen = 'lobby';
    if (S.arenaLobby?.active_battle) {
        S.arenaLobby = { ...S.arenaLobby, active_battle: null };
    }
    persistArenaDeviceState();
    if (render && S.tab === 'arena') renderArena(false);
}

function applyArenaDeviceSnapshot(snapshot) {
    if (!snapshot || typeof snapshot !== 'object') return false;
    if (snapshot.lobby) applyArenaLobbyState(snapshot.lobby, { persist: false });
    if (Array.isArray(snapshot.decks)) S.arenaDecks = normalizeArenaDecks(snapshot.decks);
    if (snapshot.activeDeckId) S.arenaActiveDeckId = String(snapshot.activeDeckId);
    if (snapshot.editingDeckId) S.arenaEditingDeckId = String(snapshot.editingDeckId);
    if (snapshot.drafts && typeof snapshot.drafts === 'object') S.arenaDrafts = snapshot.drafts;
    if (Array.isArray(snapshot.team)) S.team = snapshot.team;
    if (snapshot.battle?.battle_id && !snapshot.battle.is_finished) {
        S.arena.battle = snapshot.battle;
        S.arena.screen = 'battle';
        arenaRestoringBattle = true;
    }
    return Boolean(snapshot.lobby || snapshot.battle || snapshot.decks);
}

async function revalidateRestoredArenaBattle() {
    const battle = S.arena.battle;
    if (!arenaRestoringBattle || !battle?.battle_id) return;
    try {
        const latest = await api(`/api/arena_battle_state?id=${encodeURIComponent(battle.battle_id)}&user_id=${effectiveUid()}`, {
            dedupe: true,
            timeoutMs: 8000,
            retries: 0,
        });
        if (!latest || latest.error) throw new Error(latest?.error || 'Battle unavailable');
        S.arena.battle = latest;
        arenaRestoringBattle = false;
        S.arena.screen = latest.is_finished ? 'result' : 'battle';
        persistArenaDeviceState();
        if (latest.is_finished) _finishBattleV2(latest);
        else if (S.tab === 'arena') renderArena(false);
    } catch (error) {
        if (isArenaBattleGoneError(error)) {
            discardStaleArenaBattle();
            return;
        }
        // A temporary network failure must not throw the user out of a real
        // active battle. Keep the snapshot read-only and retry on activation.
        arenaRestoringBattle = true;
        if (S.tab === 'arena') renderArena(false);
    }
}

async function loadArenaState({ force = false, maxAge = 20_000 } = {}) {
    const uid = effectiveUid();
    if (!uid) return S.arenaLobby;
    if (!force && S.arenaLobby && Date.now() - arenaStateFetchedAt < maxAge) return S.arenaLobby;
    const lobby = await api(`/api/arena_state?user_id=${uid}`, {
        dedupe: true,
        timeoutMs: 8000,
        retries: 0,
    });
    if (lobby && !lobby.error) {
        arenaStateFetchedAt = Date.now();
        applyArenaLobbyState(lobby);
    }
    return lobby;
}

function scheduleDeviceSnapshotSave(uid = effectiveUid()) {
    if (!telegramDeviceCache.available || !uid || S.user?._pending) return;
    if (deviceSnapshotTimer) clearTimeout(deviceSnapshotTimer);
    deviceSnapshotTimer = setTimeout(() => {
        deviceSnapshotTimer = null;
        void telegramDeviceCache.set(uid, {
            user: S.user,
            cards: S.cards,
            team: S.team,
            tasks: S.tasks,
            leaderboard: { list: S.leaders, me: S.myRank },
            themeMine: S.themes.mine,
            themeMarket: (S.themes.market || []).slice(0, 100),
        });
    }, 250);
}

function revealAppShell() {
    showLoader(false);
    if (!S.user) S.user = initialUser();
    const app = $('app');
    if (app) app.style.display = 'flex';
    initCosmosArt();
    renderProfile();
}

async function loadAll({ skipRestore = false } = {}) {
    const uid = effectiveUid();
    // Telegram can occasionally stall DeviceStorage or one network request.
    // Navigation must remain usable; later responses simply fill the shell.
    if (bootShellTimer) clearTimeout(bootShellTimer);
    bootShellTimer = setTimeout(() => {
        bootShellTimer = null;
        revealAppShell();
    }, 1800);
    let restored = !skipRestore && uid ? restoreCachedState(uid) : false;
    const [deviceSnapshot, arenaSnapshot] = await Promise.all([
        !skipRestore && !restored && uid && telegramDeviceCache.available ? telegramDeviceCache.get(uid) : Promise.resolve(null),
        !skipRestore && uid ? arenaDeviceStore.load(uid) : Promise.resolve(null),
    ]);
    if (!restored && deviceSnapshot) restored = applyDeviceSnapshot(deviceSnapshot);
    if (arenaSnapshot) applyArenaDeviceSnapshot(arenaSnapshot);

    if (restored) {
        initCosmosArt();
        renderProfile();
        renderCards();
        renderTeam();
        renderTasks();
        revealAppShell();
        scheduleMediaWarmup(S.cards);
    }

    if (TGuser?.photo_url) {
        api('/api/webapp_action', {
            method: 'POST',
            body: { user_id: uid, action: 'sync_avatar', photo_url: TGuser.photo_url },
        }).catch(() => { });
    }

    // Быстрый путь: профиль + тема (хаб виден до тяжёлых запросов)
    const [statsRes, themeRes] = await Promise.allSettled([
        api(`/api/user_stats?user_id=${uid}`, { timeoutMs: 7000, retries: 0 }),
        uid ? api(`/api/theme_mine?user_id=${uid}`, { timeoutMs: 7000, retries: 0 }) : Promise.resolve(null),
    ]);

    if (statsRes.status === 'fulfilled' && statsRes.value) {
        profileReady = !statsRes.value.error;
        commitState('stats', statsRes.value, value => { S.user = value; });
    } else if (!S.user) {
        S.user = initialUser();
    }
    S.themes.mine = themeRes.status === 'fulfilled' && themeRes.value
        ? themeRes.value
        : (S.themes.mine || { submissions: [], owned: [], owned_details: [], active_theme: '' });
    if (themeRes.status === 'fulfilled' && themeRes.value) stateSignatures.set('themeMine', stableDataSignature(themeRes.value));

    initCosmosArt();
    renderProfile();

    if (bootShellTimer) clearTimeout(bootShellTimer);
    bootShellTimer = null;
    revealAppShell();

    // Без вселенной — модалка поверх хаба, дальше не пускаем
    if (needsUniversePick(S.user)) {
        await openUniverseGate({ blocking: true });
    }

    // Тяжёлые данные — в фоне, не блокируют открытие меню
    const [cardsRes, teamRes, tasksRes, lbRes] = await Promise.allSettled([
        api(`/api/user_cards?user_id=${uid}`),
        api(`/api/user_team?user_id=${uid}`),
        api(`/api/user_tasks?user_id=${uid}`),
        api(`/api/leaderboard?user_id=${uid}&type=${S.lbType}`),
    ]);

    if (cardsRes.status === 'fulfilled' && Array.isArray(cardsRes.value)) commitState('cards', cardsRes.value, value => { S.cards = value; });
    if (teamRes.status === 'fulfilled' && Array.isArray(teamRes.value)) commitState('team', teamRes.value, value => { S.team = value; });
    if (tasksRes.status === 'fulfilled' && Array.isArray(tasksRes.value)) commitState('tasks', tasksRes.value, value => { S.tasks = value; });
    if (lbRes.status === 'fulfilled' && lbRes.value) commitState(`leaderboard:${S.lbType}`, lbRes.value, value => {
        S.leaders = value.list || [];
        S.myRank = value.me || null;
    });

    if (Array.isArray(S.arenaLobby?.team)) {
        S.team = S.arenaLobby.team.map((card, index) => card ? { ...card, slot: card.slot || index + 1 } : null);
    }
    renderProfile();
    renderCards();
    renderTeam();
    renderArena();
    renderTasks();

    // Прогрев ассетов призыва / рубашки — без ожидания
    try { scheduleMediaWarmup(S.cards); } catch (_) {}
    scheduleDeviceSnapshotSave(uid);
    persistArenaDeviceState(uid);
    if (arenaRestoringBattle) void revalidateRestoredArenaBattle();
}

/** Предзагрузка рубашки и первых артов колоды (без DOM-прогрева оверлея — он крашил TG) */
function scheduleMediaWarmup(cards) {
    if (isArenaWorkbenchVisible()) return;
    const urls = new Set(['assets/card-back.png']);
    (cards || []).slice(0, 10).forEach((c) => {
        let src = c?.image_url || '';
        if (!src) return;
        if (c.type === 'animation' || c.type === 'video' || c.rank === 'Мифический') return;
        if (!src.startsWith('http') && !src.startsWith('blob:')) {
            src = `${API_BASE}${src.startsWith('/') ? '' : '/'}${src}`;
        }
        urls.add(src);
    });
    const start = () => urls.forEach((u) => {
        mediaWarmupQueue.add(() => new Promise(resolve => {
            try {
                const img = new Image();
                img.decoding = 'async';
                img.onload = img.onerror = () => resolve();
                img.src = u;
            } catch (_) { resolve(); }
        })).catch(() => {});
    });
    if (typeof requestIdleCallback === 'function') requestIdleCallback(start, { timeout: 1800 });
    else setTimeout(start, 350);
}

function needsUniversePick(user) {
    if (user?._pending) return false;
    if (!user) return true;
    if (user.needs_universe === true) return true;
    const v = String(user.universe || user.verce || '').trim();
    return !v || v === '?' || v === '—';
}

let universePicker = null;

async function openUniverseGate({ blocking = true } = {}) {
    const root = $('universe-gate');
    if (!root) return;
    if (!universePicker) universePicker = createUniversePicker({
        root, api, apiBase: API_BASE,
        current: () => needsUniversePick(S.user) ? '' : (S.user?.universe || S.user?.verce || ''),
        select: async name => {
            pauseLiveRefresh();
            await Promise.allSettled([...refreshRuns.values()].map(run => run.promise));
            try {
                const res = await api('/api/set_universe', {
                    method: 'POST', body: { user_id: effectiveUid(), universe: name },
                });
                if (!res?.success) throw new Error(res?.error || 'Не удалось сохранить выбор');
                S.user = { ...S.user, universe: res.universe || name, verce: res.verce || name, needs_universe: false };
                apiDataCache.clear();
                stateSignatures.clear();
                S.cards = []; S.team = []; S.leaders = []; S.myRank = null;
                S.arenaLobby = null; S.arenaDecks = []; S.arenaDrafts = {}; S.arenaDraftSlots = {};
                S.arenaActiveDeckId = '1'; S.arenaEditingDeckId = '1';
                arenaStateFetchedAt = 0;
                await arenaDeviceStore.clear();
                renderProfile(); renderCards(); renderTeam();
                scheduleDeviceSnapshotSave();
                showToast(`Вселенная выбрана: ${name}`);
            } catch (error) {
                startLiveRefresh(S.tab);
                throw error;
            }
        },
    });
    const chosen = await universePicker.open({ blocking });
    if (chosen && !blocking) {
        await loadAll({ skipRestore: true });
        startLiveRefresh(S.tab);
    }
}

function closeUniverseGate() { universePicker?.close(); }
window.openUniverseGate = openUniverseGate;
window.closeUniverseGate = closeUniverseGate;

function initialUser() {
    if (!IS_TG && ['localhost','127.0.0.1'].includes(location.hostname)) return demoUser();
    return {...demoUser(), name: TGuser?.first_name || 'Подключение…', universe:'', verce:'', _pending:true};
}

function demoUser() {
    return {
        name: 'ДЕМО', universe: 'Блич', verce: 'Блич', needs_universe: false,
        pts: 0, cards: 0, spins: 0,
        coins: 0, restars: 0, trophy: 0, wins: 0, loses: 0, league: '— ЛИГА', photo_url: ''
    };
}

// ── Render ────────────────────────────────────────────
const RCOLOR = { Обычный: '#94A3B8', Редкий: '#38BDF8', Эпический: '#C084FC', Легендарный: '#FBBF24', Мифический: '#FB7185' };
const RANK_ORDER = { 'Мифический': 5, 'Легендарный': 4, 'Эпический': 3, 'Редкий': 2, 'Обычный': 1 };

function cardMediaSrc(c) {
    return arenaNormalMediaSrc(c?.image_url, c?.file_id);
}
function cssUrl(s) { return String(s || '').replace(/'/g, '%27'); }
/** Avatar img — never HTML-escape URLs (& must stay &). Prefer /api/avatar/{uid}. */
function userAvatarHtml(url, name = '?', className = '', uid = '') {
    const letter = String(name || '?').trim().charAt(0).toUpperCase() || '?';
    const fbHtml = `<span class="av-fallback ${className}">${esc(letter)}</span>`;
    const apiSrc = uid ? `${API_BASE}/api/avatar/${encodeURIComponent(String(uid))}` : '';
    let direct = '';
    const raw = String(url || '').trim();
    if (/^https?:\/\//i.test(raw)) direct = raw;
    else if (raw.startsWith('/')) direct = `${API_BASE}${raw}`;

    const primary = apiSrc || direct;
    if (!primary) return fbHtml;

    const safePrimary = primary.replace(/"/g, '');
    const alt = (direct && direct !== primary) ? direct.replace(/"/g, '') : '';
    return `<img class="${className}" src="${safePrimary}" alt="" loading="lazy" decoding="async" data-av-letter="${esc(letter)}" data-av-class="${esc(className)}" data-av-alt="${esc(alt)}" onerror="window.__avImgErr&&window.__avImgErr(this)">`;
}

window.__avImgErr = function (img) {
    if (!img) return;
    const alt = img.getAttribute('data-av-alt') || '';
    if (alt && img.dataset.avfb !== '1') {
        img.dataset.avfb = '1';
        img.src = alt;
        return;
    }
    const letter = img.getAttribute('data-av-letter') || '?';
    const cls = img.getAttribute('data-av-class') || '';
    img.outerHTML = `<span class="av-fallback ${cls}">${letter}</span>`;
};

function resolveClanEmblemUrl(clanLike) {
    if (!clanLike) return '';
    if (clanLike.avatar) {
        const a = String(clanLike.avatar);
        if (a.startsWith('http') || a.startsWith('/')) return a.startsWith('/') ? `${API_BASE}${a}` : a;
    }
    const fid = clanLike.file_id || clanLike.emblem_file_id || '';
    if (fid) return `${API_BASE}/api/proxy_image?file_id=${encodeURIComponent(fid)}`;
    return '';
}
function rankStars(rank) {
    return ({ 'Обычный': 1, 'Редкий': 2, 'Эпический': 3, 'Легендарный': 4, 'Мифический': 5 }[rank] || 1);
}
function topCards(n = 4) {
    return [...(S.cards || [])]
        .filter(c => cardMediaSrc(c))
        .sort((a, b) => (RANK_ORDER[b.rank] || 0) - (RANK_ORDER[a.rank] || 0) || (+b.rarity || 0) - (+a.rarity || 0))
        .slice(0, n);
}
function taskIcon(title = '') {
    const t = String(title).toLowerCase();
    if (t.includes('бой') || t.includes('арен')) return '⚔';
    if (t.includes('карт') || t.includes('откр')) return '🎴';
    if (t.includes('клан')) return '🛡';
    if (t.includes('монет') || t.includes('рекоин')) return '◉';
    return '🎁';
}
function setArtPhoto(elId, src, user = false) {
    const el = $(elId);
    if (!el || !src) return;
    el.innerHTML = `<img class="cosmos-cover" src="${cssUrl(src)}" alt="" loading="lazy" decoding="async">`;
    el.classList.toggle('has-user', !!user);
}
function initCosmosArt() {
    // Hub tiles keep local art from HTML/CSS — do not overwrite
    setCosmosArt('tasks-hero-art', 'tasks', 1, 520);
    setCosmosArt('shop-hero-art', 'shop', 0, 560);
}
function applyHubVisuals() {
    // arena hero keeps fixed video background
}
function playArenaHeroVideo() {
    const v = $('arena-hero-video');
    if (!v) return;
    try {
        v.muted = true;
        v.playsInline = true;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
}
window.playArenaHeroVideo = playArenaHeroVideo;
function renderTeamHero() {
    const el = $('team-hero');
    if (!el) return;
    el.innerHTML = [1, 2, 3, 4].map(i => {
        const c = (S.team || [])[i - 1];
        if (c?.image_url) {
            return `<div class="team-hero-slot filled" style="background-image:url('${cssUrl(cardMediaSrc(c))}')" title="${esc(c.name || '')}"></div>`;
        }
        return `<div class="team-hero-slot">${i}</div>`;
    }).join('');
}

function renderPlayerJourney() {
    const next = playerJourney({ authenticated: !!effectiveUid(), user: S.user || {}, cards: S.cards || [], team: S.team || [] });
    setText('journey-title', next.title); setText('journey-text', next.text); setText('journey-action', next.label);
}
function openBotForPlay() {
    const url = 'https://t.me/reversecard_robot?start=web';
    if (IS_TG && tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
}
window.openBotForPlay = openBotForPlay;
window.runPlayerJourney = function () {
    const next = playerJourney({ authenticated: !!effectiveUid(), user: S.user || {}, cards: S.cards || [], team: S.team || [] });
    if (next.action === 'telegram') return openBotForPlay();
    if (next.action === 'universe') return openUniverseGate();
    if (next.action === 'card') return botAction('open_card');
    return switchTab('team');
};

let _homeOpeningBusy = false;
let _homeOpeningError = '';

function renderHomeOpening() {
    const root = document.querySelector<HTMLElement>('.hub-opening');
    const button = document.getElementById('btn-open-card') as HTMLButtonElement | null;
    if (!root || !button) return;
    const user = S.user;
    const pending = !user || !!user._pending;
    const spins = Math.max(0, Number(user?.spins) || 0);
    const waiting = !spins && _cardCooldownUntil > Date.now();
    root.dataset.state = _homeOpeningBusy ? 'busy' : pending ? 'pending' : _homeOpeningError ? 'error' : waiting ? 'cooldown' : 'ready';
    button.disabled = _homeOpeningBusy || pending;
    button.setAttribute('aria-busy', String(_homeOpeningBusy));
    setText('hub-opening-label', _homeOpeningBusy ? 'Открываем…' : 'Крутить');
    setText('hub-opening-stock', pending ? 'Проверяем крутки…' : spins ? `Крутки в запасе: ${spins.toLocaleString('ru-RU')}` : 'Бесплатное открытие');
    const availableAt = waiting ? new Date(_cardCooldownUntil).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
    setText('hub-opening-status', _homeOpeningBusy ? 'Получаем карту…' : pending ? 'Загружаем профиль' : _homeOpeningError || (spins ? '1 открытие · 1 крутка' : waiting ? `Следующая бесплатная карта в ${availableAt}` : 'Нажми, чтобы проверить доступность'));
}

function renderProfile() {
    const u = S.user; if (!u) return;
    setText('p-name', u.name);
    setText('p-verce', `Вселенная: ${u.universe || '—'}`);
    setText('p-league', u.league || '— Лига');
    const xp = $('p-xp-fill');
    if (xp) {
        const t = Number(u.trophy || 0);
        xp.style.width = `${Math.max(6, Math.min(100, t % 100 || (t > 0 ? 100 : 6)))}%`;
    }
    const photoUrl = TGuser?.photo_url || u.photo_url || u.avatar_url || '';
    const i = $('av-img');
    const fb = $('av-fallback');
    if (i && effectiveUid()) {
        i.loading = 'lazy';
        i.style.display = 'block';
        i.dataset.avfb = '';
        i.onerror = () => {
            if (photoUrl && i.dataset.avfb !== '1') {
                i.dataset.avfb = '1';
                i.src = photoUrl;
                return;
            }
            i.style.display = 'none';
            if (fb) fb.style.display = '';
        };
        // Stable proxied avatar first (TG CDN urls expire)
        i.src = `${API_BASE}/api/avatar/${effectiveUid()}`;
        if (fb) fb.style.display = 'none';
    } else if (i && photoUrl) {
        i.src = photoUrl;
        i.loading = 'lazy';
        i.style.display = 'block';
        if (fb) fb.style.display = 'none';
    }
    setText('p-cards', u.cards || 0);
    setText('p-pts', (u.pts || 0).toLocaleString('ru-RU'));
    setText('p-coins', (u.coins || 0).toLocaleString('ru-RU'));
    setText('p-stars', u.restars || 0);
    setText('p-spins', u.spins || 0);
    renderHomeOpening();
    setText('p-trophy', u.trophy || 0);
    setText('h-spins', u.spins || 0);
    setText('h-coins', (u.coins || 0).toLocaleString('ru-RU'));
    if (u._pending) {
        for (const id of ['p-pts','p-coins','p-stars','p-spins','p-cards','p-trophy','h-spins','h-coins']) setText(id, '—');
        setText('p-verce', 'Ожидаем данные профиля…');
    }
    applyHubVisuals();
    const activeThemeId = S.themes.mine?.active_theme || (!IS_TG ? PREVIEW_HUB_THEME.id : '');
    if (!IS_TG && !u._pending && activeThemeId === PREVIEW_HUB_THEME.id) {
        seedPreviewHubTheme();
    } else {
        applyActiveTheme(activeThemeId || '');
    }

    const rc = $('recent-cards-list');
    if (rc) {
        if (!S.cards || !S.cards.length) {
            rc.innerHTML = '<div class="empty-state">Пока нет карт — открой первую</div>';
        } else {
            rc.innerHTML = S.cards.slice(0, 4).map(c => {
                const bg = c.image_url ? ` style="background-image:url('${cssUrl(cardMediaSrc(c))}');background-size:cover;background-position:center"` : '';
                return `
            <div class="recent-item${c.image_url ? ' has-bg' : ''}" onclick="openCardModal('${c.id}')"${bg}>
                <div class="recent-icon-wrap">${renderMedia(c, 'recent-icon')}</div>
                <div class="recent-info">
                    <div class="recent-name">${esc(c.name)}</div>
                    <div class="recent-rank r-${c.rank || ''}">${c.rank || ''}</div>
                </div>
                <div class="recent-pts">+${c.rarity || 0}</div>
            </div>`;
            }).join('');
        }
    }
}

function changeCardsPage(delta: number) {
    S.page += delta;
    renderCards();
    haptic('light');
}

function renderCards() {

    const countEl = $('cards-count');
    if (countEl) {
        const n = (S.cards || []).length;
        const word = n === 1 ? 'карта' : (n >= 2 && n <= 4 ? 'карты' : 'карт');
        countEl.textContent = `${n} ${word}`;
    }
    const grid = $('cards-grid'); if (!grid) return;
    let list = S.cards;
    if (S.filter !== 'all') list = list.filter(c => c.rank === S.filter);
    if (!list.length) {
        grid.innerHTML = '<div class="empty-state" style="grid-column:1/-1;padding:28px 12px">Карт не найдено</div>';
        $('cards-pagination').innerHTML = '';
        return;
    }

    const itemsPerPage = 12;
    const totalPages = Math.ceil(list.length / itemsPerPage) || 1;
    if (S.page > totalPages) S.page = totalPages;
    if (S.page < 1) S.page = 1;

    const startIndex = (S.page - 1) * itemsPerPage;
    const pageList = list.slice(startIndex, startIndex + itemsPerPage);

    const rankClass = (rank) => ({
        'Обычный': 'rank-common',
        'Редкий': 'rank-rare',
        'Эпический': 'rank-epic',
        'Легендарный': 'rank-legend',
        'Мифический': 'rank-mythic',
    }[rank] || 'rank-common');

    const rankStarsFn = (rank) => rankStars(rank);

    grid.innerHTML = pageList.map(c => {
        return `<div class="card-item ${rankClass(c.rank)}" onclick="openCardModal('${c.id}')">
            <div class="card-img-wrap">
                ${c.image_url ? renderMedia(c) : `<div class="card-img-placeholder">${esc((c.name || '?')[0])}</div>`}
                <div class="card-stars">${'★'.repeat(rankStarsFn(c.rank))}</div>
            </div>
            <div class="card-rank-bar bg-${c.rank || ''}"></div>
            <div class="card-info">
                <div class="card-name font-black">${esc(c.name || '—')}</div>
                <div class="card-rank-lbl text-[9px] font-bold r-${c.rank || ''}">${c.rank || ''}</div>
                <div class="card-stats flex gap-1">
                    <span class="card-stat atk">ATK ${c.damage || 0}</span>
                    <span class="card-stat hp">HP ${c.health || 0}</span>
                </div>
            </div>
        </div>`;
    }).join('');

    // Pagination UI
    const pag = $('cards-pagination');
    if (pag) {
        pag.innerHTML = `
            <button type="button" class="page-btn" aria-label="Предыдущая страница" ${S.page === 1 ? 'disabled' : ''} onclick="changeCardsPage(-1)">◀</button>
            <span class="page-info">${S.page} / ${totalPages}</span>
            <button type="button" class="page-btn" aria-label="Следующая страница" ${S.page === totalPages ? 'disabled' : ''} onclick="changeCardsPage(1)">▶</button>
        `;
    }
    queueLazyMediaScan();
}

function renderArenaTeamPreview() {
    const el = $('arena-team-preview'); if (!el) return;
    el.innerHTML = [1, 2, 3, 4].map(i => {
        const c = (S.team && S.team[i - 1]) || null;
        if (!c) return `<div class="mini-slot empty">${i}</div>`;
        const img = c.image_url ? renderArenaStaticMedia(c, 'mini-slot-img') : `<div style="padding:2px; font-size:9px; font-weight:800; text-align:center; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; display:flex; align-items:center; justify-content:center; width:100%; height:100%; background:linear-gradient(135deg,#e11d48,#ff8c00); color:#fff; border-radius:6px;">${esc(c.name?.substring(0,6) || '⚔️')}</div>`;
        return `<div class="mini-slot filled" style="position:relative;overflow:hidden;border:1px solid rgba(255,106,0,0.5);" title="${esc(c.name || 'Карта')}">${img}<div class="role-ico" style="position:absolute;bottom:2px;right:2px;font-size:9px;background:rgba(0,0,0,0.7);border-radius:3px;padding:0 3px;z-index:10;">⚔</div></div>`;
    }).join('');
    scheduleArenaMediaLoads(el);
    queueLazyMediaScan();
}
window.renderArenaTeamPreview = renderArenaTeamPreview;

function updateArenaFightButton(lobby = S.arenaLobby) {
    const button = $('btn-arena-fight'); if (!button) return;
    const draft = arenaDeckDraft(S.arenaEditingDeckId || S.arenaActiveDeckId || '1');
    const count = arenaDeckCardCount(draft);
    const resting = Object.values(draft).filter(id => arenaCardRecovery(id) > 0).length;
    const ready = count === 4 && !resting;
    const activeBattle = Boolean(lobby?.active_battle?.battle_id);
    const offline = navigator.onLine === false;
    button.disabled = !ready || activeBattle || offline || Boolean(S.arenaPreparingFight);
    const label = button.querySelector('.btn-text');
    if (label) label.textContent = S.arenaPreparingFight ? 'Сохраняем и ищем…' : activeBattle ? 'Продолжи бой' : count < 4 ? 'Собери отряд' : resting ? 'Замени отдыхающие' : offline ? 'Нет сети' : S.arenaNetworkUnavailable ? 'Повторить' : 'Начать бой';
    setText('arena-save-status', S.arenaPreparingFight ? 'Готовим бой' : activeBattle ? 'Есть незавершённый бой' : offline ? 'Нет подключения к интернету' : S.arenaNetworkUnavailable ? 'Нет связи с ареной' : S.arenaDeckNotice || (count < 4 ? `Выбрано ${count} из 4` : resting ? `На отдыхе: ${resting}` : isArenaDeckDirty(S.arenaEditingDeckId) ? 'Сохранится перед боем' : '4 карты готовы'));
}
window.addEventListener('online', () => { if (isArenaWorkbenchVisible()) renderTeam(); });
window.addEventListener('offline', () => { if (isArenaWorkbenchVisible()) renderTeam(); });

function setArenaBattleChrome(inBattle) {
    const battleUi = !!inBattle && S.tab === 'arena';
    const resultUi = S.arena.screen === 'result' && S.tab === 'arena';
    if (document.body.classList.contains('arena-battle-open') !== battleUi) { tutorials.close(); activeGuide?.close('screen-change'); }
    if (!battleUi) cancelArenaCombatMotion();
    document.body.classList.toggle('arena-workbench-open', S.tab === 'arena');
    document.body.classList.toggle('arena-battle-open', battleUi);
    document.body.classList.toggle('arena-result-open', resultUi);
    const nav = $('bottom-nav');
    if (!nav) return;
    if (S.tab === 'exchange' || S.tab === 'player-profile') return;
    nav.hidden = battleUi || resultUi;
}

function renderArenaLeagueProgress(lobby) {
    const fill = $('ar-prog-fill');
    const progress = $('ar-progress');
    const raw = Number(lobby?.league?.progress_pct);
    const value = Number.isFinite(raw) ? Math.min(100, Math.max(0, raw)) : 0;
    if (fill) fill.style.width = `${value}%`;
    if (!lobby?.league) {
        progress?.removeAttribute('aria-valuenow');
        setText('ar-prog-text', 'Рейтинг загружается…');
        return;
    }
    progress?.setAttribute('aria-valuenow', String(value));
    const next = Number(lobby.league.next_trophies || 0);
    const left = Math.max(0, next - Number(lobby.trophies || 0));
    setText('ar-prog-text', left > 0 ? `До следующей лиги: ${left.toLocaleString('ru-RU')}` : 'Высшая лига');
}

function renderArena(syncRemote = false) {
    const sectionMenu = $('arena-section-menu');
    if (sectionMenu) sectionMenu.removeAttribute('open');
    const u = S.user || {};
    const scr = S.arena.screen || 'lobby';
    const sectionNav = $('arena-section-nav');
    if (sectionNav) sectionNav.hidden = !['lobby', 'ranking', 'stats', 'history'].includes(scr);
    document.querySelectorAll('[data-arena-screen]').forEach(button => {
        if (button.getAttribute('data-arena-screen') === scr) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
    });
    setArenaBattleChrome(scr === 'battle');

    // visibility toggles
    const ids = ['arena-lobby','arena-loading','arena-battle','arena-result','arena-stats-screen','arena-lb-wrap','arena-replays-section'];
    ids.forEach(id => { const el = $(id); if (el) el.style.display = 'none'; });

    if (scr === 'lobby') {
        stopArenaLoadingStatus();
        warmArenaLoadingAsset();
        $('arena-lobby').style.display = 'block';
        animCount('ar-trophy', u.trophy || u.trophies || 0);
        setText('ar-league', u.league?.name || u.league_name || (typeof u.league === 'string' ? u.league : 'Бронзовая I'));
        setText('arena-my-name', u.name || u.nickname || 'Ты');
        renderTeam();
        applyArenaEffects();
        const lobby = S.arenaLobby;
        renderArenaLeagueProgress(lobby);
        if (lobby) {
            setText('ar-league', lobby.league?.league_name || 'Бронзовая I');
            setText('ar-trophy', Number(lobby.trophies || 0).toLocaleString('ru-RU'));
            setText('ar-mmr', `MMR ${Number(lobby.mmr || 1000)}`);
            const banner = $('arena-active-battle-banner');
            if (banner) banner.style.display = lobby.active_battle ? 'flex' : 'none';
            if (lobby.active_battle) setText('active-banner-opp', lobby.active_battle.opponent_name || 'Реальный соперник');
        }
        
        // Sync v2 state asynchronously
        const uid = effectiveUid();
        if (uid && syncRemote) {
            const arenaEpochAtStart = arenaMutationEpoch;
            loadArenaState().then(res => {
                if (arenaEpochAtStart !== arenaMutationEpoch) return;
                if (!res || res.error) {
                    S.arenaNetworkUnavailable = !res || /(?:network|fetch|соедин|сеть|timeout|сервер)/i.test(String(res.error || ''));
                    S.arenaDeckNotice = res?.error || 'Не удалось загрузить арену';
                    renderTeam();
                    return;
                }
                S.arenaNetworkUnavailable = false;
                S.arenaDeckNotice = '';
                S.arenaLobby = res;
                renderArenaLeagueProgress(res);
                mergeArenaDecksFromServer(res.decks);
                S.arenaActiveDeckId = String(res.active_deck_id || '1');
                S.arenaRecoveryLoadedAt = Date.now();
                syncArenaStats(res);
                if (res.trophies !== undefined) animCount('ar-trophy', res.trophies);
                if (res.league?.league_name) setText('ar-league', res.league.league_name);
                if (res.nickname) setText('arena-my-name', res.nickname);
                if (res.team && Array.isArray(res.team)) {
                    S.team = res.team.map((t, idx) => t ? { ...t, slot: t.slot || idx + 1 } : null);
                    renderArenaTeamPreview();
                }

                updateArenaFightButton(res);

                const banner = $('arena-active-battle-banner');
                if (banner) {
                    banner.style.display = res.active_battle ? 'flex' : 'none';
                    if (res.active_battle) {
                        setText('active-banner-opp', `Противник: ${res.active_battle.opponent_name}`);
                    }
                }
            }).catch(() => { S.arenaNetworkUnavailable = true; if (isArenaWorkbenchVisible()) renderTeam(); });
        }
    }
    if (scr === 'loading') {
        $('arena-loading').style.display = 'flex';
        $('arena-loading').style.flexDirection = 'column';
        startArenaLoadingStatus();
    }
    if (scr === 'battle') { stopArenaLoadingStatus(); $('arena-battle').style.display = 'flex'; renderBattleV2(); }
    if (scr === 'result') { $('arena-result').style.display = 'flex'; $('arena-result').style.flexDirection='column'; }
    if (scr === 'stats') { $('arena-stats-screen').style.display = 'block'; renderArenaStats(); }
    if (scr === 'ranking') $('arena-lb-wrap').style.display = 'block';
    if (scr === 'history') $('arena-replays-section').style.display = 'block';

    // The ranking is its own screen, never appended below the collection.
    if (scr === 'ranking') {
        const lb = $('leaderboard-list'); if (!lb) return;
        const isClanBoard = S.lbType === 'clan';
        const openRow = (p) => (!isClanBoard && p.uid) ? ` data-profile-uid="${esc(String(p.uid))}" role="button" tabindex="0"` : '';
        const meCard = renderMyRankCard(isClanBoard);
        if (!S.leaders.length) { lb.innerHTML = meCard + '<div class="empty-state">Нет данных</div>'; }
        else {
            const av = (p) => userAvatarHtml(p.avatar, p.name || '?', 'lb-avatar-img', p.uid);
            if (isClanBoard) {
                lb.innerHTML = meCard + `<div class="clan-rank-list">${S.leaders.slice(0, 100).map((p, i) => {
                    const place = i + 1;
                    return `<div class="clan-rank-row top-${Math.min(place, 4)}">
                        <div class="clan-rank-place">${place === 1 ? '♛' : place}</div>
                        <div class="clan-rank-avatar">${userAvatarHtml(p.avatar, p.name || '?', 'lb-avatar-img', '')}</div>
                        <div class="clan-rank-copy">
                            <b>${esc(p.name || 'Клан')}</b>
                            <span>${p.members_count || 0} участников · уровень ${p.level || 1}</span>
                        </div>
                        <div class="clan-rank-score">${p.score?.toLocaleString('ru-RU') || 0}</div>
                    </div>`;
                }).join('')}</div>`;
                return;
            }
            const top = S.leaders.slice(0, 3);
            const rest = S.leaders.slice(3, 100);
            const crown = `<span class="lb-pod-crown" aria-hidden="true"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M3 8.5 7 11l5-6.5L17 11l4-2.5-1.6 9.3a1 1 0 0 1-1 .83H5.6a1 1 0 0 1-1-.83L3 8.5Z"/></svg></span>`;
            const pod = (p, place) => !p ? '' : `
                <div class="lb-pod p${place}${p.theme_url ? ' has-theme' : ''}"${openRow(p)}>
                    ${rowThemeBgHtml(p, 'lb-pod-bg')}
                    <div class="lb-pod-fg">
                        ${place === 1 ? crown : `<div class="lb-pod-place">${place}</div>`}
                        <div class="lb-pod-av">${av(p)}</div>
                        <div class="lb-pod-name">${esc(p.name || '?')}</div>
                        <div class="lb-pod-score">${p.score?.toLocaleString('ru-RU') || 0}</div>
                    </div>
                </div>`;
            const podium = top.length
                ? `<div class="lb-podium">${pod(top[1], 2)}${pod(top[0], 1)}${pod(top[2], 3)}</div>`
                : '';
            lb.innerHTML = meCard + podium + rest.map((p, i) => `
            <div class="lb-row${p.theme_url ? ' has-theme' : ''}"${openRow(p)}>
                ${rowThemeBgHtml(p, 'lb-row-bg')}
                <div class="lb-row-fg">
                    <div class="lb-rank">${i + 4}</div>
                    <div class="lb-avatar">${av(p)}</div>
                    <div class="lb-name">${esc(p.name || '?')}</div>
                    <div class="lb-trophy">${p.score?.toLocaleString('ru-RU') || 0}</div>
                </div>
            </div>`).join('');
        }
        bindLeaderboardProfileClicks(lb);
    }
}

function bindLeaderboardProfileClicks(lb) {
    if (!lb || lb.dataset.profileBound === '1') return;
    lb.dataset.profileBound = '1';
    lb.addEventListener('click', (e) => {
        const row = e.target.closest('[data-profile-uid]');
        if (!row) return;
        const uid = row.getAttribute('data-profile-uid');
        if (uid) openPlayerProfile(uid);
    });
    lb.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const row = e.target.closest('[data-profile-uid]');
        if (!row) return;
        e.preventDefault();
        const uid = row.getAttribute('data-profile-uid');
        if (uid) openPlayerProfile(uid);
    });
}

/** Renders a player's active profile-theme (photo/gif/video) as a row/podium backdrop. */
function rowThemeBgHtml(p, cls) {
    if (!p?.theme_url) return '';
    const src = themeMediaUrl(p.theme_url);
    if (!src) return '';
    if (p.theme_type === 'video') {
        return `<video class="${cls}" autoplay loop muted playsinline src="${cssUrl(src)}"></video>`;
    }
    return `<img class="${cls}" src="${cssUrl(src)}" alt="" loading="lazy" decoding="async" onerror="this.remove()">`;
}

function renderMyRankCard(isClanBoard) {
    const me = S.myRank;
    if (!me || !me.rank) return '';
    const u = S.user || {};
    const uid = effectiveUid();
    const clanList = (S.clanLeaders?.length ? S.clanLeaders : S.leaders) || [];
    const clanId = String(S.clan?.clan_id || '');
    const clanRow = isClanBoard
        ? ((clanId && clanList.find(c => String(c.uid) === clanId)) || clanList[Math.max(0, (me.rank || 1) - 1)] || null)
        : null;
    const clanName = S.clan?.name || clanRow?.name || 'Мой клан';
    const emblem = isClanBoard ? (resolveClanEmblemUrl(clanRow) || resolveClanEmblemUrl(S.clan)) : '';
    const avatarHtml = isClanBoard
        ? (emblem
            ? userAvatarHtml(emblem, clanName, 'lb-me-avatar-img', '')
            : userAvatarHtml('', u.name || clanName, 'lb-me-avatar-img', uid))
        : userAvatarHtml(u.photo_url || u.avatar_url || TGuser?.photo_url || '', u.name || '?', 'lb-me-avatar-img', uid);
    const name = isClanBoard ? clanName : (u.name || 'Игрок');
    const score = (me.score || 0).toLocaleString('ru-RU');
    const clickable = isClanBoard ? '' : ` data-profile-uid="${esc(String(uid))}" role="button" tabindex="0"`;
    const activeThemeId = !isClanBoard ? (S.themes.mine?.active_theme || '') : '';
    const myTheme = activeThemeId ? (S.themes.mine?.owned_details || []).find(t => t.id === activeThemeId) : null;
    const themeBg = myTheme ? rowThemeBgHtml({ theme_url: myTheme.media_url, theme_type: myTheme.media_type }, 'lb-me-bg') : '';
    return `
    <div class="lb-me-card${myTheme ? ' has-theme' : ''}"${clickable}>
        ${themeBg}
        <div class="lb-me-fg">
            <div class="lb-me-avatar">${avatarHtml}</div>
            <div class="lb-me-body">
                <div class="lb-me-kicker">Ваш ранг</div>
                <div class="lb-me-name">${esc(name)}</div>
            </div>
            <div class="lb-me-rank">
                <div class="lb-me-rank-num">#${me.rank}</div>
                <div class="lb-me-rank-score">${score}</div>
            </div>
        </div>
    </div>`;
}

const RANK_EMOJI = { Обычный: '⚜', Редкий: '🧨', Эпический: '🪄', Легендарный: '🍕', Мифический: '🔮' };
function emojiRank(r) { return RANK_EMOJI[r] || '◈'; }

async function arenaShowStats() {
    S.arena.screen = 'stats';
    renderArena();
    const status = $('as-stats-status');
    if (status) status.textContent = 'Обновляем данные';
    const uid = effectiveUid();
    if (!uid) return;
    const loadId = ++arenaStatsLoadId;
    try {
        const lobby = await loadArenaState({ force: true, maxAge: 0 });
        if (loadId !== arenaStatsLoadId || S.arena.screen !== 'stats' || !lobby || lobby.error) return;
        S.arenaLobby = lobby;
        mergeArenaDecksFromServer(lobby.decks);
        S.arenaActiveDeckId = String(lobby.active_deck_id || S.arenaActiveDeckId || '1');
        S.arenaRecoveryLoadedAt = Date.now();
        syncArenaStats(lobby);
        renderArenaStats();
        setText('as-stats-status', 'Обновлено сейчас');
        scheduleDeviceSnapshotSave(uid);
    } catch (error) {
        if (loadId !== arenaStatsLoadId || S.arena.screen !== 'stats') return;
        setText('as-stats-status', 'Последние сохранённые данные');
        console.warn('[ARENA STATS]', error);
    }
}
window.arenaShowStats = arenaShowStats;

function arenaBackToLobby() {
    arenaStatsLoadId += 1;
    if (S.arena.screen === 'result' && S.arenaLobby?.active_battle) S.arenaLobby = { ...S.arenaLobby, active_battle: null };
    S.arena = { screen: 'lobby', opponent: null, battle: null, selectedAttackerId: null, selectedTargetId: null, replay: null };
    arenaRestoringBattle = false;
    persistArenaDeviceState();
    renderArena();
}
window.arenaBackToLobby = arenaBackToLobby;

function arenaBattleToLobby() {
    const battle = S.arena.battle;
    if (battle?.battle_id) S.arenaLobby = { ...(S.arenaLobby || {}), active_battle: { battle_id: battle.battle_id, opponent_name: battle.opponent_info?.name || 'Противник' } };
    arenaBackToLobby();
}
window.arenaBattleToLobby = arenaBattleToLobby;

function arenaShowBattleInfo() {
    ($('arena-battle-info-dialog') as HTMLDialogElement | null)?.showModal();
}
window.arenaShowBattleInfo = arenaShowBattleInfo;

function renderArenaStats() {
    const u = S.user || {};
    const arenaStats = S.arenaLobby?.stats || {};
    const wins = Number(arenaStats.wins ?? arenaStats.win ?? u.wins ?? u.win ?? 0) || 0;
    const losses = Number(arenaStats.losses ?? arenaStats.lose ?? u.losses ?? u.loses ?? u.lose ?? 0) || 0;
    const total = wins + losses;
    const wr = total ? Math.round(wins / total * 100) : 0;
    const defSuccess = Number(arenaStats.def_success ?? arenaStats.reflected ?? u.def_success ?? u.reflected ?? 0) || 0;
    const defFail = Number(arenaStats.def_fail ?? u.def_fail ?? 0) || 0;
    const defTotal = defSuccess + defFail;
    const defRate = defTotal ? Math.round(defSuccess / defTotal * 100) : 0;
    setText('as-wins', wins);
    setText('as-losses', losses);
    setText('as-winrate', wr + '%');
    setText('as-total', total);
    setText('as-trophies', S.arenaLobby?.trophies ?? u.trophy ?? u.trophies ?? 0);
    setText('as-mmr', S.arenaLobby?.mmr ?? u.mmr ?? 1000);
    setText('as-streak', S.arenaLobby?.win_streak ?? u.win_streak ?? 0);
    setText('as-def-success', defSuccess);
    setText('as-def-fail', defFail);
    setText('as-def-rate', `${defRate}%`);
    const ring = $('stats-ring');
    if (ring) ring.style.setProperty('--pct', wr);
    const defenseBar = $('as-def-bar');
    if (defenseBar) defenseBar.style.setProperty('--def-pct', defRate);
}

function syncArenaStats(lobby) {
    const stats = lobby?.stats;
    if (!stats) return;
    S.user = S.user || {};
    S.user.wins = Number(stats.wins ?? S.user.wins ?? 0) || 0;
    S.user.losses = Number(stats.losses ?? S.user.losses ?? S.user.loses ?? 0) || 0;
    S.user.loses = S.user.losses;
    S.user.def_success = Number(stats.def_success ?? S.user.def_success ?? 0) || 0;
    S.user.def_fail = Number(stats.def_fail ?? S.user.def_fail ?? 0) || 0;
}

function warmArenaLoadingAsset() {
    if (arenaLoadingAssetWarmed) return;
    arenaLoadingAssetWarmed = true;
    const preload = new Image();
    preload.decoding = 'async';
    preload.src = 'assets/arena/matchmaking.png';
}

function startArenaLoadingStatus() {
    const image = document.querySelector('[data-arena-loading-art]');
    if (image && !image.getAttribute('src')) image.setAttribute('src', image.getAttribute('data-arena-loading-art') || '');
    if (arenaLoadingTimer) return;
    const messages = ['Связываемся с ареной', 'Ищем игрока близкого по MMR', 'Готовим поле боя'];
    let index = 0;
    setText('arena-loading-text', messages[index]);
    arenaLoadingTimer = setInterval(() => {
        index = (index + 1) % messages.length;
        setText('arena-loading-text', messages[index]);
    }, 1800);
    if (arenaLoadingWatchdog) clearTimeout(arenaLoadingWatchdog);
    arenaLoadingWatchdog = setTimeout(() => {
        if (S.arena.screen !== 'loading') return;
        arenaSearchController?.abort();
        S.arena.screen = 'lobby';
        renderArena(false);
        showToast('Арена не ответила. Попробуй ещё раз');
        void reconcileArenaSearch();
    }, 22000);
}

function stopArenaLoadingStatus() {
    if (arenaLoadingTimer) clearInterval(arenaLoadingTimer);
    arenaLoadingTimer = null;
    if (arenaLoadingWatchdog) clearTimeout(arenaLoadingWatchdog);
    arenaLoadingWatchdog = null;
}

function arenaCancelSearch(quiet = false) {
    arenaSearchController?.abort();
    arenaSearchController = null;
    stopArenaLoadingStatus();
    if (S.arena.screen === 'loading') {
        S.arena.screen = 'lobby';
        renderArena(false);
    }
    if (!quiet) showToast('Поиск отменён');
    void reconcileArenaSearch();
}
window.arenaCancelSearch = arenaCancelSearch;

let arenaSearchReconciliation = null;
function reconcileArenaSearch() {
    if (arenaSearchReconciliation) return arenaSearchReconciliation;
    arenaSearchReconciliation = (async () => {
        try {
            const state = await loadArenaState({ force: true, maxAge: 0 });
            if (!state || state.error) return;
            S.arenaLobby = state;
            if (state.active_battle?.battle_id && S.arena.screen !== 'battle' && S.arena.screen !== 'result') {
                await resumeActiveBattle();
            } else if (S.arena.screen === 'lobby') renderArena(false);
        } catch { /* The lobby remains usable while the next state refresh retries. */ }
    })().finally(() => { arenaSearchReconciliation = null; });
    return arenaSearchReconciliation;
}

function arenaFindOpponent() {
    return arenaFindActionGuard.run('find-opponent', runArenaFindOpponent);
}

async function runArenaFindOpponent() {
    haptic('medium');
    const teamReady = S.arenaLobby?.team_ready ?? ((S.team || []).filter(Boolean).length === 4);
    if (!teamReady) {
        showToast(S.arenaLobby?.team_error || 'Сначала собери команду из 4 карт');
        return;
    }
    S.arena.screen = 'loading';
    renderArena();
    const uid = effectiveUid();
    if (!uid) { showToast('◈ ОТКРОЙ ЧЕРЕЗ TELEGRAM'); S.arena.screen='lobby'; renderArena(); return; }
    try {
        const searchController = new AbortController();
        arenaSearchController = searchController;
        // Use v2 engine route
        // Always let server use the player's real universe from DB.
        // S.user.universe is the field from /api/user_stats (not .verce).
        const res = await api('/api/arena_find', {
            method: 'POST',
            timeoutMs: 20000,
            signal: searchController.signal,
            body: { user_id: uid }
        });
        if (searchController.signal.aborted || S.arena.screen !== 'loading') {
            await reconcileArenaSearch();
            return;
        }
        if (!res || res.error) {
            const err = String(res?.error || res?.message || 'Нет противников').replace(/^(Error:\s*)+/i, '');
            showToast(err);
            S.arena.screen = 'lobby'; renderArena();
            await reconcileArenaSearch();
            return;
        }
        S.arenaNetworkUnavailable = false;
        setArenaBattleState(res.state || res.battle_state || res, 'battle');
        renderArena();
    } catch (e) {
        if (arenaSearchController?.signal.aborted || S.arena.screen !== 'loading') return;
        let msg = String(e?.message || e || '').replace(/^(Error:\s*)+/i, '');
        if (msg.toLowerCase().includes('cooldown') || msg.toLowerCase().includes('охлажден') || msg.toLowerCase().includes('доступна через') || msg.toLowerCase().includes('подождите')) {
            if (!msg.includes('⌛') && !msg.includes('⏳')) msg = `⏳ ${msg}`;
            showToast(msg);
        } else if (msg.includes('DEPRECATED_USE_ARENA_V2') || msg.includes('410')) {
            showToast('🔄 Арена обновлена! Пожалуйста, перезагрузите страницу.');
        } else {
            S.arenaNetworkUnavailable = true;
            showToast('Арена не ответила. Попробуй ещё раз');
        }
        S.arena.screen = 'lobby'; renderArena();
        await reconcileArenaSearch();
    } finally {
        arenaSearchController = null;
        stopArenaLoadingStatus();
    }
}
window.arenaFindOpponent = arenaFindOpponent;

async function resumeActiveBattle() {
    const active = S.arenaLobby?.active_battle;
    if (!active?.battle_id) {
        showToast('Активный бой не найден');
        return;
    }
    S.arena.screen = 'loading';
    renderArena();
    try {
        const uid = effectiveUid();
        const state = await api(`/api/arena_battle_state?id=${encodeURIComponent(active.battle_id)}&user_id=${uid}`);
        if (!state || state.error) throw new Error(state?.error || 'Бой не найден');
        setArenaBattleState(state, state.is_finished ? 'result' : 'battle');
        renderArena();
        if (state.is_finished) _finishBattleV2(state);
    } catch (e) {
        showToast(String(e?.message || e));
        arenaBackToLobby();
    }
}
window.resumeActiveBattle = resumeActiveBattle;

function renderBattleV2Legacy() {
    const b = S.arena.battle; if (!b) return;
    const uid = effectiveUid();
    const myInfo = b.player_info || { name: 'Ты', id: uid };
    const oppInfo = b.opponent_info || { name: 'Противник' };

    setText('battle-my-name', myInfo.name);
    setText('battle-opp-name', oppInfo.name);
    setText('battle-round-badge', `РАУНД ${b.round || 1}/${b.max_rounds || 8}`);
    
    const statusPill = $('battle-turn-status');
    if (statusPill) {
        if (b.is_finished) statusPill.textContent = 'Бой завершён';
        else statusPill.textContent = b.is_my_turn ? 'Твой ход ⚔️' : 'Ход противника ⏳';
        statusPill.style.background = b.is_my_turn ? 'linear-gradient(135deg, #22c55e, #15803d)' : 'rgba(255,255,255,0.15)';
    }

    const cards = b.cards || [];
    const myCards = cards.filter(c => strEq(c.team_id, uid) || strEq(c.team_id, b.my_team_id) || strEq(c.team_id, myInfo.id));
    const oppCards = cards.filter(c => !myCards.includes(c));
    const selectedAttacker = myCards.find(c => c.id === S.arena.selectedAttackerId && c.is_alive && c.hp > 0);
    if (!selectedAttacker && b.is_my_turn) S.arena.selectedAttackerId = b.current_turn_card_id;

    // HP calculation
    const myHp = myCards.reduce((s, c) => s + Math.max(0, c.hp || 0), 0);
    const myMaxHp = myCards.reduce((s, c) => s + Math.max(1, c.max_hp || 1), 0);
    const oppHp = oppCards.reduce((s, c) => s + Math.max(0, c.hp || 0), 0);
    const oppMaxHp = oppCards.reduce((s, c) => s + Math.max(1, c.max_hp || 1), 0);

    const myPct = myMaxHp ? Math.round(Math.min(100, (myHp / myMaxHp) * 100)) : 0;
    const oppPct = oppMaxHp ? Math.round(Math.min(100, (oppHp / oppMaxHp) * 100)) : 0;

    if ($('battle-my-hp-fill')) $('battle-my-hp-fill').style.width = myPct + '%';
    if ($('battle-opp-hp-fill')) $('battle-opp-hp-fill').style.width = oppPct + '%';
    setText('battle-my-hp-num', `${myHp} / ${myMaxHp}`);
    setText('battle-opp-hp-num', `${oppHp} / ${oppMaxHp}`);

    // Render cards into front/back grids
    const renderCardUnit = (c, isOpp) => {
        const isDead = !c.is_alive || c.hp <= 0;
        const isCurrent = (b.current_turn_card_id === c.id);
        const isSelected = (S.selectedTargetId === c.id) && isOpp && !isDead;
        const isAttacker = !isOpp && b.is_my_turn && S.arena.selectedAttackerId === c.id && !isDead;
        const img = c.image_url ? renderMedia(c, 'battle-card-img', { eager: true }) : `<div class="card-icon" style="font-size:24px;">🎴</div>`;
        const hpPct = Math.max(0, Math.round((c.hp / Math.max(1, c.max_hp)) * 100));
        return `<div class="battle-card-unit ${isDead ? 'dead' : ''} ${isCurrent ? 'current-turn-glow' : ''} ${isAttacker ? 'selected-attacker' : ''}" data-id="${c.id}" data-battle-card-id="${esc(String(c.id))}" onclick="onUnitClicked('${c.id}', ${isOpp})" style="position:relative;width:64px;height:84px;background:var(--bg-card);border:2px solid ${isAttacker ? '#ff7a1a' : (isCurrent ? '#22c55e' : (isSelected ? '#eab308' : (isOpp ? '#ef4444' : '#3b82f6')))};border-radius:12px;overflow:hidden;display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;margin:3px;box-shadow:${isSelected ? '0 0 12px #eab308' : '0 4px 12px rgba(0,0,0,0.5)'};">
            ${img}
            <div class="battle-target-marker" style="display:${isSelected ? 'block' : 'none'};position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);font-size:24px;z-index:20;pointer-events:none;filter:drop-shadow(0 0 4px #000);">🎯</div>
            <div class="battle-attacker-badge" style="display:${isAttacker ? 'block' : 'none'}">АТАКУЕТ</div>
            <div style="position:absolute;top:2px;left:2px;font-size:8px;background:rgba(0,0,0,0.8);color:#fff;padding:1px 4px;border-radius:4px;font-weight:800;z-index:10;">${c.position||1}</div>
            <div style="position:absolute;bottom:0;left:0;right:0;background:rgba(0,0,0,0.8);padding:2px;z-index:10;">
                <div style="width:100%;height:4px;background:rgba(255,255,255,0.2);border-radius:2px;overflow:hidden;">
                    <div class="battle-unit-hp-fill" style="width:${hpPct}%;height:100%;background:${isOpp?'#ef4444':'#22c55e'};"></div>
                </div>
                <div class="battle-unit-name" style="font-size:8px;font-weight:900;color:#fff;text-align:center;margin-top:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(c.name?.substring(0,8)||'Унит')}</div>
            </div>
        </div>`;
    };

    const myFront = myCards.filter(c => +c.position <= 3);
    const myBack  = myCards.filter(c => +c.position > 3);
    const oppFront = oppCards.filter(c => +c.position <= 3);
    const oppBack  = oppCards.filter(c => +c.position > 3);

    const renderBattleGrid = (id, list, isOpp) => {
        const grid = $(id);
        if (!grid) return;
        const mediaSignature = stableDataSignature(list.map(card => [card.id, card.position, card.image_url, card.type, card.rank]));
        if (grid.dataset.mediaSignature !== mediaSignature) {
            grid.innerHTML = list.map(card => renderCardUnit(card, isOpp)).join('');
            grid.dataset.mediaSignature = mediaSignature;
        }
        const units = new Map([...grid.querySelectorAll('[data-battle-card-id]')].map(node => [node.dataset.battleCardId, node]));
        list.forEach(card => {
            const unit = units.get(String(card.id));
            if (!unit) return;
            const isDead = !card.is_alive || Number(card.hp) <= 0;
            const isCurrent = b.current_turn_card_id === card.id;
            const isSelected = isOpp && S.selectedTargetId === card.id && !isDead;
            const isAttacker = !isOpp && b.is_my_turn && S.arena.selectedAttackerId === card.id && !isDead;
            const hpPct = Math.max(0, Math.round(Number(card.hp || 0) / Math.max(1, Number(card.max_hp || 1)) * 100));
            unit.classList.toggle('dead', isDead);
            unit.classList.toggle('current-turn-glow', isCurrent);
            unit.classList.toggle('selected-attacker', isAttacker);
            unit.style.borderColor = isAttacker ? '#ff7a1a' : (isCurrent ? '#22c55e' : (isSelected ? '#eab308' : (isOpp ? '#ef4444' : '#3b82f6')));
            unit.style.boxShadow = isSelected ? '0 0 12px #eab308' : '0 4px 12px rgba(0,0,0,0.5)';
            const targetMarker = unit.querySelector('.battle-target-marker');
            if (targetMarker) targetMarker.style.display = isSelected ? 'block' : 'none';
            const attackerBadge = unit.querySelector('.battle-attacker-badge');
            if (attackerBadge) attackerBadge.style.display = isAttacker ? 'block' : 'none';
            const hpFill = unit.querySelector('.battle-unit-hp-fill');
            if (hpFill) hpFill.style.width = `${hpPct}%`;
            const nameNode = unit.querySelector('.battle-unit-name');
            if (nameNode) nameNode.textContent = card.name?.substring(0, 8) || 'Унит';
        });
    };
    renderBattleGrid('my-frontline', myFront, false);
    renderBattleGrid('my-backline', myBack, false);
    renderBattleGrid('opp-frontline', oppFront, true);
    renderBattleGrid('opp-backline', oppBack, true);

    // Check if selected target is dead or invalid
    if (S.selectedTargetId) {
        const tc = cards.find(c => c.id === S.selectedTargetId);
        if (!tc || !tc.is_alive || tc.hp <= 0) S.selectedTargetId = null;
    }

    // One clear action: basic attack. Target selection is optional.
    const activeCard = myCards.find(c => c.id === S.arena.selectedAttackerId && c.is_alive) || cards.find(c => c.id === b.current_turn_card_id);
    const selTarget = S.selectedTargetId ? cards.find(c => c.id === S.selectedTargetId) : null;
    if (activeCard && b.is_my_turn && !b.is_finished) {
        setText(
            'caster-name',
            selTarget
                ? `${activeCard.name.substring(0,14)} → 🎯 ${selTarget.name.substring(0,12)}`
                : 'Выбери свою атакующую карту, затем цель противника'
        );
    } else {
        setText('caster-name', activeCard ? `Ход противника (${activeCard.name.substring(0,12)})...` : 'Ожидание...');
    }

    const attackBtn = $('btn-arena-attack');
    if (attackBtn) {
        attackBtn.disabled = !activeCard || !b.is_my_turn || b.is_finished || !selTarget || S.arenaActionBusy;
        attackBtn.textContent = S.arenaActionBusy
            ? 'АТАКА...'
            : (selTarget ? `⚔ АТАКОВАТЬ: ${selTarget.name.substring(0,10)}` : 'СНАЧАЛА ВЫБЕРИ ЦЕЛЬ');
    }

    // Update Logs
    const logEl = $('battle-log');
    if (logEl && b.log) {
        logEl.innerHTML = b.log.map(l => `<div class="battle-log-entry" style="padding:4px 0;border-bottom:1px solid rgba(255,255,255,0.05);font-size:11px;">⚔ ${typeof l==='string'?l:JSON.stringify(l)}</div>`).join('');
        logEl.scrollTop = logEl.scrollHeight;
    }

    if (b.is_finished) {
        _finishBattleV2(b);
    }
}

function strEq(a, b) { return String(a || '') === String(b || ''); }

function onUnitClicked(unitId, isOpp) {
    if (!S.arena.battle || !S.arena.battle.is_my_turn) return;
    const cards = S.arena.battle.cards || [];
    const tc = cards.find(c => c.id === unitId);
    if (!tc || !tc.is_alive || tc.hp <= 0) {
        showToast('⚠ Враг уже побеждён!');
        return;
    }
    haptic('light');
    if (!isOpp) {
        S.arena.selectedAttackerId = unitId;
        showToast(`Атакует: ${tc.name}`);
        renderBattleV2();
        return;
    }
    S.selectedTargetId = unitId;
    showToast(`🎯 Цель: ${tc.name}`);
    renderBattleV2();
}

function battleUnitElement(cardId) {
    return [...document.querySelectorAll('.battle-card-unit')]
        .find(el => String(el.dataset.id) === String(cardId)) || null;
}

async function playArenaAttackAnimation(attackerId, targetId, enemyAttack = false) {
    const attacker = battleUnitElement(attackerId);
    const target = battleUnitElement(targetId);
    if (!attacker || !target) return;

    const from = attacker.getBoundingClientRect();
    const to = target.getBoundingClientRect();
    attacker.classList.add(enemyAttack ? 'arena-enemy-lunge' : 'arena-player-lunge');
    target.classList.add('arena-target-hit');

    const slash = document.createElement('div');
    slash.className = `arena-slash-projectile ${enemyAttack ? 'enemy' : 'player'}`;
    slash.style.left = `${from.left + from.width / 2}px`;
    slash.style.top = `${from.top + from.height / 2}px`;
    slash.style.setProperty('--attack-x', `${to.left + to.width / 2 - (from.left + from.width / 2)}px`);
    slash.style.setProperty('--attack-y', `${to.top + to.height / 2 - (from.top + from.height / 2)}px`);
    document.body.appendChild(slash);

    haptic('heavy');
    await new Promise(resolve => setTimeout(resolve, 520));
    attacker.classList.remove('arena-player-lunge', 'arena-enemy-lunge');
    target.classList.remove('arena-target-hit');
    slash.remove();
}

function findEnemyRetaliation(before, after, uid) {
    const beforeCards = before?.cards || [];
    const afterLogs = after?.log || [];
    const newLogs = afterLogs.slice((before?.log || []).length);
    const myIds = new Set(
        beforeCards
            .filter(card => strEq(card.team_id, uid) || strEq(card.team_id, before?.player_info?.id))
            .map(card => card.id)
    );
    const enemyCards = beforeCards.filter(card => !myIds.has(card.id));
    const myCards = beforeCards.filter(card => myIds.has(card.id));
    const event = newLogs.find(log =>
        log && log.type === 'damage'
        && enemyCards.some(card => card.name === log.caster)
        && myCards.some(card => card.name === log.target)
    );
    if (!event) return null;
    return {
        attackerId: enemyCards.find(card => card.name === event.caster)?.id,
        targetId: myCards.find(card => card.name === event.target)?.id,
    };
}

function selectSkill() {
    return arenaAttackSelected();
}
window.selectSkill = selectSkill;
window.onUnitClicked = onUnitClicked;

function arenaSkipAuto() {
    // Legacy cache compatibility. The active Arena has no auto-finish action.
    showToast('Авто-финиш отключён: выбери карту и цель');
}
window.arenaSkipAuto = arenaSkipAuto;

function arenaBattleSides(b) {
    const cards = Array.isArray(b?.cards) ? b.cards : [];
    const playerId = String(b?.player_info?.id || b?.my_team_id || effectiveUid() || '');
    const mine = cards.filter(card => String(card.team_id || card.owner_id || '') === playerId || card.side === 'player');
    const enemy = cards.filter(card => !mine.includes(card));
    const byLane = list => [...list].sort((a, z) => Number(a.lane ?? a.position ?? 0) - Number(z.lane ?? z.position ?? 0));
    return { mine: byLane(mine), enemy: byLane(enemy) };
}

function arenaBattleCardAtLane(cards, lane) {
    return cards.find(card => Number(card.lane ?? card.position) === Number(lane)) || null;
}

function arenaEventText(event, battle = S.arena.battle) {
    if (!event || typeof event !== 'object') return String(event || '');
    const cards = Array.isArray(battle?.cards) ? battle.cards : [];
    const cardName = (battleId, cardId, fallback) => {
        const card = cards.find(item =>
            String(item.id || '') === String(battleId || '')
            || String(item.card_id || '') === String(cardId || '')
        );
        return card?.name || fallback;
    };
    const actorLane = Number(event.attacker_slot ?? event.actor_lane ?? event.attacker_lane);
    const targetLane = Number(event.target_slot ?? event.target_lane);
    const actor = cardName(
        event.attacker_id ?? event.actor_battle_card_id,
        event.attacker_card_id ?? event.actor_card_id,
        Number.isFinite(actorLane) ? `Карта ${actorLane + 1}` : 'Карта',
    );
    const target = cardName(
        event.target_id ?? event.target_battle_card_id,
        event.target_card_id,
        Number.isFinite(targetLane) ? `цель ${targetLane + 1}` : 'цель',
    );
    if (event.type === 'battle_start') return 'Бой начался';
    if (event.type === 'round_start') return `Раунд ${event.round || 1}`;
    if (event.type === 'turn_start') return event.turn === 'enemy' ? 'Ход соперника' : 'Твой ход';
    if (event.type === 'card_attack' || event.type === 'attack') return `${actor} → ${target} · −${Number(event.damage ?? event.amount ?? 0)} HP`;
    if (event.type === 'damage') return `${target} получает −${Number(event.amount ?? event.damage ?? 0)} HP`;
    if (event.type === 'death') return `${target} уничтожена`;
    if (event.type === 'round_end') return `Раунд ${event.round || 1} завершён`;
    if (event.type === 'battle_end') return 'Бой завершён';
    return '';
}

function renderArenaLaneCard(card, lane, side, b, selectedTargetId, hasAttacker) {
    const isEnemy = side === 'enemy';
    const alive = Boolean(card && card.is_alive !== false && Number(card.hp ?? card.current_hp ?? 0) > 0);
    const selected = !isEnemy && alive && String(S.arena.selectedAttackerId || '') === String(card?.id || '');
    const targeted = isEnemy && alive && String(selectedTargetId || '') === String(card?.id || '');
    const attackerSelectable = !isEnemy && alive;
    const targetSelectable = isEnemy && alive && hasAttacker;
    const selectable = (attackerSelectable || targetSelectable) && b.is_my_turn && !b.is_finished && !S.arenaActionBusy && !arenaRestoringBattle;
    if (!card) {
        return `<div class="battle-lane-slot battle-lane-slot--empty" data-lane="${lane}"><div class="battle-card-frame"><div class="battle-empty-card">Пусто</div></div></div>`;
    }
    const hp = Math.max(0, Number(card.hp ?? card.current_hp ?? 0));
    const maxHp = Math.max(1, Number(card.max_hp || 1));
    const hpPct = Math.max(0, Math.min(100, Math.round(hp / maxHp * 100)));
    const media = card.image_url || card.arena_preview_url || card.file_id
        ? renderArenaStaticMedia(card, 'battle-card-img')
        : `<span class="battle-card-fallback">${esc((card.name || '?')[0])}</span>`;
    const classes = [
        'battle-card-unit',
        isEnemy ? 'enemy' : 'player',
        alive ? '' : 'dead',
        selectable ? 'selectable' : '',
        selected ? 'selected-attacker' : '',
        targeted ? 'selected-target' : '',
        hpPct <= 25 && alive ? 'low-health' : '',
        targetSelectable && !targeted ? 'target-candidate' : '',
    ].filter(Boolean).join(' ');
    return `<div class="battle-lane-slot" data-lane="${lane}">
        <div class="battle-card-frame">
        <button type="button" class="${classes}" data-id="${esc(String(card.id))}" data-battle-card-id="${esc(String(card.id))}" data-arena-side="${side}" ${selectable ? '' : 'disabled'} aria-pressed="${selected || targeted}" title="${esc(card.name || 'Карта')} · ${hp}/${maxHp} HP" aria-label="${isEnemy ? 'Соперник' : 'Твой отряд'}, слот ${lane + 1}: ${esc(card.name || 'Карта')}, атака ${Number(card.atk ?? card.current_atk ?? 0)}, здоровье ${hp} из ${maxHp}${alive ? '' : ', выбыла'}">
            <span class="battle-card-media">${media}</span>
            <span class="battle-card-lane-mark">${lane + 1}</span>
            <span class="battle-card-death-label" aria-hidden="true">Выбыла</span>
            ${selected ? '<span class="battle-card-state">Атакует</span>' : ''}
            ${targeted ? '<span class="battle-card-target-mark">Цель</span>' : ''}
            <span class="battle-card-info">
                <span class="battle-card-stats"><b>${arenaIcon('attack')}${Number(card.atk ?? card.current_atk ?? 0)}</b><b>${arenaIcon('health')}${hp}/${maxHp}</b></span>
                <span class="battle-card-hp" aria-hidden="true"><i style="width:${hpPct}%"></i></span>
            </span>
        </button>
        <button type="button" class="battle-card-inspect" data-battle-inspect="${esc(String(card.id))}" aria-label="Подробнее о карте ${esc(card.name || 'Карта')}">i</button>
        </div>
    </div>`;
}

function renderBattleCommands(b, attacker, target) {
    const busy = S.arenaActionBusy || arenaRestoringBattle;
    const interactive = b.is_my_turn && !b.is_finished && !busy;
    const root = $('arena-battle');
    if (root) root.dataset.turn = busy ? 'busy' : b.is_my_turn ? 'player' : 'enemy';
    setText('battle-attacker-name', attacker?.name || 'Твоя карта');
    setText('battle-target-name', target?.name || 'Выбери цель');
    const summary = $('battle-selection-summary');
    if (summary) summary.hidden = !attacker;
    summary?.classList.toggle('is-ready', Boolean(attacker && target));
    const guidance = arenaRestoringBattle ? 'Восстанавливаем бой…'
        : S.arenaActionBusy ? 'Атакуем…'
        : b.is_finished ? 'Бой завершён'
        : !b.is_my_turn ? 'Ход соперника'
        : !attacker ? 'Твой ход · выбери карту'
        : !target ? 'Выбери цель соперника'
        : 'Цель выбрана';
    setText('battle-turn-status', guidance);
    setText('caster-name', !attacker && interactive ? 'Нажми на героя снизу'
        : attacker && !target && interactive ? `${attacker.name || 'Карта'} готова к атаке`
        : attacker && target && interactive ? 'Нажми «Атаковать»'
        : guidance);
    const button = $('btn-arena-attack');
    if (button) {
        button.hidden = !attacker || !b.is_my_turn || b.is_finished;
        button.disabled = !attacker || !target || !interactive;
        button.textContent = busy ? 'Атакуем…' : !target ? 'Выбери цель' : 'Атаковать';
    }
}

function arenaClearBattleSelection() {
    resetArenaSelection();
    refreshBattleSelectionControls();
}
window.arenaClearBattleSelection = arenaClearBattleSelection;

function renderBattleV2() {
    const b = S.arena.battle;
    if (!b) return;
    const { mine, enemy } = arenaBattleSides(b);
    let selected = mine.find(card => String(card.id) === String(S.arena.selectedAttackerId || '')) || null;
    if (!selected || selected.is_alive === false || Number(selected.hp ?? selected.current_hp ?? 0) <= 0) {
        S.arena.selectedAttackerId = null;
        S.arena.selectedTargetId = null;
        selected = null;
    }
    let selectedTarget = enemy.find(card => String(card.id) === String(S.arena.selectedTargetId || '')) || null;
    if (!selectedTarget || selectedTarget.is_alive === false || Number(selectedTarget.hp ?? selectedTarget.current_hp ?? 0) <= 0) {
        S.arena.selectedTargetId = null;
        selectedTarget = null;
    }

    setText('battle-my-name', b.player_info?.name || 'Ты');
    setText('battle-opp-name', b.opponent_info?.name || 'Противник');
    setText('battle-opp-full-name', b.opponent_info?.name || 'Противник');
    setText('battle-my-rating', b.player_info?.mmr == null ? 'MMR —' : `MMR ${Number(b.player_info.mmr)}`);
    setText('battle-opp-rating', b.opponent_info?.mmr == null ? 'MMR —' : `MMR ${Number(b.opponent_info.mmr)}`);
    const inPlay = cards => cards.filter(card => card.is_alive !== false && Number(card.hp ?? card.current_hp ?? 0) > 0).length;
    setText('battle-my-alive', `${inPlay(mine)} / 4 в строю`);
    setText('battle-opp-alive', `${inPlay(enemy)} / 4 в строю`);
    setText('battle-score', `${inPlay(mine)} : ${inPlay(enemy)}`);
    $('battle-score')?.setAttribute('aria-label', `Живых карт: твоих ${inPlay(mine)}, противника ${inPlay(enemy)}`);
    setText('battle-round-badge', `Раунд ${Number(b.round || 1)}${b.max_rounds == null ? '' : `/${Number(b.max_rounds)}`}`);
    setText('battle-turn-status', arenaRestoringBattle ? 'Восстанавливаем бой' : (b.is_finished ? 'Бой завершён' : (b.is_my_turn ? 'Твой ход' : 'Ход соперника')));
    const lastEvent = (b.log || []).map(event => arenaEventText(event, b)).filter(Boolean).at(-1);
    setText('battle-last-event', lastEvent || '');

    const enemyRow = $('battle-opp-row');
    const playerRow = $('battle-my-row');
    patchArenaMarkup(enemyRow, [0, 1, 2, 3].map(lane => renderArenaLaneCard(arenaBattleCardAtLane(enemy, lane), lane, 'enemy', b, selectedTarget?.id, Boolean(selected))).join(''));
    patchArenaMarkup(playerRow, [0, 1, 2, 3].map(lane => renderArenaLaneCard(arenaBattleCardAtLane(mine, lane), lane, 'player', b, selectedTarget?.id, Boolean(selected))).join(''));
    scheduleArenaMediaLoads(enemyRow);
    scheduleArenaMediaLoads(playerRow);
    ensureArenaBattleTapDelegation();
    requestAnimationFrame(() => updateArenaTargetPath(selected?.id, selectedTarget?.id));

    // Selection updates mutate live nodes outside the markup cache. Restore
    // interactivity even when a failed request returns identical battle data.
    refreshBattleSelectionControls();
    const logEl = $('battle-log');
    if (logEl) {
        const events = (b.log || []).map(event => arenaEventText(event, b)).filter(Boolean).slice(-24);
        logEl.innerHTML = events.length
            ? events.map(text => `<div class="battle-log-entry">${esc(text)}</div>`).join('')
            : '<div class="battle-log-entry">Бой начался</div>';
        logEl.scrollTop = logEl.scrollHeight;
    }
    if (b.is_finished) _finishBattleV2(b);
}

function arenaSelectAttacker(cardId) {
    const b = S.arena.battle;
    if (!b?.is_my_turn || S.arenaActionBusy || arenaRestoringBattle) return;
    const card = arenaBattleSides(b).mine.find(item => String(item.id) === String(cardId));
    if (!card || card.is_alive === false || Number(card.hp ?? card.current_hp ?? 0) <= 0) return showToast('Эта карта уничтожена');
    if (String(S.arena.selectedAttackerId || '') !== String(card.id)) S.arena.selectedTargetId = null;
    S.arena.selectedAttackerId = card.id;
    haptic('light');
    refreshBattleSelectionControls();
}
window.arenaSelectAttacker = arenaSelectAttacker;

function arenaSelectTarget(cardId) {
    const b = S.arena.battle;
    if (!b?.is_my_turn || S.arenaActionBusy || arenaRestoringBattle || !S.arena.selectedAttackerId) return;
    const card = arenaBattleSides(b).enemy.find(item => String(item.id) === String(cardId));
    if (!card || card.is_alive === false || Number(card.hp ?? card.current_hp ?? 0) <= 0) return showToast('Эта цель уничтожена');
    S.arena.selectedTargetId = card.id;
    haptic('light');
    refreshBattleSelectionControls();
}
window.arenaSelectTarget = arenaSelectTarget;

function ensureArenaBattleTapDelegation() {
    const root = $('arena-battle');
    if (!root || root.dataset.tapDelegation === '1') return;
    root.dataset.tapDelegation = '1';
    const selectUnit = event => {
        const button = event.target instanceof Element
            ? event.target.closest('.battle-card-unit[data-id]')
            : null;
        if (!button || button.disabled) return;
        const cardId = button.getAttribute('data-id');
        if (!cardId) return;
        if (button.getAttribute('data-arena-side') === 'enemy') arenaSelectTarget(cardId);
        else arenaSelectAttacker(cardId);
    };
    root.addEventListener('pointerup', event => {
        root.dataset.lastArenaTap = String(Date.now());
        selectUnit(event);
    });
    root.addEventListener('click', event => {
        const inspect = event.target instanceof Element ? event.target.closest('[data-battle-inspect]') : null;
        if (inspect) {
            arenaInspectBattleCard(inspect.getAttribute('data-battle-inspect'));
            return;
        }
        if (Date.now() - Number(root.dataset.lastArenaTap || 0) < 450) return;
        selectUnit(event);
    });
}

function arenaInspectBattleCard(cardId) {
    const card = S.arena.battle?.cards?.find(item => String(item.id) === String(cardId));
    const dialog = $('arena-card-detail-dialog') as HTMLDialogElement | null;
    const content = $('arena-card-detail-content');
    if (!card || !dialog || !content) return;
    const hp = Math.max(0, Number(card.hp ?? card.current_hp ?? 0));
    const maxHp = Math.max(1, Number(card.max_hp || 1));
    content.innerHTML = `<div class="arena-card-detail-art">${renderArenaStaticMedia(card, 'arena-card-detail-image')}</div><h2>${esc(card.name || 'Карта')}</h2><div class="arena-card-detail-stats"><span>Атака <b>${Number(card.atk ?? card.current_atk ?? 0)}</b></span><span>HP <b>${hp}/${maxHp}</b></span></div><p>${hp > 0 && card.is_alive !== false ? 'В строю' : 'Выбыла из боя'}</p>`;
    scheduleArenaMediaLoads(content);
    dialog.showModal();
}

function refreshBattleSelectionControls() {
    const b = S.arena.battle;
    if (!b) return;
    const { mine, enemy } = arenaBattleSides(b);
    const attacker = mine.find(card => String(card.id) === String(S.arena.selectedAttackerId || '')) || null;
    const target = enemy.find(card => String(card.id) === String(S.arena.selectedTargetId || '')) || null;
    const interactive = b.is_my_turn && !b.is_finished && !S.arenaActionBusy && !arenaRestoringBattle;

    document.querySelectorAll('#arena-battle .battle-card-unit[data-id]').forEach(button => {
        const id = String(button.getAttribute('data-id') || '');
        const enemySide = button.getAttribute('data-arena-side') === 'enemy';
        const selected = !enemySide && id === String(attacker?.id || '');
        const targeted = enemySide && id === String(target?.id || '');
        const model = (enemySide ? enemy : mine).find(card => String(card.id) === id);
        const alive = model && model.is_alive !== false && Number(model.hp ?? model.current_hp ?? 0) > 0;
        const candidate = alive && enemySide && Boolean(attacker) && !targeted;
        button.classList.toggle('selected-attacker', selected);
        button.classList.toggle('selected-target', targeted);
        button.setAttribute('aria-pressed', String(selected || targeted));
        button.classList.toggle('target-candidate', candidate);
        button.classList.toggle('selectable', interactive && (!enemySide || Boolean(attacker)));
        button.disabled = !(alive && interactive && (!enemySide || Boolean(attacker)));
        button.querySelectorAll('.battle-card-state, .battle-card-target-mark').forEach(node => node.remove());
        if (selected) button.insertAdjacentHTML('beforeend', '<span class="battle-card-state">Атакует</span>');
        if (targeted) button.insertAdjacentHTML('beforeend', '<span class="battle-card-target-mark">Цель</span>');
    });

    renderBattleCommands(b, attacker, target);
    requestAnimationFrame(() => updateArenaTargetPath(attacker?.id, target?.id));
}

function updateArenaTargetPath(attackerId, targetId) {
    const svg = $('battle-target-path');
    const path = $('battle-target-line');
    const arena = document.querySelector('#arena-battle .battlefield-stage') || document.querySelector('#arena-battle .battlefield-arena');
    if (!svg || !path || !arena || !attackerId || !targetId) {
        if (svg) svg.classList.remove('visible');
        path?.removeAttribute('d');
        return;
    }
    const attacker = battleUnitElement(attackerId);
    const target = battleUnitElement(targetId);
    if (!attacker || !target) return svg.classList.remove('visible');
    const box = arena.getBoundingClientRect();
    const a = attacker.getBoundingClientRect();
    const t = target.getBoundingClientRect();
    const x1 = a.left + a.width / 2 - box.left;
    const y1 = a.top + 4 - box.top;
    const x2 = t.left + t.width / 2 - box.left;
    const y2 = t.bottom - 4 - box.top;
    const bend = Math.max(16, Math.abs(y1 - y2) * .32);
    svg.setAttribute('viewBox', `0 0 ${Math.max(1, box.width)} ${Math.max(1, box.height)}`);
    path.setAttribute('d', `M ${x1} ${y1} C ${x1} ${y1 - bend}, ${x2} ${y2 + bend}, ${x2} ${y2}`);
    svg.classList.add('visible');
}

function resetArenaSelection() {
    S.arena.selectedAttackerId = null;
    S.arena.selectedTargetId = null;
    const svg = $('battle-target-path');
    if (svg) {
        svg.classList.remove('visible');
        $('battle-target-line')?.removeAttribute('d');
    }
    document.querySelectorAll('.arena-lane-card.selected-attacker, .arena-lane-card.selected-target')
        .forEach(node => node.classList.remove('selected-attacker', 'selected-target'));
}

async function playArenaEvents(events) {
    await playArenaCombatEvents({ events,
        stage: document.querySelector('#arena-battle .battlefield-stage'),
        findUnit: battleUnitElement,
        // Device heuristics may quiet ambient effects, but a turn must still
        // read as an attack. Only an explicit preference reduces combat travel.
        minimal: arenaEffectsPreference === 'minimal' || window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || document.hidden,
        feedback: haptic,
        onAttack: event => {
            const enemy = event.turn === 'enemy';
            setText('battle-turn-status', enemy ? 'Удар соперника' : 'Твоя атака');
            setText('caster-name', arenaEventText(event));
        },
        onImpact: event => {
            const target = battleUnitElement(event.target_battle_card_id || event.target_id || event.target_card_id);
            const hp = Number(event.target_hp_after ?? event.hp_after);
            if (!target || !Number.isFinite(hp)) return;
            const card = S.arena.battle?.cards?.find(card => String(card.id) === target.dataset.id);
            const stats = target.querySelector('.battle-card-stats b:last-child');
            if (stats) stats.innerHTML = `${arenaIcon('health')}${Math.max(0,hp)}/${Math.max(1, Number(card?.max_hp) || 1)}`;
            const bar = target.querySelector('.battle-card-hp i') as HTMLElement;
            if (bar) bar.style.width = `${Math.min(100,Math.max(0,hp) / Math.max(1,Number(card?.max_hp) || 1)*100)}%`;
        },
    });
}

async function submitArenaBattleAction(action, attacker = null, target = null) {
    const b = S.arena.battle;
    if (!b?.battle_id || !b.is_my_turn || S.arenaActionBusy || arenaRestoringBattle) return;
    return arenaBattleActionGuard.run(`${b.battle_id}:${action}:${attacker?.id || 'turn'}:${target?.id || ''}`, async () => {
        S.arenaActionBusy = true;
        refreshBattleSelectionControls();
        const attackButton = $('btn-arena-attack');
        if (attackButton) {
            attackButton.disabled = true;
            attackButton.textContent = 'Выполняем атаку…';
        }
        if (action === 'attack') {
            setText('battle-turn-status', 'Разыгрываем ход…');
            setText('caster-name', 'Подтверждаем атаку…');
        }
        try {
            const res = await api('/api/arena_battle_action', {
                method: 'POST',
                timeoutMs: 30000,
                body: {
                    user_id: effectiveUid(), battle_id: b.battle_id, action,
                    ...(attacker ? { attacker_id: String(attacker.id) } : {}),
                    ...(target ? { target_id: String(target.id) } : {}),
                },
            });
            if (res?.error) {
                if (res.state) setArenaBattleState(res.state);
                throw new Error(res.error);
            }
            const nextBattle = res?.state || res?.battle_state || res;
            setArenaBattleState(nextBattle);
            // Commit server state first, then show the bounded final strike.
            await playArenaEvents(res?.events || []).catch(error => console.warn('[ARENA MOTION]', error));
            if (res?.status === 'finished' || nextBattle?.is_finished) {
                resetArenaSelection();
                _finishBattleV2(nextBattle);
                return;
            }
            resetArenaSelection();
        } catch (error) {
            resetArenaSelection();
            if (isArenaBattleGoneError(error)) {
                discardStaleArenaBattle();
                return;
            }
            showToast(error?.message || 'Не удалось выполнить ход');
            try {
                const latest = await api(`/api/arena_battle_state?id=${encodeURIComponent(b.battle_id)}&user_id=${effectiveUid()}`, {
                    dedupe: false,
                    timeoutMs: 8000,
                    retries: 0,
                });
                if (latest && !latest.error) setArenaBattleState(latest);
            } catch (latestError) {
                if (isArenaBattleGoneError(latestError)) {
                    discardStaleArenaBattle();
                    return;
                }
            }
        } finally {
            S.arenaActionBusy = false;
            // Exactly one full battle render per completed action. Previously
            // all eight cards were rebuilt up to four times for one attack.
            if (S.arena.screen === 'battle') renderBattleV2();
        }
    });
}

function arenaAttackSelected() {
    const b = S.arena.battle;
    const selected = arenaBattleSides(b).mine.find(card => String(card.id) === String(S.arena.selectedAttackerId || ''));
    const target = arenaBattleSides(b).enemy.find(card => String(card.id) === String(S.arena.selectedTargetId || ''));
    if (!selected) return showToast('Выбери свою карту');
    if (!target) return showToast('Выбери живую карту противника');
    return submitArenaBattleAction('attack', selected, target);
}
window.arenaAttackSelected = arenaAttackSelected;

function arenaEndTurn() {
    // Kept only for old inline handlers. New Arena does not expose this UI.
    showToast('Ход завершается после одной атаки');
}
window.arenaEndTurn = arenaEndTurn;

async function arenaAutoFinish() {
    const dialog = $('arena-auto-finish-dialog') as HTMLDialogElement | null;
    if (dialog?.showModal) {
        dialog.showModal();
        return;
    }
    if (!window.confirm('Автоматически завершить бой? Ты больше не сможешь выбирать атаки.')) return;
    return confirmArenaAutoFinish();
}
window.arenaAutoFinish = arenaAutoFinish;

async function confirmArenaAutoFinish() {
    ($('arena-auto-finish-dialog') as HTMLDialogElement | null)?.close?.();
    const b = S.arena.battle;
    if (!b?.battle_id || b.is_finished || S.arenaActionBusy || arenaRestoringBattle) return;
    S.arenaActionBusy = true;
    const button = $('btn-arena-auto-finish');
    if (button) {
        button.disabled = true;
        button.textContent = 'Симулируем бой…';
    }
    try {
        const res = await api('/api/arena_battle_skip_auto', {
            method: 'POST',
            timeoutMs: 30000,
            body: { user_id: effectiveUid(), battle_id: b.battle_id },
        });
        if (res?.error) throw new Error(res.error);
        const finished = res?.state || res?.battle_state || res;
        if (!finished?.is_finished && !['finished', 'completed'].includes(String(res?.status || ''))) {
            throw new Error('Бой не завершён');
        }
        setArenaBattleState(finished);
        resetArenaSelection();
        _finishBattleV2(finished);
    } catch (error) {
        if (isArenaBattleGoneError(error)) discardStaleArenaBattle();
        else showToast(error?.message || 'Не удалось завершить бой автоматически');
    } finally {
        S.arenaActionBusy = false;
        if (S.arena.screen === 'battle') renderBattleV2();
    }
}
window.confirmArenaAutoFinish = confirmArenaAutoFinish;

function _finishBattleV2(b) {
    b = b.state || b.battle_state || b;
    const finishedId = String(b?.battle_id || '');
    if (finishedId && finishedBattleIds.has(finishedId)) return;
    if (finishedId) finishedBattleIds.add(finishedId);
    const uid = effectiveUid();
    const draw = b.is_draw === true || ['draw', 'tie'].includes(String(b.result || b.outcome || '').toLowerCase()) || (b.is_finished && b.winner_id == null);
    const won = !draw && (strEq(b.winner_id, uid) || strEq(b.winner_id, b.player_info?.id));
    if (b.rewards?.player_stats) {
        syncArenaStats({ stats: b.rewards.player_stats });
    }
    // Pull the authoritative counters immediately after finalization.  The
    // initial profile payload uses `loses`, while Arena v2 uses `losses`.
    // Normalizing both prevents the statistics screen from staying at zero.
    loadArenaState({ force: true, maxAge: 0 }).then(res => {
        if (!res || res.error) return;
        S.arenaLobby = res;
        syncArenaStats(res);
    }).catch(() => {});
    (() => {
        S.arena.screen = 'result';
        persistArenaDeviceState();
        setText('arena-result-icon', draw ? '◇' : won ? '◈' : '✕');
        setText('arena-result-title', draw ? 'Ничья' : won ? 'Победа' : 'Поражение');
        if ($('arena-result-title')) $('arena-result-title').style.color = draw ? 'var(--text)' : won ? '#f4dfba' : 'var(--rose)';
        
        const rewards = b.rewards || {};
        const war = rewards.war || {};
        const fromWar = !!(S.arena?.fromClanWar || rewards.mode === 'clan_war' || war.points != null);
        const mmrDelta = draw ? rewards.draw_mmr_delta : won ? rewards.winner_mmr_delta : rewards.loser_mmr_delta;
        const trophDelta = draw ? rewards.draw_trophies_delta : won ? rewards.winner_trophies_delta : rewards.loser_trophies_delta;
        const coins = rewards.coins_won;
        const coinsLabel = coins == null ? 'Награда обновляется…' : `+${Number(coins)} рекоинов`;

        if (fromWar) {
            setText('arena-result-mmr', `Клановая война · ${war.points == null ? (draw ? 'ничья' : won ? 'победа' : 'отряд повержен') : `${Number(war.points) >= 0 ? '+' : ''}${Number(war.points)} очков`}`);
            setText('arena-result-coins', coinsLabel);
            if ($('arena-result-stats')) {
                $('arena-result-stats').innerHTML = `
                    Раундов: <b>${b.round || 1}</b><br>
                    <span style="font-size:11px;color:var(--text-sub)">HP выживших карт сохранено для следующей атаки.</span><br>
                    <button class="action-pill accent-btn" style="margin-top:10px" onclick="S.arena.fromClanWar=false; switchTab('clan'); switchClanTab('wars')">К карте войны</button>
                `;
            }
            showToast(won ? 'Победа: HP отряда сохранено' : 'Твой отряд повержен');
        } else {
            setText('arena-result-mmr', mmrDelta == null || trophDelta == null ? 'Обновляем рейтинг…' : `${mmrDelta >= 0 ? '+' : ''}${mmrDelta} MMR · ${trophDelta >= 0 ? '+' : ''}${trophDelta} кубков`);
            setText('arena-result-coins', coinsLabel);
            if ($('arena-result-stats')) $('arena-result-stats').innerHTML = `Раундов: <b>${b.round || 1}</b>`;
        }
        renderArena();
        void checkArenaResultReplay(finishedId);
    })();
}

async function checkArenaResultReplay(battleId) {
    const button = $('arena-result-replay');
    if (!button) return;
    button.hidden = true;
    if (!battleId || !effectiveUid()) return;
    try {
        const replays = await api(`/api/arena_replays?user_id=${encodeURIComponent(effectiveUid())}&limit=8`);
        if (S.arena.screen !== 'result' || !Array.isArray(replays) || !replays.some(replay => String(replay.id) === battleId)) return;
        button.dataset.replayId = battleId;
        button.hidden = false;
    } catch { /* A replay is optional; the result remains available. */ }
}

function arenaOpenResultReplay() {
    const replayId = $('arena-result-replay')?.dataset.replayId;
    if (!replayId) return;
    S.arena.screen = 'history';
    renderArena();
    void arenaLoadReplay(replayId);
}
window.arenaOpenResultReplay = arenaOpenResultReplay;

function toggleBattleLog() {
    const dr = $('battle-log-drawer');
    if (!dr) return;
    const opens = dr.style.display === 'none' || getComputedStyle(dr).display === 'none';
    dr.style.display = opens ? 'flex' : 'none';
    dr.setAttribute('aria-hidden', String(!opens));
    if (opens) $('battle-log')?.scrollTo({ top: $('battle-log').scrollHeight });
}
window.toggleBattleLog = toggleBattleLog;

async function arenaShowReplays() {
    S.arena.screen = 'history';
    renderArena();
    $('app-main')?.scrollTo({ top: 0 });
    haptic('light');
    const sec = $('arena-replays-section');
    const lst = $('arena-replays-list');
    if (!sec || !lst) return;
    sec.style.display = 'block';
    const close = $('arena-replays-close');
    if (close) close.hidden = false;
    lst.innerHTML = '<div class="empty-state">◈ ЗАГРУЗКА РЕПЛЕЕВ...</div>';
    const uid = effectiveUid();
    try {
        const res = await api(`/api/arena_replays?user_id=${uid}&limit=8`);
        if (!res || !Array.isArray(res) || res.length === 0) {
            lst.innerHTML = '<div class="empty-state">◈ НЕТ СОХРАНЁННЫХ БОЁВ</div>';
            return;
        }
        lst.innerHTML = res.map(r => `
        <div class="lb-row" style="display:flex;align-items:center;justify-content:space-between;background:var(--bg-card);padding:12px;border-radius:14px;margin-bottom:8px;">
            <div>
                <div style="font-weight:800;font-size:13px;">Бой #${esc(r.id?.substring(0,8))}</div>
                <div style="font-size:11px;color:var(--text-sub);">${r.winner_id===String(uid)?'🟢 Победа':'🔴 Поражение'} (${r.rounds||1} раундов)</div>
            </div>
            <button class="action-pill" style="padding:6px 12px;font-size:11px;" onclick="arenaLoadReplay('${r.id}')">▶ Смотреть</button>
        </div>`).join('');
    } catch (e) {
        lst.innerHTML = '<div class="empty-state">⚠ ОШИБКА ЗАГРУЗКИ</div>';
    }
}
window.arenaShowReplays = arenaShowReplays;

async function arenaLoadReplay(replayId) {
    haptic('medium');
    const lst = $('arena-replays-list');
    const close = $('arena-replays-close');
    if (close) close.hidden = true;
    if (lst) lst.innerHTML = '<div class="empty-state">◈ ЗАГРУЗКА БОЯ...</div>';
    try {
        const res = await api(`/api/arena_replay?id=${encodeURIComponent(replayId)}`);
        if (!res || res.error) { showToast('⚠ Реплей не найден'); return; }
        if (Array.isArray(res.initial_state?.cards) && res.initial_state.cards.length) {
            startArenaReplay(res);
            return;
        }
        const events = (res.events || []).filter(event =>
            event && ['battle_start', 'round_end', 'damage', 'heal', 'death', 'battle_end'].includes(event.type)
        );
        const winnerName = strEq(res.winner_id, res.attacker_id)
            ? res.attacker_name
            : (strEq(res.winner_id, res.defender_id) ? res.defender_name : 'Ничья');
        const rows = events.map(event => {
            if (event.type === 'battle_start') return '<div class="battle-log-entry">⚔ Бой начался</div>';
            if (event.type === 'round_end') return `<div class="battle-log-entry"><b>Раунд ${event.round} завершён</b></div>`;
            if (event.type === 'damage') return `<div class="battle-log-entry">💥 <b>${esc(event.caster)}</b> → ${esc(event.target)}: <b>−${Number(event.amount || 0).toLocaleString('ru-RU')} HP</b></div>`;
            if (event.type === 'heal') return `<div class="battle-log-entry">💚 <b>${esc(event.caster)}</b> лечит ${esc(event.target)}: +${Number(event.amount || 0).toLocaleString('ru-RU')}</div>`;
            if (event.type === 'death') return `<div class="battle-log-entry">☠ ${esc(event.target)} выбыл</div>`;
            if (event.type === 'battle_end') return `<div class="battle-log-entry"><b>🏆 Победитель: ${esc(winnerName)}</b></div>`;
            return '';
        }).join('');
        if (lst) {
            lst.innerHTML = `
                <div class="bento-card" style="padding:16px;">
                    <div style="font-weight:900;font-size:15px;margin-bottom:4px;">${esc(res.attacker_name)} ⚔ ${esc(res.defender_name)}</div>
                    <div style="font-size:11px;color:var(--text-sub);margin-bottom:14px;">${res.rounds || 1} раундов · Победитель: ${esc(winnerName)}</div>
                    <div style="max-height:380px;overflow-y:auto;">${rows || '<div class="empty-state">События боя не сохранились</div>'}</div>
                </div>
                <button class="action-pill full-pill" onclick="arenaShowReplays()" style="margin-top:10px;">← К списку боёв</button>
            `;
        }
        showToast('🎬 Реплей загружен');
    } catch (e) {
        if (close) close.hidden = false;
        showToast('⚠ Ошибка загрузки реплея');
    }
}
window.arenaLoadReplay = arenaLoadReplay;

function startArenaReplay(data) {
    if (S.arena.replay?.timer) clearInterval(S.arena.replay.timer);
    S.arena.replay = {
        data,
        cards: (data.initial_state.cards || []).map(card => ({ ...card })),
        index: 0,
        playing: false,
        timer: null,
        lastEvent: null,
    };
    renderArenaReplayViewer();
    showToast('Реплей готов к просмотру');
}

function replayFindCard(event, field) {
    const replay = S.arena.replay;
    const id = event?.[`${field}_id`];
    const name = event?.[field];
    return replay?.cards.find(card => (id && card.id === id) || (!id && name && card.name === name));
}

function arenaReplayStep() {
    const replay = S.arena.replay;
    if (!replay || replay.index >= (replay.data.events || []).length) {
        arenaReplayPause();
        return;
    }
    const event = replay.data.events[replay.index++];
    replay.lastEvent = event;
    const target = replayFindCard(event, 'target');
    if (target && ['damage', 'dot_damage'].includes(event.type)) target.hp = Math.max(0, Number(target.hp || 0) - Number(event.amount || 0));
    if (target && event.type === 'heal') target.hp = Math.min(Number(target.max_hp || target.hp), Number(target.hp || 0) + Number(event.amount || 0));
    if (target && event.type === 'death') { target.hp = 0; target.is_alive = false; }
    renderArenaReplayViewer();
}
window.arenaReplayStep = arenaReplayStep;

function arenaReplayPause() {
    const replay = S.arena.replay;
    if (!replay) return;
    if (replay.timer) clearInterval(replay.timer);
    replay.timer = null;
    replay.playing = false;
    renderArenaReplayViewer();
}

function arenaReplayToggle() {
    const replay = S.arena.replay;
    if (!replay) return;
    if (replay.playing) { arenaReplayPause(); return; }
    replay.playing = true;
    replay.timer = setInterval(arenaReplayStep, 850);
    arenaReplayStep();
}
window.arenaReplayToggle = arenaReplayToggle;

function arenaReplayReset() {
    const data = S.arena.replay?.data;
    if (data) startArenaReplay(data);
}
window.arenaReplayReset = arenaReplayReset;

function renderArenaReplayViewer() {
    const root = $('arena-replays-list');
    const replay = S.arena.replay;
    if (!root || !replay) return;
    const data = replay.data;
    const initial = data.initial_state || {};
    const event = replay.lastEvent || {};
    const caster = replayFindCard(event, 'caster');
    const target = replayFindCard(event, 'target');
    const renderSide = (teamId, enemy) => replay.cards
        .filter(card => strEq(card.team_id, teamId))
        .sort((a, b) => Number(a.position) - Number(b.position))
        .map(card => {
            const hpPct = Math.max(0, Math.round(Number(card.hp || 0) / Math.max(1, Number(card.max_hp || 1)) * 100));
            const active = caster?.id === card.id;
            const hit = target?.id === card.id;
            return `<div class="replay-unit ${!card.is_alive || card.hp <= 0 ? 'dead' : ''} ${active ? 'acting' : ''} ${hit ? 'hit' : ''}" data-replay-card-id="${esc(String(card.id))}" style="--team:${enemy ? '#e5484d' : '#ff7a1a'}">
                <div class="replay-unit-media">${workbenchMedia(card, true)}</div>
                <b>${esc(card.name || 'Карта')}</b>
                <div class="replay-unit-hp"><i data-replay-hp style="width:${hpPct}%"></i></div>
                <small data-replay-hp-text>${Math.max(0, Number(card.hp || 0))} HP</small>
            </div>`;
        }).join('');
    const progress = (data.events || []).length ? Math.round(replay.index / data.events.length * 100) : 0;
    const eventText = event.type === 'damage'
        ? `${event.caster} наносит ${Number(event.amount || 0).toLocaleString('ru-RU')} урона по ${event.target}`
        : event.type === 'heal' ? `${event.caster} восстанавливает ${event.amount} HP`
        : event.type === 'death' ? `${event.target} выбывает из боя`
        : event.type === 'round_end' ? `Раунд ${event.round} завершён`
        : event.type === 'battle_end' ? 'Бой завершён'
        : (replay.index ? 'Следующее действие' : 'Нажми «Запустить»');
    const replayKey = String(data.id || data._id || data.battle_id || `${data.attacker_id}:${data.defender_id}:${data.created_at || ''}`);
    if (root.dataset.replayKey === replayKey && root.querySelector('.arena-replay-viewer')) {
        const progressNode = root.querySelector('[data-replay-progress]');
        if (progressNode) progressNode.style.width = `${progress}%`;
        const counterNode = root.querySelector('[data-replay-counter]');
        if (counterNode) counterNode.textContent = `${replay.index}/${(data.events || []).length}`;
        const feedNode = root.querySelector('[data-replay-feed]');
        if (feedNode) feedNode.textContent = eventText;
        const playNode = root.querySelector('[data-replay-play]');
        if (playNode) playNode.textContent = replay.playing ? 'Пауза' : (replay.index ? 'Продолжить' : 'Запустить');
        const units = new Map([...root.querySelectorAll('[data-replay-card-id]')].map(node => [node.dataset.replayCardId, node]));
        replay.cards.forEach(card => {
            const unit = units.get(String(card.id));
            if (!unit) return;
            const hpPct = Math.max(0, Math.round(Number(card.hp || 0) / Math.max(1, Number(card.max_hp || 1)) * 100));
            unit.classList.toggle('dead', !card.is_alive || Number(card.hp) <= 0);
            unit.classList.toggle('acting', caster?.id === card.id);
            unit.classList.remove('hit');
            if (target?.id === card.id) {
                void unit.offsetWidth;
                unit.classList.add('hit');
            }
            const hpNode = unit.querySelector('[data-replay-hp]');
            if (hpNode) hpNode.style.width = `${hpPct}%`;
            const hpText = unit.querySelector('[data-replay-hp-text]');
            if (hpText) hpText.textContent = `${Math.max(0, Number(card.hp || 0))} HP`;
        });
        return;
    }
    root.innerHTML = `<div class="arena-replay-viewer">
        <div class="replay-head"><div><span>ВИЗУАЛЬНЫЙ РЕПЛЕЙ</span><b>${esc(data.attacker_name)} <i>VS</i> ${esc(data.defender_name)}</b></div><small data-replay-counter>${replay.index}/${(data.events || []).length}</small></div>
        <div class="replay-progress"><i data-replay-progress style="width:${progress}%"></i></div>
        <div class="replay-side enemy"><span>${esc(initial.team_b_name || data.defender_name)}</span><div class="replay-squad">${renderSide(initial.team_b_id || data.defender_id, true)}</div></div>
        <div class="replay-action-feed" data-replay-feed>${esc(eventText)}</div>
        <div class="replay-side player"><div class="replay-squad">${renderSide(initial.team_a_id || data.attacker_id, false)}</div><span>${esc(initial.team_a_name || data.attacker_name)}</span></div>
        <div class="replay-controls">
            <button type="button" onclick="arenaReplayReset()">↺</button>
            <button type="button" class="primary" data-replay-play onclick="arenaReplayToggle()">${replay.playing ? 'Пауза' : (replay.index ? 'Продолжить' : 'Запустить')}</button>
            <button type="button" onclick="arenaReplayStep()">Шаг ›</button>
        </div>
    </div><button class="action-pill full-pill" onclick="arenaShowReplays()">← К списку боёв</button>`;
    root.dataset.replayKey = replayKey;
}

function isArenaWorkbenchVisible() {
    return S.tab === 'team' || (S.tab === 'arena' && S.arena.screen === 'lobby');
}

function mergeArenaDecksFromServer(source) {
    const previous = normalizeArenaDecks(S.arenaDecks);
    const incoming = normalizeArenaDecks(source);
    const drafts = { ...(S.arenaDrafts || {}) };
    for (const deck of incoming) {
        const old = previous.find(item => item.id === deck.id);
        if (!Object.prototype.hasOwnProperty.call(drafts, deck.id) || !arenaDeckChanged(old?.slots || {}, drafts[deck.id])) {
            drafts[deck.id] = { ...deck.slots };
        }
    }
    S.arenaDecks = incoming;
    S.arenaDrafts = drafts;
}

function arenaIcon(kind) {
    const paths = {
        attack: '<path d="m5 3 14 14-2 2L3 5V3Zm9 11 5-5 2 2-5 5M3 21l5-5m-3-3 6 6"/>',
        health: '<path d="M20.5 4.8a5.5 5.5 0 0 0-8.5 1 5.5 5.5 0 0 0-8.5-1c-3.5 3.5.5 8.5 8.5 14.7 8-6.2 12-11.2 8.5-14.7Z"/>',
        check: '<path d="m5 12 4 4L19 6"/>',
        plus: '<path d="M12 5v14M5 12h14"/>',
    };
    return `<svg class="arena-inline-icon" viewBox="0 0 24 24" aria-hidden="true">${paths[kind] || paths.plus}</svg>`;
}

function workbenchMedia(card, eager = false) {
    let source = arenaStoredPreviewSrc(card?.arena_preview_url);
    const normal = arenaNormalMediaSrc(card?.image_url, card?.file_id);
    if (!source && (isCardVideo(card) || /gif/i.test(String(card?.type || normal)))) {
        if (normal.includes('/api/proxy_image?')) source = normal.replace('/api/proxy_image?', '/api/arena_card_preview?');
        else if (card?.file_id) source = `${API_BASE}/api/arena_card_preview?file_id=${encodeURIComponent(card.file_id)}`;
        // Never start dozens of original video decoders as a poster fallback.
    } else if (!source) source = normal;
    if (!source) return `<span class="workbench-art-placeholder">${esc((card?.name || '?')[0])}</span>`;
    return `<img class="workbench-card-art" data-arena-source="${esc(source)}" src="${esc(source)}" alt="" loading="${eager ? 'eager' : 'lazy'}" decoding="async" onerror="this.hidden=true;this.parentElement.classList.add('art-unavailable')">`;
}

function renderTeam() {
    const wrap = $('team-slots'); if (!wrap) return;
    const decks = normalizeArenaDecks(S.arenaDecks);
    const editingId = String(S.arenaEditingDeckId || S.arenaActiveDeckId || '1');
    const editingDeck = decks.find(deck => deck.id === editingId) || decks[0];
    if (!Object.prototype.hasOwnProperty.call(S.arenaDrafts || {}, editingId)) {
        S.arenaDrafts = { ...(S.arenaDrafts || {}), [editingId]: { ...(editingDeck.slots || {}) } };
    }
    const draft = normalizeArenaSlots(S.arenaDrafts?.[editingId] || {});
    S.arenaDraftSlots = draft;
    const cardsById = new Map([...(S.team || []).filter(Boolean), ...(S.cards || [])].map(card => [String(card.id), card]));
    let damage = 0, health = 0;
    const selectedSlot = String(S.arenaReplaceSlot || '');
    patchArenaMarkup(wrap, [1, 2, 3, 4].map(position => {
        const card = cardsById.get(String(draft[String(position)] || ''));
        if (card) { damage += Number(card.damage ?? card.atk ?? 0); health += Number(card.health ?? card.hp ?? 0); }
        const remaining = card ? arenaCardRecovery(card.id) : 0;
        return `<button type="button" data-arena-key="slot-${position}-${esc(card?.id || 'empty')}" class="workbench-slot ${card ? 'filled' : 'empty'} ${selectedSlot === String(position) ? 'replacing' : ''}" onclick="arenaSelectDeckSlot(${position})" aria-pressed="${selectedSlot === String(position)}" aria-label="${esc(card ? `Заменить ${card.name}, позиция ${position}` : `Выбрать карту в позицию ${position}`)}" ${S.arenaPreparingFight ? 'disabled' : ''}>
            ${card ? workbenchMedia(card, true) : arenaIcon('plus')}
            <span class="workbench-slot-number">${position}</span>
            ${card ? `<span class="workbench-slot-name">${esc(card.name || 'Карта')}</span><span class="workbench-slot-stats"><b>${arenaIcon('attack')}${Number(card.damage ?? card.atk ?? 0)}</b><b>${arenaIcon('health')}${Number(card.health ?? card.hp ?? 0)}</b></span>` : '<span class="workbench-slot-empty">Выбрать</span>'}
            ${remaining ? `<span class="workbench-rest" data-recovery-id="${esc(card.id)}">${formatRecovery(remaining)}</span>` : ''}
        </button>`;
    }).join(''));
    const count = arenaDeckCardCount(draft);
    const resting = Object.values(draft).filter(id => arenaCardRecovery(id) > 0).length;
    setText('team-dmg', damage.toLocaleString('ru-RU'));
    setText('team-hp', health.toLocaleString('ru-RU'));
    setText('arena-picker-count', `${count} / 4`);
    setText('arena-team-hint', S.arenaDeckNotice || (selectedSlot
        ? `Выбери замену для позиции ${selectedSlot} в коллекции`
        : count < 4 ? `Добавь ещё ${4 - count} карты из коллекции` : 'Выбери карту, чтобы заменить'));
    const remove = $('arena-remove-selected');
    if (remove) remove.hidden = !draft[selectedSlot];
    patchArenaMarkup($('arena-deck-presets'), decks.map(deck => {
        const selected = deck.id === editingId;
        const slots = arenaDeckDraft(deck.id);
        const ready = isArenaDeckComplete(slots) && !Object.values(slots).some(id => arenaCardRecovery(id));
        const active = deck.id === String(S.arenaActiveDeckId);
        const minis = [1, 2, 3, 4].map(position => {
            const card = cardsById.get(String(slots[String(position)] || ''));
            return `<span class="workbench-deck-mini ${card ? 'filled' : ''}">${card ? workbenchMedia(card) : position}</span>`;
        }).join('');
        return `<button type="button" data-arena-key="deck-${deck.id}" class="workbench-deck ${selected ? 'selected' : ''}" role="tab" aria-selected="${selected}" aria-label="Колода ${deck.id}, ${ready ? 'готова' : 'неполная или карты на восстановлении'}${active ? ', активна' : ''}${selected ? ', редактируется' : ''}" onclick="arenaEditDeck('${deck.id}')" ${S.arenaPreparingFight ? 'disabled' : ''}><span class="workbench-deck-label">Колода ${deck.id}</span><span class="workbench-deck-minis" aria-hidden="true">${minis}</span><span class="workbench-deck-status">${selected ? 'Редактируется · ' : ''}${active ? 'Активна · ' : ''}${ready ? 'Готова' : 'Неполная'}</span></button>`;
    }).join(''));
    const recovery = Object.fromEntries([...cardsById.keys()].map(id => [id, arenaCardRecovery(id)]));
    const available = arenaAvailableCards([...cardsById.values()]);
    const filtered = selectWorkbenchCards([...cardsById.values()], { query: S.arenaCardQuery, filter: S.arenaCardFilter, sort: S.arenaCardSort, recovery });
    const occupiedBy = new Map(decks.filter(deck => deck.id !== editingId).flatMap(deck => Object.values(arenaDeckDraft(deck.id)).map(id => [String(id), deck.id])));
    const visible = filtered.slice(0, Math.max(6, Number(S.arenaCardRenderLimit || 6)));
    const markup = visible.map(card => {
        const order = Object.keys(draft).find(key => draft[key] === String(card.id));
        const remaining = recovery[String(card.id)] || 0;
        const occupied = occupiedBy.get(String(card.id));
        return `<button type="button" data-arena-key="card-${esc(card.id)}" class="workbench-card ${order ? 'selected' : ''} ${remaining ? 'resting' : ''} ${occupied ? 'occupied' : ''}" onclick="arenaToggleDeckCard('${encodeURIComponent(card.id)}')" aria-pressed="${Boolean(order)}" aria-label="${esc(card.name || 'Карта')}, атака ${Number(card.damage ?? card.atk ?? 0)}, здоровье ${Number(card.health ?? card.hp ?? 0)}${remaining ? ', на восстановлении' : ''}${occupied ? `, в колоде ${occupied}` : ''}" ${S.arenaPreparingFight ? 'disabled' : ''}>
            ${workbenchMedia(card)}
            <span class="workbench-card-title">${esc(card.name || 'Карта')}</span>
            ${order ? `<span class="workbench-check">${arenaIcon('check')}<span class="sr-only">В колоде, позиция ${order}</span></span>` : ''}
            ${remaining ? `<span class="workbench-card-rest">Отдых<span data-recovery-id="${esc(card.id)}">${formatRecovery(remaining)}</span></span>` : ''}
            ${occupied ? `<span class="workbench-card-occupied">Колода ${occupied}</span>` : ''}
            <span class="workbench-card-stats"><b>${arenaIcon('attack')}${Number(card.damage ?? card.atk ?? 0)}</b><b>${arenaIcon('health')}${Number(card.health ?? card.hp ?? 0)}</b></span>
        </button>`;
    }).join('');
    const more = visible.length < filtered.length ? '<button type="button" data-arena-key="more" class="workbench-more" data-arena-stream-sentinel onclick="arenaLoadMoreCards()">Показать ещё карты</button>' : '';
    const allOccupied = cardsById.size > 0 && available.length === 0;
    patchArenaMarkup($('arena-card-picker'), filtered.length ? markup + more : `<div class="workbench-empty" data-arena-key="empty"><b>${allOccupied ? 'Все карты в других колодах' : cardsById.size ? 'Карты не найдены' : 'Пока нет карт'}</b><p>${allOccupied ? 'Убери карту из другой колоды — она появится здесь.' : cardsById.size ? 'Попробуй другое имя или сбрось фильтры.' : 'Получи карты в разделе «Хаб», затем собери отряд.'}</p>${allOccupied ? '' : `<button type="button" onclick="${cardsById.size ? 'arenaResetSearch()' : "switchTab('profile')"}">${cardsById.size ? 'Сбросить поиск' : 'В хаб'}</button>`}</div>`);
    setText('arena-collection-summary', `Показано ${visible.length} из ${filtered.length}`);
    document.querySelectorAll('[data-arena-card-filter]').forEach(button => {
        const active = button.getAttribute('data-arena-card-filter') === S.arenaCardFilter;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', String(active));
    });
    updateArenaFightButton();
    // Explicit pagination avoids a scroll-triggered full collection download.
    arenaCardStreamObserver?.disconnect();
    arenaCardStreamObserver = null;
}

let arenaSearchDebounce = null;
function arenaSearchCards(query) {
    S.arenaCardQuery = String(query || '');
    S.arenaCardRenderLimit = 6;
    clearTimeout(arenaSearchDebounce);
    arenaSearchDebounce = setTimeout(renderTeam, 120);
}
window.arenaSearchCards = arenaSearchCards;
function arenaSortCards(value) { S.arenaCardSort = value; renderTeam(); }
window.arenaSortCards = arenaSortCards;
function arenaResetSearch() {
    S.arenaCardQuery = '';
    S.arenaCardFilter = 'all';
    const input = $('arena-card-search'); if (input) input.value = '';
    renderTeam();
}
window.arenaResetSearch = arenaResetSearch;
function arenaSelectDeckSlot(position) {
    if (S.arenaPreparingFight) return;
    S.arenaReplaceSlot = Number(S.arenaReplaceSlot) === position ? null : position;
    S.arenaDeckNotice = '';
    renderTeam();
}
window.arenaSelectDeckSlot = arenaSelectDeckSlot;
function arenaClearSelectedSlot() {
    if (S.arenaReplaceSlot) arenaRemoveDeckSlot(S.arenaReplaceSlot);
}
window.arenaClearSelectedSlot = arenaClearSelectedSlot;
function arenaShowLeaderboard() {
    S.arena.screen = 'ranking';
    renderArena();
    $('app-main')?.scrollTo({ top: 0 });
}
window.arenaShowLeaderboard = arenaShowLeaderboard;

let arenaEffectsPreference = 'auto';
try { arenaEffectsPreference = localStorage.getItem('reverse-arena-effects') || 'auto'; } catch {}
function applyArenaEffects() {
    if (document.hidden) cancelArenaCombatMotion();
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;
    const minimal = useMinimalArenaEffects({ preference: arenaEffectsPreference, reducedMotion, saveData: navigator.connection?.saveData, cores: navigator.hardwareConcurrency || 8, memory: navigator.deviceMemory || 8 });
    document.documentElement.dataset.arenaEffects = minimal ? 'minimal' : 'full';
    document.documentElement.classList.toggle('arena-background-paused', document.hidden);
    const control = $('arena-effects'); if (control) control.value = arenaEffectsPreference;
}
function arenaSetEffects(value) {
    arenaEffectsPreference = ['minimal', 'full'].includes(value) ? value : 'auto';
    try { localStorage.setItem('reverse-arena-effects', arenaEffectsPreference); } catch {}
    applyArenaEffects();
}
window.arenaSetEffects = arenaSetEffects;
function arenaToggleSettings() {
    const panel = $('arena-settings-panel');
    if (!panel) return;
    panel.hidden = !panel.hidden;
    document.querySelector('.workbench-settings-toggle')?.setAttribute('aria-expanded', String(!panel.hidden));
}
window.arenaToggleSettings = arenaToggleSettings;
document.addEventListener('visibilitychange', applyArenaEffects);
window.matchMedia?.('(prefers-reduced-motion: reduce)').addEventListener?.('change', applyArenaEffects);

async function arenaStartWithDraft() {
    if (S.arenaPreparingFight) return;
    S.arenaPreparingFight = true;
    renderTeam();
    const id = String(S.arenaEditingDeckId || '1');
    try {
        // Await an in-flight autosave, then activate exactly the visible draft.
        await autosaveArenaDeck(id);
        if (!await saveArenaDeckState(id, { activate: true, notify: false })) {
            showToast(S.arenaDeckNotice || 'Не удалось сохранить колоду. Попробуй ещё раз.');
            return;
        }
        await arenaFindOpponent();
    } finally {
        S.arenaPreparingFight = false;
        if (isArenaWorkbenchVisible()) renderTeam();
    }
}
window.arenaStartWithDraft = arenaStartWithDraft;

function setArenaDraft(slots) {
    arenaMutationEpoch += 1;
    const deckId = String(S.arenaEditingDeckId || '1');
    const normalized = normalizeArenaSlots(slots);
    S.arenaDraftSlots = normalized;
    S.arenaDrafts = { ...(S.arenaDrafts || {}), [deckId]: normalized };
}

function arenaAvailableCards(cards) {
    return cardsAvailableForDeck(cards, S.arenaDecks, S.arenaEditingDeckId || S.arenaActiveDeckId, S.arenaDrafts);
}

function arenaDeckDraft(deckId) {
    const id = String(deckId || S.arenaEditingDeckId || '1');
    const deck = normalizeArenaDecks(S.arenaDecks).find(item => item.id === id);
    return normalizeArenaSlots(S.arenaDrafts?.[id] ?? deck?.slots ?? {});
}

function isArenaDeckDirty(deckId) {
    const id = String(deckId || S.arenaEditingDeckId || '1');
    const persisted = normalizeArenaDecks(S.arenaDecks).find(item => item.id === id)?.slots || {};
    return arenaDeckChanged(persisted, arenaDeckDraft(id));
}

function arenaFocusCollection() {
    $('arena-card-picker')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
window.arenaFocusCollection = arenaFocusCollection;

function arenaLoadMoreCards() {
    S.arenaCardRenderLimit = Math.max(6, Number(S.arenaCardRenderLimit || 6)) + 6;
    renderTeam();
}
window.arenaLoadMoreCards = arenaLoadMoreCards;

function observeArenaCardStream() {
    arenaCardStreamObserver?.disconnect();
    arenaCardStreamObserver = null;
    const sentinel = document.querySelector('[data-arena-stream-sentinel]');
    if (!sentinel || !('IntersectionObserver' in window)) return;
    arenaCardStreamObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting) || S.tab !== 'team') return;
        arenaCardStreamObserver?.disconnect();
        arenaCardStreamObserver = null;
        arenaLoadMoreCards();
    }, { rootMargin: '280px 0px', threshold: 0.01 });
    arenaCardStreamObserver.observe(sentinel);
}

let arenaCardScrollFallbackBound = false;
function ensureArenaCardScrollFallback() {
    if (arenaCardScrollFallbackBound) return;
    const scroller = $('app-main');
    if (!scroller) return;
    arenaCardScrollFallbackBound = true;
    let ticking = false;
    scroller.addEventListener('scroll', () => {
        if (ticking || S.tab !== 'team') return;
        ticking = true;
        requestAnimationFrame(() => {
            ticking = false;
            const sentinel = document.querySelector('[data-arena-stream-sentinel]');
            if (!sentinel) return;
            const box = sentinel.getBoundingClientRect();
            if (box.top < window.innerHeight + 520) arenaLoadMoreCards();
        });
    }, { passive: true });
}

function arenaSetCardFilter(filter) {
    S.arenaCardFilter = ['all', 'ready', 'recovery'].includes(filter) ? filter : 'all';
    S.arenaCardRenderLimit = 6;
    haptic('light');
    renderTeam();
}
window.arenaSetCardFilter = arenaSetCardFilter;

function arenaCardRecovery(cardId) {
    const stored = Number(S.arenaLobby?.card_recovery?.[String(cardId)] || 0);
    const elapsed = Math.floor((Date.now() - Number(S.arenaRecoveryLoadedAt || Date.now())) / 1000);
    return recoveryRemaining(cardId, { [String(cardId)]: Math.max(0, stored - elapsed) });
}

async function refreshArenaDeckBuilder() {
    const uid = effectiveUid();
    if (!uid) return;
    try {
        const [lobby, cards] = await Promise.all([
            loadArenaState({ force: true, maxAge: 0 }),
            api(`/api/user_cards?user_id=${uid}`, { cacheMode: 'cache-first', maxAge: 60_000 }),
        ]);
        if (Array.isArray(cards)) S.cards = cards;
        if (!lobby || lobby.error) throw new Error(lobby?.error || 'Не удалось загрузить колоды');
        S.arenaLobby = lobby;
        S.arenaNetworkUnavailable = false;
        S.arenaDeckNotice = '';
        mergeArenaDecksFromServer(lobby.decks);
        S.arenaActiveDeckId = String(lobby.active_deck_id || '1');
        S.arenaEditingDeckId = S.arenaActiveDeckId;
        S.arenaRecoveryLoadedAt = Date.now();
        if (Array.isArray(lobby.team)) {
            S.team = lobby.team.map((card, index) => card ? { ...card, slot: card.slot || index + 1 } : null);
        }
        S.arenaDrafts = Object.fromEntries(S.arenaDecks.map(deck => [deck.id, { ...(deck.slots || {}) }]));
        const deck = S.arenaDecks.find(item => item.id === String(S.arenaEditingDeckId)) || S.arenaDecks[0];
        setArenaDraft(deck?.slots || {});
        scheduleDeviceSnapshotSave(uid);
        persistArenaDeviceState(uid);
        renderTeam();
    } catch (error) {
        S.arenaNetworkUnavailable = navigator.onLine === false || /(?:network|fetch|соедин|сеть|timeout|сервер)/i.test(String(error?.message || error));
        const picker = $('arena-card-picker');
        if (picker) picker.innerHTML = `<div class="empty-state">⚠ ${esc(error?.message || 'Ошибка загрузки')}</div>`;
    }
}

async function arenaEditDeck(deckId) {
    if (S.arenaPreparingFight) return;
    const nextId = String(deckId);
    const currentId = String(S.arenaEditingDeckId || S.arenaActiveDeckId || '1');
    if (nextId === currentId) return;
    const deck = normalizeArenaDecks(S.arenaDecks).find(item => item.id === nextId);
    if (!deck) return;
    // Keep the tap instant. The save owns currentId explicitly and can finish
    // after the next preset is already visible without overwriting its draft.
    void autosaveArenaDeck(currentId, { reason: 'switch' });
    S.arenaEditingDeckId = deck.id;
    S.arenaReplaceSlot = null;
    S.arenaDraftSlots = { ...(S.arenaDrafts?.[deck.id] || deck.slots || {}) };
    S.arenaDeckNotice = '';
    persistArenaDeviceState();
    haptic('light');
    renderTeam();
}
window.arenaEditDeck = arenaEditDeck;

function arenaToggleDeckCard(encodedCardId) {
    if (S.arenaPreparingFight) return;
    const cardId = decodeURIComponent(encodedCardId || '');
    if (!cardId) return;
    const draft = arenaDeckDraft();
    const existing = Object.keys(draft).find(key => draft[key] === cardId);
    if (!existing && !arenaAvailableCards([{ id: cardId }]).length) {
        showToast('Карта уже в другой колоде. Сначала убери её оттуда.');
        renderTeam();
        return;
    }
    if (arenaCardRecovery(cardId) && (!existing || S.arenaReplaceSlot)) {
        showToast(`Карта восстанавливается: ${formatRecovery(arenaCardRecovery(cardId))}`);
        return;
    }
    if (!existing && !S.arenaReplaceSlot && arenaDeckCardCount(draft) === 4) {
        S.arenaDeckNotice = 'Сначала выбери позицию в своём отряде, которую хочешь заменить.';
        renderTeam();
        return;
    }
    setArenaDraft(chooseDeckCard(draft, cardId, S.arenaReplaceSlot));
    S.arenaReplaceSlot = null;
    S.arenaDeckNotice = '';
    haptic('light');
    renderTeam();
}
window.arenaToggleDeckCard = arenaToggleDeckCard;

function arenaRemoveDeckSlot(slot) {
    if (S.arenaPreparingFight) return;
    const draft = { ...(S.arenaDraftSlots || {}) };
    delete draft[String(slot)];
    S.arenaReplaceSlot = null;
    S.arenaDeckNotice = '';
    setArenaDraft(draft);
    haptic('light');
    renderTeam();
}
window.arenaRemoveDeckSlot = arenaRemoveDeckSlot;

function arenaAutoDeck() {
    if (S.arenaPreparingFight) return;
    const selected = autoSelectArenaCards(arenaAvailableCards(S.cards), Object.fromEntries(
        Object.keys(S.arenaLobby?.card_recovery || {}).map(cardId => [cardId, arenaCardRecovery(cardId)])
    ));
    if (selected.length < 4) {
        showToast(`Готовых карт: ${selected.length} из 4`);
        haptic('error');
        return;
    }
    S.arenaDeckNotice = '';
    setArenaDraft(slotsFromCards(selected));
    haptic('success');
    renderTeam();
}
window.arenaAutoDeck = arenaAutoDeck;

let arenaDeckWriteChain = Promise.resolve(false);
function saveArenaDeckState(deckId, options = {}) {
    const task = arenaDeckWriteChain.catch(() => false).then(() => performSaveArenaDeckState(deckId, options));
    arenaDeckWriteChain = task.catch(() => false);
    return task;
}

async function performSaveArenaDeckState(deckId, { activate = false, notify = false, keepalive = false } = {}) {
    const id = String(deckId || S.arenaEditingDeckId || '1');
    const slots = arenaDeckDraft(id);
    const submittedSignature = stableDataSignature(slots);
    const uid = effectiveUid();
    if (!uid) return false;
    if (activate && !isArenaDeckComplete(slots)) {
        showToast('Выбери 4 разные карты');
        return false;
    }
    if (activate && Object.values(slots).some(cardId => arenaCardRecovery(cardId))) {
        showToast('В этой колоде есть карты на восстановлении');
        return false;
    }
    const activeNeedsSync = activate && id !== String(S.arenaActiveDeckId);
    if (!isArenaDeckDirty(id) && !activeNeedsSync) return true;
    const deck = normalizeArenaDecks(S.arenaDecks).find(item => item.id === id);
    const syncsActiveDeck = activate || id === String(S.arenaActiveDeckId);
    return arenaDeckActionGuard.run(`deck:${id}`, async () => {
        const status = $('arena-save-status');
        if (status && isArenaWorkbenchVisible()) status.textContent = 'Сохраняем…';
        try {
            const res = await api('/api/arena_deck_save', {
                method: 'POST',
                timeoutMs: 15000,
                keepalive,
                body: { user_id: uid, deck_id: id, name: deck?.name || `Колода ${id}`, slots, activate },
            });
            if (!res?.success || !Array.isArray(res.decks)) throw new Error(res?.error || 'Не удалось сохранить колоду');
            S.arenaNetworkUnavailable = false;
            arenaMutationEpoch += 1;
            mergeArenaDecksFromServer(res.decks);
            S.arenaActiveDeckId = String(res.active_id || S.arenaActiveDeckId || '1');
            const savedDeck = S.arenaDecks.find(item => item.id === id);
            const draftUnchangedDuringSave = stableDataSignature(arenaDeckDraft(id)) === submittedSignature;
            if (draftUnchangedDuringSave) {
                S.arenaDrafts = { ...(S.arenaDrafts || {}), [id]: { ...(savedDeck?.slots || slots) } };
                if (String(S.arenaEditingDeckId) === id) S.arenaDraftSlots = { ...(savedDeck?.slots || slots) };
            }
            if (syncsActiveDeck) {
                const cardsById = new Map((S.cards || []).map(card => [String(card.id), card]));
                const activeDeckRecovery = Object.values(slots)
                    .map(cardId => ({ card_id: String(cardId), remaining_sec: arenaCardRecovery(cardId) }))
                    .filter(item => item.remaining_sec > 0);
                S.team = ['1', '2', '3', '4'].map((position, index) => {
                    const card = cardsById.get(String(slots[position] || ''));
                    return card ? { ...card, slot: index + 1 } : null;
                });
                S.arenaLobby = {
                    ...(S.arenaLobby || {}),
                    decks: S.arenaDecks,
                    active_deck_id: S.arenaActiveDeckId,
                    team: S.team,
                    team_ready: S.team.filter(Boolean).length === 4,
                    active_deck_recovery: activeDeckRecovery,
                };
                updateArenaFightButton(S.arenaLobby);
            }
            if (notify) {
                haptic('success');
                showToast(activate ? `Колода ${id} выбрана` : `Колода ${id} сохранена`);
            }
            S.arenaDeckNotice = '';
            scheduleDeviceSnapshotSave(uid);
            persistArenaDeviceState(uid);
            if (S.tab === 'team') renderTeam();
            if (S.tab === 'arena') renderArena(false);
            return true;
        } catch (error) {
            console.error('[ARENA DECK SAVE]', error);
            S.arenaNetworkUnavailable = navigator.onLine === false || /(?:network|fetch|соедин|сеть|timeout|сервер)/i.test(String(error?.message || error));
            S.arenaDeckNotice = error?.message || 'Ошибка сохранения колоды';
            if (notify) haptic('error');
            if (notify) showToast(S.arenaDeckNotice);
            if (S.tab === 'team' || isArenaWorkbenchVisible()) renderTeam();
            return false;
        }
    });
}

async function autosaveArenaDeck(deckId, { reason = '', keepalive = false } = {}) {
    const id = String(deckId || S.arenaEditingDeckId || '1');
    for (let attempt = 0; attempt < 3; attempt += 1) {
        if (!isArenaDeckDirty(id)) return true;
        const slots = arenaDeckDraft(id);
        const isActive = id === String(S.arenaActiveDeckId);
        const canActivate = isActive
            && isArenaDeckComplete(slots)
            && !Object.values(slots).some(cardId => arenaCardRecovery(cardId));
        const saved = await saveArenaDeckState(id, { activate: canActivate, notify: false, keepalive });
        if (!saved) return false;
    }
    return !isArenaDeckDirty(id);
}

function autosaveCurrentArenaDeck(options = {}) {
    return autosaveArenaDeck(String(S.arenaEditingDeckId || '1'), options);
}

async function arenaSaveDeck() {
    return saveArenaDeckState(String(S.arenaEditingDeckId || '1'), { activate: true, notify: true });
}
window.arenaSaveDeck = arenaSaveDeck;

function arenaActivateEditedDeck() {
    if (String(S.arenaEditingDeckId) === String(S.arenaActiveDeckId)) return;
    void arenaSaveDeck();
}
window.arenaActivateEditedDeck = arenaActivateEditedDeck;

function taskPeriodMeta(t) {
    const period = t.period || (String(t.type || '').startsWith('daily') ? 'daily' : 'regular');
    const labels = { daily: 'День', weekly: 'Неделя', regular: 'Обычное' };
    const label = labels[period] || 'Обычное';
    const expires = t.expires ? String(t.expires) : '';
    let until = '';
    if (expires && !t.claimed) {
        const d = new Date(`${expires}T00:00:00`);
        until = Number.isNaN(d.getTime())
            ? `до ${expires}`
            : `до ${d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`;
    }
    return { period, label, until };
}

function renderTasks() {
    const el = $('tasks-list'); if (!el) return;
    if (!S.tasks.length) { el.innerHTML = '<div class="empty-state">Заданий пока нет</div>'; return; }
    const order = { daily: 0, weekly: 1, regular: 2 };
    const sorted = [...S.tasks].sort((a, b) => {
        const pa = taskPeriodMeta(a).period;
        const pb = taskPeriodMeta(b).period;
        return (order[pa] ?? 9) - (order[pb] ?? 9);
    });
    el.innerHTML = sorted.map(t => {
        const prog = Math.min(+t.progress || 0, +t.target || 1);
        const tgt = +t.target || 1;
        const pct = Math.round(prog / tgt * 100);
        const done = prog >= tgt;
        const meta = taskPeriodMeta(t);
        const rewardParts = [];
        if (t.reward_coins) rewardParts.push(`◉ ${(+t.reward_coins).toLocaleString('ru-RU')}`);
        if (t.reward_spins) rewardParts.push(`◈ ${t.reward_spins}`);
        const reward = rewardParts.join(' · ') || '—';
        const spinHint = t.reward_spins
            ? '<span class="task-spin-hint">крутка</span>'
            : '';
        return `<div class="task-item task-${meta.period} ${t.claimed ? 'task-completed' : ''}">
            <div class="task-head">
                <div class="task-icon">${taskIcon(t.title)}</div>
                <div style="flex:1;min-width:0">
                    <div class="task-title-row">
                        <div class="task-title">${esc(t.title || 'Задание')}</div>
                        <span class="task-period task-period-${meta.period}">${meta.label}</span>
                    </div>
                    <div class="task-desc">${esc(t.desc || '')}</div>
                    ${meta.until ? `<div class="task-expires">⏳ обновление ${esc(meta.until)}</div>` : ''}
                </div>
            </div>
            <div class="task-progress-text">${prog} / ${tgt}</div>
            <div class="task-progress"><div class="task-bar" style="width:${pct}%"></div></div>
            <div class="task-footer">
                <span class="task-reward">${reward}${spinHint}</span>
                ${done && !t.claimed ? `<button class="task-claim" onclick="claimTask('${String(t.id || '').replace(/'/g, '')}')">Забрать</button>` : ''}
                ${t.claimed ? '<span class="task-done-badge">✓ Выполнено</span>' : ''}
            </div>
        </div>`;
    }).join('');
}

// ── Referral ────────────────────────────────────────
async function loadReferral() {
    const uid = effectiveUid();
    if (!uid) {
        if (!S._refRetry) { S._refRetry = 1; setTimeout(loadReferral, 500); }
        return;
    }
    if (!S.ref) renderReferral(referralStarterData(uid));
    try {
        const d = await api(`/api/referral?user_id=${uid}`);
        if (!d || d.error) throw new Error(d?.error || 'load failed');
        if (!Array.isArray(d.verses) || !d.verses.length) {
            try {
                const uni = await api('/api/universes');
                d.verses = uni?.list || uni?.verses || [];
            } catch (_) { /* chips stay on Любая */ }
        }
        if (!d.stats) d.stats = emptyRefStats();
        renderReferral(d);
    } catch (e) {
        console.error('Referral Load Failure:', e);
        renderReferral(S.ref || referralStarterData(uid));
    }
}

function referralStarterData(uid) {
    const milestones = [
        [1, '10 круток'],
        [3, '10 ReStars'],
        [5, '20 ReStars'],
        [10, '40 ReStars'],
        [25, '80 ReStars'],
        [50, '200 ReStars'],
        [100, '410 ReStars'],
    ].map(([step, label], index) => ({
        step, label, done: false, current: index === 0,
    }));
    milestones.push({
        step: 101,
        label: '+5 ReStars за каждого нового друга',
        endless: true,
        current: false,
        done: false,
    });
    return {
        link: `https://t.me/reversecard_robot?start=u_${uid}`,
        invited: 0,
        verified: 0,
        friends: 0,
        pending: 0,
        claimed: [],
        remaining: 1,
        next_step: 1,
        next_label: '10 круток',
        activation_bonus: 0,
        newcomer_bonus: 5,
        per_head_after: 5,
        milestones,
        links: [],
        verses: [],
        max_links: 8,
        is_admin: false,
        default_verce: '',
        stats: emptyRefStats(),
    };
}

function emptyRefStats() {
    return {
        clicks: 0,
        invited: 0,
        verified: 0,
        pending: 0,
        conversion: 0,
        verses: [],
        heatmap: Array(56).fill(0),
        growth: Array(12).fill(0),
        gauge: 0,
        gauge_label: '0/1',
        campaigns: [],
    };
}

function refInviteWord(n) {
    const abs = Math.abs(Number(n) || 0) % 100;
    const d = abs % 10;
    if (abs > 10 && abs < 20) return 'приглашенных';
    if (d === 1) return 'приглашенного';
    if (d >= 2 && d <= 4) return 'приглашенных';
    return 'приглашенных';
}

function renderReferral(d) {
    S.ref = d;
    const friends = Number(d.friends ?? d.verified ?? 0);
    const claimed = Array.isArray(d.claimed) ? d.claimed : [];
    setText('ref-link', d.link || '');
    setText('ref-friends', refNum(friends));
    const stats = d.stats || emptyRefStats();
    setText('ref-kpi-clicks', refNum(stats.clicks || 0));
    setText('ref-kpi-invited', refNum(stats.invited || d.invited || 0));

    const sub = $('ref-invited-sub');
    if (sub) {
        const pending = Number(d.pending || 0);
        sub.textContent = pending > 0
            ? `Ещё ждут первую карту: ${refNum(pending)}`
            : 'Засчитаются после первой карты друга';
    }

    const rewards = $('ref-rewards-list');
    if (rewards) {
        const rows = (d.milestones || [])
            .filter(m => m.endless || m.current || !m.done)
            .map(m => {
                if (m.endless) {
                    return `<div class="ref-reward-row endless ${m.current ? 'current' : ''}">
                        <span class="ref-reward-step">Далее</span>
                        <span class="ref-reward-label">${esc(m.label)}</span>
                    </div>`;
                }
                return `<div class="ref-reward-row ${m.current ? 'current' : ''}">
                    <span class="ref-reward-step">${m.step}</span>
                    <span class="ref-reward-label">${esc(m.label)}</span>
                    <span class="ref-reward-mark">${m.current ? '➤' : ''}</span>
                </div>`;
            }).join('');
        rewards.innerHTML = rows;
    }

    const claimedEl = $('ref-claimed-list');
    if (claimedEl) {
        if (!claimed.length) {
            claimedEl.hidden = true;
            claimedEl.textContent = '';
        } else {
            claimedEl.hidden = false;
            claimedEl.textContent = `Получено: ${claimed.join(', ')}`;
        }
    }

    const bar = $('ref-progress-bar');
    const nextVal = $('ref-next-value');
    if (d.next_step) {
        const left = Math.max(0, Number(d.remaining ?? (d.next_step - friends)));
        if (nextVal) nextVal.innerHTML = `<b>${left}</b> приглашенных до ${esc(d.next_label || 'награды')}`;
        if (bar) bar.style.width = Math.min(100, Math.round(friends / d.next_step * 100)) + '%';
        setText('ref-progress-hint', `Цель: ${d.next_step} друзей`);
    } else {
        if (nextVal) nextVal.innerHTML = `<b>+${d.per_head_after || 5} ReStars</b> за каждого нового друга`;
        if (bar) bar.style.width = '100%';
        setText('ref-progress-hint', 'Все основные награды уже получены');
    }
    try { renderRefVersePicks(d); } catch (error) { console.error('[REF VERSE]', error); }
    try { renderRefStats(d); } catch (error) { console.error('[REF STATS]', error); }
    try { renderRefCampaigns(d); } catch (error) { console.error('[REF CAMPAIGNS]', error); }
}

function renderRefVersePicks(d) {
    const box = $('ref-verse-picks');
    const hint = $('ref-verse-hint');
    if (!box) return;
    const selected = String(d.default_verce || '');
    const verses = Array.isArray(d.verses) ? d.verses : [];
    if (hint) {
        hint.textContent = selected
            ? `Новичок сразу попадёт в ${selected}`
            : 'Новичок по личной ссылке выберет вселенную сам';
    }
    const chips = [{ id: '', name: 'Любая' }].concat(
        verses.map(v => ({ id: String(v.id || v.name || ''), name: String(v.name || v.id || '') }))
    );
    box.innerHTML = chips.map(chip => {
        const on = chip.id === selected ? ' is-on' : '';
        return `<button type="button" class="ref-verse-chip${on}" onclick="setRefVerse(${JSON.stringify(chip.id)})">${esc(chip.name)}</button>`;
    }).join('');
}

async function setRefVerse(verce) {
    const next = String(verce || '');
    try {
        const res = await api('/api/referral_verse', {
            method: 'POST',
            body: { user_id: effectiveUid(), verce: next },
        });
        if (res?.error) throw new Error(res.error);
        if (S.ref) S.ref.default_verce = res.default_verce ?? next;
        renderRefVersePicks(S.ref || { default_verce: next, verses: [] });
        showToast(next ? `Вселенная: ${next}` : 'Игрок выберет сам');
        haptic('light');
    } catch (error) {
        showToast(error?.message || 'Не удалось сохранить вселенную');
    }
}

function refNum(n) {
    return Number(n || 0).toLocaleString('ru-RU');
}

function refMeterRow(label, value, max) {
    const n = Number(value || 0);
    const pct = n <= 0 ? 0 : Math.max(6, Math.round(100 * n / Math.max(1, max)));
    return `<div class="ref-meter">
        <span class="ref-meter-name">${esc(label)}</span>
        <b class="ref-meter-val">${refNum(n)}</b>
        <div class="ref-meter-track" aria-hidden="true"><i style="width:${pct}%"></i></div>
    </div>`;
}

function renderRefStats(d) {
    const box = $('ref-stats');
    if (!box) return;
    const stats = d.stats || emptyRefStats();
    const clicks = Number(stats.clicks || 0);
    const invited = Number(stats.invited || d.invited || 0);
    const verified = Number(stats.verified || d.verified || d.friends || 0);
    const pending = Number(stats.pending || d.pending || 0);
    if (clicks + invited + verified === 0) {
        box.innerHTML = '<p class="ref-empty">Пока нет переходов. Поделись ссылкой выше, и здесь появятся клики и вселенные.</p>';
        return;
    }
    const max = Math.max(clicks, invited, verified, pending, 1);
    const conversion = Number(stats.conversion || 0);
    let html = `<div class="ref-panel">
        <div class="ref-panel-head">
            <span>Воронка</span>
            <b>${conversion}%</b>
        </div>
        ${refMeterRow('Клики', clicks, max)}
        ${refMeterRow('Зашли', invited, max)}
        ${refMeterRow('Открыли карту', verified, max)}
        ${pending ? refMeterRow('Ждут карту', pending, max) : ''}
    </div>`;
    const verses = (stats.verses || []).filter(v => Number(v.invited || 0) > 0).slice(0, 6);
    if (verses.length) {
        const vmax = Math.max(...verses.map(v => Number(v.invited || 0)), 1);
        html += `<div class="ref-panel">
            <div class="ref-panel-head"><span>Вселенные</span></div>
            ${verses.map(v => refMeterRow(String(v.name || v.id || 'Вселенная'), Number(v.invited || 0), vmax)).join('')}
        </div>`;
    }
    box.innerHTML = html;
}

function renderRefCampaigns(d) {
    const select = $('ref-new-verse');
    if (select && Array.isArray(d.verses)) {
        const current = select.value;
        const options = ['<option value="">Игрок выберет сам</option>'].concat(
            d.verses.map(v => `<option value="${esc(v.id || v.name)}">${esc(v.name)}</option>`)
        );
        select.innerHTML = options.join('');
        if ([...select.options].some(opt => opt.value === current)) select.value = current;
    }
    const cap = $('ref-links-cap');
    const links = Array.isArray(d.links) ? d.links : [];
    const maxLinks = Number(d.max_links || 8);
    if (cap) cap.textContent = `${links.length} / ${maxLinks}`;
    const list = $('ref-links-list');
    if (!list) return;
    if (!links.length) {
        list.innerHTML = '';
        return;
    }
    list.innerHTML = links.map(item => {
        const verse = item.verce ? esc(item.verce) : 'Любая вселенная';
        const slug = String(item.slug || '').replace(/'/g, '');
        return `<article class="ref-link-item">
            <div class="ref-link-item-top">
                <div>
                    <div class="ref-link-item-name">${esc(item.name || 'Ссылка')}</div>
                    <div class="ref-link-item-verse">${verse}</div>
                </div>
            </div>
            <div class="ref-link-item-url">${esc(item.link || '')}</div>
            <div class="ref-link-stats">
                <div class="ref-link-stat"><b>${Number(item.clicks || 0)}</b><span>клики</span></div>
                <div class="ref-link-stat"><b>${Number(item.invited || 0)}</b><span>зашли</span></div>
                <div class="ref-link-stat"><b>${Number(item.verified || 0)}</b><span>карты</span></div>
            </div>
            <div class="ref-link-item-acts">
                <button type="button" onclick="copyCampaignLink('${slug}')">Копировать</button>
                <button type="button" class="danger" onclick="deleteRefCampaign('${slug}')">Скрыть</button>
            </div>
        </article>`;
    }).join('');
}

async function createRefCampaign(event) {
    event?.preventDefault?.();
    const name = ($('ref-new-name')?.value || '').trim();
    const verce = $('ref-new-verse')?.value || '';
    try {
        const res = await api('/api/referral_link_create', {
            method: 'POST',
            body: { user_id: effectiveUid(), name, verce },
        });
        if (res?.error) throw new Error(res.error);
        if ($('ref-new-name')) $('ref-new-name').value = '';
        showToast('Ссылка создана');
        haptic('medium');
        await loadReferral();
    } catch (error) {
        showToast(error?.message || 'Не удалось создать ссылку');
    }
    return false;
}

function campaignBySlug(slug) {
    return (S.ref?.links || []).find(item => item.slug === slug);
}

function copyCampaignLink(slug) {
    const item = campaignBySlug(slug);
    const link = item?.link;
    if (!link) return;
    haptic('light');
    const done = () => showToast('Ссылка скопирована');
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(() => showToast(link));
    } else showToast(link);
}

async function deleteRefCampaign(slug) {
    try {
        const res = await api('/api/referral_link_delete', {
            method: 'POST',
            body: { user_id: effectiveUid(), slug },
        });
        if (res?.error) throw new Error(res.error);
        showToast('Ссылка скрыта');
        await loadReferral();
    } catch (error) {
        showToast(error?.message || 'Не удалось скрыть');
    }
}

function copyRefLink() {
    const link = S.ref?.link || ($('ref-link')?.textContent || '').trim();
    if (!link || link === '…') return;
    haptic('light');
    const done = () => showToast('✓ Ссылка скопирована');
    const fallback = () => {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand('copy'); done(); }
        catch (_) { showToast('⚠ Не удалось скопировать'); }
        ta.remove();
    };
    if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(link).then(done).catch(fallback);
    } else fallback();
}

function shareRefLink() {
    const link = S.ref?.link;
    if (!link) return;
    haptic('medium');
    const text = '🎴 Залетай в REVERSE: аниме-карты, арена, кланы. По моей ссылке бонус при входе!';
    const share = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
    if (tg && IS_TG) { try { tg.openTelegramLink(share); return; } catch (_) {} }
    window.open(share, '_blank');
}

function renderShop() {
    const u = S.user; if (!u) return;
    setText('s-coins', (u.coins || 0).toLocaleString('ru-RU'));
    setText('s-stars', u.restars || 0);
    setText('s-spins', u.spins || 0);
    const rate = Math.max(1, Number(u.spin_exchange_rate) || 5);
    setText('shop-spin-rate', `${rate} круток`);
    document.querySelectorAll<HTMLButtonElement>('.spin-pack').forEach((button, index) => {
        const costs = [1, 5, 10];
        const amount = costs[index] * rate;
        const label = button.querySelector('span');
        if (label) label.textContent = `${amount} круток`;
    });
    renderEliteStatus(u);
    const hasFirstBonus = !u._pending && u.first_stars_bonus === false;
    document.querySelectorAll<HTMLElement>('[data-restars-first-bonus]').forEach(el => {
        el.hidden = !hasFirstBonus;
    });
    // Темы грузим отложенно — синхронный рынок с video крашил TG при входе в магазин
    if (!S.themes.market.length && !S.themes.loading) {
        setTimeout(() => {
            if (S.tab === 'shop') loadThemeShop();
        }, 180);
    } else {
        renderThemeMarket();
    }
}

const exchangeDenBusy = new Set();
let exchangeThemeQuery = '';
let exchangeThemePage = 0;
let exchangeMarket = null;
let exchangeMarketClockOffset = 0;

function syncExchangeEconomy() {
    const user = S.user || {};
    const coins = Math.max(0, Number(user.coins) || 0);
    const restars = Math.max(0, Number(user.restars) || 0);
    const spins = Math.max(0, Number(user.spins) || 0);
    setText('xd-coins', coins.toLocaleString('ru-RU'));
    setText('xd-restars', restars.toLocaleString('ru-RU'));
    setText('xd-spins', spins.toLocaleString('ru-RU'));
    setText('h-coins', coins.toLocaleString('ru-RU'));
    setText('h-spins', spins.toLocaleString('ru-RU'));
    setText('p-coins', coins.toLocaleString('ru-RU'));
    setText('p-stars', restars.toLocaleString('ru-RU'));
    setText('p-spins', spins.toLocaleString('ru-RU'));
    renderHomeOpening();
    setText('s-coins', coins.toLocaleString('ru-RU'));
    setText('s-stars', restars.toLocaleString('ru-RU'));
    setText('s-spins', spins.toLocaleString('ru-RU'));
}

function exchangeSellableThemes() {
    const uid = String(effectiveUid() || '');
    const details = Array.isArray(S.themes.mine?.owned_details) ? S.themes.mine.owned_details : [];
    return details.filter(theme => (
        String(theme?.author_id || '') !== uid
        && Number(theme?.price) > 0
        && String(theme?.status || 'approved') === 'approved'
    ));
}

function exchangeThemeMedia(theme) {
    const src = themeMediaUrl(theme?.media_url || '');
    if (!src) return '<div class="xd-theme-media"></div>';
    if (_themeIsVideo(theme, src)) {
        return `<div class="xd-theme-media"><video class="lazy-media" src="${esc(src)}" data-src="${esc(src)}" muted playsinline loop preload="metadata"></video></div>`;
    }
    return `<div class="xd-theme-media"><img src="${esc(src)}" alt="" loading="lazy" decoding="async" onerror="rvMediaError(this)"></div>`;
}

function exchangeOfferTypeLabel(type) {
    return ({
        rare_card: 'Карта',
        limited_theme: 'Лимитная тема',
        discounted_restars: 'ReStars',
        arena_reset: 'Арена',
        spins: 'Крутки',
        clan_discount: 'Клан',
    })[String(type || '')] || 'Редкий лот';
}

function exchangeOfferPrice(price) {
    const currency = String(price?.currency || '');
    const name = currency === 'recoin' ? 'рекоинов' : (currency === 'restars' ? 'ReStars' : 'TG Stars');
    return `${Math.max(0, Number(price?.amount) || 0).toLocaleString('ru-RU')} ${name}`;
}

function exchangeOfferMedia(offer) {
    const src = themeMediaUrl(offer?.media_url || '');
    if (!src) {
        const art = { rare_card: 'assets/hub/cards.jpg', limited_theme: 'assets/shop/themes.webp',
            discounted_restars: 'assets/shop/reward-pack.webp', arena_reset: 'assets/hub/arena.jpg',
            spins: 'assets/shop/spins.webp', clan_discount: 'assets/hub/clan.jpg' };
        return `<div class="xd-offer-media"><img src="${art[offer?.type] || 'assets/hub/shop.jpg'}" alt="" loading="lazy" decoding="async"></div>`;
    }
    if (String(offer?.media_type || '').toLowerCase() === 'video' || _themeIsVideo(offer, src)) {
        return `<div class="xd-offer-media"><video class="lazy-media" src="${esc(src)}" data-src="${esc(src)}" muted playsinline loop preload="metadata"></video></div>`;
    }
    return `<div class="xd-offer-media"><img src="${esc(src)}" alt="${esc(offer?.title || 'Редкое предложение')}" loading="lazy" decoding="async" onerror="rvMediaError(this)"></div>`;
}

function updateExchangeMarketTimer() {
    const target = Number(exchangeMarket?.ends_at) || 0;
    if (!target) { setText('xd-market-timer', '--:--:--'); return; }
    const now = Math.floor(Date.now() / 1000) + exchangeMarketClockOffset;
    const left = Math.max(0, target - now);
    const hours = Math.floor(left / 3600);
    const minutes = Math.floor((left % 3600) / 60);
    const seconds = left % 60;
    setText('xd-market-timer', [hours, minutes, seconds].map(value => String(value).padStart(2, '0')).join(':'));
    if (!left && S.tab === 'exchange' && !exchangeDenBusy.has('market-refresh')) {
        void loadExchangeMarket();
    }
}

setInterval(updateExchangeMarketTimer, 1000);

function renderExchangeMarket() {
    const host = $('xd-market-offers');
    if (!host) return;
    const offers = Array.isArray(exchangeMarket?.offers) ? exchangeMarket.offers : [];
    host.setAttribute('aria-busy', 'false');
    if (!offers.length) {
        host.classList.add('is-empty');
        host.innerHTML = '<div class="xd-market-empty">Предложений пока нет. Загляни после обновления витрины.</div>';
        updateExchangeMarketTimer();
        return;
    }
    host.classList.remove('is-empty');
    host.innerHTML = offers.map(offer => {
        const remaining = Math.max(0, Number(offer?.remaining) || 0);
        const quantity = Math.max(0, Number(offer?.quantity) || 0);
        const bought = Boolean(offer?.purchased);
        const sold = remaining <= 0;
        const buttonClass = bought ? ' is-bought' : (sold ? ' is-sold' : '');
        const pending = exchangeDenBusy.has(`offer:${offer?.id}`);
        const buttonText = pending ? 'Покупка…' : bought ? 'Куплено' : (sold ? 'Раскуплено' : 'Купить');
        return `<article class="xd-offer${bought || sold ? ' is-unavailable' : ''}" data-offer-type="${esc(offer?.type || '')}">
            ${exchangeOfferMedia(offer)}
            <div class="xd-offer-body">
                <h3>${esc(offer?.title || 'Редкий лот')}</h3>
                <p class="xd-offer-subtitle">${esc(offer?.subtitle || '')}</p>
                <div class="xd-offer-meta"><span>${esc(exchangeOfferTypeLabel(offer?.type))}</span><span>Осталось ${remaining} из ${quantity}</span></div>
            </div>
            <p class="xd-offer-description">${esc(offer?.description || '')}</p>
            <footer class="xd-offer-footer"><div><span>Цена</span><b>${exchangeOfferPrice(offer?.price)}</b></div>
              <button class="xd-offer-buy${buttonClass}" type="button" onclick="exchangeDenBuyOffer('${esc(offer?.id || '')}')" aria-label="${buttonText}: ${esc(offer?.title || 'товар')}" ${bought || sold || pending ? 'disabled' : ''}>${buttonText}</button>
            </footer>
        </article>`;
    }).join('');
    updateExchangeMarketTimer();
}

async function loadExchangeMarket() {
    const uid = effectiveUid();
    if (!uid || exchangeDenBusy.has('market-refresh')) return;
    exchangeDenBusy.add('market-refresh');
    try {
        const snapshot = await api(`/api/exchange_offers?user_id=${uid}`, { dedupe: false, retries: 1 });
        exchangeMarket = snapshot;
        exchangeMarketClockOffset = Number(snapshot?.server_time) - Math.floor(Date.now() / 1000);
        setText('xd-market-note', 'Остатки общие для всех игроков и обновляются после каждой сделки.');
        renderExchangeMarket();
    } catch (error) {
        setText('xd-market-note', error?.message || 'Не удалось загрузить общую витрину.');
        const host = $('xd-market-offers');
        if (host) {
            host.setAttribute('aria-busy', 'false');
            host.classList.add('is-empty');
            host.innerHTML = '<div class="xd-market-empty">Связь с общим рынком потеряна. Остальные операции обменника продолжают работать.</div>';
        }
    } finally {
        exchangeDenBusy.delete('market-refresh');
    }
}

async function exchangeDenBuyOffer(offerId) {
    const offer = (exchangeMarket?.offers || []).find(item => String(item?.id) === String(offerId));
    const busyKey = `offer:${offerId}`;
    if (!offer || offer.purchased || Number(offer.remaining) <= 0 || exchangeDenBusy.has(busyKey)) return;
    const priceText = exchangeOfferPrice(offer.price);
    const accepted = await customConfirm('Редкое предложение', `${offer.title}: купить за ${priceText}?`, 'Купить', 'Назад');
    if (!accepted) return;
    exchangeDenBusy.add(busyKey);
    renderExchangeMarket();
    setText('xd-market-note', 'Фиксируем единицу из общего остатка...');
    try {
        if (String(offer?.price?.currency) === 'stars') {
            if (!IS_TG || !tg?.openInvoice) throw new Error('Оплата Stars доступна только в Telegram');
            const invoice = await api('/api/exchange_offer_invoice', {
                method: 'POST',
                body: { user_id: effectiveUid(), offer_id: offerId },
            });
            if (!invoice?.invoice_link) throw new Error('Не удалось создать счёт');
            tg.openInvoice(invoice.invoice_link, async status => {
                if (status === 'paid') {
                    haptic('success');
                    showToast(`+${invoice.restars} ReStars`);
                    setText('xd-market-note', 'Оплата принята. Обновляем баланс и общий остаток...');
                    setTimeout(async () => {
                        try { await Promise.all([loadAll(), loadExchangeMarket()]); } catch (_) { }
                        renderExchangeDen();
                    }, 900);
                } else {
                    setText('xd-market-note', status === 'cancelled' ? 'Оплата отменена. Резерв освободится автоматически.' : 'Оплата не прошла.');
                    if (status === 'failed') haptic('error');
                }
            });
            return;
        }

        const result = await api('/api/exchange_offer_buy', {
            method: 'POST',
            body: { user_id: effectiveUid(), offer_id: offerId },
        });
        if (S.user) {
            S.user.coins = Number(result.new_recoin) || 0;
            S.user.restars = Number(result.new_restars) || 0;
            S.user.spins = Number(result.new_spins) || 0;
        }
        if (result.offer_type === 'limited_theme') {
            try { S.themes.mine = await api(`/api/theme_mine?user_id=${effectiveUid()}`, { dedupe: false }); } catch (_) { }
        }
        if (['rare_card', 'arena_reset', 'clan_discount'].includes(String(result.offer_type || ''))) {
            try { await loadAll(); } catch (_) { }
        }
        await loadExchangeMarket();
        syncExchangeEconomy();
        renderExchangeDen();
        setText('xd-market-note', result.message || 'Покупка завершена.');
        showToast(result.message || 'Покупка завершена');
        haptic('success');
    } catch (error) {
        const message = error?.message || 'Не удалось купить редкий лот';
        setText('xd-market-note', message);
        showToast(message);
        haptic('error');
        await loadExchangeMarket();
    } finally {
        exchangeDenBusy.delete(busyKey);
        renderExchangeMarket();
    }
}

function renderExchangeDen() {
    syncExchangeEconomy();
    updateExchangeQuote();
    renderExchangeMarket();
    const list = $('xd-theme-list');
    if (!list) return;
    if (!S.themes.mine) {
        list.innerHTML = '<div class="xd-empty">Проверяем коллекцию…</div>';
        return;
    }
    const themes = exchangeSellableThemes();
    if (!themes.length) {
        setText('xd-theme-page', '0 / 0');
        const previous = $('xd-theme-prev');
        const next = $('xd-theme-next');
        if (previous) previous.disabled = true;
        if (next) next.disabled = true;
        list.innerHTML = '<div class="xd-empty">Нет тем для выкупа. Собственные авторские и бесплатные темы посредник не принимает.</div>';
        return;
    }
    const query = exchangeThemeQuery.trim().toLocaleLowerCase('ru-RU');
    const filtered = query
        ? themes.filter(theme => `${theme?.title || ''} ${theme?.author_name || ''}`.toLocaleLowerCase('ru-RU').includes(query))
        : themes;
    const pageSize = window.innerWidth <= 720 ? 4 : 6;
    const totalPages = Math.ceil(filtered.length / pageSize);
    exchangeThemePage = Math.max(0, Math.min(exchangeThemePage, Math.max(0, totalPages - 1)));
    setText('xd-theme-page', totalPages ? `${exchangeThemePage + 1} / ${totalPages}` : '0 / 0');
    const previous = $('xd-theme-prev');
    const next = $('xd-theme-next');
    if (previous) previous.disabled = exchangeThemePage <= 0;
    if (next) next.disabled = totalPages === 0 || exchangeThemePage >= totalPages - 1;
    if (!filtered.length) {
        list.innerHTML = '<div class="xd-empty">По этому запросу тем нет. Попробуй другое название.</div>';
        return;
    }
    const visibleThemes = filtered.slice(exchangeThemePage * pageSize, (exchangeThemePage + 1) * pageSize);
    list.innerHTML = visibleThemes.map(theme => {
        const price = Math.max(0, Number(theme.price) || 0);
        const payout = exchangeThemePayout(price);
        const themeId = themeIdAttr(theme.id);
        return `<article class="xd-theme-item">
            ${exchangeThemeMedia(theme)}
            <div class="xd-theme-copy">
                <strong>${esc(theme.title || 'Без названия')}</strong>
                <span>Стоимость ${price.toLocaleString('ru-RU')}</span>
            </div>
            <div class="xd-theme-payout"><span>Получишь</span><strong>${payout.toLocaleString('ru-RU')} <small>рекоинов</small></strong></div>
            <button class="xd-theme-sell" type="button" onclick="exchangeDenSellTheme('${themeId}')" aria-label="Продать ${esc(theme.title || 'тему')} за ${payout} рекоинов" ${exchangeDenBusy.has(`theme:${theme.id}`) ? 'disabled' : ''}>${exchangeDenBusy.has(`theme:${theme.id}`) ? 'Продаём…' : 'Продать тему'}</button>
        </article>`;
    }).join('');
}

function exchangeThemeSearch(value) {
    exchangeThemeQuery = String(value || '').slice(0, 80);
    exchangeThemePage = 0;
    renderExchangeDen();
}

function exchangeThemePageMove(direction) {
    exchangeThemePage = Math.max(0, exchangeThemePage + (Number(direction) < 0 ? -1 : 1));
    renderExchangeDen();
    $('xd-theme-list')?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
}

async function loadExchangeDen() {
    const uid = effectiveUid();
    if (!uid) {
        setText('xd-status', 'Открой обменник через Telegram, чтобы провести сделку.');
        return;
    }
    setText('xd-status', 'Сверяем коллекцию…');
    try {
        const [mine] = await Promise.all([
            api(`/api/theme_mine?user_id=${uid}`),
            loadExchangeMarket(),
            loadExchangeHistory(),
        ]);
        if (mine && !mine.error) S.themes.mine = mine;
        setText('xd-status', '');
        renderExchangeDen();
    } catch (error) {
        setText('xd-status', error?.message || 'Коллекция временно недоступна. Баланс остаётся без изменений.');
        renderExchangeDen();
    }
}

function openExchangeDen(panel = 'swap') {
    setExchangePanel(panel);
    void switchTab('exchange');
}

let exchangeRoute = 'restars';
let exchangeHistoryRequest = 0;
const exchangeCurrencyNames = { restars: 'ReStars', coins: 'Рекоины', spins: 'Крутки' };
function exchangeCurrencyIcon(currency) {
    const paths = currency === 'restars'
        ? '<path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9Z"/>'
        : currency === 'coins' ? '<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5.5"/>'
        : '<rect x="6" y="3" width="13" height="17" rx="2"/><path d="M6 7H3v13a2 2 0 0 0 2 2h10m-2-15-3 5h4l-3 5"/>';
    return `<svg viewBox="0 0 24 24" aria-hidden="true">${paths}</svg>`;
}
function setExchangePanel(panel) {
    if (!['swap', 'market', 'themes'].includes(panel)) return;
    setText('xd-page-title', { swap: 'Обмен валют', market: 'Товары дня', themes: 'Продать темы' }[panel]);
    const names = ['swap', 'market', 'themes'];
    const previous = names.find(name => $(`xd-panel-${name}`) && !$(`xd-panel-${name}`).hidden);
    changePanels($(`xd-panel-${previous}`), $(`xd-panel-${panel}`), () => {
        for (const name of names) {
            const region = $(`xd-panel-${name}`);
            if (region) region.hidden = name !== panel;
            $(`xd-tab-${name}`)?.setAttribute('aria-pressed', String(name === panel));
        }
    }, names.indexOf(panel) < names.indexOf(previous) ? -1 : 1);
    if (panel === 'themes') renderExchangeDen();
}
function setExchangeRoute(route) {
    if (exchangeDenBusy.has('swap')) return;
    exchangeRoute = route || (exchangeRoute === 'restars' ? 'coins' : 'restars');
    if (!EXCHANGE_ROUTES[exchangeRoute]) exchangeRoute = 'restars';
    const input = $('xd-swap-amount');
    if (input) input.value = '';
    setText('xd-status', '');
    updateExchangeQuote();
}
let exchangeCurrencyOpener = null;
function openExchangeCurrencies(side = 'source') {
    if (exchangeDenBusy.has('swap')) return;
    exchangeCurrencyOpener = document.activeElement;
    setText('xd-sheet-title', side === 'target' ? 'Вы получаете' : 'Вы отправляете');
    $('xd-currency-options').innerHTML = Object.entries(EXCHANGE_ROUTES).map(([key, route]) => {
        const currency = route[side];
        const balance = Math.max(0, Number(S.user?.[currency]) || 0).toLocaleString('ru-RU');
        return `<button class="xd-currency-option" type="button" onclick="chooseExchangeCurrency('${key}')" aria-pressed="${key === exchangeRoute}"><span class="xd-currency-icon" data-currency="${currency}">${exchangeCurrencyIcon(currency)}</span><span><strong>${exchangeCurrencyNames[currency]}</strong><small>${exchangeCurrencyNames[route.source]} → ${exchangeCurrencyNames[route.target]}</small></span><span class="xd-option-balance">${balance}</span><svg class="xd-option-check" viewBox="0 0 24 24" aria-hidden="true"><path d="m5 12 4 4L19 6"/></svg></button>`;
    }).join('');
    $('xd-currency-sheet')?.showModal();
}
function closeExchangeCurrencies() {
    $('xd-currency-sheet')?.close();
    exchangeCurrencyOpener?.focus?.({ preventScroll: true });
}
function chooseExchangeCurrency(route) {
    if (route !== exchangeRoute) setExchangeRoute(route);
    closeExchangeCurrencies();
}
function setExchangeMax() {
    if (exchangeDenBusy.has('swap')) return;
    const input = $('xd-swap-amount');
    if (input) input.value = String(exchangeMaximum(exchangeRoute, S.user));
    updateExchangeQuote();
}
function updateExchangeQuote() {
    const route = EXCHANGE_ROUTES[exchangeRoute];
    const busy = exchangeDenBusy.has('swap');
    const quote = exchangeQuote(exchangeRoute, $('xd-swap-amount')?.value, S.user);
    for (const side of ['source', 'target']) {
        const currency = route[side];
        const select = $(`xd-${side}`);
        if (select) { select.disabled = busy; }
        setText(`xd-${side}-name`, exchangeCurrencyNames[currency]);
        setText(`xd-${side}-balance`, Math.max(0, Number(S.user?.[currency]) || 0).toLocaleString('ru-RU'));
        const icon = $(`xd-${side}-icon`);
        if (icon) { icon.innerHTML = exchangeCurrencyIcon(currency); icon.dataset.currency = currency; }
    }
    for (const id of ['xd-max', 'xd-direction', 'xd-swap-amount']) {
        const control = $(id);
        if (control) control.disabled = busy;
    }
    setText('xd-swap-result', quote.received.toLocaleString('ru-RU'));
    setText('xd-swap-rate', route.rate);
    setText('xd-swap-hint', quote.error || (exchangeRoute === 'coins' ? 'Сумма кратна 800 · обратный обмен недоступен' : 'Обратный обмен недоступен'));
    $('xd-swap-hint')?.classList.toggle('is-error', Boolean(quote.error));
    $('xd-swap-amount')?.setAttribute('aria-invalid', String(Boolean(quote.error)));
    const button = $('xd-swap-submit');
    if (button) { button.disabled = busy || !quote.valid; button.textContent = busy ? 'Обмен выполняется…' : 'Продолжить'; }
}
async function loadExchangeHistory() {
    const uid = effectiveUid();
    const host = $('xd-history-list');
    if (!host) return;
    if (!uid) { host.innerHTML = '<p class="xd-empty">Открой Mini App через Telegram, чтобы увидеть свои обмены.</p>'; return; }
    const requestId = ++exchangeHistoryRequest;
    host.setAttribute('aria-busy', 'true');
    try {
        const data = await api(`/api/exchange_history?user_id=${uid}`, { dedupe: false, retries: 0 });
        if (requestId !== exchangeHistoryRequest) return;
        const rows = Array.isArray(data?.items) ? data.items : [];
        host.innerHTML = rows.length ? rows.map(item => {
            const from = exchangeCurrencyNames[item.source];
            const to = exchangeCurrencyNames[item.target];
            if (!from || !to) return '';
            const date = new Date(Number(item.at) * 1000);
            const stamp = Number.isFinite(date.getTime()) ? date.toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
            return `<article class="xd-history-row"><span class="xd-currency-icon" data-currency="${esc(item.target)}">${exchangeCurrencyIcon(item.target)}</span><div><strong>${from} → ${to}</strong><time>${esc(stamp)}</time></div><div class="xd-history-sums"><b>+${Math.max(0, Number(item.received) || 0).toLocaleString('ru-RU')}</b><small>−${Math.max(0, Number(item.spent) || 0).toLocaleString('ru-RU')}</small></div></article>`;
        }).join('') : '<p class="xd-empty">Пока нет обменов.<br>Первый появится здесь после подтверждения.</p>';
    } catch (error) {
        if (requestId === exchangeHistoryRequest) {
            const message = Number(error?.status) === 404 ? 'История пока недоступна на сервере.' : Number(error?.status) === 401 ? 'Открой Mini App заново через Telegram.' : 'История недоступна. Попробуй обновить позже.';
            host.innerHTML = `<p class="xd-empty">${message}</p>`;
        }
    } finally {
        if (requestId === exchangeHistoryRequest) host.setAttribute('aria-busy', 'false');
    }
}
async function submitExchangeSwap() {
    if (exchangeDenBusy.has('swap')) return;
    const routeKey = exchangeRoute;
    const route = EXCHANGE_ROUTES[routeKey];
    const quote = exchangeQuote(routeKey, $('xd-swap-amount')?.value, S.user);
    if (!quote.valid) { updateExchangeQuote(); return; }
    // Lock before confirmation as well as during POST to prevent double submissions.
    exchangeDenBusy.add('swap');
    updateExchangeQuote();
    try {
        const accepted = await customConfirm('Подтвердить обмен', `${exchangeAmount(route.source, quote.amount)} → ${exchangeAmount(route.target, quote.received)}`, 'Обменять', 'Назад');
        if (!accepted) return;
        setText('xd-status', 'Выполняем обмен…');
        const res = routeKey === 'restars'
            ? await api('/api/exchange_restars', { method: 'POST', retries: 0, body: { user_id: effectiveUid(), amount: quote.count } })
            : await api('/api/exchange_buy_spins', { method: 'POST', retries: 0, body: { user_id: effectiveUid(), count: quote.count } });
        if (S.user) {
            S.user.coins = Number(res.new_recoin) || 0;
            if (routeKey === 'restars') S.user.restars = Number(res.new_restars) || 0;
            else S.user.spins = Number(res.new_spins) || 0;
        }
        const received = Number(routeKey === 'restars' ? res.gained : res.count) || quote.received;
        syncExchangeEconomy();
        $('xd-swap-amount').value = '';
        setText('xd-status', `Обмен завершён: +${exchangeAmount(route.target, received)}.`);
        haptic('success');
        await loadExchangeHistory();
    } catch (error) {
        setText('xd-status', error?.message || 'Не удалось получить результат. Обнови баланс и историю перед повтором.');
        haptic('error');
    } finally {
        exchangeDenBusy.delete('swap');
        updateExchangeQuote();
    }
}

async function exchangeDenSellTheme(themeId) {
    const theme = exchangeSellableThemes().find(item => String(item.id) === String(themeId));
    if (!theme || exchangeDenBusy.has(`theme:${themeId}`)) return;
    const payout = exchangeThemePayout(theme.price);
    const accepted = await customConfirm('Продать тему', `${theme.title || 'Тема'}: получить ${payout.toLocaleString('ru-RU')} рекоинов?`, 'Продать', 'Назад', true);
    if (!accepted) return;
    exchangeDenBusy.add(`theme:${themeId}`);
    renderExchangeDen();
    setText('xd-status', 'Проверяем право владения…');
    try {
        const res = await api('/api/exchange_sell_theme', { method: 'POST', body: { user_id: effectiveUid(), theme_id: themeId } });
        if (S.user) S.user.coins = Number(res.new_recoin) || 0;
        if (S.themes.mine) {
            S.themes.mine.owned = Array.isArray(res.owned) ? res.owned : [];
            S.themes.mine.owned_details = (S.themes.mine.owned_details || []).filter(item => String(item.id) !== String(themeId));
            S.themes.mine.active_theme = res.active_theme || '';
        }
        if (!res.active_theme) applyActiveTheme('');
        renderExchangeDen();
        setText('xd-status', `Тема выкуплена: +${Number(res.payout || payout).toLocaleString('ru-RU')} рекоинов.`);
        showToast(`+${Number(res.payout || payout).toLocaleString('ru-RU')} рекоинов`);
        haptic('success');
    } catch (error) {
        const message = error?.message || 'Скупка временно недоступна';
        setText('xd-status', message);
        showToast(message);
        haptic('error');
    } finally {
        exchangeDenBusy.delete(`theme:${themeId}`);
        renderExchangeDen();
    }
}

window.openExchangeDen = openExchangeDen;
window.setExchangePanel = setExchangePanel;
window.openExchangeCurrencies = openExchangeCurrencies;
window.closeExchangeCurrencies = closeExchangeCurrencies;
window.chooseExchangeCurrency = chooseExchangeCurrency;
window.setExchangeRoute = setExchangeRoute;
window.setExchangeMax = setExchangeMax;
window.updateExchangeQuote = updateExchangeQuote;
window.submitExchangeSwap = submitExchangeSwap;
window.loadExchangeHistory = loadExchangeHistory;




window.exchangeDenSellTheme = exchangeDenSellTheme;
window.exchangeThemeSearch = exchangeThemeSearch;
window.exchangeThemePageMove = exchangeThemePageMove;
window.exchangeDenBuyOffer = exchangeDenBuyOffer;

function scrollShopTo(id) {
    setShopSection(id === 'shop-themes' ? 'themes' : id === 'shop-restars' ? 'restars' : id === 'tab-shop' ? 'featured' : 'resources', false);
    const target = $(id);
    if (!target) return;
    document.querySelectorAll('.shop-jumpbar [data-shop-target]').forEach(button => {
        const active = button.getAttribute('data-shop-target') === id;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-current', active ? 'true' : 'false');
    });
    target.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
}

function setShopSection(section, scroll = true) {
    const selected = ['featured', 'restars', 'themes', 'resources'].includes(section) ? section : 'featured';
    document.querySelectorAll('[data-store-section]').forEach(button => {
        if (button.getAttribute('data-store-section') === selected) button.setAttribute('aria-current', 'page');
        else button.removeAttribute('aria-current');
    });
    for (const key of ['featured', 'restars', 'themes', 'resources']) {
        const panel = $(`shop-view-${key}`);
        if (panel) panel.hidden = key !== selected;
    }
    if (selected === 'themes') renderThemeMarket();
    if (scroll) document.querySelector('.shop-section-tabs')?.scrollIntoView({ block: 'start', behavior: 'instant' });
}
window.setShopSection = setShopSection;

async function openReStarsShop() {
    await switchTab('shop');
    setShopSection('restars');
}
window.openReStarsShop = openReStarsShop;

let _shopSpinPurchaseBusy = false;

function isReStarsShortage(error) {
    const message = String(error?.message || error || '');
    return /ReStars/i.test(message) && /(недостаточно|не хватает|нужно)/i.test(message);
}

async function openReStarsPurchase(requiredRestars) {
    const current = Math.max(0, Number(S.user?.restars) || 0);
    const missing = Math.max(1, Number(requiredRestars) - current);
    const starsNeeded = missing * 10;
    const starsPack = [10, 50, 100, 500].find((amount) => amount >= starsNeeded) || 500;
    await buyStarsPack(starsPack);
}
async function buyShopSpins(restars) {
    const uid = effectiveUid();
    if (!uid) { showToast('Открой магазин через Telegram'); return; }
    if (_shopSpinPurchaseBusy) return;
    const cost = Math.max(1, Math.min(100, Number(restars) || 0));
    _shopSpinPurchaseBusy = true;
    const buttons = Array.from(document.querySelectorAll<HTMLButtonElement>('.spin-pack'));
    buttons.forEach(button => { button.disabled = true; });
    setText('shop-spin-status', 'Проводим обмен…');
    haptic('medium');
    try {
        const res = await api('/api/shop_buy_spins', {
            method: 'POST',
            body: { user_id: uid, restars: cost, init_data: tg?.initData || '' },
        });
        if (!res?.success) throw new Error(res?.error || 'Покупка не выполнена');
        if (S.user) {
            S.user.restars = Number(res.restars) || 0;
            S.user.spins = Number(res.spins) || 0;
            S.user.spin_exchange_rate = Number(res.rate) || 5;
        }
        renderShop();
        setText('h-spins', S.user?.spins || 0);
        setText('p-spins', S.user?.spins || 0);
        renderHomeOpening();
        setText('shop-spin-status', `Готово: +${res.gained_spins} круток`);
        showToast(`✓ +${res.gained_spins} круток`);
        haptic('success');
    } catch (error) {
        const message = String(error?.message || 'Покупка временно недоступна').replace(/^HTTP \d+\s*/i, '');
        if (isReStarsShortage(error)) {
            setText('shop-spin-status', 'Пополнение ReStars');
            await openReStarsPurchase(cost);
            return;
        }
        setText('shop-spin-status', message);
        showToast('⚠ ' + message.slice(0, 90));
        haptic('error');
    } finally {
        _shopSpinPurchaseBusy = false;
        buttons.forEach(button => { button.disabled = false; });
    }
}

let _cardCooldownTimer = null;
let _cardCooldownUntil = 0;

function updateCardCooldownSheet() {
    const left = Math.max(0, Math.ceil((_cardCooldownUntil - Date.now()) / 1000));
    setText('card-cooldown-value', _fmtSec(left));
    renderHomeOpening();
    if (left <= 0) closeCardCooldownSheet();
}

function showCardCooldownSheet(seconds) {
    _cardCooldownUntil = Date.now() + Math.max(1, Number(seconds) || 1) * 1000;
    if (_cardCooldownTimer) clearInterval(_cardCooldownTimer);
    updateCardCooldownSheet();
    _cardCooldownTimer = setInterval(updateCardCooldownSheet, 1000);
    const sheet = $('card-cooldown-sheet');
    if (sheet) {
        sheet.classList.add('open');
        sheet.setAttribute('aria-hidden', 'false');
    }
}

function closeCardCooldownSheet() {
    if (_cardCooldownTimer) clearInterval(_cardCooldownTimer);
    _cardCooldownTimer = null;
    const sheet = $('card-cooldown-sheet');
    if (sheet) {
        sheet.classList.remove('open');
        sheet.setAttribute('aria-hidden', 'true');
    }
}

async function openSpinShop() {
    closeCardCooldownSheet();
    closeModal();
    await switchTab('shop');
    requestAnimationFrame(() => scrollShopTo('shop-spins'));
}

(window as any).scrollShopTo = scrollShopTo;
(window as any).buyShopSpins = buyShopSpins;
(window as any).closeCardCooldownSheet = closeCardCooldownSheet;
(window as any).openSpinShop = openSpinShop;

function renderEliteStatus(u) {
    const pity = (u && u.elite_pity) || {};
    const sinceL = Number(pity.since_legend || 0);
    const sinceM = Number(pity.since_myth || 0);
    const maxL = 50, maxM = 100;
    setText('elite-pity-legend', `${sinceL}/${maxL}`);
    setText('elite-pity-myth', `${sinceM}/${maxM}`);
    const fillL = $('elite-pity-legend-fill');
    const fillM = $('elite-pity-myth-fill');
    if (fillL) fillL.style.width = `${Math.min(100, (sinceL / maxL) * 100)}%`;
    if (fillM) fillM.style.width = `${Math.min(100, (sinceM / maxM) * 100)}%`;
}

async function eliteSpin(count) {
    const uid = effectiveUid();
    if (!uid) { showToast('Войди через Telegram'); return; }
    if (_eliteSpinLock || _packBusy) return;
    _eliteSpinLock = true;
    const btn1 = $('elite-btn-1');
    const btn10 = $('elite-btn-10');
    const unlockBtns = () => {
        _eliteSpinLock = false;
        if (btn1) {
            btn1.disabled = false;
            btn1.classList.remove('is-pressed');
            try { btn1.blur(); } catch (_) {}
        }
        if (btn10) {
            btn10.disabled = false;
            btn10.classList.remove('is-pressed');
            try { btn10.blur(); } catch (_) {}
        }
    };
    if (btn1) btn1.disabled = true;
    if (btn10) btn10.disabled = true;
    try {
        const res = await api('/api/elite_spin', {
            method: 'POST',
            body: { user_id: uid, count: count === 10 ? 10 : 1 },
        });
        // Сразу отпускаем кнопки — иначе на iOS TG они «западают» в pressed/disabled
        unlockBtns();
        if (!res || res.ok === false || res.error) {
            const message = res?.error || 'Не хватает ReStars';
            if (isReStarsShortage(message)) {
                await openReStarsPurchase(res?.need || (count === 10 ? 9 : 1));
                return;
            }
            showToast(message);
            return;
        }
        if (S.user) {
            S.user.restars = res.new_restars ?? res.status?.restars ?? S.user.restars;
            S.user.elite_pity = {
                since_legend: res.status?.since_legend ?? 0,
                since_myth: res.status?.since_myth ?? 0,
            };
            setText('p-stars', S.user.restars || 0);
            setText('s-stars', S.user.restars || 0);
            renderEliteStatus(S.user);
        }
        const results = Array.isArray(res.results) ? res.results : [];
        _fillEliteResultSummary(results);
        if (results.length) {
            playPackReveal(results, {
                packLabel: results.length > 1 ? `Пак ×${results.length}` : 'Элитный призыв',
            }).catch((e) => console.warn('[PACK]', e));
        }
        haptic('medium');
        try { renderCards(); } catch (_) {}
    } catch (e) {
        console.warn('[ELITE]', e);
        const errMsg = String(e?.message || e || '').replace(/^HTTP \d+\s*/i, '').trim();
        if (isReStarsShortage(e)) {
            unlockBtns();
            await openReStarsPurchase(count === 10 ? 9 : 1);
            return;
        }
        showToast(errMsg.slice(0, 80) || 'Ошибка сети');
        unlockBtns();
    }
}
window.eliteSpin = eliteSpin;

let _eliteSpinLock = false;

const _RANK_ORDER = ['Обычный', 'Редкий', 'Эпический', 'Легендарный', 'Мифический'];
const _RANK_COLOR = {
    Обычный: '#94A3B8',
    Редкий: '#38BDF8',
    Эпический: '#C084FC',
    Легендарный: '#FBBF24',
    Мифический: '#FB7185',
};

function _eliteMediaSrc(r) {
    let src = r?.image_url || '';
    if (src && !src.startsWith('http') && !src.startsWith('blob:')) {
        src = `${API_BASE}${src.startsWith('/') ? src : '/' + src}`;
    }
    return src;
}

function _eliteIsVideo(r) {
    if (!r) return false;
    if (r.type === 'animation' || r.type === 'video') return true;
    if (r.rank === 'Мифический') return true;
    const path = String(r.image_url || '').split('?')[0];
    const ext = path.split('.').pop().toLowerCase();
    return ext === 'mp4' || ext === 'webm' || ext === 'mov';
}

/** Миниатюра дропа: фото — img; анимация/легенда/миф — muted video (не пустой цветной блок) */
function _eliteThumbHtml(r) {
    const src = _eliteMediaSrc(r);
    if (!src) {
        const color = _RANK_COLOR[r?.rank] || '#C9A24A';
        return `<div class="pr-thumb-ph" style="background:${color}33"></div>`;
    }
    if (_eliteIsVideo(r)) {
        return `<video src="${esc(src)}" muted autoplay loop playsinline webkit-playsinline preload="metadata"></video>`;
    }
    return `<img src="${esc(src)}" alt="" draggable="false" loading="lazy">`;
}

function _eliteFallbackFace(r) {
    const color = _RANK_COLOR[r?.rank] || '#C9A24A';
    return `<div class="pr-fallback" style="--pr-c:${color}">
        ${_eliteCardOverlayHtml(r)}
    </div>`;
}

function _eliteCardOverlayHtml(r) {
    const name = esc(r?.name || '???');
    const verse = esc(r?.verse || r?.universe || '');
    const damage = _fmtStat(r?.damage ?? r?.atk ?? 0);
    const health = _fmtStat(r?.health ?? r?.hp ?? 0);
    const points = _fmtStat(r?.rarity ?? r?.points ?? 0);
    return `<div class="pr-card-overlay">
        <strong class="pr-card-title">${name}</strong>
        ${verse ? `<span class="pr-card-verse">${verse}</span>` : ''}
        <span class="pr-card-stats">
            <span><i>Урон</i><b>${damage}</b></span>
            <span><i>HP</i><b>${health}</b></span>
            <span><i>Очки</i><b>${points}</b></span>
        </span>
    </div>`;
}

function _eliteCardHtml(r) {
    const src = _eliteMediaSrc(r);
    const safe = esc(src || '');
    if (!safe) return _eliteFallbackFace(r);
    // Миф/анимация: обязательно autoplay — иначе после флипа чёрный кадр / poster-рубашка
    if (_eliteIsVideo(r)) {
        return `<video class="pr-media" src="${safe}" muted autoplay loop playsinline webkit-playsinline preload="auto"></video>${_eliteCardOverlayHtml(r)}`;
    }
    return `<img class="pr-media" src="${safe}" alt="" draggable="false">${_eliteCardOverlayHtml(r)}`;
}

async function _prWaitMedia(cardEl, ms = 2800) {
    if (!cardEl) return;
    const media = cardEl.querySelector('video, img.pr-media, img');
    if (!media) return;
    if (media.tagName === 'IMG') {
        if (media.complete && media.naturalWidth > 0) return;
        await Promise.race([
            new Promise((resolve) => {
                media.onload = () => resolve();
                media.onerror = () => resolve();
            }),
            _prWait(ms),
        ]);
        if (!media.naturalWidth) {
            // картинка не загрузилась — оставим как есть, caller может заменить
            media.dataset.failed = '1';
        }
        return;
    }
    // video
    try { media.muted = true; media.playsInline = true; } catch (_) {}
    try { const p = media.play?.(); if (p && p.catch) p.catch(() => {}); } catch (_) {}
    if (media.readyState >= 2) return;
    await Promise.race([
        new Promise((resolve) => {
            const done = () => resolve();
            media.addEventListener('loadeddata', done, { once: true });
            media.addEventListener('error', done, { once: true });
        }),
        _prWait(ms),
    ]);
}

function _bestDrop(results) {
    let best = results[0];
    let bestScore = -1;
    for (const r of results) {
        const score = _RANK_ORDER.indexOf(r.rank);
        if (score > bestScore) {
            bestScore = score;
            best = r;
        }
    }
    return best;
}

function _fillEliteResultSummary(results) {
    const box = $('elite-result');
    if (!box || !results.length) return;
    const rankClass = (r) => {
        if (r === 'Мифический') return 'rank-myth';
        if (r === 'Легендарный') return 'rank-legend';
        return '';
    };
    box.style.display = 'flex';
    box.innerHTML = results.map((r) => {
        const tag = r.duplicate ? 'повтор' : 'новая';
        const pity = r.forced_myth ? ' · pity миф' : (r.forced_legend ? ' · pity легенда' : '');
        const reward = r.reward ? ` · ${esc(r.reward)}` : '';
        return `<div class="elite-drop ${rankClass(r.rank)}">
            <div class="elite-drop-art">${_eliteThumbHtml(r)}</div>
            <div class="elite-drop-body">
                <div class="elite-drop-name">${esc(r.name || '???')}</div>
                <div class="elite-drop-meta">${esc(r.rank || '')} · ${tag}${pity}${reward}</div>
            </div>
        </div>`;
    }).join('');
}

let _packBusy = false;
let _packSkip = false;
let _packCloseFn = null;

function _prWait(ms) {
    return new Promise((resolve) => {
        const step = 40;
        let left = ms;
        const tick = () => {
            if (_packSkip) return resolve();
            left -= step;
            if (left <= 0) return resolve();
            setTimeout(tick, step);
        };
        setTimeout(tick, Math.min(step, ms));
    });
}

function _prSetPhase(root, phase) {
    if (!root) return;
    root.dataset.phase = phase;
}

async function _prSwapCard(cardEl, html, result) {
    if (!cardEl) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    const applyFace = async () => {
        cardEl.innerHTML = html;
        await _prWaitMedia(cardEl);
        const img = cardEl.querySelector('img.pr-media');
        if (img && img.dataset.failed === '1' && result) {
            cardEl.innerHTML = _eliteFallbackFace(result);
        }
        const vid = cardEl.querySelector('video');
        if (vid) {
            try { vid.muted = true; const p = vid.play(); if (p && p.catch) p.catch(() => {}); } catch (_) {}
        }
    };
    if (reduce || _packSkip) {
        cardEl.className = 'pr-card-frame is-front';
        cardEl.style.transform = 'none';
        cardEl.style.transition = '';
        await applyFace();
        return;
    }
    // 3D flip: до 90° → ждём медиа → обратно
    cardEl.classList.remove('is-front', 'hi-legend', 'hi-myth', 'hi-epic');
    cardEl.style.willChange = 'transform';
    cardEl.style.transition = 'transform .48s cubic-bezier(.45, .05, .25, 1)';
    cardEl.style.transform = 'rotateY(90deg) scale(.97)';
    await _prWait(480);
    if (_packSkip) {
        await applyFace();
        cardEl.style.transform = 'none';
        cardEl.style.transition = '';
        cardEl.classList.add('is-front');
        return;
    }
    await applyFace();
    void cardEl.offsetWidth;
    cardEl.style.transition = 'transform .52s cubic-bezier(.2, .8, .2, 1)';
    cardEl.style.transform = 'rotateY(0deg) scale(1)';
    await _prWait(520);
    cardEl.style.transform = 'none';
    cardEl.style.transition = '';
    cardEl.style.willChange = 'auto';
    cardEl.classList.add('is-front');
}

function closePackReveal() {
    const root = $('pack-reveal');
    if (!root) return;
    _packSkip = true;
    root.classList.remove('open');
    root.setAttribute('aria-hidden', 'true');
    root.dataset.phase = 'seal';
    const card = $('pr-card');
    if (card) {
        card.className = 'pr-card-frame';
        card.innerHTML = '';
    }
    const trail = $('pr-trail');
    if (trail) trail.innerHTML = '';
    const best = $('pr-finale-best');
    if (best) best.innerHTML = '';
    const grid = $('pr-finale-grid');
    if (grid) {
        grid.innerHTML = '';
        grid.classList.remove('is-single');
    }
    _packBusy = false;
    const fn = _packCloseFn;
    _packCloseFn = null;
    if (fn) try { fn(); } catch (_) {}
}
window.closePackReveal = closePackReveal;

async function playPackReveal(results, { packLabel = 'Пак' } = {}) {
    const root = $('pack-reveal');
    if (!root || !results.length) return;
    if (_packBusy) closePackReveal();

    _packBusy = true;
    _packSkip = false;

    const skipBtn = $('pr-skip');
    const doneBtn = $('pr-done');
    const cardEl = $('pr-card');
    const trail = $('pr-trail');
    const grid = $('pr-finale-grid');
    const bestBox = $('pr-finale-best');

    const onSkip = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        if (root.dataset.phase === 'finale') {
            closePackReveal();
            return;
        }
        _packSkip = true;
    };
    const onDone = (e) => {
        e?.preventDefault?.();
        e?.stopPropagation?.();
        closePackReveal();
    };

    skipBtn?.removeEventListener('click', onSkip);
    doneBtn?.removeEventListener('click', onDone);
    skipBtn?.addEventListener('click', onSkip);
    doneBtn?.addEventListener('click', onDone);

    const closed = new Promise((resolve) => {
        _packCloseFn = resolve;
    });

    // Open overlay
    setText('pr-n', '0');
    setText('pr-total', String(results.length));
    const sealLbl = $('pr-seal-label');
    if (sealLbl) sealLbl.textContent = packLabel;
    if (trail) trail.innerHTML = '';
    if (cardEl) {
        cardEl.className = 'pr-card-frame';
        cardEl.innerHTML = `<img src="assets/card-back.png" alt="" draggable="false">`;
    }
    _prSetPhase(root, 'seal');
    root.classList.add('open');
    root.setAttribute('aria-hidden', 'false');

    // Preload faces (в т.ч. video metadata)
    results.forEach((r) => {
        const src = _eliteMediaSrc(r);
        if (!src) return;
        if (_eliteIsVideo(r)) {
            try {
                const v = document.createElement('video');
                v.preload = 'auto';
                v.muted = true;
                v.playsInline = true;
                v.src = src;
            } catch (_) {}
        } else {
            const img = new Image();
            img.src = src;
        }
    });

    try {
        await _prWait(results.length > 1 ? 700 : 420);
        if (_packSkip) {
            // jump to finale
        } else {
            _prSetPhase(root, 'card');

            for (let i = 0; i < results.length; i++) {
                if (_packSkip) break;
                const r = results[i];
                setText('pr-n', String(i + 1));

                // show back briefly
                if (cardEl) {
                    cardEl.className = 'pr-card-frame';
                    cardEl.style.transform = 'none';
                    cardEl.innerHTML = `<img src="assets/card-back.png" alt="" draggable="false">`;
                }
                await _prWait(280);
                if (_packSkip) break;

                await _prSwapCard(cardEl, _eliteCardHtml(r), r);
                if (_packSkip) break;

                cardEl?.classList.remove('hi-legend', 'hi-myth', 'hi-epic');
                if (r.rank === 'Мифический') cardEl?.classList.add('hi-myth');
                else if (r.rank === 'Легендарный') cardEl?.classList.add('hi-legend');
                else if (r.rank === 'Эпический') cardEl?.classList.add('hi-epic');

                if (trail && results.length > 1) {
                    const thumb = document.createElement('div');
                    thumb.className = 'pr-thumb';
                    thumb.innerHTML = _eliteThumbHtml(r);
                    trail.appendChild(thumb);
                    trail.scrollLeft = trail.scrollWidth;
                }

                haptic(r.rank === 'Мифический' || r.rank === 'Легендарный' ? 'medium' : 'light');

                const hold =
                    r.rank === 'Мифический' ? 1200
                        : r.rank === 'Легендарный' ? 980
                            : r.rank === 'Эпический' ? 780
                                : results.length === 1 ? 950 : 620;
                await _prWait(hold);
            }
        }
    } catch (e) {
        console.warn('[PACK reveal]', e);
    }

    // Finale — exclusive phase (hides seal/card/trail)
    const best = _bestDrop(results);
    setText('pr-finale-title', best?.name || 'Лучший дроп');
    if (bestBox) bestBox.innerHTML = _eliteCardHtml(best);
    if (grid) {
        if (results.length <= 1) {
            grid.classList.add('is-single');
            grid.innerHTML = '';
        } else {
            grid.classList.remove('is-single');
            grid.innerHTML = results.map((r) => {
                const isBest = r === best;
                return `<div class="pr-finale-cell${isBest ? ' is-best' : ''}">
                    ${_eliteThumbHtml(r)}
                    <span>${esc(r.rank || '')}</span>
                </div>`;
            }).join('');
        }
    }
    setText('pr-n', String(results.length));
    _prSetPhase(root, 'finale');
    _packSkip = false;
    haptic('medium');
    // Донатянем видео-миниатюры легенд/мифов
    try {
        grid?.querySelectorAll?.('video')?.forEach((v) => {
            v.muted = true;
            const p = v.play?.();
            if (p && p.catch) p.catch(() => {});
        });
        bestBox?.querySelectorAll?.('video')?.forEach((v) => {
            v.muted = true;
            const p = v.play?.();
            if (p && p.catch) p.catch(() => {});
        });
    } catch (_) {}

    await closed;
    skipBtn?.removeEventListener('click', onSkip);
    doneBtn?.removeEventListener('click', onDone);
}
window.playPackReveal = playPackReveal;

function themeMediaUrl(url) {
    if (!url) return '';
    let u = String(url);
    // Старые ссылки /theme_uploads/x → API-раздача (стабильнее через nginx)
    if (u.includes('/theme_uploads/')) {
        const fname = u.split('/theme_uploads/').pop().split('?')[0];
        if (fname) u = `/api/theme_media/${fname}`;
    }
    if (u.startsWith('http')) return u;
    if (u.startsWith('/')) return `${API_BASE}${u}`;
    // Old theme records can contain a Telegram file_id instead of a served
    // path. Route it through the existing protected media proxy.
    return arenaProxySrc(u) || `${API_BASE}/${u}`;
}

/** Safe theme id for inline onclick="fn('id')" handlers. */
function themeIdAttr(id) {
    return String(id || '').replace(/\\/g, '').replace(/'/g, '');
}

function _themeIsGif(theme, src) {
    const t = String(theme?.media_type || '').toLowerCase();
    if (t === 'gif') return true;
    return /\.gif(\?|#|$)/i.test(String(src || theme?.media_url || ''));
}

function _themeIsVideo(theme, src) {
    const t = String(theme?.media_type || '').toLowerCase();
    if (t === 'gif' || _themeIsGif(theme, src)) return false;
    if (t === 'video') return true;
    return /\.(mp4|webm|mov)(\?|#|$)/i.test(String(src || theme?.media_url || ''));
}

function themePreviewBg(theme, { eager = false } = {}) {
    const src = themeMediaUrl(theme.media_url);
    if (!src) return '<div class="theme-card-bg is-broken"></div>';
    const loadMode = eager ? 'eager' : 'lazy';
    const priority = eager ? 'high' : 'low';
    // Загружаем сразу только главную тему, остальные — по мере прокрутки.
    if (_themeIsGif(theme, src) || !_themeIsVideo(theme, src)) {
        return `<img class="theme-card-bg is-loading" src="${cssUrl(src)}" data-src="${cssUrl(src)}" alt="" loading="${loadMode}" fetchpriority="${priority}" decoding="async" onload="this.classList.remove('is-loading')" onerror="rvMediaError(this);this.classList.remove('is-loading')">`;
    }
    return `<video class="theme-card-bg theme-card-video is-loading" src="${cssUrl(src)}" muted loop playsinline webkit-playsinline preload="${eager ? 'metadata' : 'none'}"></video>`;
}

let _themeVideoIO = null;
const _themeVideoPlaying = new Set();

function _themeVidShowFrame(v) {
    if (!(v instanceof HTMLVideoElement)) return;
    v.classList.remove('is-loading');
    try {
        if (v.readyState >= 2 && v.currentTime < 0.05) v.currentTime = 0.05;
    } catch (_) {}
}

function _themeVidTryPlay(v) {
    if (!(v instanceof HTMLVideoElement) || !v.isConnected) return;
    if (_themeVideoPlaying.size >= 3 && !_themeVideoPlaying.has(v)) return;
    _themeVideoPlaying.add(v);
    v.muted = true;
    const p = v.play?.();
    if (p && p.catch) p.catch(() => {});
}

function ensureThemeVideoIO() {
    if (_themeVideoIO) return _themeVideoIO;
    _themeVideoIO = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const v = entry.target;
            if (!(v instanceof HTMLVideoElement)) return;
            if (!v.isConnected) {
                _themeVideoPlaying.delete(v);
                try { _themeVideoIO.unobserve(v); } catch (_) {}
                return;
            }
            if (entry.isIntersecting && entry.intersectionRatio > 0.15) {
                _themeVidTryPlay(v);
            } else {
                _themeVideoPlaying.delete(v);
                try { v.pause(); } catch (_) {}
            }
        });
    }, { rootMargin: '80px 0px', threshold: [0, 0.15, 0.4] });
    return _themeVideoIO;
}

function bindThemeCardVideos(root) {
    const scope = root || document;
    const vids = scope.querySelectorAll?.('video.theme-card-video') || [];
    if (!vids.length) return;
    const io = ensureThemeVideoIO();
    vids.forEach((v) => {
        if (!v.dataset.themeVidBound) {
            v.dataset.themeVidBound = '1';
            const onReady = () => _themeVidShowFrame(v);
            v.addEventListener('loadeddata', onReady);
            v.addEventListener('loadedmetadata', onReady);
            v.addEventListener('error', () => {
                v.classList.add('is-broken');
                v.classList.remove('is-loading');
            });
            // уже в кэше
            if (v.readyState >= 2) onReady();
        }
        io.observe(v);
        // сразу пробуем play, если карточка уже в viewport (без ожидания IO tick)
        try {
            const rect = v.getBoundingClientRect();
            const vh = window.innerHeight || 700;
            if (rect.bottom > 0 && rect.top < vh) _themeVidTryPlay(v);
        } catch (_) {}
    });
}

function disposeThemeCardVideos(root) {
    const videos = root?.querySelectorAll?.('video.theme-card-video') || [];
    videos.forEach(video => {
        _themeVideoPlaying.delete(video);
        try { _themeVideoIO?.unobserve(video); } catch (_) {}
        try { video.pause(); } catch (_) {}
        try {
            video.removeAttribute('src');
            video.load();
        } catch (_) {}
    });
}

function themeStatusLabel(status) {
    return ({ pending: 'На проверке', approved: 'Одобрено', rejected: 'Отклонено' }[status] || status);
}

function findThemeById(id) {
    if (!id) return null;
    if (id === PREVIEW_HUB_THEME.id) return PREVIEW_HUB_THEME;
    const mine = S.themes.mine;
    const owned = mine?.owned_details?.find(t => t.id === id);
    if (owned) return owned;
    return S.themes.market.find(t => t.id === id) || mine?.submissions?.find(t => t.id === id) || null;
}

/** Для браузерного превью: кладём тему в стейт и активируем на hub-id. */
function seedPreviewHubTheme() {
    const t = PREVIEW_HUB_THEME;
    if (!S.themes.market.some(x => x.id === t.id)) {
        S.themes.market = [t, ...S.themes.market];
    }
    const mine = S.themes.mine || { submissions: [], owned: [], owned_details: [], active_theme: '' };
    if (!mine.owned.includes(t.id)) mine.owned = [...mine.owned, t.id];
    if (!mine.owned_details.some(x => x.id === t.id)) {
        mine.owned_details = [t, ...mine.owned_details];
    }
    mine.active_theme = t.id;
    S.themes.mine = mine;
    applyActiveTheme(t.id);
}

function switchShopSubTab(tab) {
    haptic('light');
    S.themes.shopSubTab = tab;
    setText('shop-creators-link', tab === 'creator' ? 'К каталогу →' : 'Для авторов ↗');
    document.querySelectorAll('[data-shop-tab]').forEach(b => {
        b.classList.toggle('active', b.dataset.shopTab === tab);
    });
    const market = $('shop-panel-market');
    const creator = $('shop-panel-creator');
    changePanels(document.querySelector('.shop-theme-panel.active'), tab === 'market' ? market : creator, () => {
        if (market) market.classList.toggle('active', tab === 'market');
        if (creator) creator.classList.toggle('active', tab === 'creator');
    }, tab === 'market' ? -1 : 1);
}

function openShopCreatHub() {
    S.themes.shopSubTab = 'creator';
    switchTab('shop').then(() => { setShopSection('themes'); switchShopSubTab('creator'); });
}

async function loadThemeShop() {
    const uid = effectiveUid();
    if (S.themes.loading) return;
    S.themes.loading = true;
    S.themes.error = '';
    S.themes.notice = '';
    renderThemeMarket();
    try {
        const marketP = S.themes.market.length
            ? Promise.resolve(S.themes.market)
            : api('/api/theme_market');
        const mineP = S.themes.mine
            ? Promise.resolve(S.themes.mine)
            : (uid
                ? api(`/api/theme_mine?user_id=${uid}`)
                : Promise.resolve({ submissions: [], owned: [], owned_details: [], active_theme: '' }));
        const [marketRes, mineRes] = await Promise.allSettled([marketP, mineP]);

        if (marketRes.status === 'fulfilled' && Array.isArray(marketRes.value)) {
            S.themes.market = marketRes.value;
        } else if (marketRes.status === 'rejected') {
            console.error('[THEME SHOP] market', marketRes.reason);
            S.themes.error = String(marketRes.reason?.message || 'Проверьте соединение и попробуйте снова.').slice(0, 100);
            return;
        }

        if (mineRes.status === 'fulfilled' && mineRes.value) {
            S.themes.mine = mineRes.value;
        } else if (mineRes.status === 'rejected') {
            console.error('[THEME SHOP] mine', mineRes.reason);
            S.themes.notice = 'Каталог доступен, но список купленных тем временно не загрузился.';
        }

        renderCreatorHub();
        if (S.tab === 'profile') {
            applyActiveTheme(S.themes.mine?.active_theme || '');
        }
    } catch (e) {
        console.error('[THEME SHOP]', e);
        S.themes.error = String(e?.message || 'Не удалось загрузить темы.').slice(0, 100);
    } finally {
        S.themes.loading = false;
        renderThemeMarket();
    }
}

function syncThemeFilterChips() {
    const sort = S.themes.sort || 'popular';
    const type = S.themes.typeFilter || 'all';
    const owned = S.themes.ownedFilter || 'all';
    document.querySelectorAll('[data-theme-sort]').forEach(b => {
        b.classList.toggle('active', b.getAttribute('data-theme-sort') === sort);
    });
    document.querySelectorAll('[data-theme-type]').forEach(b => {
        b.classList.toggle('active', b.getAttribute('data-theme-type') === type);
    });
    document.querySelectorAll('[data-theme-owned]').forEach(b => {
        const key = b.getAttribute('data-theme-owned');
        b.classList.toggle('active', owned === key);
    });
}

function setThemeSort(sort) {
    haptic('light');
    S.themes.sort = sort || 'popular';
    renderThemeMarket();
}

function setThemeTypeFilter(type) {
    haptic('light');
    S.themes.typeFilter = type || 'all';
    renderThemeMarket();
}

function setThemeOwnedFilter(mode) {
    haptic('light');
    // toggle: повторный тап снимает фильтр
    const next = mode || 'all';
    S.themes.ownedFilter = S.themes.ownedFilter === next ? 'all' : next;
    renderThemeMarket();
}

function _themeCreatedTs(t) {
    const raw = t?.created_at;
    if (!raw) return 0;
    if (typeof raw === 'number') return raw;
    const n = Date.parse(String(raw));
    return Number.isFinite(n) ? n : 0;
}

function getFilteredThemeMarket() {
    const mine = S.themes.mine || {};
    return filterThemes(S.themes.market, {
        query: S.themes.query,
        sort: S.themes.sort,
        type: S.themes.typeFilter,
        ownership: S.themes.ownedFilter,
    }, { ownedIds: mine.owned || [], userId: effectiveUid() });
}

function renderThemeMarket() {
    const root = $('theme-hub');
    if (!root) return;
    disposeThemeCardVideos(root);
    const mine = S.themes.mine || {};
    const filtered = getFilteredThemeMarket();
    renderThemeHub(root, {
        themes: filtered,
        totalCount: S.themes.market.length,
        filters: { query: S.themes.query, sort: S.themes.sort, type: S.themes.typeFilter, ownership: S.themes.ownedFilter },
        ownedIds: mine.owned || [],
        activeId: mine.active_theme || '',
        userId: effectiveUid(),
        loading: S.themes.loading && !S.themes.market.length,
        error: S.themes.error,
        notice: S.themes.notice,
        renderMedia: themePreviewBg,
        onFilters: next => {
            const filters = normalizeThemeFilters(next);
            const tactileChange = filters.query === S.themes.query;
            S.themes.query = filters.query;
            S.themes.sort = filters.sort;
            S.themes.typeFilter = filters.type;
            S.themes.ownedFilter = filters.ownership;
            if (tactileChange) haptic('light');
            renderThemeMarket();
        },
        onClearFilters: () => {
            S.themes.query = DEFAULT_THEME_FILTERS.query;
            S.themes.sort = DEFAULT_THEME_FILTERS.sort;
            S.themes.typeFilter = DEFAULT_THEME_FILTERS.type;
            S.themes.ownedFilter = DEFAULT_THEME_FILTERS.ownership;
            renderThemeMarket();
        },
        onRetry: () => {
            S.themes.market = [];
            S.themes.error = '';
            loadThemeShop();
        },
        onBuy: openThemePreview,
        onPreview: openThemePreview,
        onApply: themeApply,
        onResetTheme: themeReset,
    });
    try { bindThemeCardVideos(root); } catch (_) {}
}

function renderCreatorHub() {
    const list = $('theme-submissions-list');
    const mine = S.themes.mine || {};
    const subs = mine.submissions || [];

    let totalSales = 0;
    let totalEarnings = 0;
    subs.forEach(t => {
        totalSales += t.sales || 0;
        totalEarnings += t.earnings || 0;
    });
    setText('theme-stat-sales', totalSales.toLocaleString('ru-RU'));
    setText('theme-stat-earnings', totalEarnings.toLocaleString('ru-RU'));

    if (!list) return;
    if (!subs.length) {
        list.innerHTML = '<div class="empty-state">Загрузи первую тему — после модерации она появится в магазине</div>';
        return;
    }

    list.innerHTML = subs.map(t => {
        const src = themeMediaUrl(t.media_url);
        let thumb = '';
        let thumbStyle = '';
        if (src) {
            if (_themeIsGif(t, src) || !_themeIsVideo(t, src)) {
                thumbStyle = `style="background-image:url('${cssUrl(src)}')"`;
            } else {
                thumb = `<video class="theme-card-video" src="${cssUrl(src)}" muted loop playsinline preload="metadata"></video>`;
            }
        }
        const reject = t.status === 'rejected' && t.reject_reason
            ? `<span title="${esc(t.reject_reason)}"> — ${esc(t.reject_reason)}</span>` : '';
        return `<div class="theme-sub-row">
            <div class="theme-sub-thumb" ${thumbStyle}>${thumb}</div>
            <div class="theme-sub-info">
                <div class="theme-sub-title">${esc(t.title)}</div>
                <div class="theme-sub-meta">
                    <span class="theme-status ${esc(t.status)}">${themeStatusLabel(t.status)}</span>
                    <span>${(t.price || 0).toLocaleString('ru-RU')} ◉</span>
                    ${t.status === 'approved' ? `<span>${t.sales || 0} прод. · +${(t.earnings || 0).toLocaleString('ru-RU')} ◉</span>` : ''}
                    ${reject}
                </div>
            </div>
        </div>`;
    }).join('');
    try { bindThemeCardVideos(list); } catch (_) {}
}

function hubThemeKey(theme) {
    if (!theme?.media_url) return '';
    const src = themeMediaUrl(theme.media_url);
    if (!src) return '';
    const kind = (theme.media_type === 'video' && _themeIsVideo(theme, src)) ? 'video' : 'image';
    return `${kind}:${src}`;
}

function mediaKeepAlive(video) {
    return video?.id === 'arena-hero-video'
        || video?.classList?.contains('hub-id-theme-media')
        || video?.dataset?.keepSrc === '1';
}

let hubThemeAppliedKey = '';
let hubThemeLoadingKey = '';

function applyHubIdTheme(theme) {
    const hubId = document.querySelector('.hub-id');
    const hubIdBg = $('hub-id-bg');
    if (!hubId || !hubIdBg) return;

    // Never pin solid black via inline styles — CSS theme tokens own the surface.
    hubId.style.removeProperty('background');
    hubId.style.removeProperty('background-color');
    hubId.style.removeProperty('color');

    const clearBg = () => {
        hubIdBg.querySelectorAll('video').forEach(video => {
            try { video.pause(); } catch (_) {}
            try { video.removeAttribute('src'); video.load(); } catch (_) {}
        });
        hubId.classList.remove('has-theme');
        hubIdBg.innerHTML = '';
        hubIdBg.style.backgroundImage = '';
        hubIdBg.style.backgroundPosition = '';
        hubIdBg.style.backgroundSize = '';
        hubThemeAppliedKey = '';
        hubThemeLoadingKey = '';
    };

    const key = hubThemeKey(theme);
    if (!key) {
        clearBg();
        return;
    }
    if (hubThemeAppliedKey === key) {
        resumeHubThemeMedia();
        return;
    }
    if (hubThemeLoadingKey === key) return;

    const src = themeMediaUrl(theme.media_url);
    hubThemeLoadingKey = key;

    const showLoaded = () => {
        if (hubThemeLoadingKey !== key) return;
        hubId.classList.add('has-theme');
        if (![...hubIdBg.children].some(el => el.tagName === 'SPAN')) {
            hubIdBg.insertAdjacentHTML('beforeend', '<span style="display:none" aria-hidden="true"></span>');
        }
        hubIdBg.style.setProperty('background-image', `url('${cssUrl(src)}')`);
        hubIdBg.style.setProperty('background-size', 'cover');
        hubIdBg.style.setProperty('background-position', 'center center');
        hubIdBg.style.setProperty('background-repeat', 'no-repeat');
        hubThemeAppliedKey = key;
        hubThemeLoadingKey = '';
    };

    if (theme.media_type === 'video' && _themeIsVideo(theme, src)) {
        const video = document.createElement('video');
        video.className = 'hub-id-theme-media';
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('playsinline', '');
        video.preload = 'metadata';
        video.onloadeddata = () => {
            if (hubThemeLoadingKey !== key) {
                video.remove();
                return;
            }
            hubIdBg.querySelectorAll('video').forEach(old => {
                if (old !== video) {
                    try { old.pause(); } catch (_) {}
                    old.remove();
                }
            });
            hubId.classList.add('has-theme');
            hubIdBg.style.backgroundImage = '';
            hubThemeAppliedKey = key;
            hubThemeLoadingKey = '';
            if (S.tab !== 'profile' || document.hidden) {
                try { video.pause(); } catch (_) {}
            }
        };
        video.onerror = () => {
            console.warn('[THEME] hub-id video failed:', src);
            video.remove();
            if (hubThemeLoadingKey === key) hubThemeLoadingKey = '';
            if (!hubThemeAppliedKey) clearBg();
        };
        hubIdBg.appendChild(video);
        video.src = src;
        return;
    }

    const probe = new Image();
    probe.onload = () => {
        if (hubThemeLoadingKey !== key) return;
        hubIdBg.querySelectorAll('video').forEach(old => {
            try { old.pause(); } catch (_) {}
            old.remove();
        });
        showLoaded();
    };
    probe.onerror = () => {
        console.warn('[THEME] hub-id media failed:', src);
        if (hubThemeLoadingKey === key) hubThemeLoadingKey = '';
        if (!hubThemeAppliedKey) clearBg();
    };
    probe.src = src;
}

function suspendHubThemeMedia() {
    const root = $('hub-id-bg');
    if (!root) return;
    root.querySelectorAll('video').forEach(video => {
        try { video.pause(); } catch (_) {}
    });
}

function resumeHubThemeMedia() {
    if ($('card-modal')?.matches('.open.new-card-mode')) return;
    if (S.tab !== 'profile' || document.hidden) return;
    const root = $('hub-id-bg');
    if (!root) return;
    root.querySelectorAll('video.hub-id-theme-media').forEach(video => {
        try {
            video.muted = true;
            video.playsInline = true;
            const play = video.play();
            if (play && typeof play.catch === 'function') play.catch(() => {});
        } catch (_) {}
    });
}

function applyActiveTheme(themeId) {
    const collage = $('hub-collage');
    const appRoot = $('app-container');
    if (collage && !collage.querySelector('img.hub-collage-img')) {
        collage.innerHTML = `<img class="hub-collage-img" src="${DEFAULT_HUB_COLLAGE}" alt="" loading="lazy" decoding="async">`;
    }
    const clearBodyTheme = () => {
        document.body.style.backgroundImage = '';
        document.body.classList.remove('has-custom-theme');
        if (appRoot) {
            appRoot.style.removeProperty('--theme-bg');
            appRoot.removeAttribute('data-custom-theme');
        }
    };
    if (!themeId) {
        clearBodyTheme();
        applyHubIdTheme(null);
        return;
    }
    const theme = findThemeById(themeId);
    if (!theme?.media_url) {
        clearBodyTheme();
        applyHubIdTheme(null);
        return;
    }
    // Тема только на hub-id — body не заливаем тем же фоном (ломает визуальную центровку)
    clearBodyTheme();
    if (appRoot) appRoot.dataset.customTheme = themeId;
    applyHubIdTheme(theme);
}

let themePreviewController;
function openThemePreview(themeId) {
    if (!themePreviewController) themePreviewController = createThemePreview({
        getTheme: findThemeById,
        getUser: () => S.user || {},
        getMine: () => S.themes.mine || {},
        getUserId: effectiveUid,
        renderMedia: themePreviewBg,
        onBuy: themeBuy,
        onApply: themeApply,
        onTopUp: () => { switchTab('shop').then(() => scrollShopTo('shop-restars')); },
        onClose: () => { if (S.tab === 'shop') renderThemeMarket(); },
    });
    themePreviewController.open(themeId);
}

window.toggleShopCreator = () => switchShopSubTab(S.themes.shopSubTab === 'creator' ? 'market' : 'creator');

function themeBuy(themeId) {
    return themeActionGuard.run('theme-mutation', () => runThemeBuy(themeId));
}
async function runThemeBuy(themeId) {
    const uid = effectiveUid();
    if (!uid) { showToast('◈ Открой через Telegram'); return; }
    haptic('medium');
    try {
        const res = await api('/api/theme_buy', { method: 'POST', body: { user_id: uid, theme_id: themeId } });
        if (!res?.success) throw new Error(res?.error || 'Покупка не подтверждена');
        if (S.user && res.new_balance != null) {
            S.user.coins = res.new_balance;
            setText('s-coins', res.new_balance.toLocaleString('ru-RU'));
            setText('h-coins', res.new_balance.toLocaleString('ru-RU'));
        }
        // Reflect the purchase immediately: loadThemeShop() reuses the cached
        // S.themes.mine (to avoid refetching on every tab switch), so without
        // this the card would keep showing "buy" forever even though the
        // server already charged the user and recorded ownership.
        if (!S.themes.mine) {
            S.themes.mine = { submissions: [], owned: [], owned_details: [], active_theme: '' };
        }
        if (!S.themes.mine.owned.includes(themeId)) {
            S.themes.mine.owned = [...S.themes.mine.owned, themeId];
            const bought = S.themes.market.find(t => t.id === themeId);
            if (bought && !S.themes.mine.owned_details.some(t => t.id === themeId)) {
                S.themes.mine.owned_details = [bought, ...S.themes.mine.owned_details];
            }
        }
        renderThemeMarket();
        showToast('✓ Тема куплена');
        // Re-sync with the server in the background for full accuracy.
        S.themes.mine = null;
        await loadThemeShop();
        return true;
    } catch (e) {
        showToast(String(e.message || e).slice(0, 80));
        return false;
    }
}

function setThemeMutationBusy(themeId, busy) {
    document.querySelectorAll('[data-theme-action]').forEach(button => {
        if (busy) {
            button.dataset.themePreviousText = button.textContent || '';
            button.disabled = true;
            if (button.dataset.themeId === themeId) button.textContent = 'Применяем…';
        } else {
            if (button.dataset.themePreviousText) button.textContent = button.dataset.themePreviousText;
            button.disabled = false;
            delete button.dataset.themePreviousText;
        }
    });
}

function normalizeThemeActionId(value) {
    const id = String(value || '').trim();
    return id && id.length <= 128 && /^[a-zA-Z0-9_-]+$/.test(id) ? id : '';
}

function themeApply(rawThemeId) {
    const themeId = normalizeThemeActionId(rawThemeId);
    const uid = effectiveUid();
    if (!uid) { showToast('◈ Открой через Telegram'); return; }
    if (!themeId || !findThemeById(themeId)) { showToast('Тема не найдена'); return; }
    return themeActionGuard.run('theme-mutation', async () => {
        haptic('light');
        setThemeMutationBusy(themeId, true);
        try {
            const res = await api('/api/theme_apply', {
                method: 'POST',
                timeoutMs: 15000,
                body: { user_id: uid, theme_id: themeId },
            });
            if (!res?.success || String(res.active_theme || '') !== themeId) {
                throw new Error('Сервер не подтвердил тему');
            }
            if (!S.themes.mine) S.themes.mine = { submissions: [], owned: [], owned_details: [], active_theme: '' };
            S.themes.mine.active_theme = themeId;
            renderThemeMarket();
            if (S.tab === 'profile') applyActiveTheme(themeId);
            showToast('✓ Тема применена');
        } catch (e) {
            console.error('[THEME APPLY]', e);
            showToast(String(e?.message || e || 'Не удалось применить тему').slice(0, 80));
        } finally {
            setThemeMutationBusy(themeId, false);
        }
    });
}

function themeReset() {
    const uid = effectiveUid();
    if (!uid) { showToast('◈ Открой через Telegram'); return; }
    return themeActionGuard.run('theme-mutation', async () => {
        haptic('light');
        setThemeMutationBusy('', true);
        try {
            const res = await api('/api/theme_apply', { method: 'POST', timeoutMs: 15000, body: { user_id: uid, theme_id: '' } });
            if (!res?.success) throw new Error('Сброс темы не подтверждён');
            if (S.themes.mine) S.themes.mine.active_theme = '';
            if (S.tab === 'profile') applyActiveTheme('');
            renderThemeMarket();
            showToast('↺ Стандартное оформление');
        } catch (e) {
            console.error('[THEME RESET]', e);
            showToast(String(e?.message || e || 'Не удалось сбросить тему').slice(0, 80));
        } finally {
            setThemeMutationBusy('', false);
        }
    });
}

function themeFileLabelReset() {
    const nameEl = $('theme-upload-filename');
    if (!nameEl) return;
    nameEl.textContent = 'Файл не выбран';
    nameEl.classList.remove('has-file');
}

function themeFileLabelSync() {
    const fileEl = $('theme-upload-file');
    const nameEl = $('theme-upload-filename');
    if (!fileEl || !nameEl) return;
    const file = fileEl.files?.[0];
    if (file) {
        nameEl.textContent = file.name;
        nameEl.classList.add('has-file');
    } else {
        themeFileLabelReset();
    }
}

document.getElementById('theme-upload-file')?.addEventListener('change', themeFileLabelSync);

async function themeUpload(e) {
    e.preventDefault();
    const uid = effectiveUid();
    if (!uid) { showToast('◈ Открой через Telegram'); return; }

    const titleEl = $('theme-upload-title');
    const priceEl = $('theme-upload-price');
    const fileEl = $('theme-upload-file');
    const btn = $('theme-upload-btn');
    const file = fileEl?.files?.[0];
    if (!file) { showToast('Выбери файл'); return; }

    const fd = new FormData();
    fd.append('user_id', uid);
    fd.append('title', (titleEl?.value || '').trim());
    fd.append('price', priceEl?.value || '0');
    fd.append('media', file);
    fd.append('initData', tg?.initData || '');

    if (btn) { btn.disabled = true; btn.textContent = 'Загрузка...'; }
    haptic('medium');

    try {
        const initData = tg?.initData || '';
        // Query user_id is needed only when the local backend explicitly has
        // WEBAPP_AUTH_DISABLED=1. Production still derives it from initData.
        const res = await fetch(`${API_BASE}/api/theme_upload?user_id=${encodeURIComponent(uid)}`, {
            method: 'POST',
            headers: {
                'X-Telegram-Init-Data': initData,
                'ngrok-skip-browser-warning': 'true',
            },
            body: fd,
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        showToast('✓ Тема отправлена на модерацию');
        if (titleEl) titleEl.value = '';
        if (priceEl) priceEl.value = '';
        if (fileEl) fileEl.value = '';
        themeFileLabelReset();
        await loadThemeShop();
        switchShopSubTab('creator');
    } catch (err) {
        const message = String(err?.message || err || '');
        showToast(
            /unauthorized|initdata/i.test(message)
                ? 'Открой приложение через Telegram'
                : (message.slice(0, 80) || 'Ошибка загрузки темы')
        );
    } finally {
        if (btn) { btn.disabled = false; btn.textContent = '⬆ Отправить на модерацию'; }
    }
}

window.switchShopSubTab = switchShopSubTab;
window.openShopCreatHub = openShopCreatHub;
window.setThemeSort = setThemeSort;
window.setThemeTypeFilter = setThemeTypeFilter;
window.setThemeOwnedFilter = setThemeOwnedFilter;
window.themeBuy = themeBuy;
window.themeApply = themeApply;
window.themeReset = themeReset;
window.themeUpload = themeUpload;

// ── Clan ──────────────────────────────────────────────
// Tracks whether tab listeners have been registered to prevent duplicates
let _clanListenersAttached = false;
let _clanTopLoading = false;

async function loadClan() {
    if (!$('clan-noclan') || !$('clan-main')) return;
    
    const uid = effectiveUid();
    const connection = $('clan-connection');
    if (!uid) {
        $('clan-noclan').style.display = 'none'; $('clan-main').style.display = 'none';
        if (connection) {
            connection.hidden = false;
            connection.innerHTML = '<h2>Кланы доступны в Telegram</h2><p>Открой бота, чтобы увидеть рейтинг, найти клан и участвовать в войнах. Сейчас ты просматриваешь демоверсию.</p><button class="journey-action" type="button" onclick="openBotForPlay()">Открыть в Telegram</button>';
        }
        return;
    }
    if (connection) { connection.hidden = false; connection.textContent = 'Загружаем клан…'; }

    try {
        const data = await api(`/api/clan_info?user_id=${uid}`);
        S.clan = data;
        if (connection) connection.hidden = true;
        renderClan(data);
    } catch (e) {
        console.error('Clan Load Failure:', e);
        if (connection) {
            connection.hidden = false;
            connection.innerHTML = '<h2>Не удалось загрузить клан</h2><p>Проверь подключение и повтори попытку.</p><button class="context-help" type="button" onclick="retryClanLoad()">Повторить</button>';
        }
        
        const noclan = $('clan-noclan');
        const main = $('clan-main');
        if (noclan) noclan.style.display = 'block';
        if (main) main.style.display = 'none';
        loadClanBrowse();
    }
    if (!S.clan?.in_clan || S.clanTab !== 'top') await loadClanLeaderboard();
}

function renderClan(data) {
    const noclan = $('clan-noclan');
    const main = $('clan-main');
    if (!noclan || !main) return;

    if (!data || !data.in_clan) {
        noclan.style.display = 'block';
        main.style.display = 'none';
        loadClanBrowse();
        return;
    }
    noclan.style.display = 'none';
    main.style.display = 'block';

    // Populate Hero Card
    const heroName = $('clan-hero-name');
    const heroSub  = $('clan-hero-sub');
    const heroLvl  = $('clan-hero-level');
    if (heroName) heroName.textContent = data.name || '—';
    if (heroSub)  heroSub.textContent  = `Вселенная: ${data.verse || '—'}`;
    if (heroLvl)  heroLvl.textContent  = `Ур. ${data.level || 1}`;

    setText('clan-kpi-members', `${data.member_count || 0}/${data.max_members || 10}`);
    setText('clan-kpi-pts', (data.total_pts || 0).toLocaleString('ru-RU'));
    setText('clan-kpi-rank', `#${data.rank || '—'}`);
    setText('clan-kpi-wins', `${data.war_wins || 0}W`);

    const xpFill = $('clan-xp-fill');
    const xpText = $('clan-xp-text');
    if (xpFill && xpText) {
        const xpCur = data.xp || 0;
        const xpMax = data.xp_next || 1000;
        xpFill.style.width = Math.min(100, (xpCur / xpMax) * 100) + '%';
        xpText.textContent = `${xpCur} / ${xpMax}`;
    }

    // Attach tab listeners once
    if (!_clanListenersAttached) {
        document.querySelectorAll('.clan-tab-btn').forEach(btn => {
            btn.addEventListener('click', () => switchClanTab(btn.dataset.ctab));
        });
        _clanListenersAttached = true;
    }

    renderClanOverview();
    const warKey = `${data.clan_id || ''}:${data.active_war?.war_id || data.active_war?.status || 'idle'}`;
    const landing = S.clanRenderedWarKey === warKey ? (S.clanTab || 'overview') : clanLandingTab(S.clanTab, data.active_war);
    S.clanRenderedWarKey = warKey;
    switchClanTab(landing);
}

function switchClanTab(tabId) {
    if (!tabId) return;
    const pnl = $(`cpanel-${tabId}`);
    if (!pnl) return;
    const previousPanel = document.querySelector('.clan-panel.active');
    haptic('light');
    if (S.clanTab !== tabId) $('app-main')?.scrollTo({ top: 0, behavior: 'instant' });
    S.clanTab = tabId;
    $('clan-main')?.setAttribute('data-panel', tabId);
    const compact = $('clan-compact-identity');
    if (compact) compact.hidden = tabId === 'overview';

    document.querySelectorAll('.clan-tab-btn').forEach(b => b.classList.remove('active'));

    const navId = tabId === 'members' ? 'overview' : (['top', 'trade', 'stats', 'manage'].includes(tabId) ? 'more' : tabId);
    const btn = $(`ctab-${navId}`);
    if (btn) btn.classList.add('active');
    changePanels(previousPanel, pnl, () => {
        document.querySelectorAll('.clan-panel').forEach(p => p.classList.remove('active'));
        pnl.classList.add('active');
    });

    // Route to loader — guard against missing S.clan
    if (!S.clan) return;
    if (tabId === 'overview')  renderClanOverview();
    if (tabId === 'members')   renderClanMembers();
    if (tabId === 'chat')      loadClanChat();
    if (tabId === 'treasury')  loadClanTreasury();
    if (tabId === 'trade')     switchTradeTab('market');
    if (tabId === 'wars')      loadClanWars();
    if (tabId === 'top')       loadClanLeaderboard();
    if (tabId === 'stats')     loadClanStats();
    if (tabId === 'manage')    renderClanManage();

    if (['top', 'trade', 'stats', 'manage'].includes(tabId) && pnl && !pnl.querySelector('.clan-back-more')) {
        const back = document.createElement('button');
        back.className = 'clan-cta ghost clan-back-more';
        back.style.cssText = 'margin-bottom:12px;width:100%;min-height:42px';
        back.textContent = '← Назад в Ещё';
        back.onclick = () => switchClanTab('more');
        pnl.prepend(back);
    }
}
window.switchClanTab = switchClanTab;

let _clanBrowseKind = 'all';
let _clanBrowseRows = [];
let _clanBrowseLoading = false;
let _clanBrowseTimer = 0;
let _clanBrowseQueued = false;

function clanBrowseCard(clan) {
    const members = Number(clan.members ?? clan.members_count ?? 0);
    const maxMembers = Number(clan.max_members || 10);
    const joinable = clan.joinable !== false && (clan.open !== false) && members < maxMembers;
    const requestable = Boolean(clan.requestable) && members < maxMembers;
    return {
        id: String(clan.id || clan.uid || ''),
        name: clan.name || 'Клан',
        description: clan.description || '',
        avatar: clan.avatar || '',
        open: clan.open !== false,
        members,
        max_members: maxMembers,
        pts: Number(clan.pts ?? clan.score ?? 0),
        joinable,
        requestable,
    };
}

async function loadClanBrowse() {
    if (_clanBrowseLoading) {
        _clanBrowseQueued = true;
        return;
    }
    const list = $('clan-browse-list');
    if (!list) return;
    _clanBrowseLoading = true;
    try {
        const q = encodeURIComponent(($('clan-browse-q')?.value || '').trim());
        let rows = [];
        try {
            const data = await api(`/api/clans_browse?q=${q}&kind=${encodeURIComponent(_clanBrowseKind)}`);
            rows = Array.isArray(data?.list) ? data.list : [];
        } catch (browseError) {
            console.warn('[CLAN BROWSE] fallback to leaderboard', browseError);
            const uid = effectiveUid();
            const data = await api(`/api/clan_leaderboard?user_id=${uid || ''}`);
            rows = Array.isArray(data?.list) ? data.list : [];
        }
        const needle = (($('clan-browse-q')?.value || '').trim()).toLowerCase();
        _clanBrowseRows = rows.map(clanBrowseCard).filter(clan => {
            if (!clan.id) return false;
            if (needle && !clan.name.toLowerCase().includes(needle) && !clan.id.toLowerCase().includes(needle)) return false;
            if (_clanBrowseKind === 'open' && !clan.open) return false;
            if (_clanBrowseKind === 'free' && clan.members >= clan.max_members) return false;
            return true;
        });
        renderClanBrowse();
    } catch (error) {
        console.error('[CLAN BROWSE]', error);
        if (list) list.innerHTML = '<div class="empty-state">Не удалось загрузить кланы</div>';
    } finally {
        _clanBrowseLoading = false;
        if (_clanBrowseQueued) {
            _clanBrowseQueued = false;
            loadClanBrowse();
        }
    }
}

function setClanBrowseKind(kind) {
    _clanBrowseKind = kind || 'all';
    document.querySelectorAll('#clan-browse-filters .clan-filter').forEach(btn => {
        btn.classList.toggle('on', btn.getAttribute('data-kind') === _clanBrowseKind);
    });
    haptic('light');
    loadClanBrowse();
}

function filterClanBrowse() {
    window.clearTimeout(_clanBrowseTimer);
    _clanBrowseTimer = window.setTimeout(() => loadClanBrowse(), 180);
}

function renderClanBrowse() {
    const list = $('clan-browse-list');
    if (!list) return;
    const rows = _clanBrowseRows;
    const openCount = rows.filter(clan => clan.joinable).length;
    const freeSlots = rows.reduce((total, clan) => total + Math.max(0, clan.max_members - clan.members), 0);
    setText('clan-browse-count', String(openCount));
    setText('clan-browse-slots', String(freeSlots));
    if (!rows.length) {
        list.innerHTML = '<div class="empty-state">Пока нет кланов с свободными местами</div>';
        return;
    }
    list.innerHTML = rows.map((clan, index) => {
        const letter = esc(String(clan.name || 'R').charAt(0));
        const mark = clan.avatar
            ? `<span class="clan-join-initial">${letter}</span><img src="${esc(clan.avatar)}" alt="" loading="lazy" decoding="async" onerror="this.remove()">`
            : `<span class="clan-join-initial">${letter}</span>`;
        const typeLabel = clan.open ? 'Открытый набор' : 'По заявке';
        const desc = clan.description
            ? `<p class="clan-join-desc">${esc(String(clan.description).slice(0, 110))}</p>`
            : '<p class="clan-join-desc muted">Ищет игроков в свой отряд.</p>';
        const canJoin = clan.joinable || clan.requestable;
        const label = clan.joinable ? 'Вступить в клан' : (clan.requestable ? 'Подать заявку' : 'Мест нет');
        const state = clan.joinable ? 'ready' : (clan.requestable ? 'request' : 'full');
        const cid = String(clan.id || '').replace(/'/g, '');
        return `<article class="clan-join-card state-${state}" style="--clan-index:${index}">
            <div class="clan-join-card-top">
                <div class="clan-join-mark">${mark}</div>
                <div class="clan-join-copy">
                    <div class="clan-join-eyebrow"><span>${typeLabel}</span><span>№${String(index + 1).padStart(2, '0')}</span></div>
                    <h3 class="clan-join-name">${esc(clan.name || 'Клан')}</h3>
                    ${desc}
                </div>
            </div>
            <div class="clan-join-footer">
                <div class="clan-join-metrics" aria-label="Статистика клана">
                    <span><b>${clan.members}/${clan.max_members}</b> состав</span>
                    <span><b>${Number(clan.pts || 0).toLocaleString('ru-RU')}</b> PTS</span>
                </div>
                <button type="button" class="clan-join-btn" ${canJoin ? '' : 'disabled'} onclick="joinClanFromHub('${cid}')">${label}<i aria-hidden="true">↗</i></button>
            </div>
        </article>`;
    }).join('');
}

async function joinClanFromHub(clanId) {
    if (!clanId) return;
    try {
        const res = await api('/api/clan_join', {
            method: 'POST',
            body: { user_id: effectiveUid(), clan_id: clanId },
        });
        if (res?.error) throw new Error(res.error);
        showToast(res.message || 'Готово');
        haptic('medium');
        await loadClan();
    } catch (error) {
        showToast(error?.message || 'Не удалось вступить');
    }
}

async function loadClanLeaderboard() {
    const uid = effectiveUid();
    if (!uid || _clanTopLoading) return;
    _clanTopLoading = true;
    try {
        const data = await api(`/api/clan_leaderboard?user_id=${uid}`);
        S.clanLeaders = data?.list || [];
        S.clanLeaderboardMe = data?.me || null;
        renderClanLeaderboard();
    } catch (e) {
        console.error('[CLAN TOP]', e);
        ['clan-top-list', 'clan-public-top-list'].forEach(id => {
            const el = $(id);
            if (el) el.innerHTML = '<div class="empty-state">Не удалось загрузить рейтинг<br><button class="context-help" type="button" onclick="retryClanRanking()">Повторить</button></div>';
        });
        ['clan-top-podium', 'clan-public-podium'].forEach(id => {
            const el = $(id);
            if (el) el.innerHTML = '';
        });
    } finally {
        _clanTopLoading = false;
    }
}

window.retryClanLoad = () => loadClan();
window.retryClanRanking = () => loadClanLeaderboard();

function renderClanLeaderboard() {
    const leaders = S.clanLeaders || [];
    const myClanId = String(S.clan?.clan_id || '');
    const isMine = (clan) => myClanId && String(clan?.uid) === myClanId;
    const podium = () => {
        if (!leaders.length) return '';
        const order = [
            { clan: leaders[1], place: 2 },
            { clan: leaders[0], place: 1 },
            { clan: leaders[2], place: 3 },
        ].filter(item => item.clan);
        return order.map(({ clan, place }) => `
            <article class="clan-podium-card podium-${place}${isMine(clan) ? ' is-me' : ''}" style="--podium-order:${place === 1 ? 2 : (place === 2 ? 1 : 3)}">
                <div class="clan-podium-place">${place}</div>
                <div class="clan-podium-avatar">${userAvatarHtml(clan.avatar, clan.name || '?', 'clan-podium-avatar-img', '')}</div>
                <div class="clan-podium-name">${esc(clan.name || 'Клан')}</div>
                ${isMine(clan) ? '<div class="clan-podium-mine">Ваш клан</div>' : ''}
                <div class="clan-podium-score">${Number(clan.score || 0).toLocaleString('ru-RU')}</div>
                <div class="clan-podium-score-label">PTS</div>
                <div class="clan-podium-meta">
                    <span>Уровень ${clan.level || 1}</span>
                    <span>${clan.members_count || 0} чел.</span>
                </div>
            </article>
        `).join('');
    };
    const rows = (limit) => {
        if (!leaders.length) return '<div class="empty-state">Кланов пока нет</div>';
        return leaders.slice(3, limit).map((clan, index) => {
            const place = index + 4;
            const isMine = myClanId && String(clan.uid) === myClanId;
            return `<div class="clan-rank-row${isMine ? ' is-me' : ''}">
                <div class="clan-rank-place">${place}</div>
                <div class="clan-rank-avatar">${userAvatarHtml(clan.avatar, clan.name || '?', 'clan-rank-avatar-img', '')}</div>
                <div class="clan-rank-copy">
                    <b>${esc(clan.name || 'Клан')}${isMine ? ' / ВАШ' : ''}</b>
                    <span>Ур. ${clan.level || 1} / ${clan.members_count || 0} участников</span>
                </div>
                <div class="clan-rank-score">${Number(clan.score || 0).toLocaleString('ru-RU')}</div>
                ${!S.clan?.in_clan && clan.uid ? `<button type="button" class="clan-join-btn" onclick="joinClanFromHub('${String(clan.uid).replace(/'/g, '')}')">Вступить</button>` : ''}
            </div>`;
        }).join('');
    };

    const full = $('clan-top-list');
    const publicList = $('clan-public-top-list');
    const fullPodium = $('clan-top-podium');
    const publicPodium = $('clan-public-podium');
    if (fullPodium) fullPodium.innerHTML = podium();
    if (publicPodium) publicPodium.innerHTML = podium();
    if (full) full.innerHTML = rows(100);
    if (publicList) publicList.innerHTML = rows(10);
    const restLabel = $('clan-top-rest-label');
    if (restLabel) restLabel.hidden = leaders.length <= 3;

    const meWrap = $('clan-my-rank-card');
    if (meWrap) {
        const me = S.clanLeaderboardMe;
        meWrap.innerHTML = me?.rank
            ? `<div class="clan-top-me"><span>Место вашего клана</span><b>#${me.rank}</b><strong>✦ ${Number(me.score || 0).toLocaleString('ru-RU')}</strong></div>`
            : '';
    }
}

function clanServiceIcon(kind) {
    const paths = kind === 'bank'
        ? '<path d="M3 10V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4v13H3V10Zm0 0h18M8 3v7m8-7v7"/><rect x="10" y="9" width="4" height="5" rx="1"/>'
        : '<circle cx="9" cy="7" r="3"/><path d="M3 21v-4a5 5 0 0 1 10 0v4M16 4a3 3 0 0 1 0 6m1 3a5 5 0 0 1 4 5v3"/>';
    return `<svg class="clan-service-icon" aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round">${paths}</svg>`;
}

function clanServiceRows() {
    const c = S.clan || {};
    return `<button class="clan-service" onclick="switchClanTab('treasury')">${clanServiceIcon('bank')}
      <span class="clan-service-copy"><b>Казна</b><small><strong>${Number(c.bank || 0).toLocaleString('ru-RU')}</strong> рекоинов</small></span><span class="clan-service-link">Пополнить <span aria-hidden="true">›</span></span>
    </button>
    <button class="clan-service" onclick="switchClanTab('members')">${clanServiceIcon('members')}
      <span class="clan-service-copy"><b>Участники</b><small>${c.member_count || 0} / ${c.max_members || 10} в клане</small></span>
      <span class="clan-avatar-strip" aria-hidden="true">${clanMembers(c.members).slice(0, 4).map(m => `<span>${userAvatarHtml(m.avatar, m.nickname, '', m.id)}</span>`).join('')}</span><span class="clan-service-link">Состав <span aria-hidden="true">›</span></span>
    </button>
    <div class="clan-service-shortcuts">
      <button onclick="switchClanTab('top')">Рейтинг <span aria-hidden="true">›</span></button>
      <button onclick="switchClanTab('trade')">Обмен <span aria-hidden="true">›</span></button>
      <button onclick="switchClanTab('manage')">Управление <span aria-hidden="true">›</span></button>
    </div>`;
}

function renderClanMembers() {
    const all = clanMembers(S.clan?.members);
    const members = clanMembers(all, $('clan-member-search')?.value || '');
    setText('clan-member-count', `${members.length} / ${all.length}`);
    const list = $('clan-members-list');
    if (!list) return;
    const roles = { creator: 'Лидер', 'vice-creator': 'Заместитель', elder: 'Старейшина' };
    list.innerHTML = members.map(m => `
      <button class="clan-member" onclick="openPlayerProfile('${esc(m.id)}')">
        <span class="cm-fg">
          <span class="cm-avatar">${userAvatarHtml(m.avatar, m.nickname, '', m.id)}</span>
          <span class="cm-body"><span class="cm-name">${esc(m.nickname)}</span><span class="cm-role">${roles[m.role] || 'Участник'}</span></span>
          <span class="cm-pts">${Number(m.pts || 0).toLocaleString('ru-RU')} <small>PTS</small></span>
        </span>
      </button>`).join('') || '<div class="empty-state">Участник не найден. Попробуй другое имя.</div>';
}
window.renderClanMembers = renderClanMembers;

function renderClanOverview() {
    const c = S.clan || {};
    const war = c.active_war;
    const actions = $('clan-home-actions');
    if (actions) {
        const active = war?.status === 'active';
        const drafting = war?.status === 'drafting';
        actions.innerHTML = `<div class="clan-peace-copy"><b>${active ? 'Клановая война' : drafting ? 'Собираем боевой состав' : 'В цитадели спокойно'}</b><p>${active ? `${esc(war.opponent_name || 'Соперник')} · ${war.my_score || 0} : ${war.opp_score || 0}` : drafting ? 'Подготовка идёт. Бой начнётся, когда оба клана выставят отряды.' : 'Активной войны нет. Самое время подготовить колоды и собрать отряд.'}</p></div><button class="clan-cta" onclick="switchClanTab('wars')">${active ? 'На поле войны' : drafting ? 'Открыть состав' : 'Подготовка к войне'} <span aria-hidden="true">›</span></button>`;
    }
    setText('clan-hero-members', `${c.member_count || 0} / ${c.max_members || 10} участников`);
    const emblemUrl = resolveClanEmblemUrl(c);
    const hero = $('clan-hero-card');
    if (hero) {
        // The clan's own uploaded avatar is the cover, never a shared castle.
        if (emblemUrl) hero.style.setProperty('--clan-hero-art', `url("${cssUrl(emblemUrl)}")`);
        else hero.style.removeProperty('--clan-hero-art');
    }
    const emblemHtml = `<img src="${cssUrl(emblemUrl || 'assets/clans/crest.png')}" alt="" decoding="async" onerror="this.onerror=null;this.src='assets/clans/crest.png'">`;
    if ($('clan-hero-emblem')) $('clan-hero-emblem').innerHTML = emblemHtml;
    if ($('clan-compact-identity')) $('clan-compact-identity').innerHTML = `<span class="clan-compact-emblem">${emblemHtml}</span><span><b>${esc(c.name || 'Клан')}</b><small>Уровень ${c.level || 1} · ${c.member_count || 0} / ${c.max_members || 10} участников</small></span>`;
    if ($('clan-home-services')) $('clan-home-services').innerHTML = clanServiceRows();
    if ($('clan-war-services')) $('clan-war-services').innerHTML = clanServiceRows();
    if ($('clan-activity-log')) $('clan-activity-log').innerHTML = (c.activity || []).slice(0, 6).map(a => `
      <div class="clan-log-item"><span class="clan-log-dot"></span><div><time>${esc(a.time || '')}</time><p>${esc(a.text || '')}</p></div></div>`).join('') || '<div class="empty-state">Здесь появятся события твоего клана.</div>';
}

function renderWarDefense(war) {
    const el = $('war-defense');
    if (!el) return;
    el.hidden = war?.status !== 'active';
    if (el.hidden) return;
    const cards = warDefenseCards(war, effectiveUid(), S.cards, S.user?.universe || S.user?.verce || S.clan?.verse);
    el.innerHTML = `<div class="clan-sec-head"><h2 class="clan-sec-title">Твоя защита</h2></div>${cards.length ? `<div class="clan-defense-cards">${cards.map(item => {
        const media = item.card?.arena_preview_url || item.card?.image_url || '';
        return `<div class="clan-defense-card ${item.hp <= 0 ? 'defeated' : ''}">${media ? `<img src="${cssUrl(media)}" alt="" loading="lazy" decoding="async" onerror="this.hidden=true">` : ''}<span class="clan-defense-slot">${esc(item.slot)}</span><b>${esc(item.card?.name || `Карта ${item.slot}`)}</b><small>${item.hp > 0 ? `${item.hp.toLocaleString('ru-RU')} HP` : 'Повержена'}</small></div>`;
    }).join('')}</div><p class="clan-defense-note">Состав зафиксирован на эту войну. HP — из последнего боя.</p>` : '<p class="clan-defense-note">Тебя нет в боевом составе. Можно следить за войной и поддерживать клан в чате.</p>'}`;
}

function setTreasuryDeposit(amount) {
    haptic('light');
    const inp = $('treasury-deposit-input'); if(!inp) return;
    const ucoins = S.user?.coins || 0;
    inp.value = amount === -1 ? ucoins : amount;
}

// Stubs for next stage implementation:
let _clanChatInterval = null;

async function loadClanChat() {
    const msgsEl = $('clan-chat-msgs');
    if (!msgsEl) return;
    
    if (_clanChatInterval) clearInterval(_clanChatInterval);
    msgsEl.innerHTML = '<div class="empty-state" style="padding:32px 0">💬 Чат загружается...</div>';
    
    const fetchChat = async (scroll = false) => {
        if (S.clanTab !== 'chat') {
            clearInterval(_clanChatInterval);
            return;
        }
        try {
            const data = await api(`/api/clan_chat?user_id=${effectiveUid()}`);
            if (Array.isArray(data)) renderClanChatMessages(data, scroll);
        } catch (e) {}
    };
    
    await fetchChat(true);
    _clanChatInterval = setInterval(() => fetchChat(false), 3000);
    
    const inp = $('clan-chat-input');
    if(inp) {
        inp.onkeypress = (e) => {
            if (e.key === 'Enter') clanChatSend();
        };
    }
}

function renderClanChatMessages(messages, forceScroll = false) {
    const msgsEl = $('clan-chat-msgs');
    if (!msgsEl) return;
    
    if (!messages.length) {
        if (msgsEl.innerHTML.includes('загружается') || msgsEl.innerHTML === '') {
            msgsEl.innerHTML = '<div class="empty-state" style="padding:32px 0">Нет сообщений. Напишите первыми!</div>';
        }
        return;
    }
    
    const uid = String(effectiveUid());
    let isAtBottom = msgsEl.scrollHeight - msgsEl.scrollTop <= msgsEl.clientHeight + 50;
    
    const html = messages.map(m => {
        const isMe = String(m.uid) === uid;
        const roleBadge = m.role === 'creator' ? '<span style="color:#ffd700">👑</span>' : m.role === 'vice-creator' ? '<span style="color:#0ea5e9">⚙</span>' : '';
        return `
            <div class="clan-chat-msg ${isMe ? 'msg-me' : 'msg-other'}">
                ${!isMe ? `<div class="msg-author">${roleBadge} ${esc(m.name)}</div>` : ''}
                <div class="msg-bubble">
                    <div class="msg-text">${esc(m.text)}</div>
                    <div class="msg-time">${esc(m.time)}</div>
                </div>
            </div>
        `;
    }).join('');
    
    if (msgsEl.innerHTML !== html) {
        msgsEl.innerHTML = html;
        if (isAtBottom || forceScroll) {
            msgsEl.scrollTop = msgsEl.scrollHeight;
        }
    }
}

async function clanChatSend() {
    const inp = $('clan-chat-input');
    const btn = $('clan-chat-send-btn');
    if (!inp || !btn) return;
    
    const text = inp.value.trim();
    if (!text) return;
    if (text.length > 200) { showToast('Слишком длинное сообщение'); return; }
    
    inp.disabled = true;
    btn.style.opacity = '0.5';
    
    try {
        const res = await api('/api/clan_chat_send', {
            method: 'POST',
            body: { user_id: effectiveUid(), text: text }
        });
        
        if (res && res.success) {
            inp.value = '';
            haptic('light');
            const msgsEl = $('clan-chat-msgs');
            if(msgsEl) msgsEl.scrollTop = msgsEl.scrollHeight;
        } else {
            showToast(res?.error || 'Ошибка отправки');
            haptic('error');
        }
    } catch (e) {
        showToast('Ошибка сети');
    } finally {
        inp.disabled = false;
        btn.style.opacity = '1';
        inp.focus();
    }
}

let clanFundUI = null;
let clanFundId = null;
async function loadClanTreasury() {
    const root = $('clan-fund-root'); if (!root) return;
    clanFundUI ||= createClanFund({root, api, uid: effectiveUid, confirm: customConfirm,
        onBalance: data => { if (String(S.clan?.clan_id) !== data.clan_id) return;
            S.clan.bank = data.bank;
            if (S.user) { S.user.coins = data.wallet; S.user.spins = data.spins; }
            setText('h-coins', Number(data.wallet).toLocaleString('ru-RU')); setText('h-spins', data.spins); },
        openHoldings: () => switchTab('holdings')});
    if (clanFundId !== S.clan?.clan_id) { clanFundUI.reset(); clanFundId = S.clan?.clan_id; }
    await clanFundUI.load();
}
// Cached Telegram shells route old actions into the current fund screen.
function clanTreasuryDeposit() { void loadClanTreasury(); }
function clanTreasuryWithdraw() { showToast('Средства фонда теперь расходуются на снабжение клана'); }
function setTreasuryWithdraw() { clanTreasuryWithdraw(); }

function switchTradeTab(tabName) {
    const previousPanel = document.querySelector('.trade-panel.active');
    const pnl = $(`trade-panel-${tabName}`);
    if (!pnl) return;
    changePanels(previousPanel, pnl, () => {
    ['market','my','create'].forEach(t => {
        const p = $(`trade-panel-${t}`); if(p) p.classList.remove('active');
        const b = $(`trade-tab-${t}`); if(b) b.classList.remove('active');
    });
    pnl.classList.add('active');
    });
    const btn = $(`trade-tab-${tabName}`); if(btn) btn.classList.add('active');
    if(tabName === 'market') loadTradeMarket();
    if(tabName === 'my') loadMyTrades();
    if(tabName === 'create') loadTradeCreateForm();
}

async function loadMyTrades() {
    const el = $('clan-my-trades');
    if (!el) return;
    el.innerHTML = '<div class="empty-state">⏳ Загрузка...</div>';
    try {
        const res = await api(`/api/clan_trade_offers?user_id=${effectiveUid()}&my=1`);
        if (!res.offers || !res.offers.length) {
            el.innerHTML = '<div class="empty-state">📭 Нет ваших предложений</div>';
            return;
        }
        el.innerHTML = res.offers.map(t => {
            const req = t.request_coins > 0
                ? `${t.request_coins.toLocaleString('ru-RU')} 💰`
                : `🃏 ${esc(t.request_card_name || t.request_card_id || '?')}`;
            const c = t.card || {id: t.card_id, name: t.card_name};
            const col = _rarityColors[c.rank] || '#1A1A1A';
            return `<div class="bento-card" style="padding:12px;margin-bottom:12px;display:flex;gap:16px;background:rgba(255,255,255,0.02)">
                <div class="card-item" style="width:120px;flex-shrink:0;background:#1a1a1a;border-radius:8px;overflow:hidden;border:1px solid rgba(255,255,255,0.05)" onclick="openCardModal('${c.id}')">
                    <div class="card-img-wrap" style="height:150px;display:flex;align-items:center;justify-content:center;background:#2a2a2a">
                        ${c.image_url ? renderMedia(c) : '<div style="font-size:40px">🃏</div>'}
                    </div>
                    <div class="card-rank-bar" style="background:${col}"></div>
                    <div class="card-info" style="padding:8px">
                        <div style="font-weight:900;font-style:italic;font-size:11px;line-height:1.2;text-transform:uppercase;letter-spacing:-0.5px;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(c.name || '—')}</div>
                        <div style="font-size:9px;font-weight:700;text-transform:uppercase;margin-bottom:6px;color:${col}">${c.rank || ''}</div>
                        <div style="display:flex;gap:4px;font-size:9px;font-weight:900">
                            <span style="background:#FFD54F;border:1px solid #1A1A1A;padding:2px 4px;border-radius:4px;color:#000">⚔ ${c.damage || 0}</span>
                            <span style="background:#FF77B0;border:1px solid #1A1A1A;padding:2px 4px;border-radius:4px;color:#fff">❤ ${c.health || 0}</span>
                        </div>
                    </div>
                </div>
                <div style="flex:1;display:flex;flex-direction:column;justify-content:space-between">
                    <div>
                        <div style="font-size:11px;opacity:0.5;margin-bottom:4px">${t.time || ''}</div>
                        <div style="font-size:14px;font-weight:700;color:var(--accent);margin-bottom:4px">Хочу:</div>
                        <div style="font-size:15px;font-weight:800;color:#fff">${req}</div>
                    </div>
                    <div style="display:flex;justify-content:flex-end">
                        <button class="action-pill" style="background:rgba(255,60,60,0.15);color:#ff4444;padding:8px 16px;width:100%" onclick="clanCancelTrade('${t.offer_id}')">✕ Отменить</button>
                    </div>
                </div>
            </div>`;
        }).join('');
    } catch(e) { el.innerHTML = '<div class="empty-state">Ошибка загрузки</div>'; }
}

let _cachedUserInventory = [];
let _offerSearchTimeout = null;
let _wantSearchTimeout = null;
let _wantSearchEpoch = 0;
let _tradeInventoryLoading = false;

let _selectedTradeCardId = null;
let _selectedTradeCardName = null;
let _selectedWantCardId = null;
let _selectedWantCardName = null;

const _rarityColors = {
    'Мифический': '#ff0055',
    'Легендарный': '#ffd700',
    'Эпический': '#d000ff',
    'Редкий': '#009dff',
    'Обычный': '#aaaaaa'
};

async function loadTradeCreateForm() {
    const errEl = $('trade-error-msg');
    if (errEl) errEl.style.display = 'none';

    clearOfferSelection();
    clearWantSelection();
    _cachedUserInventory = [];
    _tradeInventoryLoading = true;
    try {
        const cards = await api(`/api/user_cards?user_id=${effectiveUid()}&all_verses=1`, { dedupe: false });
        _cachedUserInventory = Array.isArray(cards) ? cards : [];
        
        if($('trade-price-input')) $('trade-price-input').value = '';
    } catch(e) { 
        showToast('Ошибка загрузки инвентаря'); 
        _cachedUserInventory = [];
    } finally {
        _tradeInventoryLoading = false;
        if(document.activeElement === $('trade-card-offer-input')) handleOfferSearch();
    }
}

function renderDropdownItem(c, onClickStr) {
    const color = _rarityColors[c.rank] || '#ffffff';
    return `<button type="button" class="trade-card-option" onclick="${esc(onClickStr)}"><strong>${esc(c.name)}</strong><span style="color:${color};font-size:11px">${esc(c.rank)}</span><small>${esc(c.verce || c.verse || '')} · Атака ${Number(c.damage || 0).toLocaleString('ru-RU')} · HP ${Number(c.health || 0).toLocaleString('ru-RU')}</small></button>`;
}

function handleOfferSearch() {
    const input = $('trade-card-offer-input');
    const dd = $('picker-offer-dropdown');
    if (!input || !dd) return;
    if (_tradeInventoryLoading) {
        dd.innerHTML = '<div style="padding:12px">Загружаем твою коллекцию…</div>';
        dd.style.display = 'block';
        return;
    }
    
    const q = input.value.trim().toLocaleLowerCase('ru-RU');
    
    const results = _cachedUserInventory.filter(c => String(c.name || '').toLocaleLowerCase('ru-RU').includes(q)).slice(0, 10);
    
    if (results.length === 0) {
        dd.innerHTML = '<div style="padding:10px;text-align:center;color:#ff4444;font-size:13px">Карта не найдена</div>';
    } else {
        dd.innerHTML = results.map(c => renderDropdownItem(c, `selectOfferCard(${JSON.stringify(String(c.id))}, ${JSON.stringify(c.name)}, ${JSON.stringify(c.rank)})`)).join('');
    }
    dd.style.display = 'block';
}

function selectOfferCard(id, name, rank) {
    _selectedTradeCardId = id;
    _selectedTradeCardName = name;
    
    $('picker-offer-input-container').style.display = 'none';
    const sel = $('picker-offer-selected');
    sel.style.display = 'flex';
    const color = _rarityColors[rank] || '#ffffff';
    $('picker-offer-selected-content').innerHTML = `
        <div style="font-size:11px;color:${color};font-weight:800;letter-spacing:1px;text-transform:uppercase;text-shadow:0 0 5px ${color}80">${esc(rank)}</div>
        <div style="font-size:16px;font-weight:700;color:#fff">${esc(name)}</div>`;
    
    // Add glow
    sel.style.background = `linear-gradient(135deg, ${color}15, ${color}05)`;
    sel.style.borderColor = color;
    sel.style.boxShadow = `0 0 15px ${color}40, inset 0 0 10px ${color}10`;
    
    $('picker-offer-dropdown').style.display = 'none';
    $('trade-card-offer-input').value = '';
}

function clearOfferSelection() {
    _selectedTradeCardId = null;
    _selectedTradeCardName = null;
    if($('picker-offer-input-container')) $('picker-offer-input-container').style.display = 'flex';
    if($('picker-offer-selected')) $('picker-offer-selected').style.display = 'none';
    if($('picker-offer-dropdown')) $('picker-offer-dropdown').style.display = 'none';
    if($('trade-card-offer-input')) $('trade-card-offer-input').value = '';
}

function handleWantSearch() {
    const input = $('trade-card-want-input');
    const dd = $('picker-want-dropdown');
    if (!input || !dd) return;
    
    const q = input.value.trim();
    const epoch = ++_wantSearchEpoch;
    
    clearTimeout(_wantSearchTimeout);
    _wantSearchTimeout = setTimeout(async () => {
        dd.innerHTML = '<div style="padding:10px;text-align:center;font-size:13px;opacity:0.6">Поиск...</div>';
        dd.style.display = 'block';
        
        try {
            const results = await api(`/api/cards_search?q=${encodeURIComponent(q)}`, {dedupe:false});
            if(epoch !== _wantSearchEpoch) return;
            if (!Array.isArray(results) || results.length === 0) {
                dd.innerHTML = '<div style="padding:10px;text-align:center;color:#ff4444;font-size:13px">Карта не найдена в базе</div>';
            } else {
                dd.innerHTML = results.map(c => renderDropdownItem(c, `selectWantCard(${JSON.stringify(String(c.card_id))}, ${JSON.stringify(c.name)}, ${JSON.stringify(c.rank)})`)).join('');
            }
        } catch(e) {
            if(epoch !== _wantSearchEpoch) return;
            dd.innerHTML = '<div style="padding:10px;text-align:center;color:#ff4444;font-size:13px">Ошибка поиска</div>';
        }
    }, 300);
}

function selectWantCard(id, name, rank) {
    ++_wantSearchEpoch;
    clearTimeout(_wantSearchTimeout);
    _selectedWantCardId = id;
    _selectedWantCardName = name;
    
    $('picker-want-input-container').style.display = 'none';
    const sel = $('picker-want-selected');
    sel.style.display = 'flex';
    const color = _rarityColors[rank] || '#ffffff';
    $('picker-want-selected-content').innerHTML = `
        <div style="font-size:11px;color:${color};font-weight:800;letter-spacing:1px;text-transform:uppercase;text-shadow:0 0 5px ${color}80">${esc(rank)}</div>
        <div style="font-size:16px;font-weight:700;color:#fff">${esc(name)}</div>`;
        
    // Add glow
    sel.style.background = `linear-gradient(135deg, ${color}15, ${color}05)`;
    sel.style.borderColor = color;
    sel.style.boxShadow = `0 0 15px ${color}40, inset 0 0 10px ${color}10`;
    
    $('picker-want-dropdown').style.display = 'none';
    $('trade-card-want-input').value = '';
}

function clearWantSelection() {
    ++_wantSearchEpoch;
    clearTimeout(_wantSearchTimeout);
    _selectedWantCardId = null;
    _selectedWantCardName = null;
    if($('picker-want-input-container')) $('picker-want-input-container').style.display = 'flex';
    if($('picker-want-selected')) $('picker-want-selected').style.display = 'none';
    if($('picker-want-dropdown')) $('picker-want-dropdown').style.display = 'none';
    if($('trade-card-want-input')) $('trade-card-want-input').value = '';
}

document.addEventListener('click', e => {
    if (!e.target.closest('#picker-offer')) {
        const dd = $('picker-offer-dropdown');
        if (dd) dd.style.display = 'none';
    }
    if (!e.target.closest('#picker-want')) {
        const dd = $('picker-want-dropdown');
        if (dd) dd.style.display = 'none';
    }
});

async function clanCreateTrade() {
    const errEl = $('trade-error-msg');
    const showError = (msg) => {
        if (errEl) { errEl.textContent = msg; errEl.style.display = 'block'; }
        else showToast(msg);
    };
    if (errEl) errEl.style.display = 'none';

    if (!_selectedTradeCardId) { showError('Выберите карту для обмена (из выпадающего списка)'); return; }
    const coins = parseInt($('trade-price-input')?.value || '0');
    
    if (coins <= 0 && !_selectedWantCardId) { showError('Укажите цену (монеты) или выберите карту (из списка)'); return; }

    const btn = document.querySelector('#trade-panel-create button');
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Создаём...'; }
    try {
        const res = await api('/api/clan_trade_create', { method: 'POST', body: {
            user_id: effectiveUid(),
            card_id: _selectedTradeCardId,
            request_coins: coins || 0,
            request_card_id: _selectedWantCardId || ''
        }});
        if (res.success) {
            showToast('✅ Предложение создано!');
            haptic('medium');
            clearOfferSelection();
            clearWantSelection();
            if ($('trade-price-input')) $('trade-price-input').value = '';
            switchTradeTab('market'); // go show the offers
            loadTradeMarket();
        } else {
            showError(res.error || 'Ошибка создания');
        }
    } catch(e) { showError('Ошибка сети'); }
    finally { if (btn) { btn.disabled = false; btn.textContent = '➕ Создать предложение'; } }
}

function customConfirm(title, message, confirmText = 'Да', cancelText = 'Отмена', danger = false) {
    return new Promise(resolve => {
        const overlay = document.createElement('div');
        overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.6);backdrop-filter:blur(10px);z-index:9999;display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity 0.2s';
        
        const modal = document.createElement('div');
        modal.style.cssText = 'background:var(--bg-card,#1a1a1a);color:var(--text,#f2f4f7);border-radius:20px;padding:24px;width:90%;max-width:320px;box-shadow:0 10px 40px rgba(0,0,0,0.5);transform:scale(0.9);transition:transform 0.2s;border:1px solid rgba(255,255,255,0.05);text-align:center';
        
        const btnColor = danger ? '#ff4444' : 'var(--accent)';
        const btnBg = danger ? 'rgba(255,68,68,0.1)' : 'rgba(255,213,79,0.1)';
        
        modal.innerHTML = `
            <div style="font-size:20px;margin-bottom:12px">${esc(title)}</div>
            <div style="font-size:14px;opacity:0.7;margin-bottom:24px;line-height:1.4">${esc(message)}</div>
            <div style="display:flex;gap:12px">
                <button id="cc-cancel" style="flex:1;padding:12px;border-radius:12px;background:rgba(255,255,255,0.05);color:var(--text,#f2f4f7);font-weight:700">
                    ${esc(cancelText)}
                </button>
                <button id="cc-confirm" style="flex:1;padding:12px;border-radius:12px;background:${btnBg};color:${btnColor};font-weight:700">
                    ${esc(confirmText)}
                </button>
            </div>
        `;
        
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-label', String(title));
        const previousFocus = document.activeElement;
        const appShell = $('app');
        const wasInert = appShell?.inert;
        if (appShell) appShell.inert = true;
        const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion) { overlay.style.transition = 'none'; modal.style.transition = 'none'; }
        let settled = false;
        const keyboard = event => {
            if (event.key === 'Escape') { event.preventDefault(); close(false); }
            if (event.key === 'Tab') { event.preventDefault();
                const cancel = modal.querySelector<HTMLButtonElement>('#cc-cancel');
                const accept = modal.querySelector<HTMLButtonElement>('#cc-confirm');
                (document.activeElement === cancel ? accept : cancel).focus();
            }
        };
        overlay.appendChild(modal);
        document.body.appendChild(overlay);
        
        // Trigger animations
        requestAnimationFrame(() => {
            overlay.style.opacity = '1';
            modal.style.transform = 'scale(1)';
        });
        
        const close = (result) => {
            if (settled) return;
            settled = true;
            document.removeEventListener('keydown', keyboard, true);
            if (appShell) appShell.inert = Boolean(wasInert);
            if (previousFocus instanceof HTMLElement && previousFocus.isConnected) previousFocus.focus();
            overlay.style.pointerEvents = 'none';
            overlay.style.opacity = '0';
            modal.style.transform = 'scale(0.9)';
            setTimeout(() => overlay.remove(), 200);
            resolve(result);
        };
        
        modal.querySelector<HTMLButtonElement>('#cc-cancel').onclick = () => close(false);
        modal.querySelector<HTMLButtonElement>('#cc-confirm').onclick = () => close(true);
        overlay.onclick = (e) => { if (e.target === overlay) close(false); };
        document.addEventListener('keydown', keyboard, true);
        modal.querySelector<HTMLButtonElement>('#cc-cancel').focus();
    });
}
async function clanCancelTrade(offerId) {
    haptic('heavy');
    const isConfirmed = await customConfirm(
        'Отмена', 
        'Вы уверены, что хотите отменить это предложение?', 
        'Отменить', 'Назад', true
    );
    if (!isConfirmed) return;
    
    try {
        const res = await api('/api/clan_trade_cancel', { method: 'POST', body: { user_id: effectiveUid(), offer_id: offerId } });
        if (res.success) { 
            showToast('❌ Предложение отменено'); 
            loadMyTrades(); 
            loadTradeMarket(); 
        } else {
            showToast(res.error || 'Ошибка');
        }
    } catch(e) { showToast('Ошибка сети'); }
}

async function clanAcceptTrade(offerId) {
    haptic('heavy');
    const isConfirmed = await customConfirm(
        'Обмен', 
        'Принять предложение и обменяться?', 
        'Принять', 'Отмена', false
    );
    if (!isConfirmed) return;
    
    try {
        const res = await api('/api/clan_trade_accept', { method: 'POST', body: { user_id: effectiveUid(), offer_id: offerId } });
        if (res.success) { 
            showToast('✅ Обмен выполнен!'); 
            loadTradeMarket(); 
            loadMyTrades();
        } else {
            showToast(res.error || 'Ошибка обмена');
        }
    } catch(e) { showToast('Ошибка сети'); }
}

async function clanWarContribute() { return clanWarAttack(); }
window.clanWarContribute = clanWarContribute;

async function loadClanWars() {
    try {
        const res = await api(`/api/clan_wars?user_id=${effectiveUid()}`);
        if (!res || res.error) {
            showToast(res?.error || 'Ошибка загрузки войн');
            return;
        }
        S.clanWars = res;
        if (S.clan) S.clan.active_war = res.active_war || null;
        renderClanOverview();
        renderClanWarsState(res);
    } catch (e) {
        showToast('Не удалось загрузить войну. Открой вкладку ещё раз.');
    }
}

function renderClanWarsState(res) {
        wireClanWarButtons();

        const warCard = $('active-war-card');
        const idleCard = $('war-idle-card');
        const draftCard = $('war-draft-card');
        const searchBtn = $('btn-find-war');
        const attackBtn = $('btn-war-attack');
        const hint = $('war-attack-hint');
        const guide = $('war-guide-card');
        const war = res.active_war;
        $('clan-main')?.setAttribute('data-war-phase', war?.status || 'idle');
        S.clanWarTargetId = validWarTarget(war, S.clanWarTargetId, res.fighter_state === 'ready' && res.is_selected);
        renderWarDefense(war);
        const surrBtn = $('btn-war-surrender');
        const surrDraft = $('btn-war-surrender-draft');

        if (guide) {
            if (!war || war.status === 'drafting') {
                guide.classList.add('show');
                guide.innerHTML = (res.guide || []).map(line => `<div>${esc(line)}</div>`).join('');
            } else {
                guide.classList.remove('show');
                guide.innerHTML = '';
            }
        }

        if (war && war.status === 'active') {
            if (warCard) warCard.style.display = 'block';
            if (idleCard) idleCard.style.display = 'none';
            if (draftCard) draftCard.style.display = 'none';

            setText('war-my-name', war.my_name || S.clan?.name || 'Мы');
            setText('war-opp-name', war.opponent_name || '???');
            setText('war-my-score', war.my_score || 0);
            setText('war-opp-score', war.opp_score || 0);

            const my = Number(war.my_score || 0);
            const opp = Number(war.opp_score || 0);
            const total = Math.max(1, my + opp);
            const fill = $('war-progress-fill');
            if (fill) fill.style.width = `${Math.round((my / total) * 100)}%`;

            const remain = Math.max(0, Math.floor((war.end_time || 0) - Date.now() / 1000));
            setText(
                'war-time-left',
                remain > 0
                    ? `${Math.floor(remain / 3600)}ч ${Math.floor((remain % 3600) / 60)}м`
                    : 'Итог...'
            );

            const canAttack = res.fighter_state === 'ready' && res.is_selected;
            if (attackBtn) {
                if (!res.is_selected) {
                    attackBtn.disabled = true;
                    attackBtn.textContent = 'Тебя нет в составе';
                    attackBtn.style.opacity = '0.55';
                } else if (!canAttack) {
                    attackBtn.disabled = true;
                    attackBtn.textContent = res.fighter_state === 'defeated' ? 'Ты повержен' : 'Режим наблюдателя';
                    attackBtn.style.opacity = '0.55';
                } else if (!S.clanWarTargetId) {
                    attackBtn.disabled = true;
                    attackBtn.textContent = 'Выбери врага в списке';
                    attackBtn.style.opacity = '0.65';
                } else {
                    attackBtn.disabled = false;
                    const target = (war.opponent_roster || []).find(p => String(p.uid) === String(S.clanWarTargetId));
                    attackBtn.textContent = `Атаковать: ${target?.nickname || 'врага'}`;
                    attackBtn.style.opacity = '1';
                }
            }
            if (hint) {
                hint.textContent = !res.is_selected
                    ? 'Глава не включил тебя в состав. Можешь только смотреть.'
                    : (canAttack
                        ? 'Выбери соперника справа и атакуй. После боя оставшееся HP сохранится в защите.'
                        : 'Твоя атака завершена или отряд повержен. Теперь ты наблюдаешь за войной.');
            }
            if (surrBtn) surrBtn.style.display = res.can_surrender ? 'block' : 'none';

            renderWarMap(war, res, canAttack);
            const attacksEl = $('war-recent-attacks');
            if (attacksEl) {
                const rows = (war.recent_attacks || []).map(a => `
                    <div class="war-attack-row">
                        <span>${a.won ? '🟢' : '🔴'} <b>${esc(a.nickname)}</b> → ${esc(a.target_name)}</span>
                        <span>+${a.points || 0}</span>
                    </div>`).join('');
                attacksEl.innerHTML = rows || '';
            }
        } else if (war && war.status === 'drafting') {
            if (warCard) warCard.style.display = 'none';
            if (idleCard) idleCard.style.display = 'none';
            if (draftCard) draftCard.style.display = 'block';
            const rosterBtn = $('btn-war-roster');
            if (rosterBtn) rosterBtn.style.display = res.can_manage_roster ? 'block' : 'none';
            if (surrDraft) surrDraft.style.display = res.can_surrender ? 'block' : 'none';
            setText(
                'war-draft-text',
                war.my_roster_ready
                    ? `Состав готов (${(war.my_roster || []).length}). Ждём «${war.opponent_name}».`
                    : `Глава выбирает от ${res.roster_min || 3} до ${res.roster_max || 10} бойцов. У каждого — команда 4 карты во вкладке Арена (любая вселенная).`
            );
            const preview = $('war-draft-preview');
            if (preview) {
                const roster = war.my_roster || [];
                preview.innerHTML = roster.length
                    ? roster.map((p, i) => `<div class="war-draft-chip"><span>${i + 1}. ${esc(p.nickname)}</span><b>готов</b></div>`).join('')
                    : '<div class="war-list-empty">Состав ещё пуст</div>';
            }
        } else {
            if (warCard) warCard.style.display = 'none';
            if (draftCard) draftCard.style.display = 'none';
            if (idleCard) idleCard.style.display = 'block';
            if (searchBtn) {
                searchBtn.style.display = res.can_declare ? 'block' : 'none';
                searchBtn.disabled = false;
                searchBtn.style.opacity = '1';
                searchBtn.textContent = '⚔ Собрать состав и объявить войну';
            }
        }

        setText('war-wins', res.wins || 0);
        setText('war-losses', res.losses || 0);
        setText('war-streak', `${res.streak || 0}`);
        setText('war-rating', res.rating || 1000);

        const histEl = $('clan-war-history');
        if (histEl) {
            histEl.innerHTML = (res.history || []).map(w => {
                const mark = w.result === 'win' ? '🏆' : (w.result === 'draw' ? '🤝' : '💔');
                const score = (w.my_score != null) ? ` ${w.my_score}:${w.opp_score}` : '';
                return `<div class="clan-log-item" style="display:flex;justify-content:space-between">
                    <span>${mark} ${esc(w.opponent || '?')}${score}</span>
                    <span style="opacity:0.5;font-size:10px">${w.time || ''}</span>
                </div>`;
            }).join('') || '<div class="empty-state">История войн пуста</div>';
        }
}

function _warFighterRow(p, side, canAttack) {
    const state = p.state || 'ready';
    const selected = String(S.clanWarTargetId || '') === String(p.uid);
    const isEnemy = side === 'enemy';
    const clickable = isEnemy && state === 'ready' && canAttack;
    const status = state === 'ready' ? 'в строю' : (state === 'defeated' ? 'повержен' : 'наблюдает');
    const hp = Math.max(0, Math.min(100, Number(p.hp_pct ?? 100)));
    const face = state === 'defeated'
        ? '<span class="av-fallback" aria-hidden="true"><svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="m6 6 12 12M18 6 6 18"/></svg></span>'
        : userAvatarHtml(p.avatar, p.nickname, '', p.uid);
    return `<button class="war-fighter ${side} ${state} ${selected ? 'selected' : ''}"
        aria-pressed="${isEnemy && selected}" ${clickable ? `onclick="selectWarTarget('${p.uid}')"` : 'disabled'}>
        <div class="war-f-avatar">${face}</div>
        <div class="war-f-body">
            <div class="war-f-name">${esc(p.nickname)}</div>
            <div class="war-f-meta">${status}${p.alive_cards != null ? ` · ${p.alive_cards}/4 карты` : ''}</div>
            <div class="war-f-health"><span>${hp}% HP</span><div class="war-f-hp" role="progressbar" aria-label="Здоровье отряда" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${hp}"><i style="width:${hp}%"></i></div></div>
        </div>
    </button>`;
}

function renderWarMap(war, res, canAttack) {
    const el = $('war-map');
    if (!el) return;
    const own = war.my_roster || [];
    const enemy = war.opponent_roster || [];
    const ownReady = own.filter(p => p.state === 'ready').length;
    const enemyReady = enemy.filter(p => p.state === 'ready').length;
    el.innerHTML = `
        <div class="war-list-col mine">
            <div class="war-list-title"><span>Наш отряд</span><b>${ownReady}/${own.length || 0}</b></div>
            ${own.length ? own.map(p => _warFighterRow(p, 'mine', false)).join('') : '<div class="war-list-empty">Состав пуст</div>'}
        </div>
        <div class="war-list-col enemy">
            <div class="war-list-title"><span>Враги</span><b>${enemyReady}/${enemy.length || 0}</b></div>
            ${enemy.length ? enemy.map(p => _warFighterRow(p, 'enemy', canAttack)).join('') : '<div class="war-list-empty">Ждём состав врага</div>'}
        </div>`;
}

function selectWarTarget(uid) {
    const res = S.clanWars;
    S.clanWarTargetId = validWarTarget(res?.active_war, uid, res?.fighter_state === 'ready' && res?.is_selected);
    haptic('medium');
    if (res) renderClanWarsState(res);
}
window.selectWarTarget = selectWarTarget;

async function openWarRoster() {
    const war = S.clanWars?.active_war || {
        status: 'new',
        war_verse: S.clan?.verse || '',
        my_roster: [],
    };
    // Unique members only (DB sometimes has duplicate ids).
    const seen = new Set();
    const members = (S.clan?.members || []).filter(m => {
        const id = String(m.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });
    const maxPick = Math.min(10, Number(S.clanWars?.roster_max) || members.length || 10);
    const minPick = Math.min(Number(S.clanWars?.roster_min) || 3, maxPick);
    if (!members.length) {
        showToast('В клане нет участников');
        return;
    }
    if (!maxPick) {
        showToast('Некого ставить в состав');
        return;
    }

    const current = new Set((war.my_roster || []).map(p => String(p.uid)));
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay open';
    overlay.innerHTML = `<div class="custom-confirm-modal war-roster-modal">
        <div class="cc-title">Клановый состав <span id="war-roster-count">0/${maxPick}</span></div>
        <div class="cc-text">
            Выбери от <b>${minPick}</b> до <b>${maxPick}</b> бойцов.<br>
            У каждого уже должна быть команда из 4 карт во вкладке <b>Арена</b> — вселенная не важна,
            война кланов межвселенская: игрок из «Блич» может сражаться с игроком из «Наруто».
        </div>
        <div class="war-roster-list">${members.map(m => `
            <label class="war-roster-row">
                <input type="checkbox" value="${esc(m.id)}" ${current.has(String(m.id)) ? 'checked' : ''}>
                <span>${m.role === 'creator' ? '👑' : m.role === 'vice-creator' ? '⚙' : '👤'} ${esc(m.nickname)}</span>
                <b>${Number(m.pts || 0).toLocaleString('ru-RU')} PTS</b>
            </label>`).join('')}</div>
        <div class="cc-actions">
            <button class="cc-btn cancel" id="war-roster-cancel">Назад</button>
            <button class="cc-btn confirm" id="war-roster-save">Сохранить состав</button>
        </div>
    </div>`;
    document.body.appendChild(overlay);
    const updateCount = () => {
        const count = overlay.querySelectorAll('input:checked').length;
        const label = overlay.querySelector('#war-roster-count');
        if (label) label.textContent = `${count}/${maxPick}`;
        overlay.querySelectorAll('input:not(:checked)').forEach(input => { input.disabled = count >= maxPick; });
        const saveBtn = overlay.querySelector('#war-roster-save');
        if (saveBtn) saveBtn.disabled = count < minPick;
    };
    overlay.querySelectorAll('input').forEach(input => input.addEventListener('change', updateCount));
    updateCount();
    overlay.querySelector('#war-roster-cancel').onclick = () => overlay.remove();
    overlay.querySelector('#war-roster-save').onclick = async () => {
        const roster = [...overlay.querySelectorAll('input:checked')].map(input => input.value);
        if (roster.length < minPick || roster.length > maxPick) {
            showToast(minPick === maxPick ? `Выбери ${minPick} бойцов` : `Выбери от ${minPick} до ${maxPick} бойцов`);
            return;
        }
        const btn = overlay.querySelector('#war-roster-save');
        btn.disabled = true; btn.textContent = 'СОХРАНЯЮ...';
        const endpoint = war.status === 'drafting' ? '/api/clan_war_roster' : '/api/clan_war_search';
        try {
            const result = await api(endpoint, { method: 'POST', body: { user_id: effectiveUid(), roster } });
            if (result?.success) {
                overlay.remove();
                S.clanWarTargetId = null;
                showToast(endpoint.includes('search') ? '⚔ Война объявлена — ждём состав врага' : '🛡 Состав сохранён');
                loadClanWars();
            } else {
                showToast(result?.error || 'Не удалось сохранить состав');
                btn.disabled = false; btn.textContent = 'Сохранить состав';
            }
        } catch (e) {
            showToast(e?.message || 'Ошибка сети. Попробуй ещё раз');
            btn.disabled = false; btn.textContent = 'Сохранить состав';
        }
    };
}
window.openWarRoster = openWarRoster;

function wireClanWarButtons() {
    const bind = (id, handler) => {
        const el = $(id);
        if (el) el.onclick = handler;
    };
    bind('btn-find-war', clanFindWarOpponent);
    bind('btn-war-roster', openWarRoster);
    bind('btn-war-attack', clanWarAttack);
    bind('btn-war-surrender', clanWarSurrender);
    bind('btn-war-surrender-draft', clanWarSurrender);
}

async function clanFindWarOpponent() {
    haptic('heavy');
    openWarRoster();
}

async function clanWarSurrender() {
    haptic('heavy');
    const ok = await customConfirm(
        'Сдаться?',
        'Война сразу завершится поражением вашего клана. Продолжить?',
        'Сдаться', 'Отмена', true
    );
    if (!ok) return;
    try {
        const res = await api('/api/clan_war_surrender', {
            method: 'POST',
            body: { user_id: effectiveUid() }
        });
        if (res?.success) {
            showToast(res.cancelled ? 'Война отменена' : '🏳 Клан сдался');
            S.clanWarTargetId = null;
            loadClanWars();
        } else {
            showToast(res?.error || 'Не удалось сдаться');
        }
    } catch (e) {
        showToast('Ошибка сети');
    }
}
window.clanWarSurrender = clanWarSurrender;

async function clanWarAttack() {
    haptic('heavy');
    if (!S.clanWarTargetId) {
        showToast('Сначала выбери врага в списке справа');
        return;
    }
    const btn = $('btn-war-attack');
    if (btn) { btn.disabled = true; btn.textContent = 'СТАРТ БОЯ...'; btn.style.opacity = '0.6'; }

    try {
        const res = await api('/api/clan_war_attack', {
            method: 'POST',
            body: { user_id: effectiveUid(), target_id: S.clanWarTargetId }
        });
        if (res && (res.success || res.state || res.battle_id)) {
            showToast(`⚔ Атака на ${res.target_name || 'врага'}!`);
            S.arena = S.arena || {};
            setArenaBattleState(res.state || res.battle_state || res, 'battle');
            S.arena.fromClanWar = true;
            switchTab('arena');
            renderArena();
        } else {
            showToast(res?.error || 'Ошибка атаки');
            if (btn) { btn.disabled = false; btn.textContent = '⚔ Выбери врага в списке'; btn.style.opacity = '1'; }
        }
    } catch (e) {
        showToast('Ошибка сети');
        if (btn) { btn.disabled = false; btn.textContent = '⚔ Выбери врага в списке'; btn.style.opacity = '1'; }
    }
}
window.clanWarAttack = clanWarAttack;
window.clanFindWarOpponent = clanFindWarOpponent;
window.loadClanWars = loadClanWars;

async function loadTradeMarket() {
    const el = $('clan-trade-offers');
    if (!el) return;
    el.innerHTML = '<div class="empty-state">⏳ Загрузка...</div>';
    try {
        const uid = effectiveUid();
        const res = await api(`/api/clan_trade_offers?user_id=${uid}`);
        if (!res.offers || !res.offers.length) {
            el.innerHTML = '<div class="empty-state">📦 Нет активных предложений</div>';
            return;
        }
        const myUid = String(uid);
        el.innerHTML = res.offers.map(t => {
            const req = t.request_coins > 0
                ? `${t.request_coins.toLocaleString('ru-RU')} 💰`
                : `🃏 ${esc(t.request_card_name || '?')}`;
            const isMe = String(t.from_uid) === myUid;
            const actionBtn = isMe
                ? `<button class="action-pill" style="background:rgba(255,60,60,0.15);color:#ff4444;padding:8px 16px;width:100%" onclick="clanCancelTrade('${t.offer_id}')">✕ Отменить</button>`
                : `<button class="action-pill accent-btn full-pill" onclick="clanAcceptTrade('${t.offer_id}')">✅ Принять обмен</button>`;
                
            const c = t.card || {id: t.card_id, name: t.card_name};
            const col = _rarityColors[c.rank] || '#1A1A1A';
            return `<div class="bento-card" style="padding:12px;margin-bottom:12px;display:flex;gap:16px;background:rgba(255,255,255,0.02)">
                <div class="card-item" style="width:120px;flex-shrink:0;background:#1a1a1a;border-radius:8px;overflow:hidden;border:1px solid rgba(255,255,255,0.05)" onclick="openCardModal('${c.id}')">
                    <div class="card-img-wrap" style="height:150px;display:flex;align-items:center;justify-content:center;background:#2a2a2a">
                        ${c.image_url ? renderMedia(c) : '<div style="font-size:40px">🃏</div>'}
                    </div>
                    <div class="card-rank-bar" style="background:${col}"></div>
                    <div class="card-info" style="padding:8px">
                        <div style="font-weight:900;font-style:italic;font-size:11px;line-height:1.2;text-transform:uppercase;letter-spacing:-0.5px;margin-bottom:4px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(c.name || '—')}</div>
                        <div style="font-size:9px;font-weight:700;text-transform:uppercase;margin-bottom:6px;color:${col}">${c.rank || ''}</div>
                        <div style="display:flex;gap:4px;font-size:9px;font-weight:900">
                            <span style="background:#FFD54F;border:1px solid #1A1A1A;padding:2px 4px;border-radius:4px;color:#000">⚔ ${c.damage || 0}</span>
                            <span style="background:#FF77B0;border:1px solid #1A1A1A;padding:2px 4px;border-radius:4px;color:#fff">❤ ${c.health || 0}</span>
                        </div>
                    </div>
                </div>
                <div style="flex:1;display:flex;flex-direction:column;justify-content:space-between">
                    <div>
                        <div style="font-size:11px;opacity:0.5;margin-bottom:2px">${t.time || ''}</div>
                        <div style="font-size:12px;color:rgba(255,255,255,0.7);margin-bottom:8px">От: <b>${esc(t.seller_name)}</b></div>
                        <div style="font-size:12px;font-weight:700;color:var(--accent);margin-bottom:2px">Хочет взамен:</div>
                        <div style="font-size:15px;font-weight:800;color:#fff">${req}</div>
                    </div>
                    <div style="display:flex;justify-content:flex-end">
                        ${actionBtn}
                    </div>
                </div>
            </div>`;
        }).join('');
    } catch(e) { el.innerHTML = '<div class="empty-state">Ошибка загрузки</div>'; }
}


async function loadClanStats() {
    const c = S.clan || {};
    const donors = c.donors || {};
    
    setText('cs-arena-wins', c.arena_wins || 0);
    setText('cs-war-wins', c.war_wins || 0);
    setText('cs-treasury', (c.bank || 0).toLocaleString('ru-RU'));
    setText('cs-trades', c.total_trades || 0);
    setText('cs-messages', c.total_messages || 0);
    setText('cs-age', c.date || '—');
    
    const donorsEl = $('cs-top-donors');
    if (donorsEl) {
        const sorted = Object.entries(donors).sort(([,a],[,b]) => b - a).slice(0, 5);
        if (sorted.length) {
            const mems = S.clan?.members || [];
            donorsEl.innerHTML = sorted.map(([uid, amt], i) => {
                const m = mems.find(x => String(x.id) === String(uid));
                return `<div class="clan-member${m?.theme_url ? ' has-theme' : ''}" onclick="openPlayerProfile('${uid}')">
                    ${m ? rowThemeBgHtml(m, 'cm-bg') : ''}
                    <div class="cm-fg">
                        <div class="cm-rank">${i + 1}</div>
                        <div class="cm-avatar">${userAvatarHtml(m?.avatar, m?.nickname || uid, '', uid)}</div>
                        <div class="cm-body">
                            <div class="cm-name">${esc(m?.nickname || uid)}</div>
                        </div>
                        <div class="cm-pts" style="color:var(--accent-hi)">+${amt.toLocaleString('ru-RU')}</div>
                    </div>
                </div>`;
            }).join('');
        } else {
            donorsEl.innerHTML = '<div class="empty-state">Нет данных</div>';
        }
    }
    
    const rewardsEl = $('clan-season-rewards');
    if (rewardsEl) rewardsEl.innerHTML = '<div class="empty-state">Сезонные награды появятся скоро</div>';
}

function renderClanManage() {
    const el = $('clan-manage-content'); if (!el) return;
    const clan = S.clan || {};
    const role = clan.user_role || 'member';
    const isOwner = role === 'creator';
    const isOfficer = isOwner || role === 'vice-creator';

    if (!isOfficer) {
        el.innerHTML = '<div class="empty-state">⛔ Только офицеры клана имеют доступ к управлению</div>';
        return;
    }

    const vices = new Set((clan.vices || []).map(String));
    const seen = new Set();
    const members = (clan.members || []).filter(m => {
        const id = String(m.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
    });

    const roleLabel = (m) => {
        if (m.is_owner || m.role === 'creator') return { text: 'Лидер', cls: 'leader' };
        if (m.role === 'vice-creator' || vices.has(String(m.id))) return { text: 'Офицер', cls: 'vice' };
        return { text: 'Участник', cls: '' };
    };

    const memberRows = members.map(m => {
        const rl = roleLabel(m);
        const canKick = !m.is_owner && m.role !== 'creator';
        const isVice = rl.cls === 'vice';
        const promoteBtn = isOwner && canKick
            ? (isVice
                ? `<button type="button" class="cm-act demote" onclick="event.stopPropagation();clanAdminPromote('${m.id}','demote')">↓ Разжаловать</button>`
                : `<button type="button" class="cm-act promote" onclick="event.stopPropagation();clanAdminPromote('${m.id}','promote')">↑ Офицер</button>`)
            : '';
        const kickBtn = canKick && isOfficer
            ? `<button type="button" class="cm-act kick" onclick="event.stopPropagation();clanAdminKick('${m.id}','${esc(m.nickname)}')">✕ Кик</button>`
            : '';
        return `<div class="clan-admin-row">
            <button type="button" class="clan-admin-main" onclick="openPlayerProfile('${m.id}')">
                <div class="cm-avatar ${rl.cls}">${userAvatarHtml(m.avatar, m.nickname, '', m.id)}</div>
                <div class="cm-body">
                    <div class="cm-name" title="${esc(m.nickname)}">${esc(m.nickname)}</div>
                    <div class="cm-role">${rl.text} · ${(Number(m.pts) || 0).toLocaleString('ru-RU')} PTS</div>
                </div>
            </button>
            <div class="clan-admin-acts">${promoteBtn}${kickBtn}</div>
        </div>`;
    }).join('');

    let html = `<div class="bento-card">
        <div class="section-label">УЧАСТНИКИ (${members.length}/${clan.max_members || 10})</div>
        <div class="clan-admin-list">${memberRows || '<div class="empty-state">Нет участников</div>'}</div>
    </div>`;

    if (isOwner) {
        html += `<button class="action-btn-large" style="margin-top:12px;background:rgba(255,255,255,0.07)" onclick="openClanSettings()">⚙ НАСТРОЙКИ КЛАНА</button>`;
    }

    el.innerHTML = html;
}

function openClanSettings() {
    const form = $('clan-settings-form');
    if (!form) return;
    const c = S.clan || {};
    const nameInp = $('clan-settings-name');
    const descInp = $('clan-settings-desc');
    const typeInp = $('clan-settings-type');
    if (nameInp) nameInp.value = c.name || '';
    if (descInp) descInp.value = c.description || '';
    if (typeInp) typeInp.value = c.type || 'open';
    form.style.display = 'block';
    form.scrollIntoView({ behavior: 'smooth' });
}

async function clanAdminKick(targetUid, targetName) {
    haptic('heavy');
    if (!confirm(`Кикнуть ${targetName} из клана?`)) return;
    try {
        const res = await api('/api/clan_admin_kick', { method: 'POST', body: { user_id: effectiveUid(), target_uid: targetUid } });
        if (res.success) {
            showToast(`✅ ${targetName} кикнут`);
            await loadClan(); // full refetch
            renderClanManage();
        } else {
            showToast(res.error || 'Ошибка');
        }
    } catch (e) { showToast('Ошибка сети'); }
}

async function clanAdminPromote(targetUid, action) {
    haptic('medium');
    try {
        const res = await api('/api/clan_admin_promote', { method: 'POST', body: { user_id: effectiveUid(), target_uid: targetUid, action } });
        if (res.success) {
            showToast(action === 'promote' ? '⭐ Повышен до офицера' : '↓ Разжалован');
            await loadClan();
            renderClanManage();
        } else {
            showToast(res.error || 'Ошибка');
        }
    } catch (e) { showToast('Ошибка сети'); }
}

async function clanSettingsSave() {
    const btn = $('clan-settings-save-btn');
    const name = ($('clan-settings-name')?.value || '').trim();
    const desc = ($('clan-settings-desc')?.value || '').trim();
    const type = $('clan-settings-type')?.value || 'open';
    if (!name) { showToast('Введите название'); return; }
    if (btn) { btn.disabled = true; btn.textContent = '⏳ Сохранение...'; }
    try {
        const res = await api('/api/clan_settings_save', { method: 'POST', body: { user_id: effectiveUid(), name, description: desc, type } });
        if (res.success) {
            showToast('✅ Настройки сохранены');
            $('clan-settings-form').style.display = 'none';
            await loadClan();
            renderClanManage();
        } else {
            showToast(res.error || 'Ошибка');
        }
    } catch (e) { showToast('Ошибка сети'); }
    finally { if (btn) { btn.disabled = false; btn.textContent = '💾 СОХРАНИТЬ'; } }
}

async function clanExchange(amount) {
    // Backward compatibility for cached markup that still calls clanExchange(1).
    // The actual exchange is now a standalone shop screen and no longer requires a clan.
    if ($('tab-exchange')) {
        openExchangeDen();
        return;
    }
    haptic('medium');
    if (!amount) {
        const inp = prompt('Сколько ReStars обменять? (1 ReStar = 500 Рекоинов)');
        amount = parseInt(inp);
        if (!amount || amount <= 0) return;
    }
    try {
        const res = await api('/api/clan_exchange', { method: 'POST', body: { user_id: effectiveUid(), amount } });
        if (res.success) {
            showToast(`✅ +${res.gained.toLocaleString('ru-RU')} Рекоинов!`);
            if (S.user) { S.user.restars = res.new_restars; S.user.coins = res.new_recoin; updateHeaderStats(); }
        } else {
            showToast(res.error || 'Ошибка обмена');
        }
    } catch (e) { showToast('Ошибка сети'); }
}

// ── Actions ───────────────────────────────────────────

async function buyStarsPack(stars) {
    haptic('medium');
    const uid = effectiveUid();
    if (!uid) { showToast('Открой через Telegram'); return; }
    if (!IS_TG || !tg?.openInvoice) {
        showToast('Оплата Stars только в Telegram');
        return;
    }
    try {
        showLoader(true);
        const res = await api('/api/stars_invoice', {
            method: 'POST',
            body: { user_id: uid, stars: Number(stars) },
        });
        showLoader(false);
        if (!res?.invoice_link) {
            showToast(res?.error || 'Не удалось создать счёт');
            return;
        }
        tg.openInvoice(res.invoice_link, async (status) => {
            if (status === 'paid') {
                haptic('success');
                showToast(`✓ +${res.restars || ''} ReStars`);
                try { await loadAll(); } catch (_) {}
            } else if (status === 'cancelled') {
                showToast('Оплата отменена');
            } else if (status === 'failed') {
                showToast('Оплата не прошла');
                haptic('error');
            }
        });
    } catch (e) {
        showLoader(false);
        console.error('[STARS]', e);
        showToast('Ошибка сети');
        haptic('error');
    }
}
window.buyStarsPack = buyStarsPack;

function showSubscribeGate(url) {
    const link = url || 'https://t.me/reversebot_channel';
    let box = $('subscribe-gate');
    if (!box) {
        box = document.createElement('div');
        box.id = 'subscribe-gate';
        box.style.cssText = 'position:fixed;inset:0;z-index:6000;display:flex;align-items:center;justify-content:center;background:rgba(0,0,0,.72);backdrop-filter:blur(6px);padding:20px';
        document.body.appendChild(box);
    }
    box.innerHTML = `
      <div style="background:var(--bg-card,#161616);border:1px solid rgba(255,193,7,.35);border-radius:20px;padding:26px 22px;max-width:340px;width:100%;text-align:center">
        <div style="font-size:40px;margin-bottom:10px">🎁</div>
        <div style="font-size:17px;font-weight:900;margin-bottom:8px">Подпишись — получи бонус</div>
        <div style="font-size:13px;color:var(--text-sub);margin-bottom:18px">Подпишись на канал бота: там бонусы, розыгрыши и апдейты.<br><b style="color:var(--gold,#ffc107)">В подарок за подписку — крутки!</b></div>
        <button id="sub-gate-open" class="casino-main-btn" style="width:100%;margin-bottom:10px">📢 Подписаться</button>
        <button id="sub-gate-check" class="qb-btn" style="width:100%;margin-bottom:10px">✅ Я подписался</button>
        <button id="sub-gate-close" class="qb-btn" style="width:100%;opacity:.7">Позже</button>
      </div>`;
    box.style.display = 'flex';
    $('sub-gate-open').onclick = () => {
        haptic('light');
        if (tg && IS_TG) { try { tg.openTelegramLink(link); return; } catch (_) {} }
        window.open(link, '_blank');
    };
    $('sub-gate-check').onclick = async () => {
        haptic('medium');
        box.style.display = 'none';
        await botAction('open_card');
    };
    $('sub-gate-close').onclick = () => { box.style.display = 'none'; };
}

function botAction(actionId, opts = {}) {
    return webappActionGuard.run(`webapp:${String(actionId || '')}`, () => runBotAction(actionId, opts));
}

async function runBotAction(actionId, opts = {}) {
    const quiet = !!opts.quiet;
    if (!quiet) haptic('medium');
    const uid = effectiveUid();
    if (!uid) { showToast('◈ ОТКРОЙ ЧЕРЕЗ TELEGRAM'); return; }
    if (actionId === 'collection') { switchTab('cards'); return; }
    if (actionId === 'arena') { switchTab('arena'); return; }
    if (actionId === 'clan') { switchTab('clan'); return; }
    if (actionId.startsWith('buy_stars_')) {
        const stars = parseInt(actionId.replace('buy_stars_', ''), 10);
        return buyStarsPack(stars);
    }
    if (actionId === 'open_card') {
        warmPackOpening();
        _homeOpeningBusy = true;
        _homeOpeningError = '';
        renderHomeOpening();
    }
    try {
        const res = await api('/api/webapp_action', {
            method: 'POST', body: { user_id: uid, action: actionId, init_data: tg?.initData || '' }
        });
        if (!res.success) {
            if (res.error === 'need_subscribe') {
                if (actionId === 'open_card') _homeOpeningError = 'Подпишись на канал, чтобы открыть карту';
                showSubscribeGate(res.subscribe_url);
                haptic('error');
                return;
            }
            const cooldown = Number(res.cooldown_remaining || 0);
            if (actionId === 'open_card' && cooldown > 0) {
                showCardCooldownSheet(cooldown);
                haptic('error');
                return;
            }
            const message = cooldown
                ? `⌛ Бесплатная карта через ${_fmtSec(cooldown)}`
                : (res.error || 'ОШИБКА');
            if (actionId === 'open_card') _homeOpeningError = 'Не удалось открыть. Попробуй ещё раз';
            showToast('⚠ ' + message);
            haptic('error');
            return;
        }
        if (actionId === 'open_card') {
            if (res.spins != null && S.user) {
                S.user.spins = Number(res.spins) || 0;
                setText('h-spins', S.user.spins);
                setText('p-spins', S.user.spins);
            }
            if (res.card) {
                const exists = S.cards.some(x => String(x.id) === String(res.card.id));
                if (!exists) S.cards.unshift(res.card);
                else {
                    S.cards = S.cards.filter(x => String(x.id) !== String(res.card.id));
                    S.cards.unshift(res.card);
                }
                openCardModal(res.card.id, true);
            }
            if (res.sub_bonus_granted) showToast(`✅ Подписка подтверждена! 🎁 +${res.sub_bonus_granted} крутки`);
            else if (res.used_free_drop && !quiet) showToast('🃏 Бесплатная карта получена');
            // Пока модалка открыта — не трогаем DOM меню (это роняло Telegram).
            if ($('card-modal')?.classList.contains('open')) _pendingSoftRefresh = true;
            else _softRefreshAfterOpen();
        } else if (actionId === 'arena_join_queue') {
            haptic('light');
            if (tg && IS_TG) {
                try { tg.sendData('⚔️ Арена'); } catch (e) {}
                tg.close();
                return;
            }
            showToast('Доступно только в Telegram');
        } else if (actionId === 'auto_team') {
            const t = await api(`/api/user_team?user_id=${uid}`);
            S.team = t || S.team; renderTeam();
        } else if (actionId === 'exchange_restars') {
            showToast(res.message || '✓ ОБМЕНЯНО');
            await loadAll();
        }
    } catch (e) {
        if (actionId === 'open_card') _homeOpeningError = 'Не удалось открыть. Попробуй ещё раз';
        const message = String(e?.message || 'Ошибка соединения').replace(/^HTTP \d+\s*/i, '');
        showToast('⚠ ' + message.slice(0, 90));
        haptic('error');
    } finally {
        if (actionId === 'open_card') {
            _homeOpeningBusy = false;
            renderHomeOpening();
        }
    }
}
window.botAction = botAction;

function _softRefreshAfterOpen() {
    const uid = effectiveUid();
    if (!uid) return;
    const run = async () => {
        try {
            // Не перерисовываем хаб, пока висит reveal — иначе чёрный экран в TG.
            if ($('card-modal')?.classList.contains('open')) {
                _pendingSoftRefresh = true;
                return;
            }
            const stats = await api(`/api/user_stats?user_id=${uid}`);
            if (stats && !stats.error) {
                S.user = stats;
                setText('h-spins', S.user.spins || 0);
                setText('p-spins', S.user.spins || 0);
                renderHomeOpening();
            }
            const cards = await api(`/api/user_cards?user_id=${uid}`);
            if (Array.isArray(cards)) {
                S.cards = cards;
                if (S.tab === 'cards') renderCards();
            }
        } catch { /* ignore background refresh */ }
    };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(() => run(), { timeout: 1800 });
    else setTimeout(run, 600);
}

let _pendingSoftRefresh = false;
let _revealBusy = false;
let _cardModalReturnFocus: HTMLElement | null = null;
let _cardLayoutObserver: ResizeObserver | null = null;
let _packRevealAbort: AbortController | null = null;
const CARD_PACK_POSTER = 'assets/packs/reverse-pack-opening-311.webp';

function waitForRevealAnimation(element: HTMLElement, name: string, timeoutMs: number, signal: AbortSignal): Promise<void> {
    return new Promise(resolve => {
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            clearTimeout(limit);
            element.removeEventListener('animationend', onEnd);
            signal.removeEventListener('abort', finish);
            resolve();
        };
        const onEnd = (event: AnimationEvent) => {
            if (event.target === element && event.animationName === name) finish();
        };
        const limit = setTimeout(finish, timeoutMs);
        element.addEventListener('animationend', onEnd);
        signal.addEventListener('abort', finish, { once: true });
    });
}

function waitForPackRetry(opening: HTMLElement, signal: AbortSignal): Promise<boolean> {
    return new Promise(resolve => {
        const retry = opening.querySelector<HTMLButtonElement>('[data-pack-retry]');
        const skip = opening.querySelector<HTMLButtonElement>('[data-pack-skip]');
        const panel = opening.querySelector<HTMLElement>('.cr-pack-retry');
        const finish = (again: boolean) => {
            retry?.removeEventListener('click', onRetry);
            skip?.removeEventListener('click', onSkip);
            signal.removeEventListener('abort', onSkip);
            if (panel) panel.hidden = true;
            resolve(again);
        };
        const onRetry = () => finish(true);
        const onSkip = () => finish(false);
        if (signal.aborted) return finish(false);
        opening.classList.remove('is-finishing', 'pack-video-fallback');
        opening.dataset.playback = 'error';
        if (panel) panel.hidden = false;
        retry?.addEventListener('click', onRetry);
        skip?.addEventListener('click', onSkip);
        signal.addEventListener('abort', onSkip, { once: true });
        retry?.focus({ preventScroll: true });
    });
}

async function toggleSlot(slot) {
    haptic('medium');
    try {
        const uid = effectiveUid();
        const existing = S.team[slot - 1];
        await api('/api/webapp_action', { method: 'POST', body: { user_id: uid, action: existing ? 'team_clear' : 'team_fill', slot, init_data: tg.initData || '' } });
        const t = await api(`/api/user_team?user_id=${uid}`);
        S.team = t || S.team; renderTeam();
        showToast(existing ? `◈ СЛОТ ${slot} ОЧИЩЕН` : `✓ СЛОТ ${slot} ЗАПОЛНЕН`);
    } catch { showToast('⚠ ИСПОЛЬЗУЙ АВТО-ПОДБОР'); }
}

async function claimTask(id) {
    haptic('medium');
    try {
        const uid = effectiveUid();
        await api('/api/webapp_action', { method: 'POST', body: { user_id: uid, action: 'task_claim', task_id: id, init_data: tg.initData || '' } });
        showToast('✓ НАГРАДА ПОЛУЧЕНА');
        const [tasks, user] = await Promise.all([api(`/api/user_tasks?user_id=${uid}`), api(`/api/user_stats?user_id=${uid}`)]);
        S.tasks = tasks || S.tasks; S.user = user || S.user;
        renderTasks(); renderShop(); renderProfile();
    } catch { showToast('⚠ ОШИБКА'); }
}

// ── Card Modal ────────────────────────────────────────
const CARD_BACK_URL = 'assets/card-back.png';

const CARD_RANK_COLOR = {
    Обычный: '#94A3B8',
    Редкий: '#38BDF8',
    Эпический: '#C084FC',
    Легендарный: '#FBBF24',
    Мифический: '#FB7185',
};

const CARD_RANK_DROP_LABEL = {
    Обычный: 'обычная',
    Редкий: 'редкая',
    Эпический: 'эпическая',
    Легендарный: 'легендарная',
    Мифический: 'мифическая',
};

const CARD_FAVORITE_ICON = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m12 3 2.8 5.7 6.2.9-4.5 4.4 1.1 6.2-5.6-3-5.6 3 1.1-6.2L3 9.6l6.2-.9Z"/></svg>';

function cardOpenAgainLabel(spins) {
    return '<span>Крутить ещё</span>';
}

function _spinWord(value) {
    const n = Math.abs(Math.trunc(Number(value) || 0));
    const mod100 = n % 100;
    const mod10 = n % 10;
    if (mod100 >= 11 && mod100 <= 14) return 'круток';
    if (mod10 === 1) return 'крутка';
    if (mod10 >= 2 && mod10 <= 4) return 'крутки';
    return 'круток';
}

function _ownedCardsInVerse(card) {
    const verse = String(card?.verce || '');
    if (!verse) return S.cards.length;
    return S.cards.filter(item => String(item?.verce || '') === verse).length;
}

async function _hydrateRevealCollectionProgress(card) {
    const node = $('cr-collection-progress');
    if (!node) return;
    const fallbackOwned = _ownedCardsInVerse(card);
    node.textContent = `В коллекции ${fallbackOwned}`;
    try {
        const uid = effectiveUid();
        const query = uid ? `?user_id=${encodeURIComponent(uid)}` : '';
        const data = await api(`/api/universes${query}`, { allowStale: false });
        const verse = String(card?.verce || '');
        const item = Array.isArray(data?.list) ? data.list.find(entry => String(entry?.name || '') === verse) : null;
        if (!node.isConnected || !item) return;
        const owned = Number.isFinite(Number(item.owned_cards)) ? Number(item.owned_cards) : fallbackOwned;
        const total = Number.isFinite(Number(item.total_cards)) ? Number(item.total_cards) : 0;
        node.textContent = total > 0 ? `В коллекции ${owned} из ${total}` : `В коллекции ${owned}`;
    } catch { /* progress is optional */ }
}

function openCardCollection() {
    closeModal();
    switchTab('cards');
}
window.openCardCollection = openCardCollection;

function _fmtStat(v) {
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v ?? 0);
    return Math.round(n).toLocaleString('ru-RU');
}

function _cardMediaSrc(c) {
    let src = c?.image_url || '';
    if (src && !src.startsWith('http') && !src.startsWith('blob:')) {
        src = `${API_BASE}${src.startsWith('/') ? '' : '/'}${src}`;
    }
    return src;
}

function _cardIsVideo(c) {
    if (!c) return false;
    if (c.type === 'animation' || c.type === 'video') return true;
    // В REVERSE все мифики — анимации (Telegram animation / mp4)
    if (c.rank === 'Мифический') return true;
    const path = String(c.image_url || '').split('?')[0];
    const ext = path.split('.').pop().toLowerCase();
    return ext === 'mp4' || ext === 'webm' || ext === 'mov';
}

async function openAnotherCard() {
    if (_revealBusy) return;
    const btn = document.querySelector('.cr-open-again');
    if (btn) {
        if (btn.disabled) return;
        btn.disabled = true;
        btn.textContent = 'Открываем…';
    }
    haptic('medium');
    try {
        await botAction('open_card', { quiet: true });
    } catch (e) {
        console.error(e);
        if (btn) {
            btn.disabled = false;
            const n = Number(S.user?.spins || 0);
            btn.innerHTML = cardOpenAgainLabel(n);
        }
        _revealBusy = false;
    }
}
window.openAnotherCard = openAnotherCard;

const _cardSrcCache = new Map();

function _withTimeout(promise, ms, fallback) {
    let t;
    return Promise.race([
        Promise.resolve(promise).finally(() => clearTimeout(t)),
        new Promise((resolve) => { t = setTimeout(() => resolve(fallback), ms); }),
    ]);
}

async function _preloadCardSrc(src) {
    if (!src) return src;
    if (src.startsWith('blob:') || src.startsWith('data:')) return src;
    if (_cardSrcCache.has(src)) return _cardSrcCache.get(src);
    try {
        await _withTimeout(new Promise((resolve, reject) => {
            const img = new Image();
            img.decoding = 'async';
            img.onload = () => resolve();
            img.onerror = () => reject(new Error('img'));
            img.src = src;
        }), 2500, null);
        _cardSrcCache.set(src, src);
        return src;
    } catch {
        return src;
    }
}

function _buildCardMedia(c, src) {
    const safe = esc(src || '');
    if (_cardIsVideo(c)) {
        // Сразу показываем лицо; mp4/gif подгружается без блокировки flip.
        const poster = esc(arenaStoredPreviewSrc(c?.arena_preview_url) || CARD_BACK_URL);
        return `<video class="cr-media" src="${safe}" poster="${poster}"
            autoplay loop muted playsinline webkit-playsinline
            onerror="this.replaceWith(Object.assign(document.createElement('img'),{className:'cr-media',src:'${safe}',alt:''}))"></video>`;
    }
    return `<img class="cr-media" src="${safe}" alt="" decoding="async">`;
}

function openCardModal(cardId, isNew = false) {
    const c = S.cards.find(x => String(x.id) === String(cardId));
    if (!c) return;

    if (isNew && _revealBusy) return;
    if (isNew) _revealBusy = true;

    try {
        _openCardModalInner(c, isNew);
    } catch (e) {
        console.error('[CARD MODAL]', e);
        _revealBusy = false;
        showToast('⚠ Ошибка показа карты');
    }
}

function _openCardModalInner(c, isNew) {
    ensureMyFavorites().then(() => {
        const btn = document.querySelector('.cr-fav-btn');
        if (!btn) return;
        const on = (S.myFavoriteIds || []).map(String).includes(String(c.id));
        btn.classList.toggle('active', on);
        btn.setAttribute('aria-label', on ? 'В избранном' : 'В избранное');
        btn.setAttribute('aria-pressed', String(on));
        btn.innerHTML = CARD_FAVORITE_ICON;
    }).catch(() => {});

    const col = CARD_RANK_COLOR[c.rank] || '#FF4D6A';
    const favIds = new Set((S.myFavoriteIds || []).map(String));
    const isFav = favIds.has(String(c.id));
    const spinsLeft = Number(S.user?.spins || 0);
    const showOpenAgain = isNew && spinsLeft > 0;
    const dmg = _fmtStat(c.damage);
    const hp = _fmtStat(c.health);
    const pts = _fmtStat(c.rarity);
    const rawSrc = _cardMediaSrc(c);

    const buildFront = (src) => _buildCardMedia(c, src || rawSrc) + `<span class="cr-drop-caption">
        <strong>${esc(c.name || 'Карта')}</strong>
        <span class="cr-drop-verse">${esc(c.verce || c.verse || c.universe || '')}</span>
        <span class="cr-drop-stats"><span><small>Урон</small><b>${dmg}</b></span><span><small>HP</small><b>${hp}</b></span><span><small>Очки</small><b>${pts}</b></span></span>
    </span>`;
    const dropRank = CARD_RANK_DROP_LABEL[c.rank] || String(c.rank || 'карта').toLocaleLowerCase('ru');
    const headerHtml = isNew
        ? ''
        : `<header class="cr-heading"><button class="cr-close" type="button" aria-label="Закрыть" onclick="closeModal()"><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 6 12 12M6 18 18 6"/></svg></button></header>`;
    const detailsHtml = isNew
        ? `<section class="cr-details cr-reveal-summary" id="cr-overlay" aria-label="Полученная карта">
             <p id="cr-collection-progress">В коллекции ${_ownedCardsInVerse(c)}</p>
           </section>`
        : `<section class="cr-details show cr-collection-summary" id="cr-overlay" aria-label="Прогресс коллекции">
             <p id="cr-collection-progress">В коллекции ${_ownedCardsInVerse(c)}</p>
           </section>`;

    const actionsHtml = isNew
        ? `<div class="cr-reveal-actions" id="cr-dock">
             <button class="cr-open-again" type="button" disabled onclick="event.stopPropagation();openAnotherCard()">${cardOpenAgainLabel(spinsLeft)}</button>
             <button class="cr-collection-btn" type="button" onclick="event.stopPropagation();openCardCollection()">В коллекцию</button>
             <p class="cr-spin-stock">В запасе <b>${spinsLeft}</b> ${_spinWord(spinsLeft)}</p>
           </div>`
        : `<div class="cr-dock" id="cr-dock">
             <button class="cr-fav-btn${isFav ? ' active' : ''}" type="button"
               aria-label="${isFav ? 'В избранном' : 'В избранное'}" aria-pressed="${isFav}"
               onclick="event.stopPropagation();toggleProfileFavorite('${encodeURIComponent(c.id)}', { fromModal: true })">
               ${CARD_FAVORITE_ICON}
             </button>
             <button class="cr-done" type="button" onclick="closeModal()">Готово</button>
           </div>`;

    const modal = $('card-modal');
    const sheet = modal?.querySelector('.modal-sheet');
    if (!modal) {
        _revealBusy = false;
        return;
    }

    // Всегда чистая пересборка — reuse + GPU flip роняли Telegram.
    if (!modal.classList.contains('open') && document.activeElement instanceof HTMLElement) {
        _cardModalReturnFocus = document.activeElement;
    }
    sheet?.setAttribute('aria-label', String(c.name || 'Карта'));
    modal.onkeydown = (event) => {
        if (event.key === 'Escape') { event.preventDefault(); closeModal(); return; }
        if (event.key !== 'Tab') return;
        const controls = [...modal.querySelectorAll<HTMLButtonElement>('button:not([disabled])')].filter(button => button.getClientRects().length && getComputedStyle(button).visibility !== 'hidden');
        const first = controls[0], last = controls[controls.length - 1];
        if (!first) return;
        if (event.shiftKey && (document.activeElement === first || document.activeElement === sheet)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    modal.classList.add('open', 'reveal-mode');
    modal.classList.remove('stats-ready', 'card-in-flight', 'rarity-transition');
    modal.style.setProperty('--collection-rank', col);
    modal.classList.toggle('new-card-mode', isNew);
    if (sheet) sheet.classList.add('reveal-sheet');

    if (isNew) {
        suspendTabMedia(S.tab);
        suspendHubThemeMedia();
        _packRevealAbort?.abort();
        const revealController = new AbortController();
        _packRevealAbort = revealController;
        $('modal-content').innerHTML = `
        <div class="cr-screen cr-screen--drop cr-in" id="cr-screen">
          ${headerHtml}
          <div class="cr-stage">
            <div class="cr-card-wrap">
              <button type="button" class="cr-card" id="cr-card" style="--rank:${col}" aria-label="Рассмотреть карту ${esc(c.name || '')}"
                onclick="if(this.classList.contains('is-front'))openFullscreen('${esc(c.image_url || '')}', '${esc(c.type || '')}')"></button>
            </div>
          </div>
          ${detailsHtml}
          ${actionsHtml}
        </div>
        <div class="cr-pack-opening" id="cr-pack-opening" data-playback="loading">
          <video class="cr-pack-video" poster="${CARD_PACK_POSTER}" preload="auto" muted playsinline webkit-playsinline aria-hidden="true"></video>
          <img class="cr-pack-poster" width="540" height="960" alt="" decoding="async" aria-hidden="true">
          <p class="cr-pack-loading" role="status">Готовим анимацию…</p>
          <div class="cr-pack-retry" hidden><p>Анимация не загрузилась</p><button type="button" data-pack-retry>Повторить</button><button type="button" data-pack-skip>Показать карту</button></div>
        </div>`;
        void _hydrateRevealCollectionProgress(c);
        haptic('medium');

        const cardEl = $('cr-card');
        (async () => {
            try {
                const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches || false;
                const cardReady = (async () => {
                    if (!_cardIsVideo(c)) await _preloadCardSrc(rawSrc);
                    if (revealController.signal.aborted || !cardEl?.isConnected) return;
                    cardEl.innerHTML = buildFront(rawSrc);
                    cardEl.classList.add('is-front');
                    const still = cardEl.querySelector<HTMLImageElement>('img.cr-media');
                    if (still) await _withTimeout(still.decode().catch(() => {}), 1000, null);
                    // Load animated card media without decoding a second moving video during the tear.
                    cardEl.querySelector<HTMLVideoElement>('video.cr-media')?.pause();
                })();
                const opening = $('cr-pack-opening');
                const packVideo = opening?.querySelector<HTMLVideoElement>('.cr-pack-video');
                const packImage = opening?.querySelector<HTMLImageElement>('.cr-pack-poster');
                if (reduceMotion) opening?.remove();
                if (!reduceMotion && packVideo && packImage) {
                    const fadePack = () => {
                        modal.classList.add('rarity-transition');
                        opening?.classList.add('is-finishing');
                    };
                    let completed = false;
                    do {
                        completed = await playPackOpening(packVideo, packImage, revealController.signal, fadePack, status => {
                            if (!opening) return;
                            opening.dataset.playback = status;
                            opening.classList.toggle('pack-video-fallback', status === 'image');
                            if (status === 'loading' || status === 'image') {
                                opening.classList.remove('is-finishing');
                                modal.classList.remove('rarity-transition');
                            }
                        });
                    } while (!completed && !revealController.signal.aborted && await waitForPackRetry(opening!, revealController.signal));
                    if (revealController.signal.aborted) return;
                    if (completed) fadePack();
                }
                if (revealController.signal.aborted || !cardEl?.isConnected) return;
                await cardReady;
                if (revealController.signal.aborted || !cardEl?.isConnected) return;
                opening?.remove();
                packVideo?.pause();
                modal.classList.add('rarity-transition');
                if (!reduceMotion) {
                    modal.classList.add('card-in-flight');
                    haptic('heavy');
                    await waitForRevealAnimation(cardEl, 'cr-card-fly', 1200, revealController.signal);
                    if (revealController.signal.aborted) return;
                }
                cardEl.style.transform = 'none';
                cardEl.style.opacity = '1';
                modal.classList.add('stats-ready');
                modal.classList.remove('card-in-flight');
                $('cr-overlay')?.classList.add('show');
                const vid = cardEl?.querySelector('video.cr-media');
                if (vid) {
                    try { vid.muted = true; vid.playsInline = true; await vid.play(); } catch { /* autoplay block */ }
                }
                haptic('success');
            } catch (e) {
                console.error('[CARD REVEAL]', e);
                if (!revealController.signal.aborted && cardEl?.isConnected) {
                    $('cr-pack-opening')?.remove();
                    cardEl.innerHTML = buildFront(rawSrc);
                    cardEl.classList.add('is-front');
                    cardEl.style.transform = 'none';
                    cardEl.style.opacity = '1';
                    modal.classList.add('stats-ready');
                    modal.classList.remove('card-in-flight');
                    $('cr-overlay')?.classList.add('show');
                }
            } finally {
                if (_packRevealAbort === revealController) {
                    _packRevealAbort = null;
                    _revealBusy = false;
                    cardEl?.closest('.cr-screen')?.classList.add('is-revealed');
                    const again = document.querySelector('.cr-open-again');
                    if (again) {
                        const n = Number(S.user?.spins || 0);
                        again.disabled = n <= 0;
                        again.innerHTML = cardOpenAgainLabel(n);
                        const stock = document.querySelector('.cr-spin-stock');
                        if (stock) stock.innerHTML = `В запасе <b>${Math.max(0, n)}</b> ${_spinWord(n)}`;
                    }
                }
            }
        })();
    } else {
        _revealBusy = false;
        $('modal-content').innerHTML = `
        <div class="cr-screen cr-screen--collection cr-in is-revealed" id="cr-screen">
          ${headerHtml}
          <div class="cr-stage">
            <div class="cr-card-wrap">
              <button type="button" class="cr-card is-front" id="cr-card" style="--rank:${col};transform:none" aria-label="Рассмотреть карту ${esc(c.name || '')}"
                onclick="openFullscreen('${esc(c.image_url || '')}', '${esc(c.type || '')}')">
                ${buildFront(rawSrc).replace('class="cr-overlay"', 'class="cr-overlay show"')}
              </button>
            </div>
          </div>
          ${detailsHtml}
          ${actionsHtml}
        </div>`;
        void _hydrateRevealCollectionProgress(c);
        haptic('light');
    }
    _cardLayoutObserver?.disconnect();
    const stage = modal.querySelector<HTMLElement>('.cr-stage');
    const sizeCard = () => {
        if (!stage?.isConnected) return;
        const card = stage.querySelector<HTMLElement>('.cr-card');
        if (!card) return;
        const width = Math.max(0, Math.min(stage.clientWidth, stage.clientHeight * .75, 420));
        card.style.width = `${width}px`;
        card.style.height = `${width / .75}px`;
    };
    if (stage) { _cardLayoutObserver = new ResizeObserver(sizeCard); _cardLayoutObserver.observe(stage); sizeCard(); }
    (sheet as HTMLElement)?.focus({ preventScroll: true });
}

function closeModal() {
    _packRevealAbort?.abort();
    _packRevealAbort = null;
    _revealBusy = false;
    _cardLayoutObserver?.disconnect();
    _cardLayoutObserver = null;
    const modal = $('card-modal');
    const wasNew = modal?.classList.contains('new-card-mode');
    modal?.querySelectorAll<HTMLVideoElement>('video').forEach(video => video.pause());
    modal?.querySelector('.cr-pack-opening')?.remove();
    modal?.classList.remove('open', 'reveal-mode', 'new-card-mode', 'stats-ready', 'card-in-flight', 'rarity-transition');
    modal?.querySelector('.modal-sheet')?.classList.remove('reveal-sheet');
    if (modal) modal.onkeydown = null;
    if (_cardModalReturnFocus?.isConnected) _cardModalReturnFocus.focus({ preventScroll: true });
    _cardModalReturnFocus = null;
    if (wasNew) {
        resumeTabMedia(S.tab);
        if (S.tab === 'profile') resumeHubThemeMedia();
    }
    if (_pendingSoftRefresh) {
        _pendingSoftRefresh = false;
        _softRefreshAfterOpen();
    }
}
window.closeModal = closeModal;

// ── Public Player Profile Modal ──────────────────────
const PROFILE_RCOLOR = { Обычный: '#94A3B8', Редкий: '#38BDF8', Эпический: '#C084FC', Легендарный: '#FBBF24', Мифический: '#FB7185' };
const CLAN_ROLE_LABEL = { creator: 'Лидер клана', 'vice-creator': 'Заместитель', elder: 'Старейшина', member: 'Участник клана' };

async function openPlayerProfile(uid) {
    if (!uid) return;
    haptic('light');
    const body = $('profile-screen-body');
    const screen = $('tab-player-profile');
    if (!body || !screen) return;
    if (S.tab !== 'player-profile') {
        S.profileReturnTab = S.tab || 'arena';
        S.profileReturnScroll = $('app-main')?.scrollTop || 0;
    }
    S.tab = 'player-profile';
    document.querySelectorAll('.tab').forEach(tab => tab.classList.remove('active'));
    document.querySelectorAll('.nav-btn').forEach(button => button.classList.remove('active'));
    screen.classList.add('active');
    $('app-container')?.classList.add('profile-view-open');
    const nav = $('bottom-nav');
    if (nav) nav.hidden = true;
    bindProfileBackButton(true);
    body.innerHTML = `<div class="profile-screen-loading" aria-label="Загрузка профиля">
        <span></span><span></span><span></span>
    </div>`;
    $('app-main')?.scrollTo({ top: 0, behavior: 'auto' });
    try {
        // Do not leave Telegram on an indefinite spinner when the public
        // profile endpoint is slow. The profile can be reopened to retry.
        const p = await api(`/api/public_profile?view=summary&user_id=${effectiveUid()}&target_id=${encodeURIComponent(uid)}`, {
            timeoutMs: 6500,
            retries: 0,
        });
        if (S.tab !== 'player-profile') return;
        if (!p || p.error) { body.innerHTML = '<div class="empty-state">Профиль недоступен</div>'; return; }
        if (String(p.uid) === String(effectiveUid())) {
            S.myFavoriteIds = (p.favorite_cards || []).map(c => String(c.id));
        }
        S.publicProfile = p;
        S.profileDetailsLoading = '';
        body.innerHTML = renderPlayerProfileHtml(p);
        requestAnimationFrame(() => {
            const duo = body.querySelector('.pp-duo');
            if (duo && duo.getBoundingClientRect().top > (window.innerHeight - 80)) {
                duo.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        });
    } catch (e) {
        if (S.tab === 'player-profile') {
            body.innerHTML = '<div class="empty-state">Профиль недоступен</div>';
        }
    }
}
window.openPlayerProfile = openPlayerProfile;

function closePlayerProfile() {
    bindProfileBackButton(false);
    const returnTab = S.profileReturnTab || 'arena';
    const returnScroll = S.profileReturnScroll || 0;
    switchTab(returnTab);
    requestAnimationFrame(() => $('app-main')?.scrollTo({ top: returnScroll, behavior: 'auto' }));
}
window.closePlayerProfile = closePlayerProfile;

function bindProfileBackButton(show) {
    try {
        if (!tg?.BackButton) return;
        tg.BackButton.offClick?.(closePlayerProfile);
        if (show) {
            tg.BackButton.onClick(closePlayerProfile);
            tg.BackButton.show();
        } else {
            tg.BackButton.hide();
        }
    } catch (_) {}
}

function renderPlayerProfileHtml(p) {
    const avatarHtml = userAvatarHtml('', p.name || '?', 'pp-avatar-img', p.uid);
    const isOwn = String(p.uid) === String(effectiveUid());
    const favoriteIds = new Set((p.favorite_cards || []).map(c => String(c.id)));
    const rankBadge = p.rank_verse
        ? `<div class="pp-cover-rank"><b>#${p.rank_verse}</b><span>${esc(p.universe || '')}</span></div>`
        : (p.universe && p.universe !== '?' ? `<div class="pp-cover-rank">${esc(p.universe)}</div>` : '');
    const themeMedia = p.theme?.media_url ? rowThemeBgHtml({ theme_url: p.theme.media_url, theme_type: p.theme.media_type }, 'pp-cover-media') : '';
    const hasHero = !!themeMedia;
    const heroMedia = themeMedia || '';

    const clanRow = p.clan
        ? `<div class="pp-clan-chip"><span class="pp-clan-dot"></span>${esc(p.clan.name)} · ${esc(CLAN_ROLE_LABEL[p.clan.role] || 'Участник')}</div>`
        : '';

    const topCards = p.top_cards || [];
    const loadedDetails = new Set(p.details_loaded || []);
    const progressLoaded = loadedDetails.has('progress');
    const tradesLoaded = loadedDetails.has('trades');
    const featuredCard = p.featured_card || topCards[0] || null;
    const featuredId = String(featuredCard?.id || '');
    const favoriteCards = [...(p.favorite_cards || [])].slice(0, 6);
    const favSlots = [...favoriteCards];
    while (favSlots.length < 6) favSlots.push(null);

    const showcase = `
        <div class="pp-duo">
            <div class="pp-duo-lead">
                <div class="pp-duo-label"><span>◆</span> Карты</div>
                <div class="pp-waifu-wrap">
                    <div class="pp-waifu-glow" style="--glow:${PROFILE_RCOLOR[featuredCard?.rank] || 'var(--accent)'}"></div>
                    ${profileCardHtml(featuredCard, 'pp-featured-card', isOwn, favoriteIds, '', featuredId)}
                </div>
            </div>
            <div class="pp-duo-side">
                <div class="pp-duo-label">
                    <span>★</span> Избранные
                    ${isOwn ? `<button class="pp-fav-manage" type="button" onclick="openFavoritePicker()">＋ Добавить</button>` : ''}
                </div>
                <div class="pp-favorites-grid">
                    ${favSlots.map(c => {
                        if (c) return profileCardHtml(c, 'pp-favorite-card', isOwn, favoriteIds, '', featuredId);
                        if (isOwn) {
                            return `<button class="pp-favorite-card pp-card-shell pp-fav-add" type="button" onclick="openFavoritePicker()" aria-label="Добавить в избранное">
                                <span class="pp-fav-add-ico">＋</span>
                                <small>Добавить</small>
                            </button>`;
                        }
                        return `<div class="pp-favorite-card pp-card-shell is-placeholder" aria-hidden="true"><div class="pp-card-placeholder"></div></div>`;
                    }).join('')}
                </div>
            </div>
        </div>`;

    const setsDone = (p.sets || []).filter(s => s.done).length;
    const setsHtml = progressLoaded && (p.sets || []).length
        ? `<div class="pp-sets-list">${p.sets.map(s => {
            const pct = s.total ? Math.round(s.owned / s.total * 100) : 0;
            const col = PROFILE_RCOLOR[s.rank] || 'var(--border-hot)';
            const cover = s.cover_url ? profileMediaHtml({ image_url: s.cover_url, type: s.cover_type }, 'pp-set-cover', false) : '';
            return `<div class="pp-set-row${s.done ? ' done' : ''}" style="--set-color:${col}">
                <div class="pp-set-art">${cover || '<div class="pp-set-placeholder"></div>'}<div class="pp-set-scrim"></div></div>
                <div class="pp-set-head"><span class="pp-set-name">${esc(s.rank)}</span><span class="pp-set-count">${s.owned}/${s.total}</span></div>
                <div class="pp-set-track"><div class="pp-set-fill" style="width:${pct}%;background:${col}"></div></div>
            </div>`;
        }).join('')}</div>`
        : (progressLoaded
            ? '<div class="pp-empty-premium"><b>Наборов пока нет</b></div>'
            : '<div class="pp-empty-premium"><b>Открой «Прогресс»</b><small>Наборы загружаются только по запросу</small></div>');

    const achUnlocked = (p.achievements || []).filter(a => a.unlocked).length;
    const achievements = progressLoaded && (p.achievements || []).length
        ? `<div class="pp-ach-grid">${p.achievements.map(a => `
            <div class="pp-ach ${a.unlocked ? 'unlocked' : 'locked'}" title="${esc(a.desc)}">
                <div class="pp-ach-icon">${a.icon}</div>
                <div class="pp-ach-label">${esc(a.label)}</div>
                ${a.unlocked ? '<div class="pp-ach-done">✓</div>' : `<div class="pp-ach-prog">${a.progress}/${a.goal}</div>`}
            </div>`).join('')}</div>`
        : (progressLoaded
            ? '<div class="pp-empty-premium"><b>Достижений пока нет</b></div>'
            : '<div class="pp-empty-premium"><b>Открой «Прогресс»</b><small>Достижения загружаются только по запросу</small></div>');

    const tradeOffers = p.trade_offers || [];
    const tradeBody = tradesLoaded && tradeOffers.length
        ? `<div class="pp-trade-rail">${tradeOffers.map(o => `
            <div class="pp-trade-item">
                ${profileCardHtml(o.offered, 'pp-trade-card', false, new Set(), '', '')}
                <span class="pp-trade-arrow">→</span>
                ${o.wanted
                    ? profileCardHtml(o.wanted, 'pp-trade-card', false, new Set(), '', '')
                    : `<div class="pp-trade-coins">${(o.coins || 0).toLocaleString('ru-RU')}<small>рекоинов</small></div>`}
            </div>`).join('')}
        </div>`
        : (tradesLoaded
            ? `<div class="pp-empty-premium"><b>Обменов пока нет</b><small>Активные предложения появятся здесь</small></div>`
            : '<div class="pp-empty-premium"><b>Загружаем обмены…</b><small>Только когда открыта эта вкладка</small></div>');

    return `
    <header class="pp-screen-bar">
        <button class="pp-screen-back" type="button" aria-label="Назад" onclick="closePlayerProfile()">‹</button>
        <div class="pp-screen-owner">
            <span class="pp-screen-avatar">${userAvatarHtml('', p.name || '?', '', p.uid)}</span>
            <b>${esc(p.name || 'Игрок')}</b>
        </div>
        <div class="pp-screen-actions">
            ${isOwn ? '' : `<button class="pp-screen-friend" type="button" onclick="profileAddFriend('${encodeURIComponent(p.uid)}')"><span>＋</span> В друзья</button>`}
            <button type="button" aria-label="Поделиться" onclick="sharePlayerProfile('${encodeURIComponent(p.uid)}','${encodeURIComponent(p.name || '')}')">↗</button>
        </div>
    </header>

    <div class="pp-summary-card">
        <div class="pp-cover${hasHero ? ' has-media' : ''}">
            ${heroMedia}
            <div class="pp-cover-shade"></div>
            ${rankBadge}
        </div>
        <div class="pp-identity">
            <div class="pp-avatar pp-rank-${profileRankTone(p.league)}">${avatarHtml}</div>
            <div class="pp-identity-copy">
                <div class="pp-name">${esc(p.name || '?')}${p.vip ? ' <span class="pp-vip">VIP</span>' : ''}</div>
                <div class="pp-badges"><span class="pp-league">${esc(p.league || '')}</span>${clanRow}</div>
            </div>
        </div>
        <div class="pp-chin">
            <div class="pp-chin-cell">
                <span class="pp-chin-ico">▱</span>
                <b class="pp-chin-num">${(p.cards_count || 0).toLocaleString('ru-RU')}</b>
            </div>
            <div class="pp-chin-cell accent">
                <span class="pp-chin-ico">✦</span>
                <b class="pp-chin-num">${(p.pts || 0).toLocaleString('ru-RU')}</b>
            </div>
        </div>
        <div class="pp-chin-meta">
            ${p.joined ? `<span>В игре с ${esc(p.joined)}</span>` : ''}
            <span>♜ ${(p.trophy || 0).toLocaleString('ru-RU')} · ${p.winrate || 0}% WR · ${p.wins || 0}W/${p.loses || 0}L</span>
        </div>
    </div>

    <div class="pp-profile-tabs" role="tablist">
        <button class="active" type="button" role="tab" aria-selected="true" data-profile-tab="basics" onclick="switchProfileSection('basics', this)">Основное</button>
        <button type="button" role="tab" aria-selected="false" data-profile-tab="progress" onclick="switchProfileSection('progress', this)">Прогресс</button>
        <button type="button" role="tab" aria-selected="false" data-profile-tab="trades" onclick="switchProfileSection('trades', this)">Обмены</button>
    </div>

    <div class="pp-content">
        <div class="pp-tab-panel active" data-profile-panel="basics">
            ${showcase}
            <button class="pp-trade-lists" type="button" onclick="switchProfileSection('trades')">
                <span class="pp-trade-lists-ico">⇄</span>
                <span class="pp-trade-lists-label">Обмены карт</span>
                <span class="pp-trade-lists-count">${tradesLoaded ? tradeOffers.length : '›'}</span>
                <span class="pp-trade-lists-chev">›</span>
            </button>
            <section class="pp-section pp-sets-preview">
                <div class="pp-section-head"><h2>Наборы карт</h2><span>${progressLoaded ? `${setsDone} собрано` : 'по запросу'}</span></div>
                ${setsHtml}
            </section>
        </div>
        <div class="pp-tab-panel" data-profile-panel="progress" hidden>
            <section class="pp-section">
                <div class="pp-section-head"><h2>Наборы карт</h2><span>${progressLoaded ? `${setsDone} собрано` : 'загрузка…'}</span></div>
                ${setsHtml}
            </section>
            <section class="pp-section">
                <div class="pp-section-head"><h2>Достижения</h2><span>${progressLoaded ? `${achUnlocked}/${(p.achievements || []).length}` : 'загрузка…'}</span></div>
                ${achievements}
            </section>
        </div>
        <div class="pp-tab-panel" data-profile-panel="trades" hidden>
            <section class="pp-section">
                <div class="pp-section-head">
                    <h2>Обмены карт</h2>
                    <span>${tradesLoaded ? tradeOffers.length : 'загрузка…'}</span>
                </div>
                ${tradeBody}
                ${tradesLoaded && tradeOffers.length ? `<button class="pp-open-clan-trade" type="button" onclick="closePlayerProfile();switchTab('clan');setTimeout(()=>switchClanTab('trade'),120)">Открыть в клане</button>` : ''}
            </section>
        </div>
    </div>`;
}

function profileRankTone(league = '') {
    const value = String(league).toLowerCase();
    if (value.includes('платин') || value.includes('алмаз') || value.includes('легенд')) return 'platinum';
    if (value.includes('бронз')) return 'bronze';
    return 'gold';
}

function profileStarsHtml(card) {
    if (!card) return '';
    const filled = Math.max(1, Math.min(5, rankStars(card.rank)));
    return `<span class="pp-card-stars" aria-label="${filled} из 5">${Array.from({ length: 5 }, (_, i) => `<i class="${i < filled ? 'on' : ''}">★</i>`).join('')}</span>`;
}

function profileCardHtml(card, className, isOwn, favoriteIds, placeholderLabel, featuredId = '') {
    if (!card) {
        return `<div class="${className} pp-card-shell is-placeholder" aria-hidden="true"><div class="pp-card-placeholder"></div></div>`;
    }
    const color = PROFILE_RCOLOR[card.rank] || 'var(--accent)';
    const active = favoriteIds.has(String(card.id));
    const isFeatured = String(card.id) === String(featuredId || '');
    const favoriteButton = isOwn
        ? `<button class="pp-favorite-toggle${active ? ' active' : ''}" type="button" aria-label="${active ? 'Убрать из избранного' : 'Добавить в избранное'}" onclick="event.stopPropagation();toggleProfileFavorite('${encodeURIComponent(card.id)}')">★</button>`
        : '';
    const featuredButton = isOwn
        ? `<button class="pp-featured-toggle${isFeatured ? ' active' : ''}" type="button" aria-label="${isFeatured ? 'Главная карта' : 'Сделать главной картой'}" onclick="event.stopPropagation();setProfileFeatured('${encodeURIComponent(card.id)}')" ${isFeatured ? 'disabled' : ''}>♛</button>`
        : '';
    return `<div class="${className} pp-card-shell" style="--card-rank:${color}" onclick="openFullscreen('${cssUrl(cardMediaUrl(card.image_url))}','${esc(card.type || '')}')">
        ${profileMediaHtml(card, 'pp-card-media', true)}
        <div class="pp-card-scrim"></div>
        ${profileStarsHtml(card)}
        ${favoriteButton}
        ${featuredButton}
        <div class="pp-card-caption"><b>${esc(card.name || '?')}</b><span>${esc(card.rank || '')}</span></div>
    </div>`;
}

function profileMediaHtml(card, className, lazy = true) {
    const src = cardMediaUrl(card?.image_url);
    if (!src) return '<div class="pp-card-placeholder"><span>R</span></div>';
    const ext = src.split('?')[0].split('.').pop().toLowerCase();
    const isVideo = ext === 'mp4' || card?.type === 'video' || card?.type === 'animation';
    if (isVideo) {
        return `<video class="${className}" ${lazy ? 'preload="metadata"' : 'preload="auto"'} autoplay loop muted playsinline src="${cssUrl(src)}" onerror="this.parentElement.classList.add('is-placeholder');this.remove()"></video>`;
    }
    return `<img class="${className}" src="${cssUrl(src)}" alt="" ${lazy ? 'loading="lazy"' : ''} decoding="async" onerror="this.parentElement.classList.add('is-placeholder');this.remove()">`;
}

function cardMediaUrl(u) {
    if (!u) return '';
    let s = String(u);
    if (!s.startsWith('http')) s = `${API_BASE}${s.startsWith('/') ? '' : '/'}${s}`;
    return s;
}

async function toggleProfileFavorite(encodedCardId, opts = {}) {
    const cardId = decodeURIComponent(encodedCardId || '');
    if (!cardId) return;
    try {
        const res = await api('/api/profile_favorite', {
            method: 'POST',
            body: { user_id: effectiveUid(), card_id: cardId },
        });
        S.myFavoriteIds = (res?.favorite_cards || []).map(String);
        haptic('success');
        showToast(res?.active ? '★ Добавлено в избранное' : 'Убрано из избранного');
        if (opts.fromModal) {
            const btn = document.querySelector('.cr-fav-btn');
            if (btn) {
                const on = !!res?.active;
                btn.classList.toggle('active', on);
                btn.setAttribute('aria-label', on ? 'В избранном' : 'В избранное');
                btn.setAttribute('aria-pressed', String(on));
                btn.innerHTML = CARD_FAVORITE_ICON;
            }
            return;
        }
        if ($('fav-picker-overlay')?.classList.contains('open')) {
            renderFavoritePickerGrid();
            return;
        }
        if (S.tab === 'player-profile') {
            await openPlayerProfile(effectiveUid());
        }
    } catch (e) {
        showToast(e?.message || 'Не удалось изменить избранное');
    }
}
window.toggleProfileFavorite = toggleProfileFavorite;

async function ensureMyFavorites() {
    if (Array.isArray(S.myFavoriteIds)) return;
    try {
        const uid = effectiveUid();
        if (!uid) { S.myFavoriteIds = []; return; }
        const p = await api(`/api/public_profile?user_id=${uid}&target_id=${encodeURIComponent(uid)}`);
        S.myFavoriteIds = (p?.favorite_cards || []).map(c => String(c.id));
    } catch (_) {
        S.myFavoriteIds = [];
    }
}

async function openFavoritePicker() {
    haptic('light');
    await ensureMyFavorites();
    if (!Array.isArray(S.cards) || !S.cards.length) {
        try {
            const uid = effectiveUid();
            if (uid) S.cards = await api(`/api/user_cards?user_id=${uid}`) || [];
        } catch (_) {}
    }
    let overlay = $('fav-picker-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'fav-picker-overlay';
        overlay.className = 'fav-picker-overlay';
        overlay.onclick = (e) => { if (e.target === overlay) closeFavoritePicker(); };
        document.body.appendChild(overlay);
    }
    overlay.innerHTML = `
        <div class="fav-picker-sheet" onclick="event.stopPropagation()">
            <div class="fav-picker-head">
                <div>
                    <b>Избранные карты</b>
                    <span>До 6 карт · ${(S.myFavoriteIds || []).length}/6</span>
                </div>
                <button type="button" class="fav-picker-close" aria-label="Закрыть" onclick="closeFavoritePicker()">✕</button>
            </div>
            <div class="fav-picker-grid" id="fav-picker-grid"></div>
        </div>`;
    overlay.classList.add('open');
    renderFavoritePickerGrid();
}
window.openFavoritePicker = openFavoritePicker;

async function closeFavoritePicker() {
    const overlay = $('fav-picker-overlay');
    if (overlay) overlay.classList.remove('open');
    if (S.tab === 'player-profile') {
        await openPlayerProfile(effectiveUid());
    }
}
window.closeFavoritePicker = closeFavoritePicker;

function renderFavoritePickerGrid() {
    const grid = $('fav-picker-grid');
    if (!grid) return;
    const favIds = new Set((S.myFavoriteIds || []).map(String));
    const headCount = document.querySelector('.fav-picker-head span');
    if (headCount) headCount.textContent = `До 6 карт · ${favIds.size}/6`;
    const list = [...(S.cards || [])];
    if (!list.length) {
        grid.innerHTML = '<div class="pp-empty-premium"><b>Нет карт</b><small>Сначала открой карты в коллекции</small></div>';
        return;
    }
    grid.innerHTML = list.map(c => {
        const on = favIds.has(String(c.id));
        const color = PROFILE_RCOLOR[c.rank] || 'var(--accent)';
        return `<button class="fav-picker-card${on ? ' active' : ''}" type="button" style="--card-rank:${color}" onclick="toggleProfileFavorite('${encodeURIComponent(c.id)}')">
            ${profileMediaHtml(c, 'fav-picker-media', true)}
            <div class="fav-picker-scrim"></div>
            <span class="fav-picker-star">${on ? '★' : '☆'}</span>
            <div class="fav-picker-cap"><b>${esc(c.name || '?')}</b><span>${esc(c.rank || '')}</span></div>
        </button>`;
    }).join('');
}

async function setProfileFeatured(encodedCardId) {
    const cardId = decodeURIComponent(encodedCardId || '');
    if (!cardId) return;
    try {
        await api('/api/profile_featured', {
            method: 'POST',
            body: { user_id: effectiveUid(), card_id: cardId },
        });
        haptic('success');
        await openPlayerProfile(effectiveUid());
    } catch (e) {
        showToast(e?.message || 'Не удалось выбрать главную карту');
    }
}
window.setProfileFeatured = setProfileFeatured;

function switchProfileSection(section, button) {
    const root = $('profile-screen-body') || $('tab-player-profile');
    if (!root) return;
    root.querySelectorAll('[data-profile-tab]').forEach(tab => {
        const active = tab.dataset.profileTab === section;
        tab.classList.toggle('active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    root.querySelectorAll('[data-profile-panel]').forEach(panel => {
        const active = panel.dataset.profilePanel === section;
        panel.classList.toggle('active', active);
        panel.hidden = !active;
    });
    if (button) {
        root.querySelectorAll('[data-profile-tab]').forEach(tab => tab.classList.remove('active'));
        button.classList.add('active');
        button.setAttribute('aria-selected', 'true');
    }
    haptic('light');
    if ((section === 'progress' || section === 'trades') && !new Set(S.publicProfile?.details_loaded || []).has(section)) {
        loadProfileDetails(section);
    }
}
window.switchProfileSection = switchProfileSection;

async function loadProfileDetails(section) {
    const profile = S.publicProfile;
    if (!profile?.uid || S.profileDetailsLoading === section) return;
    S.profileDetailsLoading = section;
    try {
        const details = await api(`/api/public_profile?view=details&section=${encodeURIComponent(section)}&user_id=${effectiveUid()}&target_id=${encodeURIComponent(profile.uid)}`);
        if (S.tab !== 'player-profile' || String(S.publicProfile?.uid) !== String(profile.uid) || !details || details.error) return;
        const loaded = new Set([...(S.publicProfile.details_loaded || []), ...(details.details_loaded || [])]);
        S.publicProfile = { ...S.publicProfile, ...details, details_loaded: [...loaded] };
        const body = $('profile-screen-body');
        if (body) body.innerHTML = renderPlayerProfileHtml(S.publicProfile);
        switchProfileSection(section, $('profile-screen-body')?.querySelector(`[data-profile-tab="${section}"]`));
    } catch (_) {
        showToast('Не удалось загрузить раздел профиля');
    } finally {
        S.profileDetailsLoading = '';
    }
}

function profileAddFriend(encodedUid) {
    const uid = decodeURIComponent(encodedUid || '');
    if (!uid) return;
    haptic('light');
    const link = `tg://user?id=${encodeURIComponent(uid)}`;
    try {
        if (tg?.openLink) {
            tg.openLink(link);
            return;
        }
    } catch (_) {}
    window.location.href = link;
}
window.profileAddFriend = profileAddFriend;

function sharePlayerProfile(encodedUid, encodedName) {
    const uid = decodeURIComponent(encodedUid || '');
    const name = decodeURIComponent(encodedName || '') || 'игрока';
    const url = `https://t.me/reversecard_robot?start=profile_${encodeURIComponent(uid)}`;
    const share = `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Профиль ${name} в REVERSE`)}`;
    haptic('light');
    try {
        if (tg?.openTelegramLink) {
            tg.openTelegramLink(share);
            return;
        }
    } catch (_) {}
    window.open(share, '_blank');
}
window.sharePlayerProfile = sharePlayerProfile;

function openFullscreen(url, type) {
    if (!url) return;
    haptic('light');
    let src = url;
    if (src && !src.startsWith('http')) src = `${API_BASE}${src}`;
    const ext = src.split('.').pop().toLowerCase();
    const isVideo = ext === 'mp4' || type === 'animation' || type === 'video';

    // Enable native pinch-to-zoom
    const meta = document.querySelector('meta[name="viewport"]');
    if (meta) meta.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=5.0,user-scalable=yes');

    let mediaHTML = isVideo
        ? `<video src="${src}" autoplay loop controls playsinline style="width:100%; max-height:100vh; object-fit:contain; outline:none;"></video>`
        : `<img src="${src}" style="width:100%; max-height:100vh; object-fit:contain;">`;

    let overlay = document.createElement('div');
    overlay.id = 'fullscreen-overlay';
    overlay.style.cssText = 'position:fixed; inset:0; background:#000; z-index:5000; display:flex; align-items:center; justify-content:center; overflow:auto;';
    overlay.innerHTML = `
        ${mediaHTML}
        <button id="fs-close" style="position:fixed; top:24px; right:24px; background:rgba(255,255,255,0.2); border:none; color:#fff; width:44px; height:44px; border-radius:50%; font-size:24px; z-index:5001; cursor:pointer; display:flex; align-items:center; justify-content:center; backdrop-filter:blur(4px);">✕</button>
    `;

    document.body.appendChild(overlay);

    overlay.querySelector('#fs-close').onclick = (e) => {
        e.stopPropagation();
        overlay.remove();
        // Disable zoom
        if (meta) meta.setAttribute('content', 'width=device-width,initial-scale=1.0,maximum-scale=1.0,user-scalable=no');
    };
}

// ── API ───────────────────────────────────────────────
async function api(url, opts = {}) {
    const method = String(opts.method || 'GET').toUpperCase();
    const cacheable = method === 'GET' && /^\/api\/(user_stats|user_cards|user_team|user_tasks|leaderboard|arena_state|theme_market|theme_mine|universes)(\?|$)/.test(url);
    const cacheKey = `${method}:${url}`;
    const fallbackAge = Number(opts.fallbackAge ?? 12 * 60 * 60 * 1000);
    if (cacheable && opts.cacheMode === 'cache-first') {
        const cached = apiDataCache.peek(cacheKey, Number(opts.maxAge ?? 30_000));
        if (cached !== undefined) return cached;
    }

    const request = async () => {
        const initData = tg?.initData || '';
        const maxAttempts = 1 + Math.max(0, Number(opts.retries ?? (method === 'GET' ? 1 : 0)) || 0);
        const timeoutMs = Math.max(3000, Number(opts.timeoutMs ?? (method === 'GET' ? 15000 : 20000)) || 0);
        let lastError;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            const controller = new AbortController();
            const externalSignal = opts.signal;
            const abortFromExternal = () => controller.abort();
            if (externalSignal?.aborted) controller.abort();
            else externalSignal?.addEventListener?.('abort', abortFromExternal, { once: true });
            let timedOut = false;
            const timeout = setTimeout(() => {
                timedOut = true;
                controller.abort();
            }, timeoutMs);
            try {
                const res = await fetch(API_BASE + url, {
                    method,
                    cache: method === 'GET' ? 'no-cache' : 'no-store',
                    keepalive: Boolean(opts.keepalive),
                    signal: controller.signal,
                    headers: {
                        'Content-Type': 'application/json',
                        'ngrok-skip-browser-warning': 'true',
                        'X-Telegram-Init-Data': initData,
                        ...(opts.headers || {})
                    },
                    body: opts.body ? JSON.stringify({ ...opts.body, initData }) : undefined
                });
                let data;
                try { data = await res.json(); }
                catch {
                    const invalid = new Error(`Некорректный ответ сервера (HTTP ${res.status})`);
                    invalid.status = res.status;
                    throw invalid;
                }
                if (!res.ok) {
                    const failed = new Error(data.error || data.message || `HTTP ${res.status}`);
                    failed.status = res.status;
                    throw failed;
                }
                if (cacheable) apiDataCache.set(cacheKey, data);
                if (method !== 'GET') {
                    if (/^\/api\/(arena_|clan_war_attack)/.test(url)) {
                        apiDataCache.markMatchingStale(key => /GET:\/api\/(arena_state|user_stats|leaderboard)/.test(key));
                    } else {
                        apiDataCache.markAllStale();
                    }
                }
                return data;
            } catch (error) {
                const normalized = normalizeRequestError(error, {
                    timedOut,
                    offline: typeof navigator !== 'undefined' && navigator.onLine === false,
                });
                if (error?.status && !normalized.status) normalized.status = error.status;
                lastError = normalized;
                if (!shouldRetryRequest(method, normalized, attempt, maxAttempts)) throw normalized;
                await new Promise(resolve => setTimeout(resolve, requestRetryDelay(attempt)));
            } finally {
                clearTimeout(timeout);
                externalSignal?.removeEventListener?.('abort', abortFromExternal);
            }
        }
        throw lastError || new Error('Ошибка соединения');
    };

    try {
        // Не объединяем POST: у одного маршрута могут быть разные игровые действия.
        return method === 'GET' && opts.dedupe !== false
            ? await apiDataCache.coordinate(cacheKey, request)
            : await request();
    } catch (e) {
        if (cacheable && opts.allowStale !== false) {
            const cached = apiDataCache.peek(cacheKey, fallbackAge);
            if (cached !== undefined) {
                console.warn(`[API CACHE FALLBACK] ${url}`);
                return cached;
            }
        }
        console.error(`[FETCH ERROR] ${url}:`, e);
        throw e;
    }
}

// ── Notification preferences ──────────────────────────
const NOTIFICATION_OPTIONS = [
    ['enabled', 'Все автоматические сообщения', 'Быстрый общий выключатель'],
    ['card_ready', 'Бесплатная карта готова', 'Сообщение после восстановления открытия'],
    ['daily', 'Ежедневная серия', 'Напоминание, если награда ещё не получена'],
    ['progress', 'Прогресс и задания', 'Первая карта, готовая награда и проверка канала'],
    ['comeback', 'Возвращение в игру', 'Редкие напоминания после долгого перерыва'],
    ['clan', 'События клана', 'Важные события состава и клановых войн'],
];
let notificationSettings = null;
let notificationBusyKey = '';

function renderNotificationSettings() {
    const root = $('notification-settings-list');
    if (!root) return;
    if (!notificationSettings) {
        root.innerHTML = '<div class="notification-loading">Загружаем настройки…</div>';
        return;
    }
    root.innerHTML = NOTIFICATION_OPTIONS.map(([key, title, subtitle]) => {
        const enabled = Boolean(notificationSettings[key]);
        const blockedByMaster = key !== 'enabled' && !notificationSettings.enabled;
        return `<button class="notification-row${enabled ? ' is-on' : ''}" type="button" data-notify-key="${key}" onclick="toggleNotificationSetting('${key}')" ${notificationBusyKey || blockedByMaster ? 'disabled' : ''} aria-pressed="${enabled}">
            <span class="notification-copy"><b>${title}</b><span>${subtitle}</span></span>
            <i class="notification-switch" aria-hidden="true"></i>
        </button>`;
    }).join('');
    const button = $('hub-notify-btn');
    if (button) button.classList.toggle('is-off', !notificationSettings.enabled);
}

async function openNotificationSettings() {
    const modal = $('notification-modal');
    if (!modal) return;
    haptic('light');
    modal.classList.add('open');
    modal.setAttribute('aria-hidden', 'false');
    notificationSettings = null;
    renderNotificationSettings();
    const uid = getSession();
    if (!uid) {
        showToast('Открой профиль через Telegram');
        return;
    }
    try {
        notificationSettings = await api(`/api/notification_settings?user_id=${encodeURIComponent(uid)}`, { dedupe: false });
        renderNotificationSettings();
    } catch (error) {
        $('notification-settings-list').innerHTML = `<div class="notification-loading">${esc(error?.message || 'Не удалось загрузить настройки')}</div>`;
    }
}

function closeNotificationSettings() {
    const modal = $('notification-modal');
    if (!modal) return;
    modal.classList.remove('open');
    modal.setAttribute('aria-hidden', 'true');
}

async function toggleNotificationSetting(key) {
    if (!notificationSettings || notificationBusyKey || !NOTIFICATION_OPTIONS.some(item => item[0] === key)) return;
    const uid = getSession();
    if (!uid) return;
    const before = { ...notificationSettings };
    notificationBusyKey = key;
    notificationSettings[key] = !notificationSettings[key];
    renderNotificationSettings();
    try {
        const result = await api('/api/notification_settings', {
            method: 'POST',
            body: { user_id: uid, key, enabled: notificationSettings[key] },
        });
        notificationSettings = result.settings || notificationSettings;
        haptic('light');
    } catch (error) {
        notificationSettings = before;
        showToast(error?.message || 'Не удалось сохранить настройку');
    } finally {
        notificationBusyKey = '';
        renderNotificationSettings();
    }
}

window.openNotificationSettings = openNotificationSettings;
window.closeNotificationSettings = closeNotificationSettings;
window.toggleNotificationSetting = toggleNotificationSetting;

// ── Utils ─────────────────────────────────────────────
function showLoader(v) {
    const l = $('loader');
    if (!l) return;
    const spinner = l.querySelector('.loader-spinner');
    const retry = $('loader-retry');
    const label = l.querySelector('.loader-text');
    if (loaderWatchdog) clearTimeout(loaderWatchdog);
    loaderWatchdog = null;
    l.style.display = v ? 'flex' : 'none';
    if (v) {
        if (spinner) spinner.hidden = false;
        if (retry) retry.hidden = true;
        if (label) label.textContent = 'Загрузка...';
        loaderWatchdog = setTimeout(() => {
            loaderWatchdog = null;
            showBootError('Сервер отвечает слишком долго');
            console.warn('[LOADER] startup exceeded its time budget');
        }, 10000);
    }
}

function showBootError(message) {
    const loader = $('loader');
    if (!loader) return;
    if (loaderWatchdog) clearTimeout(loaderWatchdog);
    loaderWatchdog = null;
    loader.style.display = 'flex';
    const spinner = loader.querySelector('.loader-spinner');
    const label = loader.querySelector('.loader-text');
    const retry = $('loader-retry');
    if (spinner) spinner.hidden = true;
    if (label) label.textContent = message || 'Не удалось загрузить приложение';
    if (retry) retry.hidden = false;
}
function registerPerformanceWorker() {
    if (!('serviceWorker' in navigator) || location.protocol === 'file:') return;
    navigator.serviceWorker.register('/service-worker.js', { scope: '/', updateViaCache: 'none' }).catch(error => {
        console.warn('[CACHE WORKER]', error);
    });
}
let toastHideTimer = null;
let lastToastMessage = '';
let lastToastAt = 0;
function showToast(msg) {
    const t = $('toast'); if (!t) return;
    const message = String(msg || '').trim();
    if (!message) return;
    const now = Date.now();
    const isNetworkNotice = /(соедин|интернет|сервер не ответ|связаться с сервер|fetch)/i.test(message);
    const quietWindow = isNetworkNotice ? 30000 : 1200;
    if (message === lastToastMessage && now - lastToastAt < quietWindow) return;
    lastToastMessage = message;
    lastToastAt = now;
    if (toastHideTimer) clearTimeout(toastHideTimer);
    t.textContent = message;
    t.classList.add('show');
    toastHideTimer = setTimeout(() => {
        t.classList.remove('show');
        toastHideTimer = null;
    }, Math.min(12000, Math.max(5000, message.length * 65)));
}
function haptic(type) { try { tg?.HapticFeedback?.impactOccurred(type); } catch { } }
function animCount(id, to) { const e = $(id); if (e) e.textContent = Math.round(to).toLocaleString('ru-RU'); }
const esc = (s = '') => String(s).replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
function isCardVideo(c) {
    const path = String(c?.image_url || '').split('?')[0].toLowerCase();
    const type = String(c?.type || '').toLowerCase();
    // GIF stays on <img> so the browser animates it. Telegram "animation"
    // is almost always MP4 and must use <video> or the slot stays empty.
    if (type === 'gif' || /\.gif$/.test(path)) return false;
    return /\.(mp4|webm|mov)$/.test(path)
        || type === 'video'
        || type === 'mp4'
        || type === 'animation'
        || type === 'animated';
}

function renderMedia(c, className = '', { eager = false } = {}) {
    // Only the live battle/replay board stays on static posters.
    // Lobby team, collection, picker and exchange need the real animation.
    if (className === 'battle-card-img' || className === 'replay-card-img') {
        return renderArenaStaticMedia(c, className);
    }
    const isVideo = isCardVideo(c);
    // image_url may be a real URL, a relative proxy URL, or a Telegram
    // file_id. Never let a raw file_id become an invalid browser URL.
    let src = arenaNormalMediaSrc(c?.image_url, c?.file_id);
    if (!src) {
        const letter = esc((c?.name || '?')[0]);
        return `<div class="${className} media-placeholder" aria-hidden="true">${letter}</div>`;
    }

    if (isVideo) {
        const lazyClass = eager ? '' : ' lazy-media';
        return `<video class="${className}${lazyClass}" src="${cssUrl(src)}" data-src="${cssUrl(src)}" autoplay loop muted playsinline webkit-playsinline preload="metadata" onerror="rvVideoError(this)" style="pointer-events:none;"></video>`;
    }
    return `<img class="${className}" src="${cssUrl(src)}" data-src="${cssUrl(src)}" alt="" loading="eager" decoding="async" onerror="rvMediaError(this)">`;
}

function renderArenaStaticMedia(c, className = '') {
    // Arena always requests a still image. Animated originals use the cached
    // server poster and safely fall back to the normal Telegram proxy.
    const normalSrc = arenaNormalMediaSrc(c?.image_url, c?.file_id);
    const previewSrc = arenaStoredPreviewSrc(c?.arena_preview_url)
        || (isCardVideo(c) && normalSrc.includes('/api/proxy_image?')
            ? normalSrc.replace('/api/proxy_image?', '/api/arena_card_preview?') : '');
    const src = previewSrc || normalSrc;
    if (!src) return `<div class="${className} media-placeholder" aria-hidden="true">${esc((c?.name || '?')[0])}</div>`;
    const fallback = previewSrc && normalSrc && previewSrc !== normalSrc
        ? ` data-fallback-src="${cssUrl(normalSrc)}"`
        : '';
    // Arena uses static posters, so let WebView schedule these lightweight
    // requests itself instead of blocking eight cards behind a JS queue.
    const letter = esc(String(c?.name || '✦').trim().charAt(0) || '✦');
    return `<img class="${className} arena-media-pending" src="${cssUrl(src)}" alt="" loading="eager" fetchpriority="high" decoding="async" data-arena-static="1" data-arena-letter="${letter}" data-arena-source="${cssUrl(src)}" data-fallback-type="${esc(String(c?.type || 'photo'))}"${fallback} onload="arenaMediaLoaded(this)" onerror="arenaMediaError(this)">`;
}

const arenaMediaTimers = new WeakMap<Element, ReturnType<typeof setTimeout>>();
function clearArenaMediaTimer(media) {
    clearTimeout(arenaMediaTimers.get(media));
    arenaMediaTimers.delete(media);
}
function armArenaMediaTimer(media, fallback) {
    if (arenaMediaTimers.has(media) || media.dataset.arenaLoadState === 'loaded') return;
    arenaMediaTimers.set(media, setTimeout(() => {
        arenaMediaTimers.delete(media);
        if (media.isConnected && media.dataset.arenaLoadState !== 'loaded') fallback(media);
    }, 8000));
}

function scheduleArenaMediaLoads(root) {
    // Compatibility for markup restored from an older cached build.
    root?.querySelectorAll?.('img[data-arena-src]:not([src])').forEach(image => {
        const src = String(image.dataset.arenaSrc || '').trim();
        if (src) image.src = src;
    });
    const reveal = () => {
        root?.querySelectorAll?.('img[data-arena-static]').forEach(image => {
            if (!(image instanceof HTMLImageElement)) return;
            if (!image.complete) { armArenaMediaTimer(image, arenaMediaError); return; }
            if (image.naturalWidth > 0) arenaMediaLoaded(image);
            else arenaMediaError(image);
        });
    };
    reveal();
    requestAnimationFrame(reveal);
}

function arenaMediaLoaded(image) {
    if (!(image instanceof HTMLImageElement)) return;
    clearArenaMediaTimer(image);
    image.dataset.arenaLoadState = 'loaded';
    image.classList.remove('arena-media-pending', 'media-load-failed');
}
window.arenaMediaLoaded = arenaMediaLoaded;

function retainArenaBattleMedia(row) {
    const retained = new Map();
    row?.querySelectorAll('.battle-card-unit[data-battle-card-id]').forEach(card => {
        const media = card.querySelector('.battle-card-media > img, .battle-card-media > video');
        if (media) retained.set(String(card.dataset.battleCardId || ''), media);
    });
    return retained;
}

function restoreArenaBattleMedia(row, retained) {
    if (!row || !retained?.size) return;
    row.querySelectorAll('.battle-card-unit[data-battle-card-id]').forEach(card => {
        const oldMedia = retained.get(String(card.dataset.battleCardId || ''));
        const holder = card.querySelector('.battle-card-media');
        const nextMedia = holder?.querySelector('img[data-arena-source], video');
        if (!oldMedia || !holder || !nextMedia) return;
        const oldSource = String(oldMedia.dataset.arenaSource || oldMedia.dataset.arenaSrc || oldMedia.currentSrc || oldMedia.src || '');
        const nextSource = String(nextMedia.dataset.arenaSource || nextMedia.dataset.arenaSrc || nextMedia.currentSrc || nextMedia.src || '');
        if (oldSource && oldSource === nextSource) holder.replaceChildren(oldMedia);
    });
}

function arenaMediaSrc(value) {
    const src = String(value || '').trim();
    if (!src || src === 'undefined' || src === 'null') return '';
    if (src.startsWith('http') || src.startsWith('blob:') || src.startsWith('data:')) return src;
    if (src.startsWith('/')) return `${API_BASE}${src}`;
    return '';
}

function arenaProxySrc(fileId) {
    const value = String(fileId || '').trim();
    return /^[A-Za-z0-9_-]{16,220}$/.test(value)
        ? `${API_BASE}/api/proxy_image?file_id=${encodeURIComponent(value)}`
        : '';
}

function arenaNormalMediaSrc(value, fileId) {
    const raw = String(value || '').trim();
    const legacyPreview = /\/api\/arena_card_preview(?:\?|$)/i.test(raw);
    if (legacyPreview) {
        const proxy = raw.replace(/\/api\/arena_card_preview/i, '/api/proxy_image');
        return arenaMediaSrc(proxy);
    }
    return arenaMediaSrc(raw) || arenaProxySrc(raw) || arenaProxySrc(fileId);
}

function arenaStoredPreviewSrc(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    return arenaMediaSrc(raw);
}

function stripArenaPendingClass(className) {
    return String(className || '').replace(/\barena-media-pending\b/g, '').replace(/\s+/g, ' ').trim();
}

function arenaReplaceWithLetter(image) {
    clearArenaMediaTimer(image);
    const placeholder = document.createElement('span');
    placeholder.className = `${stripArenaPendingClass(image.className || image.getAttribute?.('class'))} arena-card-art-fallback`;
    placeholder.dataset.arenaSource = image.dataset?.arenaSource || image.src || '';
    placeholder.dataset.arenaLoadState = 'failed';
    placeholder.textContent = 'Без изображения';
    image.replaceWith(placeholder);
}

function arenaMediaError(image) {
    if (!(image instanceof HTMLImageElement)) {
        return;
    }
    clearArenaMediaTimer(image);
    const fallback = image.dataset.fallbackSrc;
    const original = image.dataset.arenaSource || image.currentSrc || image.src;
    if (fallback && image.dataset.arenaFallbackTried !== '1') {
        // First try the known working proxy as an image. This covers GIF and
        // Telegram photo files even when their record is labelled animation.
        image.dataset.arenaFallbackTried = '1';
        image.src = fallback;
        armArenaMediaTimer(image, arenaMediaError);
        return;
    }
    const videoSrc = fallback || original;
    const mayBeVideo = /animation|animated|video|mp4/i.test(image.dataset.fallbackType || '')
        || !/\.(avif|gif|jpe?g|png|svg|webp)(?:\?|#|$)/i.test(videoSrc || '');
    if (videoSrc && mayBeVideo && image.dataset.arenaVideoTried !== '1') {
        const video = document.createElement('video');
        video.className = stripArenaPendingClass(image.className);
        video.src = videoSrc;
        video.dataset.src = videoSrc;
        video.dataset.arenaLetter = image.dataset.arenaLetter || '✦';
        video.dataset.arenaSource = image.dataset.arenaSource || original;
        video.autoplay = false;
        video.loop = false;
        video.muted = true;
        video.playsInline = true;
        video.setAttribute('webkit-playsinline', '');
        video.preload = 'auto';
        video.dataset.arenaFallbackTried = '1';
        video.dataset.arenaVideoTried = '1';
        const freezeFrame = () => {
            if (!video.isConnected || video.readyState < 2 || !video.videoWidth || video.seeking) return;
            clearArenaMediaTimer(video);
            video.pause();
            video.dataset.arenaLoadState = 'loaded';
            // Paint the decoded frame into a retained canvas. No toDataURL:
            // cross-origin Telegram proxies can be displayed without CORS.
            try {
                const canvas = document.createElement('canvas');
                canvas.width = Math.min(420, video.videoWidth);
                canvas.height = Math.round(canvas.width * video.videoHeight / video.videoWidth);
                const context = canvas.getContext('2d');
                if (!context) return;
                context.drawImage(video, 0, 0, canvas.width, canvas.height);
                canvas.className = video.className;
                canvas.dataset.arenaSource = video.dataset.arenaSource;
                canvas.dataset.arenaLoadState = 'loaded';
                canvas.setAttribute('aria-hidden', 'true');
                video.replaceWith(canvas);
                video.removeAttribute('src'); video.load();
            } catch (_) { /* Keep the paused decoded frame if canvas is unavailable. */ }
        };
        video.onloadedmetadata = () => { try { video.currentTime = Math.min(.12, (video.duration || 1) / 2); } catch (_) {} };
        video.onseeked = freezeFrame;
        video.onloadeddata = freezeFrame;
        video.onerror = () => arenaReplaceWithLetter(video);
        image.replaceWith(video);
        armArenaMediaTimer(video, arenaReplaceWithLetter);
        const play = video.play();
        if (play?.catch) play.catch(() => {});
        return;
    }
    arenaReplaceWithLetter(image);
}
window.arenaMediaError = arenaMediaError;

function retryArenaMedia() {
    const cards = S.arena.battle?.cards || [];
    document.querySelectorAll('#arena-battle .battle-card-unit[data-id]').forEach(button => {
        const card = cards.find(card => String(card.id) === button.getAttribute('data-id'));
        const holder = button.querySelector('.battle-card-media');
        if (!card || !holder) return;
        holder.querySelectorAll('img,video').forEach(clearArenaMediaTimer);
        holder.innerHTML = renderArenaStaticMedia(card, 'battle-card-img');
        scheduleArenaMediaLoads(holder);
    });
}
window.retryArenaMedia = retryArenaMedia;

function rvMediaError(element) {
    if (!(element instanceof HTMLImageElement)) return;
    const retries = Number(element.dataset.mediaRetries || 0);
    const original = element.dataset.src || element.currentSrc || element.src;
    const ambiguousSource = /\/api\/proxy_image(?:\?|$)/i.test(original)
        || !/\.(avif|gif|jpe?g|png|svg|webp)(?:\?|#|$)/i.test(original);
    if (ambiguousSource && retries === 0 && element.dataset.mediaFallback !== 'image') {
        const video = document.createElement('video');
        video.className = element.className;
        video.src = original;
        video.dataset.src = original;
        video.dataset.mediaFallback = 'video';
        video.autoplay = true;
        video.loop = true;
        video.muted = true;
        video.playsInline = true;
        video.preload = 'auto';
        video.style.cssText = element.style.cssText;
        video.onerror = () => rvVideoError(video);
        element.replaceWith(video);
        const play = video.play();
        if (play?.catch) play.catch(() => {});
        return;
    }
    if (retries >= 2) {
        element.classList.add('media-load-failed');
        return;
    }
    element.dataset.mediaRetries = String(retries + 1);
    const join = original.includes('?') ? '&' : '?';
    setTimeout(() => { element.src = `${original}${join}rv_retry=${retries + 1}`; }, 350 * (retries + 1));
}
window.rvMediaError = rvMediaError;

function rvVideoError(element) {
    if (!(element instanceof HTMLVideoElement)) return;
    const retries = Number(element.dataset.mediaRetries || 0);
    const original = element.dataset.src || element.currentSrc || element.src;
    if (element.dataset.mediaFallback === 'video') {
        const image = document.createElement('img');
        image.className = element.className;
        image.src = `${original}${original.includes('?') ? '&' : '?'}rv_retry_image=1`;
        image.dataset.src = original;
        image.dataset.mediaFallback = 'image';
        image.alt = '';
        image.decoding = 'async';
        image.style.cssText = element.style.cssText;
        image.onerror = () => rvMediaError(image);
        element.replaceWith(image);
        return;
    }
    if (retries < 1 && element.dataset.mediaFallback !== 'video') {
        element.dataset.mediaRetries = String(retries + 1);
        const join = original.includes('?') ? '&' : '?';
        setTimeout(() => { element.src = `${original}${join}rv_retry_video=1`; element.load(); }, 250);
        return;
    }
    const image = document.createElement('img');
    image.className = element.className;
    image.src = original;
    image.dataset.src = original;
    image.dataset.mediaFallback = 'image';
    image.alt = '';
    image.decoding = 'async';
    image.style.cssText = element.style.cssText;
    image.onerror = () => rvMediaError(image);
    element.replaceWith(image);
}
window.rvVideoError = rvVideoError;

let lazyMediaObserver = null;
let lazyMediaScanQueued = false;

function ensureLazyMediaObserver() {
    if (lazyMediaObserver || !('IntersectionObserver' in window)) return lazyMediaObserver;
    lazyMediaObserver = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            const element = entry.target;
            if (!(element instanceof HTMLVideoElement)) return;
            if (entry.isIntersecting) {
                if (!element.src && element.dataset.src) element.src = element.dataset.src;
                const play = element.play();
                if (play?.catch) play.catch(() => {});
            } else {
                try { element.pause(); } catch (_) {}
            }
        });
    }, { rootMargin: '180px 0px', threshold: 0.01 });
    return lazyMediaObserver;
}

function loadLazyMedia() {
    lazyMediaScanQueued = false;
    const observer = ensureLazyMediaObserver();
    document.querySelectorAll('.lazy-media').forEach(element => {
        if (element.dataset.mediaObserved) return;
        element.dataset.mediaObserved = '1';
        if (element instanceof HTMLVideoElement) {
            if (!element.getAttribute('src') && element.dataset.src) element.src = element.dataset.src;
            if (observer) observer.observe(element);
        }
    });
}

function suspendTabMedia(tabName) {
    const root = $(`tab-${tabName}`);
    if (!root) return;
    root.querySelectorAll('video').forEach(video => {
        try { video.pause(); } catch (_) {}
        if (mediaKeepAlive(video)) return;
        try { lazyMediaObserver?.unobserve(video); } catch (_) {}
        delete video.dataset.mediaObserved;
        // Never tear src: Telegram WebView + cached posters then render
        // black cards in collection, team, arena and exchange.
    });
    if (tabName === 'team') {
        arenaCardStreamObserver?.disconnect();
        arenaCardStreamObserver = null;
    }
}

function resumeTabMedia(tabName) {
    if ($('card-modal')?.matches('.open.new-card-mode')) return;
    const root = $(`tab-${tabName}`);
    if (!root) return;
    root.querySelectorAll('video').forEach(video => {
        if (!video.getAttribute('src') && video.dataset.src) video.src = video.dataset.src;
        delete video.dataset.mediaObserved;
        video.classList.add('lazy-media');
        try {
            video.muted = true;
            video.playsInline = true;
            const play = video.play();
            if (play && typeof play.catch === 'function') play.catch(() => {});
        } catch (_) {}
    });
    queueLazyMediaScan();
    if (tabName === 'team') {
        observeArenaCardStream();
        scheduleArenaMediaLoads(root);
    }
    if (tabName === 'arena' || tabName === 'cards' || tabName === 'exchange') {
        scheduleArenaMediaLoads(root);
    }
}

function queueLazyMediaScan() {
    if (lazyMediaScanQueued) return;
    lazyMediaScanQueued = true;
    requestAnimationFrame(loadLazyMedia);
}

function _bootLazyMediaObserver() {
    if (!document.body || window.__rvLazyObs) return;
    window.__rvLazyObs = true;
    try {
        const observer = new MutationObserver(queueLazyMediaScan);
        observer.observe(document.body, { childList: true, subtree: true });
        queueLazyMediaScan();
    } catch (e) {
        console.warn('[MEDIA] observer failed', e);
    }
}
if (document.body) _bootLazyMediaObserver();
else document.addEventListener('DOMContentLoaded', _bootLazyMediaObserver);

function effectiveUid() {
    return TGuser?.id || getSession()
        || (['127.0.0.1', 'localhost'].includes(location.hostname) ? '9001' : null);
}

// ── Auth (non-TG) ─────────────────────────────────────
async function bootWithUid(uid) {
    saveSession(String(uid));
    showLoader(true);
    await loadAll();
    const ls = $('login-screen'); if (ls) ls.remove();
}

function showLoginScreen() {
    const ls = document.createElement('div');
    ls.id = 'login-screen';
    ls.innerHTML = `<div class="login-box">
        <div class="text-3xl font-black italic uppercase tracking-widest mb-1">REVERSE</div>
        <div class="text-[10px] font-bold text-[#1A1A1A]/50 uppercase tracking-widest mb-6">Игровое меню</div>
        <input id="uid-input" class="login-input" type="number" placeholder="Твой Telegram User ID">
        <input id="pwd-input" class="login-input" type="password" placeholder="Пароль" style="margin-top:10px">
        <button onclick="loginWithId()" class="w-full bg-[#1A1A1A] text-[#FFD54F] font-black uppercase tracking-widest py-3 retro-button rounded-2xl mb-2" style="margin-top:16px">▶ ВОЙТИ</button>
        <button onclick="loginDemo()" class="w-full bg-white font-black uppercase tracking-widest py-3 retro-button rounded-2xl">🎮 ДЕМО РЕЖИМ</button>
        <div class="text-[9px] font-bold text-[#1A1A1A]/40 uppercase tracking-wider mt-4">Для полного доступа открой через Telegram</div>
    </div>`;
    document.body.appendChild(ls);
    setTimeout(() => { const i = $('uid-input'); if (i) i.focus(); }, 150);
    document.addEventListener('keydown', e => { if (e.key === 'Enter') loginWithId(); });
}

async function loginWithId() {
    const inp = $('uid-input');
    const pwd = $('pwd-input');
    const val = inp ? inp.value.trim() : '';
    const pVal = pwd ? pwd.value.trim() : '';
    
    if (pVal !== '1332255') {
        if (pwd) { pwd.classList.add('shake'); setTimeout(() => pwd.classList.remove('shake'), 500); }
        showToast('⚠ НЕВЕРНЫЙ ПАРОЛЬ');
        return;
    }
    
    if (!val || isNaN(val)) { if (inp) { inp.classList.add('shake'); setTimeout(() => inp.classList.remove('shake'), 500); } return; }
    await bootWithUid(val);
}

async function loginDemo() {
    const pwd = $('pwd-input');
    const pVal = pwd ? pwd.value.trim() : '';
    
    if (pVal !== '1332255') {
        if (pwd) { pwd.classList.add('shake'); setTimeout(() => pwd.classList.remove('shake'), 500); }
        showToast('⚠ НЕВЕРНЫЙ ПАРОЛЬ');
        return;
    }
    S.user = {
        name: 'DEMO', universe: 'Блич', pts: 1337, cards: 5, spins: 10, coins: 500, restars: 3,
        trophy: 180, wins: 14, loses: 7, league: '🌊 ЛЕГЕНДА', photo_url: ''
    };
    S.cards = [
        { id: 'd1', name: 'Ичиго Куросаки', rank: 'Мифический', damage: 950, health: 820, rarity: 100, image_url: '' },
        { id: 'd2', name: 'Рукия Кучики', rank: 'Легендарный', damage: 680, health: 720, rarity: 80, image_url: '' },
        { id: 'd3', name: 'Урьюу Ишида', rank: 'Эпический', damage: 520, health: 480, rarity: 50, image_url: '' },
        { id: 'd4', name: 'Орихимэ', rank: 'Редкий', damage: 300, health: 600, rarity: 30, image_url: '' },
        { id: 'd5', name: 'Чад', rank: 'Обычный', damage: 200, health: 350, rarity: 10, image_url: '' },
    ];
    showLoader(false);
    $('app').style.display = 'flex';
    const ls = $('login-screen'); if (ls) ls.remove();
    renderProfile(); renderCards();
    showToast('◈ ДЕМО РЕЖИМ АКТИВИРОВАН');
}

// ── Dice Game ────────────────────────────────────────
const DICE_FACES  = ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅'];
const DICE_MULTS  = [0, 0.0, 0.0, 0.3, 0.5, 1.5, 2.5];
const DICE_CD_S  = 120;
let _diceCdInterval = null;
let _diceCdUntil    = 0;

function selectGame(name) {
    if (name === 'bj' || name === 'rl') {
        showToast('Игра отключена');
        name = 'dice';
    }
    haptic('light');
    document.querySelectorAll('[id^="game-pick-"]').forEach(el => el.classList.remove('game-pick-active'));
    document.querySelectorAll('[id^="game-panel-"]').forEach(el => el.style.display = 'none');
    const pick  = $(`game-pick-${name}`);
    const panel = $(`game-panel-${name}`);
    if (pick)  pick.classList.add('game-pick-active');
    if (panel) panel.style.display = 'flex';

    // Load the selected game's current balance and cooldown immediately.
    if (name === 'slots') void renderSlots();
    if (name === 'guess') void renderGuess();
}

async function renderDice() {
    const uid = effectiveUid();
    if (!uid) {
        showToast('Открой приложение через Telegram');
        return;
    }
    try {
        const st = await api(`/api/dice_status?user_id=${uid}`);
        if (st) {
            setText('games-balance', (st.balance || 0).toLocaleString('ru-RU'));
            setText('games-spins',   st.spins  || 0);
            if (st.cooldown_remaining > 0) {
                _diceCdUntil = Date.now() / 1000 + st.cooldown_remaining;
                _startDiceCooldown(st.cooldown_remaining);
            } else {
                _stopDiceCooldown();
            }
        }
    } catch (e) {
        const message = String(e?.message || e || '');
        if (/unauthorized|initdata/i.test(message)) {
            showToast('Открой приложение через Telegram');
        } else {
            showToast('Не удалось загрузить игры');
        }
    }
}

function setDiceBet(amount) {
    haptic('light');
    const inp = $('dice-bet-input');
    if (!inp) return;
    if (amount === -1) {
        const bal = parseInt((S.user?.coins || 0));
        inp.value = Math.min(50000, Math.max(100, bal));
    } else {
        inp.value = amount;
    }
}

async function rollDice() {
    const uid = effectiveUid();
    const inp = $('dice-bet-input');
    const btn = $('dice-roll-btn');
    if (!inp || !btn) return;

    const bet = parseInt(inp.value);
    if (!bet || bet < 100 || bet > 50000) {
        showToast('❌ Ставка: 100 – 50 000 💰');
        haptic('error');
        return;
    }

    btn.disabled = true;
    btn.textContent = '⏳ Бросаем...';
    haptic('medium');

    try {
        const res = await api('/api/dice_bet', {
            method: 'POST',
            body: { user_id: uid, bet }
        });

        if (!res || !res.success) {
            const msg = res?.error === 'cooldown'
                ? `⏳ Подожди ещё ${_fmtSec(res.remaining)}`
                : (res?.error || 'Ошибка');
            showToast(msg);
            haptic('error');
            return;
        }

        // Update balance in header and dice tab
        const oldDiceBal = parseInt(($('games-balance')?.textContent || '0').replace(/\D/g,'')) || 0;
        if (S.user) {
            S.user.coins = res.new_balance;
            setText('h-coins', res.new_balance.toLocaleString('ru-RU'));
            setText('p-coins', res.new_balance);
            setText('s-coins', res.new_balance);
        }
        animateCounter('games-balance', oldDiceBal, res.new_balance, 800);
        if (res.profit > 0 && res.multiplier >= 1.5) fireConfetti(res.multiplier >= 2.5 ? 80 : 48);

        // Highlight winning cell
        document.querySelectorAll('.dice-mult-cell').forEach(el => {
            el.style.transform = '';
            el.style.boxShadow = '';
        });
        const winner = document.querySelector(`.dice-mult-cell[data-face="${res.dice}"]`);
        if (winner) {
            winner.style.transform = 'scale(1.12)';
            winner.style.boxShadow = res.profit > 0
                ? '0 0 20px rgba(74,222,128,.6)'
                : res.profit === 0 ? '0 0 16px rgba(255,200,0,.4)'
                : '0 0 16px rgba(255,75,75,.5)';
            winner.style.transition = 'all .35s';
        }

        // Show result card
        const profitColor = res.profit > 0 ? '#4ade80' : res.profit < 0 ? '#ff4b4b' : '#ffd700';
        const profitSign  = res.profit > 0 ? '+' : '';
        const profitLabel = res.profit > 0 ? '🟢 ВЫИГРЫШ' : res.profit < 0 ? '🔴 ПРОИГРЫШ' : '🟡 ВОЗВРАТ';
        const resultEl = $('dice-result');
        if (resultEl) {
            resultEl.style.display = 'block';
            resultEl.innerHTML = `
              <div class="game-result-card" style="border-color:${profitColor}33">
                <div style="font-size:60px;margin-bottom:10px">${DICE_FACES[res.dice]}</div>
                <div style="font-size:18px;font-weight:900;color:${profitColor};letter-spacing:.05em">${profitLabel}</div>
                <div style="font-size:12px;color:var(--text-sub);font-weight:700;margin:6px 0">
                  Выпало <b style="color:var(--text)">${res.dice}</b> — множитель <b style="color:var(--text)">×${res.multiplier}</b>
                </div>
                <div style="display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:14px">
                  <div style="background:var(--bg-hover);border-radius:12px;padding:10px">
                    <div style="font-size:10px;color:var(--text-sub);font-weight:700">СТАВКА</div>
                    <div style="font-size:16px;font-weight:900">${res.bet.toLocaleString('ru-RU')} 💰</div>
                  </div>
                  <div style="background:var(--bg-hover);border-radius:12px;padding:10px">
                    <div style="font-size:10px;color:var(--text-sub);font-weight:700">ВЫПЛАТА</div>
                    <div style="font-size:16px;font-weight:900">${res.win.toLocaleString('ru-RU')} 💰</div>
                  </div>
                </div>
                <div style="margin-top:10px;font-size:24px;font-weight:900;color:${profitColor}">
                  ${profitSign}${res.profit.toLocaleString('ru-RU')} 💰
                </div>
                <div style="margin-top:6px;font-size:11px;color:var(--text-sub)">Баланс: <b style="color:var(--text)">${res.new_balance.toLocaleString('ru-RU')}</b></div>
              </div>`;
            resultEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        haptic(res.profit > 0 ? 'success' : 'error');

        // Start cooldown only on win
        if (res.cooldown_until > 0) {
            const cdLeft = Math.max(1, Math.round(res.cooldown_until - Date.now() / 1000));
            _diceCdUntil = Date.now() / 1000 + cdLeft;
            _startDiceCooldown(cdLeft);
        }

    } catch(e) {
        showToast('❌ ' + (e.message || 'Ошибка сети'));
        haptic('error');
    } finally {
        btn.disabled = false;
        btn.textContent = '🎲 Бросить кости';
    }
}

function _fmtSec(s) {
    const m = Math.floor(s / 60), sec = s % 60;
    return `${m}:${String(sec).padStart(2,'0')}`;
}

function _startDiceCooldown(remaining) {
    const wrap = $('dice-cooldown-wrap');
    const btn  = $('dice-roll-btn');
    if (wrap) wrap.style.display = 'block';
    if (btn)  { btn.disabled = true; btn.style.opacity = '.45'; }

    _stopDiceCooldown();
    _diceCdInterval = setInterval(() => {
        const left = Math.max(0, Math.round(_diceCdUntil - Date.now() / 1000));
        const txt  = $('dice-cd-text');
        const bar  = $('dice-cd-bar');
        if (txt) txt.textContent = _fmtSec(left);
        if (bar) bar.style.width = ((1 - left / 180) * 100) + '%';
        if (left <= 0) _stopDiceCooldown();
    }, 1000);
}

function _stopDiceCooldown() {
    if (_diceCdInterval) { clearInterval(_diceCdInterval); _diceCdInterval = null; }
    const wrap = $('dice-cooldown-wrap');
    const btn  = $('dice-roll-btn');
    if (wrap) wrap.style.display = 'none';
    if (btn)  { btn.disabled = false; btn.style.opacity = '1'; }
}

window.rollDice   = rollDice;
window.setDiceBet = setDiceBet;
window.selectGame = selectGame;

window.arenaFindOpponent = arenaFindOpponent;
window.arenaShowStats = arenaShowStats;
window.arenaBackToLobby = arenaBackToLobby;
// Совместимые имена для старых Telegram WebView-кэшей.
window.arenaAttack = arenaAttackSelected;
window.arenaSkip = arenaEndTurn;
window.arenaSkipAuto = arenaAutoFinish;
window.selectSkill = arenaAttackSelected;
// ══════════════════════════════════════════════════════════════════
// � CONFETTI + EFFECTS
// ══════════════════════════════════════════════════════════════════
function fireConfetti(count = 48) {
    const root = document.getElementById('confetti-root');
    if (!root) return;
    const colors = ['#ff4b4b','#fbbf24','#4ade80','#0ea5e9','#a78bfa','#ec4899','#ffd700'];
    for (let i = 0; i < count; i++) {
        const piece = document.createElement('div');
        piece.className = 'confetti-piece';
        piece.style.left = Math.random() * 100 + '%';
        piece.style.background = colors[(Math.random() * colors.length) | 0];
        piece.style.animationDelay = (Math.random() * 0.4) + 's';
        piece.style.animationDuration = (1.8 + Math.random() * 1.4) + 's';
        piece.style.transform = `rotate(${Math.random() * 360}deg)`;
        piece.style.width  = (6 + Math.random() * 6) + 'px';
        piece.style.height = (10 + Math.random() * 8) + 'px';
        root.appendChild(piece);
        setTimeout(() => piece.remove(), 3200);
    }
}

function animateCounter(elId, from, to, duration = 700) {
    const el = $(elId); if (!el) return;
    const start = performance.now();
    const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        const value = Math.round(from + (to - from) * eased);
        el.textContent = value.toLocaleString('ru-RU');
        if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
}

// ══════════════════════════════════════════════════════════════════
// �� SLOTS
// ══════════════════════════════════════════════════════════════════
const SLOTS_SYMS  = ['🍒','🍋','🍊','🍇','💎','⭐','7️⃣'];
const SLOTS_CD_S  = 120;
let _slotsCdInterval = null;
// `var` is deliberate: older Telegram WebViews can invoke the inline button
// while the script is still finishing evaluation. A lexical `let` then causes
// "Cannot access '_slotsBal' before initialization" after the bet is placed.
var _slotsBal = 0;

async function renderSlots() {
    const uid = effectiveUid();
    try {
        const st = await api(`/api/slots_status?user_id=${uid}`);
        if (st) {
            _slotsBal = st.balance || 0;
            setText('games-balance', _slotsBal.toLocaleString('ru-RU'));
            setText('games-spins',   st.spins || 0);
            if (st.cooldown_remaining > 0) _startSlotsCd(st.cooldown_remaining);
            else _stopSlotsCd();
        }
    } catch(e) {}
}

function setSlotsBet(v) {
    haptic('light');
    const inp = $('slots-bet-input');
    if (!inp) return;
    inp.value = v === -1 ? Math.min(_slotsBal, 50000) : v;
}

async function spinSlots() {
    const uid = effectiveUid();
    const bet = parseInt($('slots-bet-input')?.value || 0);
    if (!bet || bet < 100) { showToast('Введи ставку (мин 100)'); return; }

    const btn = $('slots-spin-btn');
    if (btn) { btn.disabled = true; btn.style.opacity = '.5'; }

    // Start spin animation
    const machine = document.querySelector('.slot-machine');
    if (machine) machine.classList.remove('lit');
    const reels = [0,1,2].map(i => $(`slot-r${i}`));
    reels.forEach(r => { r.classList.add('spinning'); r.classList.remove('winner'); });
    const ivs = reels.map(r => setInterval(() => {
        r.textContent = SLOTS_SYMS[Math.floor(Math.random() * SLOTS_SYMS.length)];
    }, 80));

    haptic('medium');

    try {
        const res = await api('/api/slots_bet', { method: 'POST', body: { user_id: uid, bet } });
        if (!res || res.error) {
            ivs.forEach(clearInterval);
            reels.forEach(r => r.classList.remove('spinning'));
            if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
            if (res?.error === 'cooldown') {
                _startSlotsCd(res.remaining);
            } else {
                showToast(res?.error || 'Ошибка');
            }
            return;
        }

        // Stop reels one by one
        await new Promise(r => setTimeout(r, 900));
        clearInterval(ivs[0]); reels[0].classList.remove('spinning');
        reels[0].textContent = res.reels[0];
        await new Promise(r => setTimeout(r, 350));
        clearInterval(ivs[1]); reels[1].classList.remove('spinning');
        reels[1].textContent = res.reels[1];
        await new Promise(r => setTimeout(r, 350));
        clearInterval(ivs[2]); reels[2].classList.remove('spinning');
        reels[2].textContent = res.reels[2];

        await new Promise(r => setTimeout(r, 200));

        if (res.mult >= 1.5) {
            haptic('heavy');
            reels.forEach(r => r.classList.add('winner'));
            if (machine) machine.classList.add('lit');
            if (res.mult >= 4) fireConfetti(res.mult >= 10 ? 80 : 48);
        } else if (res.mult > 0) {
            haptic('light');
        }

        // Show result
        const profit  = res.profit;
        const pSign   = profit > 0 ? '+' : '';
        const pColor  = profit > 0 ? '#4ade80' : profit < 0 ? '#ff4b4b' : '#ffd700';
        const lbl = $('slots-label-display');
        if (lbl) lbl.textContent = res.label;
        const slotsRes = $('slots-result');
        if (slotsRes) {
            slotsRes.style.display = 'block';
            slotsRes.innerHTML = `
              <div class="game-result-card" style="border-color:${pColor}33">
                <div style="font-size:16px;font-weight:900;margin-bottom:6px">${res.label}</div>
                <div style="font-size:12px;color:var(--text-sub)">Ставка <b>${res.bet.toLocaleString('ru-RU')}</b> → Выплата <b>${res.win.toLocaleString('ru-RU')}</b></div>
                <div style="font-size:26px;font-weight:900;color:${pColor};margin-top:8px">${pSign}${profit.toLocaleString('ru-RU')} 💰</div>
                <div style="font-size:11px;color:var(--text-sub);margin-top:4px">Баланс: <b style="color:var(--text)">${res.new_balance.toLocaleString('ru-RU')}</b></div>
              </div>`;
        }

        // Update balance with counter animation
        const oldBal = _slotsBal;
        _slotsBal = res.new_balance;
        animateCounter('games-balance', oldBal, res.new_balance, 800);
        if (S.user) { S.user.coins = res.new_balance; setText('h-coins', res.new_balance.toLocaleString('ru-RU')); }

        if (res.cooldown_until > 0) {
            const cdLeft = Math.max(1, Math.round(res.cooldown_until - Date.now() / 1000));
            _startSlotsCd(cdLeft);
        }
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }

    } catch(e) {
        ivs.forEach(clearInterval);
        reels.forEach(r => r.classList.remove('spinning'));
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
        const message = e?.name === 'AbortError'
            ? 'Сервер долго отвечает. Попробуй ещё раз'
            : String(e?.message || 'Ошибка сети').replace(/^HTTP \d+\s*/i, '');
        showToast(message.slice(0, 90));
    }
}

function _startSlotsCd(sec) {
    const wrap = $('slots-cd-wrap'); const btn = $('slots-spin-btn');
    const bar  = $('slots-cd-bar');  const txt  = $('slots-cd-text');
    if (wrap) wrap.style.display = 'block';
    if (btn)  { btn.disabled = true; btn.style.opacity = '.5'; }
    const total   = 180;
    let remaining = sec;
    const update  = () => {
        if (remaining <= 0) { _stopSlotsCd(); return; }
        const m = Math.floor(remaining/60), s = remaining%60;
        if (txt) txt.textContent = `${m}:${s.toString().padStart(2,'0')}`;
        if (bar) bar.style.width = `${(remaining/total)*100}%`;
        remaining--;
    };
    update();
    if (_slotsCdInterval) clearInterval(_slotsCdInterval);
    _slotsCdInterval = setInterval(update, 1000);
}

function _stopSlotsCd() {
    if (_slotsCdInterval) { clearInterval(_slotsCdInterval); _slotsCdInterval = null; }
    const wrap = $('slots-cd-wrap'); const btn = $('slots-spin-btn');
    if (wrap) wrap.style.display = 'none';
    if (btn)  { btn.disabled = false; btn.style.opacity = '1'; }
}

// ══════════════════════════════════════════════════════════════════
// 🃏 BLACKJACK
// ══════════════════════════════════════════════════════════════════
const BJ_CD_S   = 180;
let _bjCdInterval = null;
let _bjBal        = 0;
let _bjInGame     = false;

async function renderBJ() {
    const uid = effectiveUid();
    try {
        const st = await api(`/api/bj_status?user_id=${uid}`);
        if (!st) return;
        _bjBal   = st.balance || 0;
        _bjInGame = st.in_game || false;
        setText('games-balance', _bjBal.toLocaleString('ru-RU'));
        setText('games-spins',   st.spins || 0);
        if (_bjInGame) {
            _bjShowTable(st.player_cards, [st.dealer_card], st.player_value, true);
        } else {
            const tbl = $('bj-table'); const sp = $('bj-start-panel');
            if (tbl) tbl.style.display = 'none';
            if (sp)  sp.style.display = 'flex';
            const bjRes = $('bj-result'); if (bjRes) bjRes.style.display = 'none';
            
            if (st.cooldown_remaining > 0) _startBJCd(st.cooldown_remaining);
            else _stopBJCd();
        }
    } catch(e) {}
}

function setBJBet(v) {
    haptic('light');
    const inp = $('bj-bet-input');
    if (!inp) return;
    inp.value = v === -1 ? Math.min(_bjBal, 50000) : v;
}

function _makeCard(rank, suit) {
    const red = suit === '♥' || suit === '♦';
    const div = document.createElement('div');
    div.className = 'bj-card';
    div.style.background = red ? '#fff' : '#f0f0f0';
    div.style.color       = red ? '#e74c3c' : '#1a1a2e';
    div.textContent = rank + suit;
    return div;
}

function _makeCardBack() {
    const div = document.createElement('div');
    div.className = 'bj-card-back';
    div.textContent = '🂠';
    return div;
}

function _bjShowTable(playerCards, dealerCards, playerValue, dealerHidden) {
    const tbl = $('bj-table'); const sp = $('bj-start-panel');
    if (tbl) tbl.style.display = 'flex';
    if (sp)  sp.style.display  = 'none';

    const pc = $('bj-player-cards'); const dc = $('bj-dealer-cards');
    const pv = $('bj-player-val');
    if (!pc || !dc) return;
    pc.innerHTML = ''; dc.innerHTML = '';

    playerCards.forEach(([r,s]) => pc.appendChild(_makeCard(r,s)));
    if (dealerHidden) {
        dc.appendChild(_makeCard(dealerCards[0][0], dealerCards[0][1]));
        dc.appendChild(_makeCardBack());
    } else {
        dealerCards.forEach(([r,s]) => dc.appendChild(_makeCard(r,s)));
    }
    if (pv) pv.textContent = playerValue;
}

async function bjStart() {
    const uid = effectiveUid();
    const bet = parseInt($('bj-bet-input')?.value || 0);
    if (!bet || bet < 100) { showToast('Введи ставку (мин 100)'); return; }
    const btn = $('bj-deal-btn');
    if (btn) { btn.disabled = true; btn.style.opacity = '.5'; }
    haptic('medium');
    try {
        const res = await api('/api/bj_start', { method: 'POST', body: { user_id: uid, bet } });
        if (!res || res.error) {
            if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
            if (res?.error === 'cooldown') _startBJCd(res.remaining);
            else showToast(res?.error || 'Ошибка');
            return;
        }
        if (res.status === 'blackjack') {
            haptic('heavy');
            fireConfetti(80);
            const oldBjBal = _bjBal;
            _bjBal = res.new_balance;
            animateCounter('games-balance', oldBjBal, _bjBal, 800);
            if (S.user) S.user.coins = _bjBal;
            const tbl = $('bj-table'); const sp = $('bj-start-panel');
            if (tbl) tbl.style.display = 'flex';
            if (sp)  sp.style.display  = 'none';
            const pc = $('bj-player-cards'); const dc = $('bj-dealer-cards');
            if (pc) { pc.innerHTML=''; res.player_cards.forEach(([r,s])=>pc.appendChild(_makeCard(r,s))); }
            if (dc) { dc.innerHTML=''; dc.appendChild(_makeCardBack()); dc.appendChild(_makeCardBack()); }
            const pv = $('bj-player-val'); if (pv) pv.textContent = '21';
            _bjShowResult({ result:'blackjack', win: res.win, profit: res.win - bet, new_balance: res.new_balance, bet });
            _startBJCd(BJ_CD_S); // win: natural BJ
            return;
        }
        _bjInGame = true;
        _bjBal    = _bjBal - bet;
        _bjShowTable(res.player_cards, [res.dealer_card], res.player_value, true);
        const hitBtn = $('bj-hit-btn'); const stndBtn = $('bj-stand-btn');
        if (hitBtn)  { hitBtn.disabled  = false; hitBtn.style.opacity  = '1'; }
        if (stndBtn) { stndBtn.disabled = false; stndBtn.style.opacity = '1'; }
        const bjRes = $('bj-result'); if (bjRes) bjRes.style.display = 'none';
    } catch(e) {
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
        showToast('Ошибка сети');
    }
}

async function bjHit() {
    const uid = effectiveUid();
    const hitBtn = $('bj-hit-btn'); const stndBtn = $('bj-stand-btn');
    if (hitBtn)  { hitBtn.disabled  = true; hitBtn.style.opacity  = '.5'; }
    haptic('light');
    try {
        const res = await api('/api/bj_hit', { method: 'POST', body: { user_id: uid } });
        if (!res || res.error) { showToast(res?.error || 'Ошибка'); return; }
        const pc = $('bj-player-cards');
        if (pc) pc.appendChild(_makeCard(res.new_card[0], res.new_card[1]));
        const pv = $('bj-player-val'); if (pv) pv.textContent = res.player_value;
        if (res.status === 'bust') {
            haptic('heavy');
            if (stndBtn) { stndBtn.disabled = true; stndBtn.style.opacity = '.5'; }
            _bjBal = res.new_balance;
            setText('games-balance', _bjBal.toLocaleString('ru-RU'));
            _bjShowResult({ result:'bust', win:0, profit: -parseInt($('bj-bet-input')?.value||0), new_balance: res.new_balance });
            _bjInGame = false; // lose: no cooldown
        } else {
            if (hitBtn)  { hitBtn.disabled  = false; hitBtn.style.opacity  = '1'; }
            if (res.status === 'twentyone') {
                haptic('medium');
                showToast('21! Советуем остановиться');
            }
        }
    } catch(e) { showToast('Ошибка сети'); }
}

async function bjStand() {
    const uid = effectiveUid();
    const hitBtn = $('bj-hit-btn'); const stndBtn = $('bj-stand-btn');
    if (hitBtn)  { hitBtn.disabled  = true; hitBtn.style.opacity  = '.5'; }
    if (stndBtn) { stndBtn.disabled = true; stndBtn.style.opacity = '.5'; }
    haptic('medium');
    try {
        const res = await api('/api/bj_stand', { method: 'POST', body: { user_id: uid } });
        if (!res || res.error) { showToast(res?.error || 'Ошибка'); return; }
        const dc = $('bj-dealer-cards');
        if (dc) {
            dc.innerHTML = '';
            await new Promise(r => setTimeout(r, 100));
            for (const [rank, suit] of res.dealer_cards) {
                dc.appendChild(_makeCard(rank, suit));
                await new Promise(r => setTimeout(r, 250));
            }
        }
        if (res.result === 'win') { haptic('heavy'); fireConfetti(50); }
        else if (res.result === 'lose') haptic('light');
        const oldBjBal = _bjBal;
        _bjBal = res.new_balance;
        animateCounter('games-balance', oldBjBal, _bjBal, 800);
        if (S.user) S.user.coins = _bjBal;
        _bjShowResult(res);
        _bjInGame = false;
        if (res.result === 'win') _startBJCd(BJ_CD_S);
    } catch(e) { showToast('Ошибка сети'); }
}

function _bjShowResult(res) {
    const el = $('bj-result'); if (!el) return;
    const icons  = { win:'🟢', lose:'🔴', tie:'🟡', bust:'💥', blackjack:'🎉' };
    const labels = { win:'ПОБЕДА!', lose:'ПРОИГРЫШ', tie:'НИЧЬЯ', bust:'ПЕРЕБОР!', blackjack:'БЛЭКДЖЕК!' };
    const icon   = icons[res.result]  || '?';
    const label  = labels[res.result] || res.result;
    const profit = res.profit;
    const pColor = profit > 0 ? '#4ade80' : profit < 0 ? '#ff4b4b' : '#ffd700';
    const pSign  = profit >= 0 ? '+' : '';
    el.style.display = 'block';
    el.innerHTML = `
      <div class="game-result-card" style="border-color:${pColor}33">
        <div style="font-size:22px;font-weight:900;margin-bottom:8px">${icon} ${label}</div>
        ${res.player_value !== undefined ? `<div style="font-size:12px;color:var(--text-sub);margin-bottom:6px">Вы: <b>${res.player_value}</b>${res.dealer_value !== undefined ? ` / Дилер: <b>${res.dealer_value}</b>` : ''}</div>` : ''}
        <div style="font-size:26px;font-weight:900;color:${pColor};margin-bottom:12px">${pSign}${profit.toLocaleString('ru-RU')} 💰</div>
        <button onclick="renderBJ()" class="qb-btn" style="width:100%">🔄 Ещё раз</button>
      </div>`;
    const tbl = $('bj-table'); const sp = $('bj-start-panel');
    if (tbl && el.parentNode !== tbl) tbl.appendChild(el);
}

function _startBJCd(sec) {
    const wrap = $('bj-cd-wrap'); const btn = $('bj-deal-btn');
    const bar  = $('bj-cd-bar');  const txt  = $('bj-cd-text');
    if (wrap) wrap.style.display = 'block';
    if (btn)  { btn.disabled = true; btn.style.opacity = '.5'; }
    let remaining = sec; const total = 180;
    const update  = () => {
        if (remaining <= 0) { _stopBJCd(); return; }
        const m = Math.floor(remaining/60), s = remaining%60;
        if (txt) txt.textContent = `${m}:${s.toString().padStart(2,'0')}`;
        if (bar) bar.style.width = `${(remaining/total)*100}%`;
        remaining--;
    };
    update();
    if (_bjCdInterval) clearInterval(_bjCdInterval);
    _bjCdInterval = setInterval(update, 1000);
}

function _stopBJCd() {
    if (_bjCdInterval) { clearInterval(_bjCdInterval); _bjCdInterval = null; }
    const wrap = $('bj-cd-wrap'); const btn = $('bj-deal-btn');
    const tbl  = $('bj-table');   const sp  = $('bj-start-panel');
    if (wrap) wrap.style.display = 'none';
    if (btn)  { btn.disabled = false; btn.style.opacity = '1'; }
    if (tbl)  tbl.style.display  = 'none';
    if (sp)   sp.style.display   = 'flex';
    const bjRes = $('bj-result'); if (bjRes) bjRes.style.display = 'none';
}

// ══════════════════════════════════════════════════════════════════
// 🎯 GUESS
// ══════════════════════════════════════════════════════════════════
const GUESS_CD_S = 180;
const GUESS_FACES = ['', '⚀','⚁','⚂','⚃','⚄','⚅'];
let _guessCdInterval = null;
// Avoid temporal-dead-zone crashes in Telegram's inline button handlers.
var _guessBal = 0;
var _guessNum = 0;

async function renderGuess() {
    const uid = effectiveUid();
    try {
        const st = await api(`/api/guess_status?user_id=${uid}`);
        if (st) {
            _guessBal = st.balance || 0;
            setText('games-balance', _guessBal.toLocaleString('ru-RU'));
            setText('games-spins',   st.spins || 0);
            if (st.cooldown_remaining > 0) _startGuessCd(st.cooldown_remaining);
            else _stopGuessCd();
        }
    } catch(e) {}
}

function setGuessNum(n) {
    haptic('light');
    _guessNum = n;
    for (let i=1; i<=6; i++) {
        const btn = $(`gn-${i}`);
        if (btn) btn.classList.toggle('active', i === n);
    }
    const anim = $('guess-dice-anim');
    if (anim) anim.textContent = GUESS_FACES[n];
}

function setGuessBet(v) {
    haptic('light');
    const inp = $('guess-bet-input');
    if (!inp) return;
    inp.value = v === -1 ? Math.min(_guessBal, 50000) : v;
}

async function playGuess() {
    const uid = effectiveUid();
    if (!_guessNum) { showToast('Выбери число 1–6'); return; }
    const bet = parseInt($('guess-bet-input')?.value || 0);
    if (!bet || bet < 100) { showToast('Введи ставку (мин 100)'); return; }
    const btn = $('guess-play-btn');
    if (btn) { btn.disabled = true; btn.style.opacity = '.5'; }
    haptic('medium');

    // Dice roll animation
    const anim = $('guess-dice-anim');
    if (anim) anim.classList.add('dice-rolling');
    // Never leave the interface spinning forever while a mobile connection
    // waits for the API response.
    const stopRolling = () => {
        if (anim) anim.classList.remove('dice-rolling');
    };
    const rollSafetyTimeout = setTimeout(stopRolling, 4500);

    try {
        const res = await api('/api/guess_bet', { method: 'POST', body: { user_id: uid, bet, guess: _guessNum } });
        if (!res || res.error) {
            clearTimeout(rollSafetyTimeout);
            stopRolling();
            if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
            if (res?.error === 'cooldown') _startGuessCd(res.remaining);
            else showToast(res?.error || 'Ошибка');
            return;
        }

        // Spin through faces, then land on result
        let spins = 0; const totalSpins = 14;
        await new Promise(resolve => {
            const iv = setInterval(() => {
                spins++;
                if (anim) anim.textContent = GUESS_FACES[((spins-1) % 6) + 1];
                if (spins >= totalSpins) {
                    clearInterval(iv);
                    clearTimeout(rollSafetyTimeout);
                    if (anim) { stopRolling(); anim.textContent = GUESS_FACES[res.result]; }
                    resolve();
                }
            }, 120);
        });

        await new Promise(r => setTimeout(r, 200));
        if (res.correct) { haptic('heavy'); fireConfetti(60); } else haptic('light');

        const profit = res.profit;
        const pColor = profit > 0 ? '#4ade80' : '#ff4b4b';
        const pSign  = profit >= 0 ? '+' : '';
        const guessRes = $('guess-result');
        if (guessRes) {
            guessRes.style.display = 'block';
            guessRes.innerHTML = `
              <div class="game-result-card" style="border-color:${pColor}33">
                <div style="font-size:24px;font-weight:900;margin-bottom:8px">${res.correct ? '🎯 УГАДАЛ!' : '❌ Промах'}</div>
                <div style="font-size:12px;color:var(--text-sub);margin-bottom:6px">Твой выбор: <b>${_guessNum}</b>  •  Выпало: <b>${GUESS_FACES[res.result]} ${res.result}</b></div>
                <div style="font-size:26px;font-weight:900;color:${pColor}">${pSign}${profit.toLocaleString('ru-RU')} 💰</div>
                <div style="font-size:11px;color:var(--text-sub);margin-top:4px">Баланс: <b style="color:var(--text)">${res.new_balance.toLocaleString('ru-RU')}</b></div>
              </div>`;
        }

        const oldGuessBal = _guessBal;
        _guessBal = res.new_balance;
        animateCounter('games-balance', oldGuessBal, _guessBal, 800);
        if (S.user) { S.user.coins = res.new_balance; setText('h-coins', res.new_balance.toLocaleString('ru-RU')); }

        // Reset selection
        _guessNum = 0;
        for (let i=1; i<=6; i++) { const b=$(`gn-${i}`); if(b) b.classList.remove('active'); }
        if (anim) anim.textContent = '🎲';

        if (res.correct) _startGuessCd(GUESS_CD_S);
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }

    } catch(e) {
        clearTimeout(rollSafetyTimeout);
        stopRolling();
        if (btn) { btn.disabled = false; btn.style.opacity = '1'; }
        const message = e?.name === 'AbortError'
            ? 'Сервер долго отвечает. Попробуй ещё раз'
            : String(e?.message || 'Ошибка сети').replace(/^HTTP \d+\s*/i, '');
        showToast(message.slice(0, 90));
    }
}

function _startGuessCd(sec) {
    const wrap = $('guess-cd-wrap'); const btn = $('guess-play-btn');
    const bar  = $('guess-cd-bar');  const txt  = $('guess-cd-text');
    if (wrap) wrap.style.display = 'block';
    if (btn)  { btn.disabled = true; btn.style.opacity = '.5'; }
    let remaining = sec; const total = GUESS_CD_S;
    const update  = () => {
        if (remaining <= 0) { _stopGuessCd(); return; }
        const m = Math.floor(remaining/60), s = remaining%60;
        if (txt) txt.textContent = `${m}:${s.toString().padStart(2,'0')}`;
        if (bar) bar.style.width = `${(remaining/total)*100}%`;
        remaining--;
    };
    update();
    if (_guessCdInterval) clearInterval(_guessCdInterval);
    _guessCdInterval = setInterval(update, 1000);
}

function _stopGuessCd() {
    if (_guessCdInterval) { clearInterval(_guessCdInterval); _guessCdInterval = null; }
    const wrap = $('guess-cd-wrap'); const btn = $('guess-play-btn');
    if (wrap) wrap.style.display = 'none';
    if (btn)  { btn.disabled = false; btn.style.opacity = '1'; }
}

window.spinSlots  = spinSlots;  window.setSlotsBet = setSlotsBet;
window.playGuess  = playGuess;  window.setGuessBet = setGuessBet; window.setGuessNum = setGuessNum;

// blackjack / roulette removed from UI

/* roulette code kept inert below — not registered */

// ══════════════════════════════════════════════════════════════
//  🎡  ROULETTE
// ══════════════════════════════════════════════════════════════
const RL_RED   = new Set([1,3,5,7,9,12,14,16,18,19,21,23,25,27,30,32,34,36]);
const RL_WHEEL = [0,32,15,19,4,21,2,25,17,34,6,27,13,36,11,30,8,23,10,5,24,16,33,1,20,14,31,9,22,18,29,7,28,12,35,3,26];
const RL_CHIPS = [
  {val:10,   bg:'#2a2a2a', border:'#7a7a7a'},
  {val:50,   bg:'#0c2461', border:'#4a80c9'},
  {val:100,  bg:'#0b3d21', border:'#2ecc71'},
  {val:500,  bg:'#5c1a1a', border:'#c0392b'},
  {val:1000, bg:'#1a1a2e', border:'#b8b8d0'},
  {val:5000, bg:'#4a3000', border:'#c9a84c'},
];
let _rlChip      = 100;
let _rlBets      = {};        // key → {type, numbers, amount}
let _rlHistory   = [];        // undo stack [{key, prev}]
let _rlLastBets  = null;      // for rebet
let _rlSpinning  = false;
let _rlCdTimer   = null;
let _rlRot       = 0;
let _rlInited    = false;
let _rlBal       = 0;

// ── Init ──────────────────────────────────────────────────────
function rlInit() {
    if (_rlInited) return;
    _rlInited = true;
    _rlBuildWheel();
    _rlBuildTable();
    _rlBuildChips();
}

// ── Wheel SVG (premium) ───────────────────────────────────────
function _rlBuildWheel() {
    const svg = document.getElementById('rl-wheel-svg');
    if (!svg) return;
    const Ro = 104, Ri = 37, N = 37, step = (2 * Math.PI) / N;
    const pt = (a, r) => [r * Math.cos(a), r * Math.sin(a)];

    const defs = `<defs>
      <radialGradient id="rlgRim" cx="50%" cy="50%" r="50%">
        <stop offset="60%"  stop-color="#1a0e00"/>
        <stop offset="100%" stop-color="#5c3a00"/>
      </radialGradient>
      <radialGradient id="rlgHub" cx="50%" cy="50%" r="50%">
        <stop offset="0%"   stop-color="#2d0050"/>
        <stop offset="100%" stop-color="#080012"/>
      </radialGradient>
      <radialGradient id="rlgRed" cx="50%" cy="30%" r="80%">
        <stop offset="0%"   stop-color="#c53030"/>
        <stop offset="100%" stop-color="#7f1d1d"/>
      </radialGradient>
      <radialGradient id="rlgBlk" cx="50%" cy="30%" r="80%">
        <stop offset="0%"   stop-color="#2a2a3a"/>
        <stop offset="100%" stop-color="#0f0f18"/>
      </radialGradient>
      <radialGradient id="rlgGrn" cx="50%" cy="30%" r="80%">
        <stop offset="0%"   stop-color="#1a7a3a"/>
        <stop offset="100%" stop-color="#0f4a22"/>
      </radialGradient>
      <filter id="rlglow">
        <feGaussianBlur stdDeviation="1.5" result="blur"/>
        <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
      </filter>
    </defs>`;

    let h = '';

    // ① Outer mahogany/gold rim
    h += `<circle cx="0" cy="0" r="111" fill="url(#rlgRim)"/>`;
    h += `<circle cx="0" cy="0" r="111" fill="none" stroke="#c9a84c" stroke-width="2"/>`;
    h += `<circle cx="0" cy="0" r="107" fill="none" stroke="rgba(201,168,76,.35)" stroke-width=".7"/>`;
    h += `<circle cx="0" cy="0" r="105.5" fill="#060010" stroke="#c9a84c" stroke-width="1.2"/>`;

    // ② Number sectors
    for (let i = 0; i < N; i++) {
        const n  = RL_WHEEL[i];
        const a1 = i * step - Math.PI / 2;
        const a2 = (i + 1) * step - Math.PI / 2;
        const [ox1,oy1] = pt(a1, Ro); const [ox2,oy2] = pt(a2, Ro);
        const [ix2,iy2] = pt(a2, Ri); const [ix1,iy1] = pt(a1, Ri);
        const large = 0;
        const fillId = n === 0 ? 'url(#rlgGrn)' : RL_RED.has(n) ? 'url(#rlgRed)' : 'url(#rlgBlk)';
        const numCol = n === 0 ? '#86efac' : '#ffffff';
        h += `<path d="M${ox1},${oy1}A${Ro},${Ro},0,${large},1,${ox2},${oy2}L${ix2},${iy2}A${Ri},${Ri},0,${large},0,${ix1},${iy1}Z" fill="${fillId}" stroke="#000" stroke-width="1.2"/>`;
        const ma = (a1 + a2) / 2;
        const tr = Ri + (Ro - Ri) * 0.54;
        const [tx, ty] = pt(ma, tr);
        const rot = ma * 180 / Math.PI + 90;
        h += `<text x="${tx}" y="${ty}" text-anchor="middle" dominant-baseline="middle" font-size="6.2" fill="${numCol}" font-weight="900" font-family="Georgia,serif" transform="rotate(${rot},${tx},${ty})">${n}</text>`;
    }

    // ③ Gold fret separators between every sector + outer diamond pins
    for (let i = 0; i < N; i++) {
        const a = i * step - Math.PI / 2;
        const [x1, y1] = pt(a, Ri);
        const [x2, y2] = pt(a, Ro + 0.5);
        h += `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="#c9a84c" stroke-width=".8" opacity=".85"/>`;
        // Diamond pin just outside sector at outer ring
        const [px, py] = pt(a, Ro + 2);
        const sz = 1.8;
        h += `<polygon points="${px},${py-sz} ${px+sz},${py} ${px},${py+sz} ${px-sz},${py}" fill="#ffd700" transform="rotate(${a*180/Math.PI+90},${px},${py})" opacity=".9"/>`;
    }

    // ④ Alternating rim dots (every 2 sectors, centred between frets)
    for (let i = 0; i < N; i += 2) {
        const a = (i + 1) * step - Math.PI / 2;
        const [dx, dy] = pt(a, 108.5);
        h += `<circle cx="${dx}" cy="${dy}" r="1.6" fill="#c9a84c" opacity=".55"/>`;
    }

    // ⑤ Inner cone / hub
    h += `<circle cx="0" cy="0" r="${Ri}" fill="url(#rlgHub)" stroke="#c9a84c" stroke-width="1.6"/>`;
    h += `<circle cx="0" cy="0" r="${Ri-6}" fill="none" stroke="rgba(201,168,76,.45)" stroke-width=".9" stroke-dasharray="3 2"/>`;
    h += `<circle cx="0" cy="0" r="${Ri-12}" fill="none" stroke="rgba(168,85,247,.35)" stroke-width=".7"/>`;

    // ⑥ Centre spindle (gold + dark)
    h += `<circle cx="0" cy="0" r="14" fill="#c9a84c" stroke="#5c3a00" stroke-width="1"/>`;
    h += `<circle cx="0" cy="0" r="9.5" fill="#0a0015"/>`;
    h += `<circle cx="0" cy="0" r="5"   fill="#c9a84c"/>`;
    h += `<circle cx="0" cy="0" r="2.5" fill="#080014"/>`;
    h += `<circle cx="0" cy="0" r="1"   fill="#c9a84c"/>`;

    svg.innerHTML = defs + `<g id="rl-wg">${h}</g>`;
}

// ── Table builder ─────────────────────────────────────────────
function _rlBuildTable() {
    const tbl = document.getElementById('rl-table');
    if (!tbl) return;
    const low  = Array.from({length:18}, (_,i) => i+1);
    const high = Array.from({length:18}, (_,i) => i+19);
    const even = [2,4,6,8,10,12,14,16,18,20,22,24,26,28,30,32,34,36];
    const odd  = [1,3,5,7,9,11,13,15,17,19,21,23,25,27,29,31,33,35];
    const red  = [...RL_RED].sort((a,b)=>a-b);
    const blk  = [2,4,6,8,10,11,13,15,17,20,22,24,26,28,29,31,33,35];
    const dz1  = Array.from({length:12}, (_,i) => i+1);
    const dz2  = Array.from({length:12}, (_,i) => i+13);
    const dz3  = Array.from({length:12}, (_,i) => i+25);
    const col1 = [3,6,9,12,15,18,21,24,27,30,33,36];
    const col2 = [2,5,8,11,14,17,20,23,26,29,32,35];
    const col3 = [1,4,7,10,13,16,19,22,25,28,31,34];

    const key = ns => ns.slice().sort((a,b)=>a-b).join(',');
    const numClass = n => n===0?'rl-zero':RL_RED.has(n)?'rl-red':'rl-black';
    const numBtn = n => {
        const k = key([n]);
        return `<div class="rl-num-cell ${numClass(n)}" data-betkey="${k}" onclick="rlBet('straight',[${n}])"><span>${n}</span></div>`;
    };
    const colBtn = (ns, label) => {
        const k = key(ns);
        return `<div class="rl-col-btn" data-betkey="${k}" onclick="rlBet('column',${JSON.stringify(ns)})">${label}</div>`;
    };
    const dznBtn = (ns, label) => {
        const k = key(ns);
        return `<div class="rl-dozen-btn" data-betkey="${k}" onclick="rlBet('dozen',${JSON.stringify(ns)})" style="flex:1">${label}</div>`;
    };
    const outBtn = (ns, label, cls='') => {
        const k = key(ns);
        return `<div class="rl-out-btn ${cls}" data-betkey="${k}" onclick="rlBet('outside',${JSON.stringify(ns)})" style="flex:1">${label}</div>`;
    };

    // Row top (3,6...36), mid (2,5...35), bot (1,4...34) + col 2:1
    let topRow='', midRow='', botRow='';
    for (let c=1; c<=12; c++) {
        topRow += numBtn(c*3);
        midRow += numBtn(c*3-1);
        botRow += numBtn(c*3-2);
    }
    topRow += colBtn(col1,'2:1');
    midRow += colBtn(col2,'2:1');
    botRow += colBtn(col3,'2:1');

    tbl.innerHTML = `
      <div class="rl-tbl-main" style="display:flex;gap:3px">
        <div class="rl-zero-cell" data-betkey="0" onclick="rlBet('straight',[0])" style="writing-mode:vertical-rl;padding:6px 0">0</div>
        <div style="display:flex;flex-direction:column;gap:3px;flex:1">
          <div class="rl-tbl-row">${topRow}</div>
          <div class="rl-tbl-row">${midRow}</div>
          <div class="rl-tbl-row">${botRow}</div>
        </div>
      </div>
      <div class="rl-tbl-row" style="margin-top:3px;padding-left:39px">
        ${dznBtn(dz1,'1–12')} ${dznBtn(dz2,'13–24')} ${dznBtn(dz3,'25–36')}
      </div>
      <div class="rl-tbl-row" style="margin-top:3px;padding-left:39px">
        ${outBtn(low,'1–18')} ${outBtn(even,'ЧЁТ')} ${outBtn(red,'RED','rl-red')}
        ${outBtn(blk,'BLACK','rl-black')} ${outBtn(odd,'НЕЧЁТ')} ${outBtn(high,'19–36')}
      </div>`;
}

// ── Chips ─────────────────────────────────────────────────────
function _rlBuildChips() {
    const bar = document.getElementById('rl-chips-bar');
    if (!bar) return;
    bar.innerHTML = RL_CHIPS.map(c => {
        const sel = _rlChip === c.val ? ' rl-chip-sel' : '';
        const lbl = c.val >= 1000 ? c.val/1000+'K' : c.val;
        return `<button class="rl-chip${sel}" onclick="rlSelectChip(${c.val})"
          style="background:radial-gradient(circle,${c.bg}dd 0%,${c.bg}88 100%);border-color:${c.border};box-shadow:0 0 8px ${c.border}44">${lbl}</button>`;
    }).join('');
}

function rlSelectChip(val) {
    haptic('light'); _rlChip = val; _rlBuildChips();
}

// ── Bet placement ─────────────────────────────────────────────
function rlBet(type, numbers) {
    if (_rlSpinning) return;
    haptic('light');
    const k = numbers.slice().sort((a,b)=>a-b).join(',');
    if (!_rlBets[k]) _rlBets[k] = {type, numbers, amount: 0};
    _rlHistory.push({k, prev: _rlBets[k].amount});
    _rlBets[k].amount += _rlChip;
    _rlRefreshMarkers();
    _rlRefreshTotals();
}

function _rlRefreshMarkers() {
    document.querySelectorAll('.rl-chip-marker').forEach(e => e.remove());
    for (const [k, bet] of Object.entries(_rlBets)) {
        if (!bet.amount) continue;
        const el = document.querySelector(`[data-betkey="${k}"]`);
        if (!el) continue;
        const m = document.createElement('div');
        m.className = 'rl-chip-marker';
        m.textContent = bet.amount >= 1000 ? Math.round(bet.amount/1000)+'K' : bet.amount;
        el.appendChild(m);
    }
}

function _rlRefreshTotals() {
    const total = Object.values(_rlBets).reduce((s,b) => s + b.amount, 0);
    setText('rl-total-bet', total.toLocaleString('ru-RU'));
    const payouts = {1:35,2:17,3:11,4:8,6:5,12:2,18:1};
    let maxWin = 0;
    for (let n = 0; n <= 36; n++) {
        let w = 0;
        for (const b of Object.values(_rlBets)) {
            if (b.numbers.includes(n)) w += b.amount * ((payouts[b.numbers.length]||0) + 1);
        }
        if (w > maxWin) maxWin = w;
    }
    setText('rl-potential-win', maxWin.toLocaleString('ru-RU'));
}

// ── Undo / Clear / Double / Rebet ─────────────────────────────
function rlUndo() {
    haptic('light');
    if (!_rlHistory.length) return;
    const {k, prev} = _rlHistory.pop();
    if (_rlBets[k]) _rlBets[k].amount = prev;
    _rlRefreshMarkers(); _rlRefreshTotals();
}

function rlClear() {
    haptic('light');
    _rlBets = {}; _rlHistory = [];
    _rlRefreshMarkers(); _rlRefreshTotals();
}

function rlDouble() {
    haptic('medium');
    const total = Object.values(_rlBets).reduce((s,b) => s + b.amount, 0);
    if (!total) return;
    if (total * 2 > _rlBal) { showToast('Недостаточно рекоинов'); return; }
    _rlHistory = [];
    for (const k of Object.keys(_rlBets)) _rlBets[k].amount *= 2;
    _rlRefreshMarkers(); _rlRefreshTotals();
}

function rlRebet() {
    haptic('light');
    if (!_rlLastBets) { showToast('Нет предыдущих ставок'); return; }
    _rlBets = {}; _rlHistory = [];
    for (const [k, b] of Object.entries(_rlLastBets)) {
        _rlBets[k] = {type: b.type, numbers: b.numbers.slice(), amount: b.amount};
    }
    _rlRefreshMarkers(); _rlRefreshTotals();
}

// ── Spin ─────────────────────────────────────────────────────
async function rlSpin() {
    if (_rlSpinning) return;
    const total = Object.values(_rlBets).reduce((s,b) => s + b.amount, 0);
    if (!total) { showToast('Сделайте ставку!'); return; }
    const uid = effectiveUid();
    const btn = document.getElementById('rl-spin-btn');
    _rlSpinning = true;
    if (btn) { btn.disabled = true; }
    haptic('medium');

    // Phase 1: start free spin immediately (5 full rotations over 2s)
    const wheel = document.getElementById('rl-wheel');
    if (wheel) {
        _rlRot += 360 * 5;
        wheel.style.transition = 'transform 2s linear';
        wheel.style.transform  = `rotate(${_rlRot}deg)`;
    }

    // Save bets for rebet before clearing
    _rlLastBets = {};
    for (const [k, b] of Object.entries(_rlBets)) {
        _rlLastBets[k] = {type: b.type, numbers: b.numbers.slice(), amount: b.amount};
    }

    try {
        const betsArr = Object.values(_rlBets)
            .filter(b => b.amount > 0)
            .map(b => ({type: b.type, numbers: b.numbers, amount: b.amount}));

        const res = await api('/api/rl_spin', {method: 'POST', body: {user_id: uid, bets: betsArr}});

        if (!res || res.error) {
            if (res?.error === 'cooldown') _rlStartCd(res.remaining);
            else showToast(res?.error || 'Ошибка');
            _rlSpinning = false;
            if (btn) btn.disabled = false;
            if (wheel) { wheel.style.transition = ''; }
            return;
        }

        // Phase 2: snap to winning number (decelerate over 3s)
        const idx = RL_WHEEL.indexOf(res.win_num);
        const step = 360 / 37;
        const targetDeg = 360 - (idx * step + step / 2);
        const currentMod = _rlRot % 360;
        let delta = ((targetDeg - currentMod) + 360) % 360;
        if (delta < 20) delta += 360;
        _rlRot += delta + 360 * 2;
        if (wheel) {
            wheel.style.transition = 'transform 3s cubic-bezier(0.17,0.67,0.12,1.0)';
            wheel.style.transform  = `rotate(${_rlRot}deg)`;
        }

        await new Promise(r => setTimeout(r, 3200));
        haptic('heavy');

        // Show result
        const numEl = document.getElementById('rl-win-num');
        if (numEl) {
            numEl.textContent = res.win_num;
            numEl.className = `rl-win-num rl-col-${res.color}`;
        }

        // Highlight winning cells
        document.querySelectorAll('.rl-num-cell,.rl-zero-cell').forEach(el => el.classList.remove('rl-won'));
        const wonCell = document.querySelector(`[data-betkey="${res.win_num}"]`);
        if (wonCell) wonCell.classList.add('rl-won');

        // Toast result
        const icon = res.color === 'red' ? '🔴' : res.color === 'black' ? '⚫' : '🟢';
        const sign = res.profit >= 0 ? '+' : '';
        showToast(`${icon} ${res.win_num} → ${sign}${res.profit.toLocaleString('ru-RU')} 💰`);
        if (res.profit > 0) fireConfetti(50);

        // Update balance
        const old = _rlBal;
        _rlBal = res.new_balance;
        animateCounter('games-balance', old, _rlBal, 800);
        if (S.user) { S.user.coins = _rlBal; setText('h-coins', _rlBal.toLocaleString('ru-RU')); }

        // Add to history
        const hist = document.getElementById('rl-history');
        if (hist) {
            const d = document.createElement('div');
            d.className = `rl-hist-num rl-col-${res.color}`;
            d.textContent = res.win_num;
            hist.prepend(d);
            if (hist.children.length > 20) hist.removeChild(hist.lastChild);
        }

        rlClear();
        if (res.cooldown_until > 0) {
            _rlStartCd(Math.max(1, Math.round(res.cooldown_until - Date.now() / 1000)));
        }

    } catch(e) {
        showToast('Ошибка сети');
    } finally {
        _rlSpinning = false;
        if (btn) btn.disabled = false;
    }
}

// ── Cooldown ─────────────────────────────────────────────────
function _rlStartCd(sec) {
    const wrap = document.getElementById('rl-cd-wrap');
    const btn  = document.getElementById('rl-spin-btn');
    if (wrap) wrap.style.display = 'block';
    if (btn)  btn.disabled = true;
    let rem = sec; const tot = sec;
    const tick = () => {
        if (rem <= 0) { _rlStopCd(); return; }
        const m = Math.floor(rem/60), s = rem % 60;
        setText('rl-cd-text', `${m}:${s.toString().padStart(2,'0')}`);
        const bar = document.getElementById('rl-cd-bar');
        if (bar) bar.style.width = `${(rem/tot)*100}%`;
        rem--;
    };
    tick();
    if (_rlCdTimer) clearInterval(_rlCdTimer);
    _rlCdTimer = setInterval(tick, 1000);
}

function _rlStopCd() {
    if (_rlCdTimer) { clearInterval(_rlCdTimer); _rlCdTimer = null; }
    const wrap = document.getElementById('rl-cd-wrap');
    const btn  = document.getElementById('rl-spin-btn');
    if (wrap) wrap.style.display = 'none';
    if (btn)  btn.disabled = false;
}

// ── Render (called on tab switch) ────────────────────────────
async function renderRL() {
    rlInit();
    const uid = effectiveUid();
    try {
        const st = await api(`/api/rl_status?user_id=${uid}`);
        if (!st) return;
        _rlBal = st.balance || 0;
        setText('games-balance', _rlBal.toLocaleString('ru-RU'));
        if (st.cooldown > 0) _rlStartCd(st.cooldown);
        else _rlStopCd();
    } catch(e) {}
}

// Register in selectGame renderer map and expose globals
// roulette removed
// _gameRenderers['rl'] = renderRL;
window.rlSelectChip = rlSelectChip;
window.rlBet    = rlBet;
window.rlUndo   = rlUndo;
window.rlClear  = rlClear;
window.rlDouble = rlDouble;
window.rlRebet  = rlRebet;
window.rlSpin   = rlSpin;

// HTML onclick=... видит только window.* — после esbuild IIFE без этого кнопки «мертвые»
try {
    Object.assign(window, {
        switchTab,
        openUniverseGate,
        closeUniverseGate,
        botAction,
        closeModal,
        toggleTheme,
        loginWithId,
        loginDemo,
        openCardModal,
        changeCardsPage,
        openAnotherCard,
        openPlayerProfile,
        closePlayerProfile,
        claimTask,
        toggleSlot,
        eliteSpin,
        playPackReveal,
        closePackReveal,
        switchShopSubTab,
        openShopCreatHub,
        setThemeSort,
        setThemeTypeFilter,
        setThemeOwnedFilter,
        themeBuy,
        themeApply,
        themeReset,
        themeUpload,
        buyStarsPack,
        clanExchange,
        switchClanTab,
        clanChatSend,
        clanTreasuryDeposit,
        clanTreasuryWithdraw,
        setTreasuryDeposit,
        setTreasuryWithdraw,
        switchTradeTab,
        handleOfferSearch,
        handleWantSearch,
        selectOfferCard,
        selectWantCard,
        clearOfferSelection,
        clearWantSelection,
        clanCreateTrade,
        clanCancelTrade,
        clanAcceptTrade,
        clanSettingsSave,
        clanAdminKick,
        clanAdminPromote,
        openClanSettings,
        clanWarAttack,
        clanWarSurrender,
        clanFindWarOpponent,
        selectWarTarget,
        openWarRoster,
        copyRefLink,
        shareRefLink,
        createRefCampaign,
        copyCampaignLink,
        deleteRefCampaign,
        setRefVerse,
        setClanBrowseKind,
        filterClanBrowse,
        joinClanFromHub,
        selectGame,
        rollDice,
        setDiceBet,
        spinSlots,
        setSlotsBet,
        playGuess,
        setGuessBet,
        setGuessNum,
        bjStart,
        bjHit,
        bjStand,
        setBJBet,
        arenaFindOpponent,
        arenaShowStats,
        arenaShowReplays,
        arenaBackToLobby,
        arenaSkipAuto,
        arenaLoadReplay,
        resumeActiveBattle,
        selectSkill,
        onUnitClicked,
        toggleBattleLog,
        renderArenaTeamPreview,
    });
} catch (e) {
    console.error('[BOOT] window export failed', e);
}
